// UsedFindr JavaScript

// Side-effect import: ensures mobile menu (hamburger / sidebar toggle) is in the bundle and runs
import './mobile-menu.js';

// Import dependencies
import { apiService } from './api-service.js';
import { analyticsManager } from './analytics.js';
import { categoryManager } from './categories.js';
import { getCategoryIcon, getCategoryIconSvg, injectCategoryIconSprite } from './category-icon-resolver.js';
import { consentManager } from './consent-manager.js';
import {
  initializeCookielessPageAnalytics,
  postOutboundClick,
} from './cookieless-page-analytics.js';
import { showOutboundNotice } from './external-link-notice.js';
import { attachModalA11y, detachModalA11y } from './a11y-modal.js';
import { showFormError, clearFormError } from './a11y-form.js';
import { DEFAULT_INTENT, TRUSTED_PRESETS, parseInput } from './trusted-query-parser.js';
import { FLOOR_NAMES, clears as sellerClears, getFloor, onFloorChange, thresholds as floorThresholds } from './floor-state.js';
import { getView, onViewChange } from './console-view.js';
import { grid as consoleGrid, rows as consoleRows, toCard, priceDelta, ringHtml } from './console-cards.js';
import { getSort, onSortChange, sortCards, toggleSort, setSort } from './console-sort.js';
import { getFloorCounts, paintFeedStats, setScopedFloorCounts } from './console-stats.js';
import {
  emptyFloorHtml as consoleEmptyFloorHtml,
  emptyQueryHtml as consoleEmptyQueryHtml,
  skeletonGrid as consoleSkeletonGrid,
} from './console-states.js';
import { watchlistHtml as consoleWatchlistHtml } from './console-watchlist.js';
import { initNotificationsInbox, refreshNotificationBadge, migrateLocalCategoryFollows } from './notifications-inbox.js';

// Inject category icon sprite early so condition cards and filters render without flash
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => injectCategoryIconSprite());
  } else {
    injectCategoryIconSprite();
  }
}

// Make imports available on window for backward compatibility
if (typeof window !== 'undefined') {
  window.apiService = apiService;
  window.analyticsManager = analyticsManager;
  window.categoryManager = categoryManager;
  window.consentManager = consentManager;
}

// Initialize logger for this module
export const logger = window.Logger ? window.Logger.create('script') : {
  debug: () => {},
  info: () => {},
  warn: () => {},
  error: console.error.bind(console)
};

/**
 * AdminManager's chunk, requested at module-evaluation time on admin pages only.
 *
 * The class lives in its own module so marketing pages never download it. Kicking
 * the fetch off here rather than inside the DOMContentLoaded handler means the
 * chunk is already in flight by the time that handler awaits it, so the admin
 * console starts no later than it did when the class was inlined.
 *
 * Exposed as window.adminManagerReady for anything that needs to wait for the
 * instance rather than poll for window.adminManager.
 */
const adminManagerModule =
  typeof window !== 'undefined' && window.location.pathname.includes('/admin')
    ? import('./admin-manager.js')
    : null;

// Default condition list for fallback when condition-policies API is not used or fails (eBay US condition IDs)
const DEFAULT_CONDITION_PILLS = [
  { conditionId: '1000', conditionDescription: 'New' },
  { conditionId: '1500', conditionDescription: 'Open Box' },
  { conditionId: '2000', conditionDescription: 'Certified Refurbished' },
  { conditionId: '2010', conditionDescription: 'eBay Refurbished - Excellent' },
  { conditionId: '2020', conditionDescription: 'eBay Refurbished - Very Good' },
  { conditionId: '2030', conditionDescription: 'eBay Refurbished - Good' },
  { conditionId: '2500', conditionDescription: 'Seller refurbished / Remanufactured' },
  { conditionId: '2750', conditionDescription: 'Like New' },
  { conditionId: '3000', conditionDescription: 'Used' },
  { conditionId: '4000', conditionDescription: 'Very Good' },
  { conditionId: '5000', conditionDescription: 'Good' },
  { conditionId: '6000', conditionDescription: 'Acceptable' },
  { conditionId: '7000', conditionDescription: 'For Parts or not working' }
];

/**
 * Fetch condition policies for an eBay category. Returns conditions array or null on error/empty.
 * @param {string} categoryId - eBay category ID
 * @param {string} [marketplaceId] - Marketplace ID (default EBAY_US)
 * @returns {Promise<Array<{conditionId: string, conditionDescription: string}>|null>}
 */
const conditionPolicyCache = new Map();

async function fetchConditionPolicies(categoryId, marketplaceId = 'EBAY_US') {
  if (!categoryId || !window.apiService) return null;
  const cacheKey = `${categoryId}_${marketplaceId}`;
  if (conditionPolicyCache.has(cacheKey)) {
    return conditionPolicyCache.get(cacheKey);
  }
  try {
    const url = `/products/ebay/condition-policies/?category_id=${encodeURIComponent(categoryId)}&marketplace_id=${encodeURIComponent(marketplaceId)}`;
    const data = await window.apiService.get(url);
    const conditions = data && Array.isArray(data.conditions) ? data.conditions : null;
    const result = conditions && conditions.length > 0 ? conditions : null;
    if (result) conditionPolicyCache.set(cacheKey, result);
    return result;
  } catch (e) {
    logger.debug('[fetchConditionPolicies] failed, using fallback:', e);
    return null;
  }
}

// eBay refurbished condition IDs - any of these should match when selecting a refurbished pill.
// Resolve API may return 2030 (Good - Refurbished) but category API may only have 2500 (Seller refurbished).
const REFURBISHED_CONDITION_IDS = new Set(['2000', '2010', '2020', '2030', '2500']);

/** Associates Store ID — keep in sync with products/amazon_used.py */
export const AMAZON_ASSOCIATE_TAG = 'usedfindr0f-20';

/** USED-FIRST Amazon condition allowlist (normalized keys). Keep in sync with amazon_used.py */
const AMAZON_USED_CONDITION_KEYS = new Set([
  'used', 'like new', 'very good', 'good', 'acceptable', 'refurbished', 'open box',
  'warehouse', 'amazon warehouse', 'renewed', 'amazon renewed', 'for parts',
  'for parts or not working',
]);

function normalizeAmazonConditionKey(label) {
  if (label == null) return '';
  return String(label).trim().toLowerCase().replace(/[-_/]+/g, ' ').replace(/\s+/g, ' ');
}

export function isAmazonUsedCondition(label) {
  const key = normalizeAmazonConditionKey(label);
  if (!key) return false;
  if (key === 'new' || key.startsWith('new ')) return false;
  if (AMAZON_USED_CONDITION_KEYS.has(key)) return true;
  for (const allowed of AMAZON_USED_CONDITION_KEYS) {
    if (key.includes(allowed) || allowed.includes(key)) return true;
  }
  return false;
}

/**
 * Normalize offer/listing title → clean catalog (parent) name.
 * Keep in sync with products/catalog_name.py and fixtures/catalog_name_examples.json.
 */
export function normalizeCatalogName(title) {
  const collapse = (s) => String(s || '').trim().replace(/\s+/g, ' ');
  const original = collapse(title);
  if (!original) return '';

  let result = original;
  const conditionSuffix = /(?:\s*[\(\[\{]\s*(?:renewed|refurbished|used|warehouse\s+deals?|amazon\s+warehouse|certified\s+refurbished|seller\s+refurbished|ebay\s+refurbished)\s*[\)\]\}]|\s*[-–—]\s*(?:renewed|refurbished|used|warehouse\s+deals?|amazon\s+warehouse|certified\s+refurbished|seller\s+refurbished|ebay\s+refurbished)\s*$)/gi;
  for (let i = 0; i < 4; i++) {
    const next = collapse(result.replace(conditionSuffix, ''));
    if (next === result) break;
    result = next;
  }
  const marketingTrailer = /(?:,\s*pack\s+of\s+\d+|\s*[\(\[\{]\s*latest\s+model\s*[\)\]\}])\s*$/gi;
  for (let i = 0; i < 2; i++) {
    const next = collapse(result.replace(marketingTrailer, ''));
    if (next === result) break;
    result = next;
  }

  // Safe "Model by Brand Rest" → "Brand Model Rest" (single-token brand, ≤6 left tokens)
  const lower = result.toLowerCase();
  if ((lower.match(/ by /g) || []).length === 1 && !result.includes('/')) {
    const m = result.match(/^(.{1,120}?)\s+by\s+([A-Za-z0-9][\w.&'-]{0,40})\s+(.+)$/i);
    if (m) {
      const left = m[1].trim();
      const brand = m[2].trim();
      const rest = m[3].trim();
      if (left.split(/\s+/).length <= 6 && !/\s/.test(brand)) {
        result = collapse(`${brand} ${left} ${rest}`);
      }
    }
  }

  const letters = result.replace(/[^A-Za-zÀ-ÿ]/g, '');
  if (letters.length > 12) {
    const upper = (letters.match(/[A-ZÀ-Ý]/g) || []).length;
    if (upper / letters.length >= 0.8) {
      result = result.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
    }
  }
  result = collapse(result);
  return result || original;
}

export function getApprovalOfferTitle() {
  return (document.getElementById('approvalProductName')?.value || '').trim();
}

export function getApprovalCatalogName() {
  return (document.getElementById('approvalCatalogName')?.value || '').trim();
}

export function getApprovalModeConfig(marketplace, linkMode, overrideExpanded) {
  const isAmazon = marketplace === 'amazon';
  const linkingActive = !isAmazon || !!overrideExpanded;
  return {
    marketplace: isAmazon ? 'amazon' : 'ebay',
    linkMode: isAmazon ? 'auto' : 'manual',
    overrideExpanded: isAmazon ? !!overrideExpanded : true,
    showAmazonChrome: isAmazon,
    categoryRequired: !isAmazon,
    linkingActive,
    requireParentVariant: !isAmazon,
  };
}

const CATEGORY_CATCHALL_DENYLIST = [
  'every other thing',
  'wholesale',
  'other whlsl',
  'unspecified',
  'lots',
];

export function processCategorySuggestions(rawSuggestions, opts = {}) {
  const inlineLimit = opts.inlineLimit != null ? opts.inlineLimit : 5;
  const list = Array.isArray(rawSuggestions) ? rawSuggestions : [];
  const seenIds = new Set();
  const seenNames = new Set();
  const processed = [];
  for (const s of list) {
    const categoryId = String(s.categoryId || s.category_id || '').trim();
    const categoryName = s.categoryName || s.category_name || '';
    const path = s.path || categoryName;
    const nameKey = String(categoryName).toLowerCase().replace(/\s+/g, ' ').trim();
    if (!categoryId) continue;
    if (seenIds.has(categoryId) || (nameKey && seenNames.has(nameKey))) continue;
    seenIds.add(categoryId);
    if (nameKey) seenNames.add(nameKey);
    const rank = s.rank != null ? Number(s.rank) : processed.length + 1;
    const hay = `${categoryName} ${path}`.toLowerCase();
    const pathDepth = String(path).split(/\s*>\s*/).filter(Boolean).length;
    let demoted = CATEGORY_CATCHALL_DENYLIST.some((d) => hay.includes(d));
    if (pathDepth <= 1 && rank !== 1) demoted = true;
    if (rank > inlineLimit) demoted = true;
    processed.push({
      categoryId,
      categoryName,
      path,
      rank,
      demoted,
    });
  }
  // Re-rank after dedupe if server didn't send rank
  processed.forEach((item, i) => {
    if (item.rank == null || Number.isNaN(item.rank)) item.rank = i + 1;
  });
  return processed;
}

if (typeof window !== 'undefined') {
  window.normalizeCatalogName = normalizeCatalogName;
  window.processCategorySuggestions = processCategorySuggestions;
  window.getApprovalModeConfig = getApprovalModeConfig;
}

export function isPlainAmazonDpAsinUrl(url) {
  if (!url || typeof url !== 'string') return false;
  let parsed;
  try {
    const withProto = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    parsed = new URL(withProto);
  } catch (_e) {
    return false;
  }
  const host = (parsed.hostname || '').toLowerCase();
  if (!host.includes('amazon.') && host !== 'a.co') return false;
  const path = (parsed.pathname || '').toLowerCase();
  if (path.includes('/gp/offer-listing/') || path.includes('renewed') || path.includes('warehouse') || path.includes('refurb')) {
    return false;
  }
  if (parsed.searchParams.has('aod') || parsed.searchParams.has('smid')) return false;
  for (const [key, value] of parsed.searchParams.entries()) {
    const keyL = key.toLowerCase();
    if (['tag', 'ascsubtag', 'linkcode', 'creative', 'camp'].includes(keyL)) continue;
    const joined = `${keyL} ${String(value).toLowerCase()}`;
    if (/(used|renewed|warehouse|refurb|offer)/.test(joined)) return false;
  }
  return /\/dp\/[a-z0-9]{10}/i.test(path);
}

export function looksLikeAmazonUrlClient(url) {
  if (!url || typeof url !== 'string') return false;
  const lower = url.trim().toLowerCase();
  return lower.includes('amazon.') || lower.includes('amzn.to') || lower.includes('a.co/') || /(^|[/.])a\.com([/?#]|$)/.test(lower);
}

/** Queue page size for Item Submissions lists (client-side pagination). */
export const SUBMISSIONS_QUEUE_PAGE_SIZE = 25;

export const APPROVAL_DRAFT_STORAGE_KEY = 'usedfindr_approval_draft';

/**
 * Shared Amazon bulk / quick-approve eligibility (A5c path).
 * @param {{ url?: string, name?: string, price?: string, condition?: string, marketplace?: string }} attrs
 * @param {(url: string) => string|null} extractAsinFn
 * @returns {{ isAmazon: boolean, eligible: boolean, incomplete: boolean, reasons: string[], asin: string|null, name: string, price: string, condition: string, affiliate_link: string }}
 */
export function getAmazonSubmissionEligibility(attrs, extractAsinFn) {
  const url = String(attrs?.url || '').trim();
  const name = String(attrs?.name || '').trim();
  const price = String(attrs?.price || '').trim();
  const condition = String(attrs?.condition || '').trim();
  const marketplace = String(attrs?.marketplace || '').trim();
  const isAmazon = marketplace === 'amazon' || looksLikeAmazonUrlClient(url);
  if (!isAmazon) {
    return {
      isAmazon: false,
      eligible: false,
      incomplete: false,
      reasons: [],
      asin: null,
      name,
      price,
      condition,
      affiliate_link: url,
    };
  }
  const reasons = [];
  if (!name) reasons.push('name');
  const cleanPrice = price.replace(/[^0-9.]/g, '');
  const numPrice = parseFloat(cleanPrice);
  if (isNaN(numPrice) || numPrice <= 0) reasons.push('price');
  if (!isAmazonUsedCondition(condition)) reasons.push('condition');
  const asin = typeof extractAsinFn === 'function' ? extractAsinFn(url) : null;
  if (!asin) reasons.push('ASIN');
  return {
    isAmazon: true,
    eligible: reasons.length === 0,
    incomplete: reasons.length > 0,
    reasons,
    asin: asin || null,
    name,
    price,
    condition,
    affiliate_link: url,
  };
}

function isAmazonBulkEligible(attrs, extractAsinFn) {
  return getAmazonSubmissionEligibility(attrs, extractAsinFn).eligible;
}

export function normalizeAmazonAffiliateUrlClient(url) {
  if (!url) return url;
  const trimmed = String(url).trim();
  if (!trimmed) return trimmed;
  try {
    const withProto = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const urlObj = new URL(withProto);
    if (urlObj.hostname.includes('amzn.to')) {
      throw new Error('amzn.to');
    }
    // Require extractable ASIN (parity with server normalize_amazon_affiliate_url)
    const path = urlObj.pathname || '';
    const dpMatch = path.match(/\/dp\/([A-Z0-9]{10})/i);
    const gpMatch = path.match(/\/gp\/product\/([A-Z0-9]{10})/i);
    const asinPathMatch = path.match(/\/asin\/([A-Z0-9]{10})/i);
    let asin = null;
    if (dpMatch) asin = dpMatch[1];
    else if (gpMatch) asin = gpMatch[1];
    else if (asinPathMatch) asin = asinPathMatch[1];
    else if (urlObj.searchParams.has('dp') && /^[A-Z0-9]{10}$/i.test(urlObj.searchParams.get('dp') || '')) {
      asin = urlObj.searchParams.get('dp');
    } else if (urlObj.searchParams.has('asin') && /^[A-Z0-9]{10}$/i.test(urlObj.searchParams.get('asin') || '')) {
      asin = urlObj.searchParams.get('asin');
    }
    if (!asin) {
      throw new Error('asin');
    }
    urlObj.searchParams.set('tag', AMAZON_ASSOCIATE_TAG);
    urlObj.protocol = 'https:';
    return urlObj.toString();
  } catch (e) {
    if (e && (e.message === 'amzn.to' || e.message === 'asin')) throw e;
    throw new Error('asin');
  }
}

/**
 * Toggle Amazon-only / Amazon-hidden condition options in a pills container or <select>.
 * @param {HTMLElement|null} root - pills container or select
 * @param {boolean} isAmazon
 * @param {{ hiddenInputId?: string, clearNew?: boolean }} [opts]
 */
export function applyAmazonConditionUi(root, isAmazon, opts = {}) {
  if (!root) return;
  const clearNew = opts.clearNew !== false;
  const list = root.tagName === 'SELECT'
    ? Array.from(root.options)
    : Array.from(root.querySelectorAll('.condition-pill'));

  list.forEach((el) => {
    const hideForAmazon = el.getAttribute('data-amazon-hide') === 'true';
    const amazonOnly = el.getAttribute('data-amazon-only') === 'true';
    if (hideForAmazon) {
      if (el.tagName === 'OPTION') {
        el.disabled = !!isAmazon;
        el.hidden = !!isAmazon;
      } else {
        el.hidden = !!isAmazon;
        el.style.display = isAmazon ? 'none' : '';
        if (isAmazon) el.classList.remove('active', 'selected');
      }
    }
    if (amazonOnly) {
      if (el.tagName === 'OPTION') {
        el.hidden = !isAmazon;
        el.disabled = !isAmazon;
      } else {
        el.hidden = !isAmazon;
        el.style.display = isAmazon ? '' : 'none';
      }
    }
  });

  if (isAmazon && clearNew) {
    if (root.tagName === 'SELECT') {
      const cur = root.value;
      if (!cur || normalizeAmazonConditionKey(cur) === 'new' || normalizeAmazonConditionKey(cur).startsWith('new ')) {
        root.value = '';
      }
    } else if (opts.hiddenInputId) {
      const hidden = document.getElementById(opts.hiddenInputId);
      if (hidden) {
        const cur = hidden.value || '';
        if (!cur || normalizeAmazonConditionKey(cur) === 'new' || normalizeAmazonConditionKey(cur).startsWith('new ')) {
          hidden.value = '';
          root.querySelectorAll('.condition-pill').forEach((p) => p.classList.remove('active', 'selected'));
        }
      }
    }
  }
}

/**
 * Build status HTML for Amazon link field (ASIN + tag + plain /dp/ warn).
 * @returns {{ html: string, normalizedUrl: string|null, asin: string|null, error: string|null }}
 */
export function buildAmazonLinkStatus(url, extractAsinFn) {
  if (!url || !looksLikeAmazonUrlClient(url)) {
    return { html: '', normalizedUrl: null, asin: null, error: null };
  }
  const lower = url.toLowerCase();
  if (lower.includes('amzn.to')) {
    return {
      html: '<span style="color:#b91c1c;">Reject amzn.to — paste a full amazon.com product/offer URL.</span>',
      normalizedUrl: null,
      asin: null,
      error: 'amzn.to',
    };
  }
  let asin = null;
  try {
    asin = typeof extractAsinFn === 'function' ? extractAsinFn(url) : null;
  } catch (_e) {
    asin = null;
  }
  let normalizedUrl = null;
  let error = null;
  try {
    normalizedUrl = normalizeAmazonAffiliateUrlClient(url);
  } catch (e) {
    error = e && e.message === 'amzn.to' ? 'amzn.to' : 'asin';
  }
  const parts = [];
  if (asin) {
    parts.push(`ASIN: <strong>${asin}</strong>`);
  } else {
    parts.push('<span style="color:#b91c1c;">Could not extract ASIN (/dp/, /gp/product/, or ?asin=)</span>');
  }
  if (normalizedUrl) {
    parts.push(`tag=<strong>${AMAZON_ASSOCIATE_TAG}</strong> applied`);
  }
  if (normalizedUrl && isPlainAmazonDpAsinUrl(normalizedUrl)) {
    parts.push('<span style="color:#b45309;">Confirm Buy box / linked offer is used (plain /dp/ link).</span>');
  }
  return {
    html: parts.join(' · '),
    normalizedUrl,
    asin,
    error,
  };
}

// Display labels for refurbished pills (override category API descriptions for consistent UX).
const REFURBISHED_PILL_LABELS = {
  '2000': 'Certified Refurbished',
  '2010': 'eBay Refurbished - Excellent',
  '2020': 'eBay Refurbished - Very Good',
  '2030': 'eBay Refurbished - Good',
  '2500': 'Seller refurbished / Remanufactured',
};

// eBay category API can return different condition labels (e.g. "Pre-Owned" vs our "Used").
// Map our resolve-ebay-item labels to possible category-API descriptions for pill matching.
const CONDITION_DESCRIPTION_SYNONYMS = {
  'new': ['new'],
  'open box': ['open box', 'new other', 'new without box'],
  'refurbished': ['refurbished', 'seller refurbished', 'certified refurbished', 'ebay refurbished', 'excellent - refurbished', 'very good - refurbished', 'good - refurbished', 'ebay refurbished - excellent', 'ebay refurbished - very good', 'ebay refurbished - good', 'seller refurbished / remanufactured'],
  'like new': ['like new', 'like-new'],
  'used': ['used', 'pre-owned', 'preowned'],
  'very good': ['very good', 'very good - refurbished'],
  'good': ['good', 'good - refurbished'],
  'acceptable': ['acceptable'],
  'for parts or not working': ['for parts or not working', 'for parts', 'for parts or not working'],
};

function _conditionDescriptionMatches(selStr, descNorm) {
  if (!selStr || !descNorm) return false;
  if (selStr === descNorm) return true;
  const synonyms = CONDITION_DESCRIPTION_SYNONYMS[selStr];
  if (synonyms && synonyms.some(s => s === descNorm || descNorm.includes(s))) return true;
  for (const key of Object.keys(CONDITION_DESCRIPTION_SYNONYMS)) {
    const list = CONDITION_DESCRIPTION_SYNONYMS[key];
    if (list.includes(descNorm) && (key === selStr || list.includes(selStr))) return true;
  }
  return false;
}

/**
 * Render condition pills into a container. Works for Add New Find (data-condition, modalItemCondition) and Admin Review (data-value, approvalCondition).
 * @param {Element|string} container - DOM element or selector for the pill container
 * @param {Array<{conditionId: string, conditionDescription: string}>} conditions - List from API or default
 * @param {string|number} selectedValue - conditionId or conditionDescription to pre-select
 * @param {{ hiddenInputId: string, dataAttribute: string }} options - e.g. { hiddenInputId: 'modalItemCondition', dataAttribute: 'data-condition' }
 */
function renderConditionPills(container, conditions, selectedValue, options = {}) {
  const el = typeof container === 'string' ? document.querySelector(container) : container;
  if (!el || !conditions || conditions.length === 0) return false;
  const hiddenInputId = options.hiddenInputId || 'modalItemCondition';
  const dataAttribute = options.dataAttribute || 'data-condition';
  const selectedClass = el.closest('#approvalModal') ? 'selected' : 'active';
  const hiddenInput = document.getElementById(hiddenInputId);
  el.innerHTML = '';
  let matched = false;
  const selStr = selectedValue != null ? String(selectedValue).trim().toLowerCase() : '';
  const selStrId = selectedValue != null ? String(selectedValue).trim() : '';
  const isNumericSelection = /^\d+$/.test(selStrId);
  conditions.forEach(({ conditionId, conditionDescription }) => {
    const idStr = String(conditionId).trim();
    const displayLabel = REFURBISHED_CONDITION_IDS.has(idStr) && REFURBISHED_PILL_LABELS[idStr]
      ? REFURBISHED_PILL_LABELS[idStr]
      : (conditionDescription || '');
    const matchById = isNumericSelection && selStrId === idStr;
    // When resolve returns 2030 (Good - Refurbished) but category only has 2500 (Seller refurbished), match by refurbished ID group
    const matchByRefurbishedGroup = isNumericSelection && REFURBISHED_CONDITION_IDS.has(selStrId) && REFURBISHED_CONDITION_IDS.has(idStr);
    // When matching by refurbished group, show the resolved label (e.g. "eBay Refurbished - Good") on the pill, not the category's label ("Seller refurbished / Remanufactured")
    const effectiveLabel = (isNumericSelection && matchByRefurbishedGroup && REFURBISHED_PILL_LABELS[selStrId])
      ? REFURBISHED_PILL_LABELS[selStrId]
      : displayLabel;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'condition-pill';
    btn.setAttribute(dataAttribute, effectiveLabel);
    btn.setAttribute('data-condition-id', conditionId);
    btn.textContent = effectiveLabel;
    const descNorm = (effectiveLabel || '').trim().toLowerCase();
    const matchByDescription = selStr === descNorm || _conditionDescriptionMatches(selStr, descNorm);
    const isSelected = matchById || matchByRefurbishedGroup || matchByDescription || (!isNumericSelection && selStrId === idStr);
    if (isSelected) {
      btn.classList.add(selectedClass);
      if (hiddenInput) hiddenInput.value = effectiveLabel;
      matched = true;
    }
    btn.addEventListener('click', () => {
      el.querySelectorAll('.condition-pill').forEach(p => { p.classList.remove('active', 'selected'); });
      btn.classList.add(selectedClass);
      if (hiddenInput) {
        hiddenInput.value = btn.getAttribute(dataAttribute);
        hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    el.appendChild(btn);
  });
  if (!matched && selectedValue && hiddenInput && typeof selectedValue === 'string') {
    hiddenInput.value = selectedValue;
  }
  return matched;
}

// Refurbished type display labels (used in Add New Find and Item Approval modal)
const REFURBISHED_TYPE_LABELS = {
  certified: 'Certified Refurbished',
  ebay_refurbished: 'eBay Refurbished',
  seller_refurbished: 'Seller refurbished',
};
const CERTIFIED_REFURBISHED_HUB_URL = 'https://pages.ebay.com/ba/en-us/certified-refurbished/cr-program-hub.html';

function getRefurbishedTypeLabel(refurbishedType) {
  return (refurbishedType && REFURBISHED_TYPE_LABELS[refurbishedType]) || '';
}

/**
 * Returns content for the refurbished indicator: string for text-only, DocumentFragment for certified (text + "see more here" link).
 * @param {string} refurbishedType - 'certified' | 'ebay_refurbished' | 'seller_refurbished'
 * @returns {string|DocumentFragment}
 */
function getRefurbishedIndicatorContent(refurbishedType) {
  if (refurbishedType === 'certified') {
    const fragment = document.createDocumentFragment();
    fragment.appendChild(document.createTextNode('Certified Refurbished '));
    const link = document.createElement('a');
    link.href = CERTIFIED_REFURBISHED_HUB_URL;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.className = 'refurbished-indicator-link';
    link.textContent = 'see more here';
    fragment.appendChild(link);
    return fragment;
  }
  if (refurbishedType === 'ebay_refurbished') return 'eBay Refurbished';
  if (refurbishedType === 'seller_refurbished') return 'Seller refurbished / Remanufactured';
  return '';
}

if (typeof window !== 'undefined') {
  window.DEFAULT_CONDITION_PILLS = DEFAULT_CONDITION_PILLS;
  window.fetchConditionPolicies = fetchConditionPolicies;
  window.renderConditionPills = renderConditionPills;
  window.REFURBISHED_TYPE_LABELS = REFURBISHED_TYPE_LABELS;
  window.getRefurbishedTypeLabel = getRefurbishedTypeLabel;
  window.getRefurbishedIndicatorContent = getRefurbishedIndicatorContent;
}

// Search data structure
export const searchData = {
  categories: [
    { 
      name: 'Like New', 
      type: 'condition', 
      description: 'Minimal wear, excellent condition',
      words: ['minimal', 'wear', 'excellent', 'condition', 'pristine', 'mint']
    },
    { 
      name: 'Very Good', 
      type: 'condition', 
      description: 'Light wear, great value',
      keywords: ['light', 'wear', 'great', 'value', 'good', 'condition', 'slight']
    },
    { 
      name: 'TVs', 
      type: 'product', 
      description: 'Smart TVs, LED, OLED displays',
      keywords: ['smart', 'tv', 'tvs', 'led', 'oled', 'display', 'displays', 'television', 'televisions', '4k', '8k', 'hdr', 'samsung', 'lg', 'sony']
    },
    { 
      name: 'Coffee Makers', 
      type: 'product', 
      description: 'Espresso machines, drip coffee makers',
      keywords: ['espresso', 'machine', 'machines', 'drip', 'coffee', 'maker', 'makers', 'breville', 'nespresso', 'keurig', 'french', 'press', 'pour', 'over']
    },
    { 
      name: 'Laptops', 
      type: 'product', 
      description: 'Gaming laptops, business notebooks',
      keywords: ['gaming', 'laptop', 'laptops', 'business', 'notebook', 'notebooks', 'macbook', 'dell', 'hp', 'lenovo', 'asus', 'acer', 'chromebook']
    },
    { 
      name: 'Vacuums', 
      type: 'product', 
      description: 'Upright, canister, robot vacuums',
      keywords: ['upright', 'canister', 'robot', 'vacuum', 'vacuums', 'dyson', 'shark', 'roomba', 'irobot', 'cordless', 'bagless']
    },
    { 
      name: 'DVDs', 
      type: 'product', 
      description: 'Movies, TV shows, documentaries',
      keywords: ['movies', 'movie', 'tv', 'shows', 'documentaries', 'documentary', 'dvd', 'dvds', 'blu', 'ray', 'bluray', 'films', 'film']
    },
    { 
      name: 'Headphones & Audio', 
      type: 'product', 
      description: 'Wireless earbuds, studio monitors, speakers',
      keywords: ['wireless', 'earbuds', 'earbud', 'studio', 'monitors', 'speakers', 'speaker', 'headphones', 'headphone', 'airpods', 'sony', 'bose', 'audio', 'sound']
    },
    { 
      name: 'Cameras & Lenses', 
      type: 'product', 
      description: 'DSLR, mirrorless, lenses, accessories',
      keywords: ['dslr', 'mirrorless', 'lens', 'lenses', 'camera', 'cameras', 'canon', 'nikon', 'sony', 'fujifilm', 'accessories', 'tripod', 'flash']
    },
    { 
      name: 'Smartphones', 
      type: 'product', 
      description: 'iPhone, Android, unlocked phones',
      keywords: ['iphone', 'android', 'unlocked', 'phone', 'phones', 'smartphone', 'smartphones', 'samsung', 'galaxy', 'google', 'pixel', 'oneplus']
    },
    { 
      name: 'Power Tools', 
      type: 'product', 
      description: 'Drills, saws, sanders, workshop tools',
      keywords: ['drill', 'drills', 'saw', 'saws', 'sander', 'sanders', 'workshop', 'tools', 'dewalt', 'milwaukee', 'makita', 'bosch', 'ryobi', 'circular', 'reciprocating']
    }
  ],
  items: [
    { name: 'Ninja NC301 CREAMi Ice Cream Maker', category: 'Coffee Makers', condition: 'Like New', price: '$127.56' },
    { name: 'Nespresso Vertuo Coffee and Espresso Maker by Breville', category: 'Coffee Makers', condition: 'Like New', price: '$76.41' }
  ]
};

// State management for deals filtering
let currentDealType = 'hot';
let currentCategoryFilter = 'all';
let allDealsData = [];
let homepageAvailableCategories = [];
let currentHomepageQuickChip = 'for-you';
let currentQuickChipCondition = '';
let currentQuickChipMaxPrice = null;

// Helper function to parse price string to numeric value
function parsePrice(priceString) {
  if (!priceString) return 0;
  // Extract numeric value from "$127.56" or "127.56"
  const numeric = parseFloat(priceString.replace(/[^0-9.]/g, ''));
  return isNaN(numeric) ? 0 : numeric;
}

// Helper function to calculate days on site
function getDaysOnSite(addedAt) {
  if (!addedAt) return 0;
  try {
    const added = new Date(addedAt);
    const now = new Date();
    const diffTime = Math.abs(now - added);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  } catch (e) {
    return 0;
  }
}

// Slug generation utility
function generateProductSlug(productName) {
  if (!productName) return '';
  
  return productName
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-')      // Replace spaces with hyphens
    .replace(/-+/g, '-')       // Replace multiple hyphens with single hyphen
    .substring(0, 100);        // Limit length to 100 characters
}

// Generate product detail URL
export function generateProductDetailURL(productId, productName) {
  const slug = generateProductSlug(productName);
  return `/product.html?id=${encodeURIComponent(productId)}${slug ? '&slug=' + encodeURIComponent(slug) : ''}`;
}

// Click tracking functions
function hasAnalyticsConsent() {
  return window.consentManager?.hasConsent('analytics') === true;
}

function canSendGaEvent() {
  return Boolean(window.gtag) && hasAnalyticsConsent();
}

function getGaMeasurementId() {
  return window.consentManager?.measurementId || window.USEDFINDR_CONFIG?.ga4MeasurementId || null;
}

function getRecentViews() {
  if (!hasAnalyticsConsent()) {
    return [];
  }

  try {
    const recentViews = JSON.parse(localStorage.getItem('usedfindr_recent_views') || '[]');
    return Array.isArray(recentViews) ? recentViews : [];
  } catch (error) {
    logger.error('Error getting recent views:', error);
    return [];
  }
}

function trackProductClick(productId, options = {}) {
  // Cookieless first, and before every guard below. This is the only outbound
  // click record that survives a visitor who never accepted analytics cookies,
  // and a click with no product id is still a click worth counting.
  postOutboundClick({
    productId,
    marketplace: options.marketplace
      || (options.url ? classifyMarketplaceFromUrl(options.url) : ''),
    destinationUrl: options.url || options.destinationUrl || '',
  });

  if (!productId) return;
  if (!hasAnalyticsConsent()) return;
  try {
    // NOTE: localStorage clicks are for client-side analytics only.
    // API clickCount is the source of truth for display and sorting.
    // This localStorage tracking doesn't overwrite API clickCount values.
    
    // Enhanced click tracking with timestamp and session data
    const clicks = getProductClicks();
    clicks[productId] = (clicks[productId] || 0) + 1;
    localStorage.setItem('usedfindr_product_clicks', JSON.stringify(clicks));
    
    // Update in-memory item clickCount if it exists (for immediate UI update)
    // But only if the item doesn't have an API clickCount (to avoid overwriting API data)
    let matchedItem = null;
    if (searchData && searchData.items) {
      matchedItem = searchData.items.find(i => (i.productId || i.name) === productId);
      if (matchedItem && matchedItem.clickCount === undefined) {
        // Only update if clickCount wasn't set from API
        matchedItem.clickCount = clicks[productId] || 0;
      }
    }

    const destinationUrl = options.url || options.destinationUrl || '';
    let marketplace = options.marketplace || '';
    if (!marketplace && matchedItem && typeof getProductMarketplace === 'function') {
      marketplace = getProductMarketplace(matchedItem);
    }
    if (!marketplace && destinationUrl) {
      marketplace = classifyMarketplaceFromUrl(destinationUrl);
    }
    const condition = options.condition
      || matchedItem?.condition
      || matchedItem?.conditionDisplay
      || '';
    
    // Track detailed analytics if AnalyticsManager is available
    if (window.analyticsManager) {
      window.analyticsManager.trackClick(productId, {
        category: matchedItem?.category || options.category || '',
        pagePath: window.location.pathname,
        pageTitle: document.title,
        marketplace: marketplace || '',
        condition: condition || '',
        destinationUrl: destinationUrl || '',
      });
    }
    
    // Track Google Analytics event if available (with consent check)
    if (canSendGaEvent()) {
      window.gtag('event', 'product_click', {
        'product_id': productId,
        'event_category': 'engagement',
        'event_label': 'Product Click',
        'marketplace': marketplace || undefined,
      });
    }
  } catch (error) {
    logger.error('Error tracking product click:', error);
  }
}

if (typeof window !== 'undefined') {
  window.trackProductClick = trackProductClick;
}

function getProductClicks() {
  if (!hasAnalyticsConsent()) {
    return {};
  }

  try {
    const clicksData = localStorage.getItem('usedfindr_product_clicks');
    return clicksData ? JSON.parse(clicksData) : {};
  } catch (error) {
    logger.error('Error getting product clicks:', error);
    return {};
  }
}

function getProductClickCount(productId) {
  const clicks = getProductClicks();
  return clicks[productId] || 0;
}

// View tracking with Intersection Observer
let viewObserver = null;
const viewedProducts = new Set();

function initViewTracking() {
  if (!('IntersectionObserver' in window)) {
    return; // Browser doesn't support Intersection Observer
  }
  
  // Create observer for product cards
  viewObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const productCard = entry.target;
        const productId = productCard.getAttribute('data-product-card-id');
        
        if (productId && !viewedProducts.has(productId)) {
          viewedProducts.add(productId);
          trackProductView(productId);
        }
      }
    });
  }, {
    root: null,
    rootMargin: '0px',
    threshold: 0.5 // Trigger when 50% of card is visible
  });
  
  // Observe all product cards
  observeProductCards();
}

function observeProductCards() {
  if (!viewObserver) return;
  
  const productCards = document.querySelectorAll('[data-product-card-id]');
  productCards.forEach(card => {
    const productId = card.getAttribute('data-product-card-id');
    if (productId && !viewedProducts.has(productId)) {
      viewObserver.observe(card);
    }
  });
}

function trackProductView(productId) {
  if (!productId) return;
  if (!hasAnalyticsConsent()) return;
  try {
    // Store recent views for "Continue Browsing" feature
    const recentViews = getRecentViews();
    // Remove if already exists (to avoid duplicates)
    const filteredViews = recentViews.filter(id => id !== productId);
    // Add to front
    filteredViews.unshift(productId);
    // Keep only last 10
    const limitedViews = filteredViews.slice(0, 10);
    localStorage.setItem('usedfindr_recent_views', JSON.stringify(limitedViews));
    
    // Track detailed analytics if AnalyticsManager is available
    if (window.analyticsManager) {
      const matchedItem = (searchData?.items || []).find(i => (i.productId || i.name) === productId);
      window.analyticsManager.trackView(productId, {
        category: matchedItem?.category || '',
        pagePath: window.location.pathname,
        pageTitle: document.title,
      });
    }
    
    // Track Google Analytics event if available (with consent check)
    if (canSendGaEvent()) {
      window.gtag('event', 'product_view', {
        'product_id': productId,
        'event_category': 'engagement',
        'event_label': 'Product View'
      });
    }
  } catch (error) {
    logger.error('Error tracking product view:', error);
  }
}

// Track page views
function trackPageView() {
  try {
    const pagePath = window.location.pathname;
    const pageTitle = document.title;
    
    // Track detailed analytics if AnalyticsManager is available
    if (window.analyticsManager) {
      window.analyticsManager.trackPageView(pagePath, pageTitle);
    }
    
    // Track Google Analytics page view if available (with consent check)
    const measurementId = getGaMeasurementId();
    if (canSendGaEvent() && measurementId) {
      window.gtag('config', measurementId, {
        'page_path': pagePath,
        'page_title': pageTitle
      });
    }
  } catch (error) {
    logger.error('Error tracking page view:', error);
  }
}

// Track search queries
function trackSearchQuery(query, searchType = 'general') {
  try {
    if (!query || !query.trim()) return;
    
    // Track Google Analytics event if available (with consent check)
    if (canSendGaEvent()) {
      window.gtag('event', 'search_query', {
        'search_term': query,
        'search_type': searchType,
        'event_category': 'engagement',
        'event_label': 'Search Query'
      });
    }
  } catch (error) {
    logger.error('Error tracking search query:', error);
  }
}

// Track form submissions
function trackFormSubmit(formName, formType = 'general') {
  try {
    // Track Google Analytics event if available (with consent check)
    if (canSendGaEvent()) {
      window.gtag('event', 'form_submit', {
        'form_name': formName,
        'form_type': formType,
        'event_category': 'engagement',
        'event_label': 'Form Submission'
      });
    }
  } catch (error) {
    logger.error('Error tracking form submission:', error);
  }
}

// Helper function to check if an item is removed (used by loadItemsFromStorage)
function isItemRemovedFromStorage(item) {
  if (!item) return false;
  const removedItemsStorage = localStorage.getItem('usedfindr_removed_items');
  if (!removedItemsStorage) return false;
  
  const removedItems = JSON.parse(removedItemsStorage);
  const productId = item.productId || item.name;
  if (!productId) return false;
  
  // Normalize productId for matching
  function normalizeProductId(value) {
    if (!value) return '';
    return String(value).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }
  
  function matchProduct(itemToCheck, productIdToMatch) {
    if (!productIdToMatch || !itemToCheck) return false;
    
    // Exact matches
    if (itemToCheck.productId === productIdToMatch || itemToCheck.name === productIdToMatch) {
      return true;
    }
    
    // Normalized matches
    const normalizedProductId = normalizeProductId(productIdToMatch);
    const normalizedItemProductId = normalizeProductId(itemToCheck.productId);
    const normalizedItemName = normalizeProductId(itemToCheck.name);
    
    if (normalizedProductId && (normalizedProductId === normalizedItemProductId || normalizedProductId === normalizedItemName)) {
      return true;
    }
    
    return false;
  }
  
  // Check if item is in removed items list (filter both 'removed' and 'archived' status)
  return removedItems.some(removedItem => {
    // Filter out items with 'removed' or 'archived' status
    if (removedItem.status === 'removed' || removedItem.status === 'archived') {
      return matchProduct(removedItem, productId);
    }
    return false;
  });
}


// Load items from API (with localStorage fallback) and merge with searchData.items
let isLoadingItems = false; // Guard to prevent concurrent loads
let loadItemsPromise = null; // Track the current load promise
/**
 * Load items from storage (localStorage or API)
 * 
 * IMPORTANT: This function calls getProducts({ status: 'active' }) which is a HEAVY operation (47KB payload).
 * This should ONLY be called on:
 * - Page load (DOMContentLoaded)
 * - User actions: Create/Edit/Delete product
 * - Manual refresh button clicks
 * 
 * DO NOT call this from the dashboard heartbeat or any polling mechanism.
 * 
 * @param {boolean} forceRefresh - If true, force refresh from API even if localStorage has data
 */
async function loadItemsFromStorage(forceRefresh = false, options = {}) {
  // If already loading and not forcing refresh, wait for current load to complete and return
  if (isLoadingItems && !forceRefresh) {
    logger.info('loadItemsFromStorage: Already loading, waiting for current load to complete');
    if (loadItemsPromise) {
      await loadItemsPromise;
      return;
    }
  }
  
  // If forcing refresh, wait for any in-progress load first, then proceed with fresh load
  if (isLoadingItems && forceRefresh) {
    logger.info('loadItemsFromStorage: Forcing refresh, waiting for current load to complete');
    if (loadItemsPromise) {
      await loadItemsPromise;
    }
    // Reset the flag so we can start a new load
    isLoadingItems = false;
    loadItemsPromise = null;
  }
  
  isLoadingItems = true;
  loadItemsPromise = (async () => {
    try {
      const clicks = getProductClicks();
      
      // Start with empty array - all items come from API/localStorage
      searchData.items = [];
      
      let items = [];
      let loadedFromAPI = false;
      
      // CRITICAL: Always try API first - it's the source of truth
      // Default to in_stock_only true so homepage/listing only show available items (opt-out via localStorage)
      const inStockOnly = (typeof localStorage !== 'undefined' && localStorage.getItem('usedfindr_in_stock_only') !== 'false');
      if (window.apiService) {
        try {
          // Get active products from API (this is the source of truth)
          const params = { status: 'active', in_stock_only: true };
          if (!inStockOnly) delete params.in_stock_only;
          // The homepage asks for a first page it can render immediately and
          // tops the rest up in the background; every other caller wants the
          // server's full default.
          if (options.limit) params.limit = options.limit;
          // The trust floor is global under the Console shell, so the feed and
          // the search results are fetched at whatever the top bar says. Only
          // under the shell: the legacy UI has no selector, and sending a floor
          // it cannot change would filter its feed with nothing to explain why.
          if (document.body && document.body.classList.contains('console')) {
            params.floor = getFloor();
          }
          items = await window.apiService.getProducts(params);
          items = filterAvailableItems(Array.isArray(items) ? items : (items && items.results ? items.results : []));
          loadedFromAPI = true;
          logger.info(`Loaded ${items.length} items from API.`);
          
          // CRITICAL FIX: Immediately update localStorage with fresh API data
          // This ensures localStorage is always in sync with the database
          try {
            localStorage.setItem('usedfindr_items', JSON.stringify(items));
            logger.info(`Updated localStorage with ${items.length} fresh items from API.`);
          } catch (storageError) {
            logger.warn('Failed to update localStorage (may be full or disabled):', storageError);
            // Continue anyway - API data is loaded
          }
        } catch (apiError) {
          logger.warn('Failed to load items from API, falling back to localStorage:', apiError);
          // Only use localStorage if API completely fails
          loadedFromAPI = false;
        }
      }
      
      // Fallback to localStorage ONLY if API failed or not available
      if (!loadedFromAPI) {
        const storedItems = localStorage.getItem('usedfindr_items');
        if (storedItems) {
          items = JSON.parse(storedItems);
          logger.info(`Loaded ${items.length} items from localStorage (API unavailable).`);
        } else {
          // Nothing cached either: leave the feed empty. This used to fall back
          // to a hardcoded demo item, which a slow API turned into the only
          // "product" on the live homepage.
          items = [];
          logger.info('No items in storage and API unavailable; showing an empty feed.');
        }
      }
      
      // Get removed items to filter them out (for localStorage fallback)
      const removedItemsStorage = localStorage.getItem('usedfindr_removed_items');
      const removedItems = removedItemsStorage ? JSON.parse(removedItemsStorage) : [];
      
      // Create a map of existing items by productId or name to avoid duplicates
      const existingItemsMap = new Map();
      searchData.items.forEach(item => {
        const key = item.productId || item.name;
        if (key) {
          existingItemsMap.set(key, item);
        }
      });
      
      // Add items from API/localStorage, avoiding duplicates and removed items
      items.forEach(storedItem => {
        // Skip if item is removed (for localStorage items, check removedItems; API items have status='removed')
        if (!loadedFromAPI && isItemRemovedFromStorage(storedItem)) {
          return;
        }
        if (loadedFromAPI && storedItem.status === 'removed') {
          return;
        }
        
        const key = storedItem.productId || storedItem.name;
        if (key && !existingItemsMap.has(key)) {
          // Convert stored item to searchData.items format
          const item = {
            name: storedItem.name,
            category: storedItem.category,
            mainCategory: storedItem.mainCategory || storedItem.main_category || null, // New hierarchical field
            subcategory: storedItem.subcategory || null, // New hierarchical field
            condition: storedItem.condition,
            price: storedItem.price,
            retailPrice: storedItem.retailPrice || storedItem.retail_price || null, // For discount calculation
            affiliateLink: storedItem.affiliateLink || storedItem.affiliate_link,
            imageUrl: storedItem.imageUrl || storedItem.image_url,
            productId: storedItem.productId || storedItem.product_id,
            addedAt: storedItem.addedAt || storedItem.added_at || storedItem.createdAt || storedItem.created_at, // Prefer addedAt; fall back to created_at for bot discoveries
            // Click count: API is source of truth, localStorage is fallback only
            clickCount: storedItem.clickCount !== undefined ? storedItem.clickCount : (storedItem.click_count !== undefined ? storedItem.click_count : (clicks[storedItem.productId || storedItem.product_id] || 0)),
            submitterUserId: storedItem.submitterUserId || storedItem.submitter_user_id || null,
            submitterUsername: storedItem.submitterUsername || storedItem.submitter_username || null,
            voteScore: storedItem.voteScore || storedItem.vote_score || 0,
            upvotes: storedItem.upvotes || 0,
            downvotes: storedItem.downvotes || 0,
            currentUserVote: storedItem.currentUserVote || storedItem.current_user_vote || null,
            hotDealBonusAwarded: storedItem.hotDealBonusAwarded || storedItem.hot_deal_bonus_awarded || false,
            earlyAccess: storedItem.earlyAccess || storedItem.early_access || false,
            earlyAccessUntil: storedItem.earlyAccessUntil || storedItem.early_access_until || null,
            quantityAvailable: storedItem.quantityAvailable != null ? storedItem.quantityAvailable : storedItem.quantity_available,
            availabilityStatus: storedItem.availabilityStatus || storedItem.availability_status || null,
            shippingDisplay: storedItem.shippingDisplay || storedItem.shipping_display || null,
            returnsAccepted: storedItem.returnsAccepted ?? storedItem.returns_accepted ?? null,
            sellerFeedbackPct: storedItem.sellerFeedbackPct ?? storedItem.seller_feedback_pct ?? null,
            sellerSalesCount: storedItem.sellerSalesCount ?? storedItem.seller_sales_count ?? null,
            // The median-ask receipt behind the tile's "vs typical ask" delta.
            // Dropping it here is why the client tile had no delta while the
            // server-rendered one did.
            priceContext: storedItem.priceContext ?? storedItem.price_context ?? null,
            masterParentId: storedItem.masterParentId ?? storedItem.master_parent_id ?? null,
            ebayCategoryId: storedItem.ebayCategoryId ?? storedItem.ebay_category_id ?? null,
            fetchedAt: storedItem.fetchedAt || storedItem.fetched_at || null
          };
          searchData.items.push(item);
          existingItemsMap.set(key, item);
        } else if (key && existingItemsMap.has(key)) {
          // Update existing item with missing or newer data
          const existingItem = existingItemsMap.get(key);
          if (!existingItem.addedAt && (storedItem.addedAt || storedItem.added_at || storedItem.createdAt || storedItem.created_at)) {
            existingItem.addedAt = storedItem.addedAt || storedItem.added_at || storedItem.createdAt || storedItem.created_at;
          }
          // Update click count: API is source of truth, localStorage is fallback only
          if (loadedFromAPI) {
            // When loaded from API, use API clickCount as source of truth
            existingItem.clickCount = storedItem.clickCount !== undefined ? storedItem.clickCount : (storedItem.click_count !== undefined ? storedItem.click_count : existingItem.clickCount);
          } else if (!existingItem.clickCount) {
            // Only use localStorage if not from API and clickCount is missing
            existingItem.clickCount = clicks[storedItem.productId || storedItem.product_id] || storedItem.clickCount || storedItem.click_count || 0;
          }
          // Update vote-related fields from API (API is source of truth)
          if (loadedFromAPI) {
            existingItem.voteScore = storedItem.voteScore !== undefined ? storedItem.voteScore : (storedItem.vote_score !== undefined ? storedItem.vote_score : existingItem.voteScore);
            existingItem.upvotes = storedItem.upvotes !== undefined ? storedItem.upvotes : existingItem.upvotes;
            existingItem.downvotes = storedItem.downvotes !== undefined ? storedItem.downvotes : existingItem.downvotes;
            existingItem.currentUserVote = storedItem.currentUserVote !== undefined ? storedItem.currentUserVote : (storedItem.current_user_vote !== undefined ? storedItem.current_user_vote : existingItem.currentUserVote);
            // Refresh shipping and seller info from API
            const ship = storedItem.shippingDisplay || storedItem.shipping_display;
            if (ship != null) existingItem.shippingDisplay = ship;
            const ebayCat = storedItem.ebayCategoryId ?? storedItem.ebay_category_id;
            if (ebayCat != null && ebayCat !== '') existingItem.ebayCategoryId = ebayCat;
          }
        }
      });
      
      // Also update click counts for hardcoded items (only if missing and not from API)
      // Note: Items from API already have clickCount set above, so this only applies to legacy/localStorage items
      searchData.items.forEach(item => {
        if (item.productId && item.clickCount === undefined) {
          // Only use localStorage clicks if clickCount wasn't set from API
          item.clickCount = clicks[item.productId] || 0;
        }
      });
      
      const itemsCount = searchData.items.length;
      logger.info(`Total items after filtering removed: ${itemsCount}`);
      return itemsCount; // Return count so caller can verify completion
    } catch (error) {
      logger.error('Error loading items:', error);
      // On error, ensure we at least have the hardcoded items
      const filteredHardcodedItems = originalHardcodedItems.filter(item => !isItemRemovedFromStorage(item));
      searchData.items = [...filteredHardcodedItems];
      return searchData.items.length;
    } finally {
      isLoadingItems = false;
      loadItemsPromise = null;
    }
  })();
  
  // Wait for the promise to fully complete and get the items count
  const itemsCount = await loadItemsPromise;
  
  // Verify items were loaded
  if (itemsCount !== searchData.items.length) {
    logger.warn(`loadItemsFromStorage: Count mismatch - promise returned ${itemsCount} but searchData.items.length is ${searchData.items.length}`);
  }
  
  // Double-check that items are loaded (defensive check)
  // Only warn if this was a forced refresh and we expected items (not first load)
  if (searchData.items.length === 0 && forceRefresh) {
    logger.debug('loadItemsFromStorage: Items array is empty after load (may be expected if API has no items)');
  }
  
  return itemsCount;
}

/**
 * Refetch the listing set at the floor now in force and redraw whatever this
 * page draws with it.
 *
 * The floor cannot be applied to `searchData.items` in place: the server sends
 * a bounded page, so loosening the floor has to go back for rows the last
 * response never contained. Only the fetch knows the full set.
 *
 * Note the localStorage side effect inherited from loadItemsFromStorage: the
 * filtered set is what gets cached under `usedfindr_items`. That cache is a
 * fallback for a failed API call and every load refetches over it, so the worst
 * case is one paint of the previous floor's rows on a page whose API is down.
 */
let floorRefreshInFlight = null;
async function refreshListingsForFloor() {
  if (floorRefreshInFlight) return floorRefreshInFlight;
  floorRefreshInFlight = (async () => {
    try {
      await loadItemsFromStorage(true, isHomepagePath() ? { limit: HOME_FIRST_PAGE_LIMIT } : {});
      if (window.advancedSearch && typeof window.advancedSearch.updateResults === 'function') {
        window.advancedSearch.allItems = [...(searchData.items || [])];
        window.advancedSearch.updateResults();
      } else {
        await initializeItemDisplay();
      }
    } catch (error) {
      logger.error('Failed to refresh listings for the new trust floor:', error);
    } finally {
      floorRefreshInFlight = null;
    }
  })();
  return floorRefreshInFlight;
}

/** Prefer visible search field (homepage hides global header; topbar search is primary). */
function getFirstVisibleSearchInput() {
  if (typeof document === 'undefined') return null;
  const inputs = document.querySelectorAll('.search-input');
  for (const el of inputs) {
    if (!(el instanceof HTMLElement)) continue;
    if (el.offsetParent !== null) return el;
  }
  return inputs[0] || null;
}

// Search functionality
export class SearchAutofill {
  constructor() {
    this.searchInput = getFirstVisibleSearchInput();
    const container = this.searchInput && this.searchInput.closest
      ? this.searchInput.closest('.search-container')
      : null;
    this.searchButton = container && container.querySelector
      ? container.querySelector('.search-button')
      : document.querySelector('.search-button');
    this.dropdown = null;
    this.isOpen = false;
    this.availableCategories = [];

    if (this.searchInput && this.searchButton) {
      this.init();
    }
  }
  
  init() {
    this.createDropdown();
    this.bindEvents();
    this.loadAvailableCategories();
  }

  async loadAvailableCategories() {
    if (!window.apiService || typeof window.apiService.getAvailableCategories !== 'function') {
      return;
    }
    try {
      const list = await window.apiService.getAvailableCategories();
      this.availableCategories = Array.isArray(list) ? list : [];
    } catch (_err) {
      this.availableCategories = [];
    }
  }
  
  createDropdown() {
    this.dropdown = document.createElement('div');
    this.dropdown.className = 'search-dropdown';
    this.dropdown.style.display = 'none';
    
    // Append to .nav-search (parent of .search-container) to avoid overflow clipping
    const searchContainer = this.searchInput.parentElement;
    const navSearch = searchContainer.parentElement;
    
    if (navSearch && navSearch.classList.contains('nav-search')) {
      navSearch.style.position = 'relative';
      navSearch.appendChild(this.dropdown);
    } else {
      // Fallback: if structure is different, use searchContainer
      searchContainer.style.position = 'relative';
      searchContainer.appendChild(this.dropdown);
    }
  }
  
  bindEvents() {
    this.searchInput.addEventListener('input', (e) => {
      this.handleInput(e.target.value);
    });
    
    this.searchInput.addEventListener('focus', () => {
      if (this.searchInput.value.length > 0) {
        this.showDropdown();
      }
    });
    
    this.searchInput.addEventListener('blur', (e) => {
      // Delay hiding to allow clicking on dropdown items
      setTimeout(() => {
        this.hideDropdown();
      }, 200);
    });
    
    this.searchButton.addEventListener('click', () => {
      this.performSearch(this.searchInput.value);
    });
    
    this.searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.performSearch(this.searchInput.value);
      }
    });
    
    // Update dropdown position on window resize
    window.addEventListener('resize', () => {
      if (this.isOpen) {
        this.updateDropdownPosition();
      }
    });
    
    // Update dropdown position on scroll (in case page scrolls)
    window.addEventListener('scroll', () => {
      if (this.isOpen) {
        this.updateDropdownPosition();
      }
    }, true);
  }
  
  handleInput(query) {
    if (query.length < 2) {
      this.hideDropdown();
      return;
    }
    
    const results = this.search(query);
    this.displayResults(results);
    this.showDropdown();
  }
  
  search(query) {
    const lowerQuery = query.toLowerCase();
    const results = [];
    
    // Search categories by name, description, and keywords
    const liveCategories = this.availableCategories || [];
    searchData.categories.forEach(category => {
      // Prefer live catalog categories for product types so suggestions use
      // real ids (112529) instead of stale names ("Headphones & Audio").
      if (category.type === 'product' && liveCategories.length > 0) {
        return;
      }
      const nameMatch = category.name.toLowerCase().includes(lowerQuery);
      const descMatch = category.description && category.description.toLowerCase().includes(lowerQuery);
      // Some categories use 'keywords', others use 'words'
      const keywords = category.keywords || category.words || [];
      const keywordMatch = keywords.length > 0 && keywords.some(keyword => 
        keyword.toLowerCase().includes(lowerQuery) || 
        lowerQuery.includes(keyword.toLowerCase())
      );
      
      if (nameMatch || descMatch || keywordMatch) {
        results.push({
          ...category,
          matchType: category.type === 'condition' ? 'condition' : 'category',
          matchReason: nameMatch ? 'name' : (descMatch ? 'description' : 'keyword')
        });
      }
    });

    liveCategories.forEach((cat) => {
      const label = cat.short_label ?? cat.shortLabel ?? cat.label ?? cat.id;
      const twoSeg = cat.label ?? '';
      const full = cat.full_label ?? cat.fullLabel ?? '';
      const hay = [cat.id, label, twoSeg].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(lowerQuery)) return;
      results.push({
        name: label,
        description: full && full !== label ? full : 'Browse this category',
        type: 'product',
        matchType: 'category',
        categoryId: cat.id,
        matchReason: 'name',
      });
    });
    
    // Search items (ensure items are loaded)
    if (searchData.items && searchData.items.length > 0) {
      searchData.items.forEach(item => {
        const nameMatch = item.name && item.name.toLowerCase().includes(lowerQuery);
        const categoryMatch = item.category && item.category.toLowerCase().includes(lowerQuery);
        if (nameMatch || categoryMatch) {
          results.push({
            ...item,
            matchType: 'item'
          });
        }
      });
    }
    
    return results.slice(0, 8); // Limit to 8 results
  }
  
  displayResults(results) {
    if (results.length === 0) {
      this.dropdown.innerHTML = '<div class="search-no-results">No results found</div>';
      return;
    }
    
    const html = results.map(result => {
      if (result.matchType === 'category' || result.matchType === 'condition') {
        const value = result.categoryId || result.name;
        return `
          <div class="search-result" data-type="${result.matchType}" data-value="${escapeHtml(value)}" data-label="${escapeHtml(result.name)}">
            <div class="search-result-icon">${result.type === 'condition' ? '✨' : '📦'}</div>
            <div class="search-result-content">
              <div class="search-result-title">${escapeHtml(result.name)}</div>
              <div class="search-result-desc">${escapeHtml(result.description || '')}</div>
            </div>
          </div>
        `;
      } else {
        return `
          <div class="search-result" data-type="item" data-value="${result.name}">
            <div class="search-result-icon">🛍️</div>
            <div class="search-result-content">
              <div class="search-result-title">${result.name}</div>
              <div class="search-result-desc">${result.category} • ${result.condition} • ${result.price}</div>
            </div>
          </div>
        `;
      }
    }).join('');
    
    this.dropdown.innerHTML = html;
    
    // Add click handlers to results
    this.dropdown.querySelectorAll('.search-result').forEach(result => {
      result.addEventListener('click', (e) => {
        const value = e.currentTarget.dataset.value;
        const type = e.currentTarget.dataset.type;
        const label = e.currentTarget.dataset.label;
        this.selectResult(value, type, label);
      });
    });
  }
  
  selectResult(value, type, label) {
    this.searchInput.value = label || value;
    this.hideDropdown();
    this.performSearch(value, type);
  }
  
  showDropdown() {
    this.updateDropdownPosition();
    this.dropdown.style.display = 'block';
    this.isOpen = true;
  }
  
  updateDropdownPosition() {
    const searchContainer = this.searchInput.parentElement;
    const navSearch = searchContainer.parentElement;
    
    if (!navSearch || !this.dropdown) return;
    
    // Get positions relative to viewport
    const containerRect = searchContainer.getBoundingClientRect();
    const navSearchRect = navSearch.getBoundingClientRect();
    
    // Calculate position relative to nav-search
    // Top: distance from nav-search top to bottom of search-container
    const topOffset = containerRect.bottom - navSearchRect.top;
    // Left: distance from nav-search left to search-container left
    const leftOffset = containerRect.left - navSearchRect.left;
    
    // Position dropdown directly below the search container
    this.dropdown.style.top = `${topOffset}px`;
    this.dropdown.style.left = `${leftOffset}px`;
    this.dropdown.style.width = `${containerRect.width}px`;
  }
  
  hideDropdown() {
    this.dropdown.style.display = 'none';
    this.isOpen = false;
  }
  
  performSearch(query, type = null) {
    if (!query || query.trim().length === 0) {
      // If no query, just go to advanced search page
      window.location.href = 'search.html';
      return;
    }
    
    const trimmedQuery = query.trim();
    
    // Track search query
    trackSearchQuery(trimmedQuery, type || 'general');
    
    // Navigate to search page with query parameter
    if (type === 'condition') {
      window.location.href = `search.html?condition=${encodeURIComponent(trimmedQuery)}`;
    } else if (type === 'category') {
      window.location.href = `search.html?category=${encodeURIComponent(trimmedQuery)}`;
    } else if (type === 'item') {
      window.location.href = `search.html?q=${encodeURIComponent(trimmedQuery)}`;
    } else {
      // General search - navigate to search page with query
      window.location.href = `search.html?q=${encodeURIComponent(trimmedQuery)}`;
    }
  }
}
// Note: Initialization happens in the global section below to avoid duplicates
// Account Management System
export class AccountManager {
  constructor() {
    // Migrate single user to users array if needed
    this.migrateUserData();
    
    // Initialize rate limit state
    this.rateLimitState = null;
    this.rateLimitCountdownInterval = null;
    
    // Initialize modal close handlers early so they work on all pages
    setTimeout(() => {
      this.initModalCloseHandlers();
    }, 100);
    
    this.currentUser = this.getCurrentUser();
    this.userFinds = this.getUserFinds();
    
    this.initRegistration();
    this.initDashboard();
    
    // CRITICAL FIX: Only run updateNavigation() on non-admin pages
    // This prevents the public navigation from interfering with admin pages (path segment /admin)
    if (!window.location.pathname.includes('/admin')) {
      // Call updateNavigation immediately and also after DOM is ready as backup
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          this.updateNavigation();
        });
      } else {
        this.updateNavigation();
      }
      // Also call it after a short delay to ensure DOM is fully ready
      setTimeout(() => {
        this.updateNavigation();
      }, 100);
    }
  }
  
  // Migrate single user storage to users array for backward compatibility
  migrateUserData() {
    const singleUserData = localStorage.getItem('usedfindr_user');
    const usersData = localStorage.getItem('usedfindr_users');
    
    // If users array doesn't exist but single user does, migrate it
    if (singleUserData && !usersData) {
      try {
        const user = JSON.parse(singleUserData);
        // Ensure user has all required fields
        if (!user.locked) user.locked = false;
        if (!user.deleted) user.deleted = false;
        
        const usersArray = [user];
        localStorage.setItem('usedfindr_users', JSON.stringify(usersArray));
        logger.info('Migrated single user to users array');
      } catch (e) {
        logger.error('Error migrating user data:', e);
      }
    }
  }
  
  // Setup form validation with dynamic messages
  setupFormValidation(form) {
    if (!form) return;
    
    const inputs = form.querySelectorAll('input[required], input[minlength]');
    inputs.forEach(input => {
      // Clear validation on input
      input.addEventListener('input', () => {
        this.clearValidationMessage(input);
      });
      
      // Validate on blur
      input.addEventListener('blur', () => {
        this.validateInput(input);
      });
    });
  }
  
  // Show validation message
  showValidationMessage(inputId, message) {
    const errorElement = document.getElementById(inputId + '-error');
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.classList.add('show');
    }
  }
  
  // Clear validation message
  clearValidationMessage(input) {
    const inputId = input.id || input.name;
    const errorElement = document.getElementById(inputId + '-error');
    if (errorElement) {
      errorElement.textContent = '';
      errorElement.classList.remove('show');
    }
  }
  
  // Validate individual input
  validateInput(input) {
    const inputId = input.id || input.name;
    this.clearValidationMessage(input);
    
    if (!input.value.trim() && input.required) {
      this.showValidationMessage(inputId, 'This field is required');
      return false;
    }
    
    if (input.type === 'email' && input.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.value)) {
      this.showValidationMessage(inputId, 'Please enter a valid email address');
      return false;
    }
    
    if (input.type === 'password' && input.minLength && input.value.length > 0 && input.value.length < input.minLength) {
      this.showValidationMessage(inputId, `Must be at least ${input.minLength} characters`);
      return false;
    }
    
    return true;
  }
  
  // Registration functionality
  initRegistration() {
    const registrationForm = document.getElementById('registrationForm');
    if (registrationForm) {
      registrationForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleRegistration(e);
      });
      // Add real-time validation
      this.setupFormValidation(registrationForm);
    }
    
    // Login functionality
    const loginForm = document.getElementById('loginForm');
    if (loginForm) {
      loginForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleLogin(e);
      });
      // Add real-time validation
      this.setupFormValidation(loginForm);
      // Pre-fill email from "remember email" if present
      try {
        const savedEmail = localStorage.getItem('usedfindr_remember_email');
        const emailInput = loginForm.querySelector('#email');
        const rememberMe = loginForm.querySelector('#rememberMe');
        if (savedEmail && emailInput) {
          emailInput.value = savedEmail;
          if (rememberMe) rememberMe.checked = true;
        }
      } catch (e) {}
    }
    
    // Password visibility toggles (login and register pages)
    document.querySelectorAll('.password-toggle').forEach((btn) => {
      btn.addEventListener('click', () => {
        const wrapper = btn.closest('.password-input-wrapper');
        const input = wrapper && wrapper.querySelector('input');
        if (!input) return;
        const isPassword = input.type === 'password';
        input.type = isPassword ? 'text' : 'password';
        btn.setAttribute('aria-pressed', isPassword ? 'true' : 'false');
        btn.setAttribute('aria-label', isPassword ? 'Hide password' : 'Show password');
        btn.setAttribute('title', isPassword ? 'Hide password' : 'Show password');
      });
    });
  }
  
  // Dashboard functionality (token-as-source-of-truth: only redirect when we cannot get a valid token)
  async initDashboard() {
    const onAccountPage = window.location.pathname.includes('account.html');

    if (this.currentUser && onAccountPage) {
      // Cached user present - verify we have a token so API calls work
      const token = window.apiService && typeof window.apiService.ensureValidToken === 'function'
        ? await window.apiService.ensureValidToken()
        : (window.apiService && window.apiService.getAccessToken && window.apiService.getAccessToken()) || null;
      if (token) {
        if (!window.location.hash) window.location.hash = 'dashboard';
        this.updateDashboard();
        this.initAddItemForm();
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.setupSettingsForms());
        } else {
          this.setupSettingsForms();
        }
        return;
      }
      // Token invalid - fall through to token-first flow
    }

    if (onAccountPage) {
      // Token-first: try to get valid token (refresh if needed)
      const token = window.apiService && typeof window.apiService.ensureValidToken === 'function'
        ? await window.apiService.ensureValidToken()
        : null;
      if (!token) {
        logger.info('[initDashboard] No valid token, redirecting to register');
        window.location.href = 'register.html';
        return;
      }
      // We have a token - fetch user from API
      try {
        const profile = (window.apiService && typeof window.apiService.getUserProfile === 'function')
          ? await window.apiService.getUserProfile()
          : null;
        if (profile && profile.id) {
          logger.info('[initDashboard] Restored session from token, user from API');
          this.currentUser = profile;
          localStorage.setItem('usedfindr_user', JSON.stringify(profile));
          if (typeof migrateLocalCategoryFollows === 'function') {
            migrateLocalCategoryFollows(profile.id).catch(() => {});
          }
          if (!window.location.hash) window.location.hash = 'dashboard';
          this.updateDashboard();
          this.initAddItemForm();
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setupSettingsForms());
          } else {
            this.setupSettingsForms();
          }
          return;
        }
      } catch (e) {
        logger.warn('[initDashboard] Failed to fetch user after token', e);
      }
      window.location.href = 'register.html';
    }
  }
  
  // Modal handlers
  openAddItemModal() {
    logger.info('openAddItemModal called');
    const elements = showAddItemModalUI();
    if (!elements || !elements.modal) {
      logger.error('openAddItemModal: addItemModal element not found');
      return;
    }
    logger.info('Modal opened with transition');

    // Add ESC key listener
    AccountManager.addEscapeKeyListener();

    // Close handlers are now handled by inline onclick in HTML - no need for additional setup

    // Ensure form is initialized when modal is opened
    // This handles cases where form wasn't initialized on page load
    // Use setTimeout to ensure DOM is ready
    setTimeout(() => {
      try {
        this.initAddItemForm();
      } catch (error) {
        logger.error('Error initializing add item form:', error);
        // Continue anyway - modal is already open
      }
    }, 50);
    
    // Focus first input with a small delay to ensure modal is visible
    setTimeout(() => {
      const firstInput = document.getElementById('modalItemName');
      if (firstInput) {
        try {
          firstInput.focus();
        } catch (error) {
          logger.warn('Could not focus first input:', error);
        }
      }
    }, 150);
    
    // Close handlers are initialized in constructor, no need to do it here again
  }
  
  closeAddItemModal() {
    logger.info('closeAddItemModal called');
    AccountManager.removeEscapeKeyListener();
    const elements = hideAddItemModalUI(() => {
      logger.info('Modal closed and form reset');
    });

    if (!elements || !elements.modal) {
      logger.warn('closeAddItemModal: addItemModal not found');
    }
  }
  
  // Global close function that can be called without accountManager instance
  static closeAddItemModalGlobal() {
    logger.info('closeAddItemModalGlobal called');
    AccountManager.removeEscapeKeyListener();
    const elements = hideAddItemModalUI(() => {
      logger.info('Modal closed via global function');
    });

    if (!elements || !elements.modal) {
      logger.warn('closeAddItemModalGlobal: addItemModal not found');
    }
  }

  // Global open function that can be called from any page
  static openAddItemModalGlobal() {
    logger.info('openAddItemModalGlobal called');
    const elements = showAddItemModalUI();
    if (!elements || !elements.modal) {
      logger.error('openAddItemModalGlobal: addItemModal element not found');
      return;
    }

    logger.info('Modal opened via global function');

    // Add ESC key listener
    AccountManager.addEscapeKeyListener();
    
    // Category dropdowns removed - no longer needed

    // Initialize form if AccountManager is available, otherwise try global init
    setTimeout(() => {
      try {
        if (window.accountManager && typeof window.accountManager.initAddItemForm === 'function') {
          window.accountManager.initAddItemForm();
        } else {
          // Fallback: try to initialize form directly
          const form = document.getElementById('addItemForm');
          if (form && !form.dataset.initialized) {
            // Basic form initialization - you might need more complex setup
            logger.info('Initializing form via global fallback');
            form.dataset.initialized = 'true';
          }
        }
      } catch (error) {
        logger.error('Error initializing add item form globally:', error);
      }
    }, 50);

    // Initialize submission dropdown
    // initSubmissionDropdown();  // Removed: now handled in initAddItemForm
  }

  // Add ESC key listener for modal
  static addEscapeKeyListener() {
    // Remove existing listener if any
    if (window._modalEscapeListener) {
      document.removeEventListener('keydown', window._modalEscapeListener);
    }
    
    // Create and store new listener
    window._modalEscapeListener = function(e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        const modal = document.getElementById('addItemModal');
        if (modal && modal.dataset.state === 'open') {
          AccountManager.closeAddItemModalGlobal();
        }
      }
    };
    
    document.addEventListener('keydown', window._modalEscapeListener);
  }

  // Remove ESC key listener
  static removeEscapeKeyListener() {
    if (window._modalEscapeListener) {
      document.removeEventListener('keydown', window._modalEscapeListener);
      window._modalEscapeListener = null;
    }
  }
  
  // Initialize modal close handlers (called on page load)
  // Simplified - now using inline onclick handlers in HTML, this is just for any additional setup if needed
  initModalCloseHandlers() {
    // Inline onclick handlers in HTML handle the close functionality now
    // This function can be used for any additional setup if needed in the future
    logger.debug('Modal close handlers: Using inline onclick handlers in HTML');
  }
  
  async handleRegistration(e) {
    e.preventDefault();
    e.target.classList.add('form-submitted');
    
    // Clear all validation messages first
    const form = e.target;
    const inputs = form.querySelectorAll('input');
    inputs.forEach(input => this.clearValidationMessage(input));
    
    const formData = new FormData(e.target);
    const userData = {
      firstName: formData.get('firstName'),
      lastName: formData.get('lastName'),
      email: formData.get('email'),
      password: formData.get('password'),
      confirmPassword: formData.get('confirmPassword'),
      terms: formData.get('terms'),
      newsletter: formData.get('newsletter')
    };
    
    // Validate all fields
    let isValid = true;
    const firstNameInput = form.querySelector('#firstName');
    const lastNameInput = form.querySelector('#lastName');
    const emailInput = form.querySelector('#email');
    const passwordInput = form.querySelector('#password');
    const confirmPasswordInput = form.querySelector('#confirmPassword');
    const termsInput = form.querySelector('#terms');
    
    if (!this.validateInput(firstNameInput)) isValid = false;
    if (!this.validateInput(lastNameInput)) isValid = false;
    if (!this.validateInput(emailInput)) isValid = false;
    if (!this.validateInput(passwordInput)) isValid = false;
    
    // Custom validation for confirm password
    if (userData.password !== userData.confirmPassword) {
      this.showValidationMessage('confirmPassword', 'Passwords do not match');
      isValid = false;
    } else {
      this.clearValidationMessage(confirmPasswordInput);
    }
    
    // Custom validation for terms
    if (!userData.terms) {
      this.showValidationMessage('terms', 'Please accept the Terms of Service to continue');
      isValid = false;
    } else {
      this.clearValidationMessage(termsInput);
    }
    
    if (!isValid) {
      return;
    }
    
    // Get message and loading elements
    const messageEl = document.getElementById('registrationMessage');
    const loadingEl = document.getElementById('registrationLoading');
    
    // Hide any previous messages
    if (messageEl) {
      messageEl.style.display = 'none';
      messageEl.className = 'form-message';
      messageEl.textContent = '';
    }
    
    // Disable submit button and show loading state
    const submitButton = e.target.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Creating Account...';
    }
    
    // Show loading indicator
    if (loadingEl) {
      loadingEl.style.display = 'flex';
    }
    
    try {
      // Register via API
      if (!window.apiService) {
        throw new Error('API service not available');
      }
      
      const response = await window.apiService.register({
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        password: userData.password,
        newsletter: !!userData.newsletter,
        terms: userData.terms
      });
      
      if (response && response.success) {
        // Store user data locally for backward compatibility
        const newUser = {
          id: response.user.id.toString(),
          firstName: response.user.firstName || userData.firstName,
          lastName: response.user.lastName || userData.lastName,
          email: response.user.email,
          username: response.user.username,
          createdAt: new Date().toISOString(),
          newsletter: !!userData.newsletter,
          pointsBalance: 0,
          bonusEntries: 0,
          locked: false,
          deleted: false,
          reputation: 0
        };
        
        // Store user in users array
        const users = this.getAllUsers();
        users.push(newUser);
        localStorage.setItem('usedfindr_users', JSON.stringify(users));
        localStorage.setItem('usedfindr_user', JSON.stringify(newUser));
        
        // Token is already stored by apiService.register()
        
        // Update current user
        this.currentUser = newUser;
        
        // Hide loading and show success message
        if (loadingEl) {
          loadingEl.style.display = 'none';
        }
        if (messageEl) {
          messageEl.className = 'form-message success';
          messageEl.textContent = '✓ Account created successfully! Redirecting to your dashboard...';
          messageEl.style.display = 'flex';
        }
        
        // Redirect to dashboard after a brief delay to show success message
        setTimeout(() => {
          window.location.href = 'account.html#dashboard';
        }, 1500);
      } else {
        throw new Error('Registration failed. Please try again.');
      }
    } catch (error) {
      logger.error('Registration error:', error);
      
      // Hide loading indicator
      if (loadingEl) {
        loadingEl.style.display = 'none';
      }
      
      // Provide user-friendly error messages
      let errorMessage = 'Registration failed. Please try again.';
      if (error.message) {
        if (error.message.includes('already exists') || error.message.includes('409')) {
          errorMessage = error.message.includes('already exists') 
            ? error.message 
            : 'An account with this email already exists. Please use a different email or log in.';
        } else if (error.message.includes('400') || error.message.includes('required')) {
          errorMessage = 'Please fill in all required fields correctly.';
        } else if (error.message.includes('timeout') || error.message.includes('backend unavailable')) {
          errorMessage = 'The request took too long. Your account may have been created. Please try logging in, or try again in a moment.';
        } else if (error.message.includes('network') || error.message.includes('fetch') || error.message.includes('Failed to fetch')) {
          errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
        } else if (error.message.includes('API service not available')) {
          errorMessage = 'Service unavailable. Please refresh the page and try again.';
        } else {
          errorMessage = error.message;
        }
      }
      
      // Show error message inline
      if (messageEl) {
        messageEl.className = 'form-message error';
        messageEl.textContent = errorMessage;
        messageEl.style.display = 'flex';
        // Scroll to message
        messageEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    } finally {
      // Re-enable submit button
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = 'Create Account';
      }
    }
  }
  
  // Get all users from storage
  getAllUsers() {
    const usersData = localStorage.getItem('usedfindr_users');
    if (usersData) {
      try {
        return JSON.parse(usersData);
      } catch (e) {
        logger.error('Error parsing users data:', e);
        return [];
      }
    }
    return [];
  }
  // Update user in users array
  updateUserInArray(userId, updates) {
    const users = this.getAllUsers();
    const userIndex = users.findIndex(u => u.id === userId);
    if (userIndex !== -1) {
      users[userIndex] = { ...users[userIndex], ...updates };
      localStorage.setItem('usedfindr_users', JSON.stringify(users));
      return users[userIndex];
    }
    return null;
  }
  async handleLogin(e) {
    e.preventDefault();
    e.target.classList.add('form-submitted');
    
    // Clear all validation messages first
    const form = e.target;
    clearFormError(form);
    const inputs = form.querySelectorAll('input');
    inputs.forEach(input => this.clearValidationMessage(input));
    
    // Validate inputs
    const emailInput = form.querySelector('#email');
    const passwordInput = form.querySelector('#password');
    
    let isValid = true;
    if (!this.validateInput(emailInput)) isValid = false;
    if (!this.validateInput(passwordInput)) isValid = false;
    
    if (!isValid) {
      showFormError(form, 'Please enter your email and password.', { fieldIds: ['email', 'password'] });
      return;
    }
    
    const formData = new FormData(e.target);
    const email = formData.get('email');
    const password = formData.get('password');
    
    // Disable submit button during login
    const submitButton = e.target.querySelector('button[type="submit"]');
    const originalText = submitButton ? submitButton.textContent : '';
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Signing in...';
    }
    
    try {
      // Use API authentication with backend
      if (!window.apiService) {
        throw new Error('API service not available. Please refresh the page and try again.');
      }
      
      const response = await window.apiService.login(email, password, false, false); // Regular login, not admin
      
      if (response && response.user) {
        // Check if user is admin (defensive check - backend should have already rejected)
        if (response.user.isSuperuser || response.user.isAdmin) {
          // Admin user somehow got through - clear tokens and redirect
          logger.warn('[AccountManager] Admin user detected in regular login flow - redirecting to admin login');
          if (window.apiService && typeof window.apiService.clearAccessToken === 'function') {
            window.apiService.clearAccessToken();
          }
          alert('Admin accounts must log in through the admin portal. Redirecting...');
          window.location.href = 'admin-login.html';
          return;
        }
        
        // Store user data in localStorage for backward compatibility
        const userData = {
          id: response.user.id,
          email: response.user.email,
          username: response.user.username,
          firstName: response.user.firstName || response.user.first_name,
          lastName: response.user.lastName || response.user.last_name,
          lastLogin: new Date().toISOString(),
          reputation: response.user.reputation || 0
        };
        
        localStorage.setItem('usedfindr_user', JSON.stringify(userData));
        
        // Fetch full profile using token from login response so auth is sent even if in-memory state was cleared (e.g. cached SW/old bundle)
        try {
          const fullProfile = await window.apiService.getUserProfile(response.access);
          if (fullProfile) {
            const completeUserData = { ...userData, ...fullProfile };
            localStorage.setItem('usedfindr_user', JSON.stringify(completeUserData));
            // Update currentUser if set
            if (this.currentUser) {
              this.currentUser = completeUserData;
            }
          }
        } catch (profileError) {
          logger.warn('Could not fetch full profile after login:', profileError);
        }
        
        // Re-store user so account page has it even if profile 401 triggered clearAccessToken
        localStorage.setItem('usedfindr_user', JSON.stringify(userData));
        
        // Update AccountManager current user if available
        if (this.currentUser) {
          this.currentUser = { ...this.currentUser, ...userData };
        }
        
        // Migrate localStorage data to API
        try {
          await this.migrateSavedItemsToAPI();
          await this.migrateUserFindsToAPI();
          if (typeof migrateLocalCategoryFollows === 'function') {
            await migrateLocalCategoryFollows(userData.id);
          }
          if (window.categoryFollowingManager) {
            await window.categoryFollowingManager.loadFromApi(true);
          }
        } catch (migrationError) {
          logger.warn('Error during data migration:', migrationError);
          // Don't block login if migration fails
        }
        
        // Remember email for next visit if "Keep me signed in" is checked (UI-only; backend can use for extended session if supported)
        const rememberMe = form.querySelector('#rememberMe');
        if (rememberMe && rememberMe.checked) {
          try { localStorage.setItem('usedfindr_remember_email', email || ''); } catch (e) {}
        } else {
          try { localStorage.removeItem('usedfindr_remember_email'); } catch (e) {}
        }
        
        // Return the user to wherever they came from (e.g. a product page they
        // tried to watch), falling back to the dashboard.
        window.location.href = getSafeReturnTo() || 'account.html#dashboard';
      } else {
        showFormError(form, 'Login failed. Please check your credentials and try again.', { fieldIds: ['email', 'password'] });
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = originalText;
        }
      }
    } catch (error) {
      logger.error('Login error:', error);
      
      // Provide user-friendly error messages
      let errorMessage = 'Login failed. Please try again.';
      if (error.message) {
        if (error.message.includes('Admin accounts must log in') || error.message.includes('admin portal')) {
          // Admin user tried regular login - redirect to admin login
          errorMessage = 'Admin accounts must log in through the admin portal. Redirecting...';
          setTimeout(() => {
            window.location.href = 'admin-login.html';
          }, 1500);
        } else if (error.message.includes('401') || error.message.includes('Invalid') || error.message.includes('credentials')) {
          errorMessage = 'Invalid email or password. Please try again.';
        } else if (error.message.includes('404') || error.message.includes('not found')) {
          errorMessage = 'No account found with that email. Please create an account first.';
        } else if (error.message.includes('403') || error.message.includes('Forbidden') || error.message.includes('CSRF')) {
          errorMessage = 'Authentication error. Please refresh the page and try again.';
        } else if (error.message.includes('500') || error.message.includes('Internal server error') || error.message.includes('data stream')) {
          errorMessage = 'Server error occurred. The backend may be experiencing issues. Please try again in a moment.';
      } else {
          errorMessage = error.message;
        }
      }
      
      // Check error status for more specific messages
      if (error.status === 500) {
        errorMessage = 'Server error occurred. Please try again in a moment.';
      } else if (error.status === 403) {
        errorMessage = 'Authentication error. Please refresh the page and try again.';
      }
      
      showFormError(form, errorMessage, { fieldIds: ['email', 'password'] });
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = originalText;
      }
    }
  }
  
  initAddItemForm() {
    const addItemForm = document.getElementById('addItemForm');
    if (!addItemForm) {
      logger.warn('initAddItemForm: addItemForm element not found. Will retry when modal opens.');
      return;
    }
    
    // Check if already initialized to prevent duplicate listeners
    if (addItemForm.dataset.initialized === 'true') {
      logger.info('Form already initialized, skipping');
      return;
    }
    
    logger.info('Initializing add item form...');
    addItemForm.dataset.initialized = 'true';

    // Reset per-modal autofill request count so each modal open gets a fresh allowance
    this._autofillRequestCount = 0;
    
    // Store a reference to 'this' for use in event handlers
    const self = this;
    
    addItemForm.addEventListener('submit', function(e) {
      e.preventDefault();
      e.stopPropagation(); // Prevent any other handlers from running
      
      // Prevent double submission
      if (addItemForm.dataset.processing === 'true') {
        logger.info('Form already processing, ignoring duplicate submission');
        return;
      }
      
      addItemForm.dataset.processing = 'true';
      
      // Track form submission
      trackFormSubmit('add_item', 'product_submission');
      
      self.handleAddItem(e);
    });
    
    // Initialize price field formatting (check both modal and old fields)
    const priceField = document.getElementById('modalItemPrice') || document.getElementById('itemPrice');
    if (priceField) {
      // Only add listeners if not already initialized (check for a marker)
      if (!priceField.dataset.priceInitialized) {
        priceField.dataset.priceInitialized = 'true';
        
        // Format on input
        priceField.addEventListener('input', (e) => {
          this.formatPriceField(e.target);
        });
        
        // Format on focus (ensure $ is there if value exists)
        priceField.addEventListener('focus', (e) => {
          if (e.target.value && !e.target.value.startsWith('$')) {
            this.formatPriceField(e.target);
          }
        });
        
        // Format on blur (ensure proper formatting)
        priceField.addEventListener('blur', (e) => {
          this.formatPriceField(e.target);
        });
      }
    }
    
    // Initialize condition pills
    this.initConditionPills();
    
    // Initialize URL field (validation and open link button)
    this.initUrlField();
    
    // Setup eBay URL auto-fill on paste
    this.setupEbayUrlAutoFill();
    
    logger.info('Add item form initialized successfully');

    // Add global variable at the top if not present, but since it's class, perhaps inside class
    // In initAddItemForm method, after addItemForm.dataset.initialized = 'true';

    // Add the functions before or after initAddItemForm

    let suggestionTimeout;  // For debouncing - add this at the top of the file or in class

    function initSubmissionDropdown() {
      logger.info('initSubmissionDropdown called');
      const nameInput = document.getElementById('modalItemName');
      const suggestionsDiv = document.getElementById('nameSuggestions');
      const hiddenNormId = document.getElementById('normalizedProductId');

      if (!nameInput || !suggestionsDiv || !hiddenNormId) {
        logger.warn('initSubmissionDropdown: Required elements not found');
        return;
      }

      let timeout;

      const updateDropdownPosition = () => {
        // Simple positioning: place dropdown directly below the input
        // The form-group has position: relative, so we can use offsetTop/offsetHeight
        const inputTop = nameInput.offsetTop;
        const inputHeight = nameInput.offsetHeight;
        const inputWidth = nameInput.offsetWidth;
        
        // Position dropdown below input with small gap
        suggestionsDiv.style.top = `${inputTop + inputHeight + 4}px`;
        suggestionsDiv.style.left = `${nameInput.offsetLeft}px`;
        suggestionsDiv.style.width = `${inputWidth}px`;
        
        logger.info('[initSubmissionDropdown] Positioned dropdown:', {
          top: suggestionsDiv.style.top,
          left: suggestionsDiv.style.left,
          width: suggestionsDiv.style.width,
          inputTop,
          inputHeight
        });
      };

      const search = async (query) => {
        if (query.length < 3) {
          suggestionsDiv.style.display = 'none';
          return;
        }
        try {
          logger.info('[initSubmissionDropdown] Searching for:', query);
          const data = await window.apiService.request(`${window.apiService.baseUrl}/products/search-normalized/?q=${encodeURIComponent(query)}`);
          logger.info('[initSubmissionDropdown] API response:', data);
          if (!data) {
            logger.warn('[initSubmissionDropdown] No data returned');
            suggestionsDiv.style.display = 'none';
            return;
          }
          suggestionsDiv.innerHTML = '';
          const matches = data.matches || [];
          logger.info('[initSubmissionDropdown] Found matches:', matches.length);
          matches.slice(0, 5).forEach(match => {
            const item = document.createElement('div');
            item.className = 'suggestion-item';
            item.textContent = match.canonical_name || match.name || 'Unknown';
            item.onclick = () => {
              nameInput.value = match.canonical_name || match.name;
              hiddenNormId.value = match.normalized_id || match.normalized_product_id || '';
              suggestionsDiv.style.display = 'none';
            };
            suggestionsDiv.appendChild(item);
          });
          if (matches.length > 0) {
            logger.info('[initSubmissionDropdown] Showing dropdown with', matches.length, 'items');
            updateDropdownPosition();
            suggestionsDiv.style.display = 'block';
            logger.info('[initSubmissionDropdown] Dropdown display set to block, computed style:', window.getComputedStyle(suggestionsDiv).display);
          } else {
            logger.info('[initSubmissionDropdown] No matches, hiding dropdown');
            suggestionsDiv.style.display = 'none';
          }
        } catch (e) {
          logger.error('[initSubmissionDropdown] Search error:', e);
          suggestionsDiv.style.display = 'none';
        }
      };

      nameInput.oninput = () => {
        clearTimeout(timeout);
        timeout = setTimeout(() => search(nameInput.value.trim()), 300);
        hiddenNormId.value = '';
      };

      // Close dropdown when clicking outside (use capture to handle properly)
      const handleClickOutside = (e) => {
        // Check if click is outside both the input and the dropdown
        if (!suggestionsDiv.contains(e.target) && e.target !== nameInput && !nameInput.contains(e.target)) {
          suggestionsDiv.style.display = 'none';
        }
      };
      
      // Use capture phase and remove previous listeners
      document.removeEventListener('click', handleClickOutside, true);
      document.addEventListener('click', handleClickOutside, true);

      // Update position on window resize or scroll
      window.addEventListener('resize', () => {
        if (suggestionsDiv.style.display === 'block') {
          updateDropdownPosition();
        }
      });

      // Update position when modal opens
      const modal = document.getElementById('addItemModal');
      if (modal) {
        const observer = new MutationObserver(() => {
          if (suggestionsDiv.style.display === 'block') {
            updateDropdownPosition();
          }
        });
        observer.observe(modal, { attributes: true, attributeFilter: ['class', 'style'] });
      }

      logger.info('Basic dropdown initialized');
    }

    // In initAddItemForm, after existing setup:
    initSubmissionDropdown();

    // In the submit event listener, when building formData:
    const normalizedProductIdValue = document.getElementById('normalizedProductId')?.value || '';
    if (normalizedProductIdValue) {
      formData.normalizedProductId = normalizedProductIdValue;
    }

    // Then in apiService.createSubmission(formData)
  }
  
  formatUsername(userId, fullName) {
    if (!userId || !fullName) return '@Anonymous';
    
    // Create username from full name: "John Doe" -> "@Jdoe" or "@JohnDoe"
    const nameParts = fullName.trim().split(' ');
    if (nameParts.length >= 2) {
      const firstName = nameParts[0];
      const lastName = nameParts[nameParts.length - 1];
      // Use first letter of first name + last name
      return `@${firstName.charAt(0)}${lastName}`;
    } else if (nameParts.length === 1) {
      return `@${nameParts[0]}`;
    }
    return '@User';
  }
  formatPriceField(input) {
    let value = input.value.replace(/[^0-9.]/g, ''); // Remove all non-numeric except decimal
    
    // If empty, clear and return
    if (value === '') {
      input.value = '';
      return;
    }
    
    // Remove leading zeros except if it's just "0" or "0."
    if (value.startsWith('0') && value.length > 1 && value[1] !== '.') {
      value = value.substring(1);
    }
    
    // Ensure only one decimal point
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Limit to 2 decimal places
    if (parts.length === 2 && parts[1].length > 2) {
      value = parts[0] + '.' + parts[1].substring(0, 2);
    }
    
    // Update value (don't add $ prefix since it's now in CSS)
    input.value = value;
  }
  
  initConditionPills() {
    const pills = document.querySelectorAll('.condition-pill');
    const hiddenInput = document.getElementById('modalItemCondition');
    
    if (!hiddenInput) {
      logger.warn('Condition hidden input not found');
      return;
    }
    
    pills.forEach(pill => {
      pill.addEventListener('click', () => {
        // Remove active class from all pills
        pills.forEach(p => p.classList.remove('active'));
        
        // Add active class to clicked pill
        pill.classList.add('active');
        
        // Set hidden input value
        const condition = pill.getAttribute('data-condition');
        hiddenInput.value = condition;
        
        // Trigger validation
        hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }
  
  /**
   * Highlight the condition pill that matches the given condition value.
   * This is a reusable helper function for auto-filling condition from enriched data.
   * 
   * @param {string} conditionValue - The condition value to highlight (e.g., "New", "Like New", etc.)
   * @returns {boolean} - Returns true if a matching pill was found and highlighted, false otherwise
   */
  highlightConditionPill(conditionValue) {
    if (!conditionValue || typeof conditionValue !== 'string') {
      return false;
    }
    
    const container = document.querySelector('#addItemModal .condition-pills');
    const pills = container ? container.querySelectorAll('.condition-pill') : document.querySelectorAll('.condition-pill');
    const hiddenInput = document.getElementById('modalItemCondition');
    const normalizedCondition = conditionValue.trim().toLowerCase();
    
    let pillMatched = false;
    
    pills.forEach(pill => {
      pill.classList.remove('active');
      const pillCondition = pill.getAttribute('data-condition');
      const pillNorm = pillCondition ? pillCondition.trim().toLowerCase() : '';
      
      if (pillNorm && (pillNorm === normalizedCondition || _conditionDescriptionMatches(normalizedCondition, pillNorm))) {
        pillMatched = true;
        // Small delay for smooth animation
        setTimeout(() => {
          pill.classList.add('active');
          logger.info(`[highlightConditionPill] Condition pill activated: "${pillCondition}"`);
        }, 50);
      }
    });
    
    // Update hidden input value if a match was found
    if (pillMatched && hiddenInput) {
      // Find the exact condition value from the pill (preserving original case)
      pills.forEach(pill => {
        const pillCondition = pill.getAttribute('data-condition');
        const pNorm = pillCondition ? pillCondition.trim().toLowerCase() : '';
        if (pillCondition && pNorm && (pNorm === normalizedCondition || _conditionDescriptionMatches(normalizedCondition, pNorm))) {
          hiddenInput.value = pillCondition;
          hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    } else if (conditionValue && !pillMatched) {
      logger.warn(`[highlightConditionPill] Condition "${conditionValue}" did not match any pill. Available pills:`, 
        Array.from(pills).map(p => p.getAttribute('data-condition')));
    }
    
    return pillMatched;
  }
  
  initUrlField() {
    const urlInput = document.getElementById('modalItemUrl');
    const openLinkBtn = document.getElementById('openUrlLinkBtn');
    const submitButton = document.querySelector('#addItemForm .submit-button');
    const validationIcon = document.getElementById('urlValidationIcon');
    
    if (!urlInput) {
      logger.warn('URL input not found');
      return;
    }
    
    // Validate eBay listing URL (item or product page)
    const validateEbayUrl = (url) => {
      if (!url || url.trim() === '') return false;
      
      let normalizedUrl = url.trim();
      if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
        normalizedUrl = 'https://' + normalizedUrl;
      }
      
      // Allow eBay domains (main + common country TLDs)
      const ebayHostPattern = /^(https?:\/\/)?(www\.)?(ebay\.(com|co\.uk|de|fr|it|es|com\.au|ca|ie|at|ch|be|nl|pl|com\.my|com\.sg|com\.hk|co\.jp|com\.mx|com\.br))/i;
      if (!ebayHostPattern.test(normalizedUrl)) return false;
      
      // Require item or product path (/itm/ or /p/)
      return /\/itm\/[A-Za-z0-9|]+/i.test(normalizedUrl) || /\/p\/\d+/i.test(normalizedUrl);
    };
    
    // Create SVG icons
    const createCheckmarkIcon = () => {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '20');
      svg.setAttribute('height', '20');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('stroke', 'currentColor');
      svg.setAttribute('stroke-width', '2');
      svg.setAttribute('stroke-linecap', 'round');
      svg.setAttribute('stroke-linejoin', 'round');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M20 6L9 17l-5-5');
      svg.appendChild(path);
      return svg;
    };
    
    const createLinkIcon = () => {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '20');
      svg.setAttribute('height', '20');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('stroke', 'currentColor');
      svg.setAttribute('stroke-width', '2');
      svg.setAttribute('stroke-linecap', 'round');
      svg.setAttribute('stroke-linejoin', 'round');
      const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path1.setAttribute('d', 'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71');
      svg.appendChild(path1);
      const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path2.setAttribute('d', 'M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71');
      svg.appendChild(path2);
      return svg;
    };
    
    // Update validation icon
    const updateValidationIcon = (isValid, hasValue) => {
      if (!validationIcon) return;
      
      // Clear existing content
      validationIcon.innerHTML = '';
      validationIcon.className = 'url-validation-icon';
      
      if (hasValue) {
        if (isValid) {
          // Show green checkmark
          validationIcon.classList.add('valid');
          validationIcon.appendChild(createCheckmarkIcon());
        } else {
          // Show link icon (gray)
          validationIcon.classList.add('invalid');
          validationIcon.appendChild(createLinkIcon());
        }
      }
      // If no value, don't show any icon
    };
    
    // Update Open Link button visibility and submit button state
    const updateUrlState = () => {
      const url = urlInput.value.trim();
      const isValid = validateEbayUrl(url);
      const hasValue = url.length > 0;
      
      // Update validation icon
      updateValidationIcon(isValid, hasValue);
      
      // Show/hide Open Link button
      if (openLinkBtn) {
        if (isValid && hasValue) {
          openLinkBtn.style.display = 'block';
        } else {
          openLinkBtn.style.display = 'none';
        }
      }
      
      // Enable/disable submit button based on URL validity only
      // Other fields (name, price, condition) are optional - backend will auto-populate from eBay API
      if (submitButton) {
        if (isValid) {
          submitButton.disabled = false;
        } else {
          submitButton.disabled = true;
        }
      }
    };
    
    // Listen for URL input changes
    urlInput.addEventListener('input', updateUrlState);
    urlInput.addEventListener('blur', updateUrlState);
    
    // Note: No longer listening for condition/name/price changes since they're optional
    // Submit button state is now only based on URL validity
    
    // Initialize state on load
    updateUrlState();
    
    // Open Link button click handler
    if (openLinkBtn) {
      openLinkBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        let url = urlInput.value.trim();
        if (!url) return;
        
        // Normalize URL
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          url = 'https://' + url;
        }
        
        // Open in new tab
        window.open(url, '_blank', 'noopener,noreferrer');
      });
    }
    
    // Initial state update
    updateUrlState();
  }
  
  setupEbayUrlAutoFill() {
    const urlInput = document.getElementById('modalItemUrl');
    const titleInput = document.getElementById('modalItemName');
    const priceInput = document.getElementById('modalItemPrice');
    const conditionInput = document.getElementById('modalItemCondition');
    const imagePreview = document.getElementById('ebayItemImagePreview');
    const imagePreviewContainer = document.getElementById('ebayItemImagePreviewContainer');
    const validationIcon = document.getElementById('urlValidationIcon');
    
    if (!urlInput) {
      logger.warn('URL input not found for auto-fill setup');
      return;
    }
    
    // Track if we're currently fetching to prevent duplicate requests
    let isFetching = false;
    
    // Create spinner SVG icon
    const createSpinnerIcon = () => {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '20');
      svg.setAttribute('height', '20');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('stroke', 'currentColor');
      svg.setAttribute('stroke-width', '2');
      svg.setAttribute('stroke-linecap', 'round');
      svg.setAttribute('stroke-linejoin', 'round');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M21 12a9 9 0 1 1-6.219-8.56');
      svg.appendChild(path);
      return svg;
    };
    
    // Create error X icon
    const createErrorIcon = () => {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '20');
      svg.setAttribute('height', '20');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('stroke', 'currentColor');
      svg.setAttribute('stroke-width', '2');
      svg.setAttribute('stroke-linecap', 'round');
      svg.setAttribute('stroke-linejoin', 'round');
      const line1 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line1.setAttribute('x1', '18');
      line1.setAttribute('y1', '6');
      line1.setAttribute('x2', '6');
      line1.setAttribute('y2', '18');
      svg.appendChild(line1);
      const line2 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line2.setAttribute('x1', '6');
      line2.setAttribute('y1', '6');
      line2.setAttribute('x2', '18');
      line2.setAttribute('y2', '18');
      svg.appendChild(line2);
      return svg;
    };
    
    // Create success checkmark icon
    const createSuccessIcon = () => {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '20');
      svg.setAttribute('height', '20');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('stroke', 'currentColor');
      svg.setAttribute('stroke-width', '2');
      svg.setAttribute('stroke-linecap', 'round');
      svg.setAttribute('stroke-linejoin', 'round');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M20 6L9 17l-5-5');
      svg.appendChild(path);
      return svg;
    };
    
    // Show loading state in validation icon
    const showLoadingState = () => {
      if (validationIcon) {
        validationIcon.innerHTML = '';
        validationIcon.appendChild(createSpinnerIcon());
        validationIcon.className = 'url-validation-icon loading';
      }
      urlInput.style.borderColor = '#ff9900'; // Orange border during loading
      urlInput.disabled = true; // Disable input during loading
    };
    
    // Clear loading state
    const clearLoadingState = () => {
      if (validationIcon) {
        validationIcon.innerHTML = '';
        validationIcon.className = 'url-validation-icon';
      }
      urlInput.disabled = false; // Re-enable input
    };
    
    // Show error state with visual feedback
    const showErrorState = (errorMessage) => {
      // Always show notification with fallback
      if (this.showNotification) {
        this.showNotification(errorMessage, 'error');
      } else {
        // Fallback: Show inline error message
        const existingError = urlInput.parentElement.querySelector('.url-error-message');
        if (existingError) {
          existingError.remove();
        }
        const errorElement = document.createElement('div');
        errorElement.className = 'url-error-message';
        errorElement.textContent = errorMessage;
        urlInput.parentElement.appendChild(errorElement);
        setTimeout(() => {
          if (errorElement.parentElement) {
            errorElement.remove();
          }
        }, 5000);
      }
      
      // Visual error indicators
      if (validationIcon) {
        validationIcon.innerHTML = '';
        validationIcon.appendChild(createErrorIcon());
        validationIcon.className = 'url-validation-icon error';
      }
      urlInput.style.borderColor = '#dc2626'; // Red border
      urlInput.classList.add('error-state');
    };
    
    // Clear error state
    const clearErrorState = () => {
      urlInput.classList.remove('error-state');
      urlInput.style.borderColor = '';
      if (validationIcon) {
        validationIcon.innerHTML = '';
        validationIcon.className = 'url-validation-icon';
      }
      // Remove any inline error messages
      const errorMsg = urlInput.parentElement.querySelector('.url-error-message');
      if (errorMsg) {
        errorMsg.remove();
      }
    };
    
    // Add field auto-fill animation
    const animateFieldFill = (field) => {
      if (field) {
        field.classList.add('field-auto-filled');
        setTimeout(() => {
          field.classList.remove('field-auto-filled');
        }, 400);
      }
    };

    // Debounce timer for input-triggered autofill; cleared when runUrlAutofill starts
    let autofillDebounceTimer = null;
    const AUTOFILL_DEBOUNCE_MS = 550;
    const MIN_URL_LENGTH_FOR_DEBOUNCE = 25;
    const AUTOFILL_SESSION_CAP = 25;

    // Validate URL is suitable for resolve-ebay-item (same domains/path as submit)
    const isValidEbayUrlForAutofill = (url) => {
      if (!url || typeof url !== 'string') return false;
      const trimmed = url.trim();
      const lower = trimmed.toLowerCase();
      const ebayDomains = ['ebay.com', 'ebay.co.uk', 'ebay.de', 'ebay.fr', 'ebay.it', 'ebay.es', 'ebay.com.au', 'ebay.ca', 'ebay.ie', 'ebay.at', 'ebay.ch', 'ebay.be', 'ebay.nl', 'ebay.pl'];
      const hasEbayDomain = ebayDomains.some(d => lower.includes(d));
      if (!hasEbayDomain) return false;
      return /\/itm\/[A-Za-z0-9|]+/i.test(trimmed) || /\/p\/\d+/.test(trimmed);
    };

    /**
     * Single entry point for URL autofill: validates URL, calls resolve-ebay-item, fills form.
     * Called from paste (with delay), change (dropdown/autocomplete), and debounced input.
     */
    const runUrlAutofill = async () => {
      if (autofillDebounceTimer != null) {
        clearTimeout(autofillDebounceTimer);
        autofillDebounceTimer = null;
      }
      const url = urlInput.value.trim();
      if (!isValidEbayUrlForAutofill(url)) return;
      if (isFetching) return;

      const count = (this._autofillRequestCount || 0);
      if (count >= AUTOFILL_SESSION_CAP) {
        if (this.showNotification) {
          this.showNotification('Too many lookups this session. Please try again later.', 'error');
        }
        return;
      }
      this._autofillRequestCount = count + 1;

      clearErrorState();
      const refurbishedIndicatorEl = document.getElementById('addItemRefurbishedIndicator');
      if (refurbishedIndicatorEl) {
        refurbishedIndicatorEl.style.display = 'none';
        refurbishedIndicatorEl.textContent = '';
      }
      isFetching = true;
      showLoadingState();
      const originalBorderColor = urlInput.style.borderColor;

      try {
        const response = await apiService.get(`/products/resolve-ebay-item/?url=${encodeURIComponent(url)}`);

        const resCatId = response?.ebay_category_id ?? response?.ebayCategoryId;
        const resCatPath = response?.ebay_category_path ?? response?.ebayCategoryPath;
        logger.info('[setupEbayUrlAutoFill] Full API response:', response);
        logger.info('[setupEbayUrlAutoFill] condition:', response?.condition, 'condition_id:', response?.condition_id, 'ebay_category_id:', resCatId, 'ebay_category_path:', resCatPath || '(none)');
        if (resCatId == null || String(resCatId).trim() === '') {
          logger.warn('[setupEbayUrlAutoFill] resolve-ebay-item did not return ebay_category_id - category will be empty on submit and in approval until backend backfill or manual selection');
        }

        if (response && response.success) {
          let conditionApplied = false;
          if (titleInput && response.title) {
            titleInput.value = response.title;
            animateFieldFill(titleInput);
          }

          if (priceInput && response.price) {
            const priceValue = response.price.replace(/^\$/, '');
            priceInput.value = priceValue;
            animateFieldFill(priceInput);
            priceInput.dispatchEvent(new Event('input', { bubbles: true }));
          }

          const ebayCatIdForPills = response.ebay_category_id ?? response.ebayCategoryId;
          if (ebayCatIdForPills && typeof window.fetchConditionPolicies === 'function' && typeof window.renderConditionPills === 'function') {
            const conditions = await window.fetchConditionPolicies(ebayCatIdForPills);
            if (conditions && conditions.length > 0) {
              const container = document.querySelector('#addItemModal .condition-pills');
              if (container) {
                const selectedValue = response.condition_id != null ? response.condition_id : response.condition;
                const pillMatched = window.renderConditionPills(container, conditions, selectedValue, {
                  hiddenInputId: 'modalItemCondition',
                  dataAttribute: 'data-condition'
                });
                if (pillMatched && conditionInput && (response.condition || response.condition_id)) {
                  animateFieldFill(conditionInput);
                }
                logger.info('[setupEbayUrlAutoFill] Category condition pills rendered and selection set');
                conditionApplied = pillMatched;
                if (!pillMatched && (response.condition || response.condition_id)) {
                  conditionApplied = this.highlightConditionPill(response.condition || '');
                }
              } else {
                conditionApplied = this.highlightConditionPill(response.condition || '');
              }
            } else {
              conditionApplied = this.highlightConditionPill(response.condition || '');
            }
          } else if (response.condition) {
            const conditionValue = response.condition.trim();
            const pillMatched = this.highlightConditionPill(conditionValue);
            conditionApplied = pillMatched;
            if (pillMatched && conditionInput) {
              animateFieldFill(conditionInput);
              logger.info(`[setupEbayUrlAutoFill] Condition "${conditionValue}" auto-filled and pill highlighted`);
            } else if (conditionValue && conditionInput) {
              conditionInput.value = conditionValue;
              conditionInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
          if (!conditionApplied && response.condition_id != null && window.DEFAULT_CONDITION_PILLS) {
            const cid = String(response.condition_id).trim();
            const match = window.DEFAULT_CONDITION_PILLS.find(p => String(p.conditionId).trim() === cid);
            if (match && match.conditionDescription) {
              conditionApplied = this.highlightConditionPill(match.conditionDescription);
              if (conditionApplied && conditionInput) {
                animateFieldFill(conditionInput);
                logger.info('[setupEbayUrlAutoFill] Condition from condition_id:', match.conditionDescription);
              }
            }
          }
          if (!conditionApplied) {
            logger.debug('[setupEbayUrlAutoFill] Condition not applied');
          }

          if (imagePreview && imagePreviewContainer && response.image_url) {
            imagePreview.src = response.image_url;
            imagePreviewContainer.classList.remove('hidden');
            imagePreviewContainer.classList.add('image-preview-visible');
          }

          if (validationIcon) {
            validationIcon.innerHTML = '';
            validationIcon.appendChild(createSuccessIcon());
            validationIcon.className = 'url-validation-icon valid';
          }

          urlInput.style.borderColor = '#4CAF50';
          setTimeout(() => {
            urlInput.style.borderColor = originalBorderColor || '';
          }, 3000);

          if (this.showNotification) {
            this.showNotification('✓ Item details loaded', 'success');
          }

          const catId = (resCatId != null && String(resCatId).trim()) ? String(resCatId).trim() : '';
          const catPath = (resCatPath != null && String(resCatPath).trim()) ? String(resCatPath).trim() : '';
          const form = document.getElementById('addItemForm');
          const ensureHidden = (id, name) => {
            let el = document.getElementById(id);
            if (!el && form) {
              el = document.createElement('input');
              el.type = 'hidden';
              el.id = id;
              el.name = name;
              form.appendChild(el);
            }
            return el;
          };
          const idEl = ensureHidden('modalEbayCategoryId', 'ebay_category_id');
          const pathEl = ensureHidden('modalEbayCategoryPath', 'ebay_category_path');
          if (idEl) idEl.value = catId;
          if (pathEl) pathEl.value = catPath;
          if (catId) {
            logger.info('[setupEbayUrlAutoFill] Stored ebay_category_id for submit:', catId);
          } else {
            logger.info('[setupEbayUrlAutoFill] No category to store (hidden inputs set to empty)');
          }
          const conditionIdEl = ensureHidden('modalConditionId', 'condition_id');
          const refurbishedTypeEl = ensureHidden('modalRefurbishedType', 'refurbished_type');
          if (conditionIdEl) conditionIdEl.value = (response.condition_id != null ? String(response.condition_id) : '');
          if (refurbishedTypeEl) refurbishedTypeEl.value = (response.refurbished_type || response.refurbishedType || '');
          const categoryGroup = document.getElementById('addItemModalEbayCategoryGroup');
          const categoryDisplay = document.getElementById('addItemModalEbayCategoryDisplay');
          if (categoryGroup && categoryDisplay) {
            if (catPath || catId) {
              categoryDisplay.textContent = catPath || `Category ID: ${catId}`;
              categoryGroup.style.display = '';
            } else {
              categoryGroup.style.display = 'none';
              categoryDisplay.textContent = '';
            }
          }
          // Refurbished type indicator (Certified / eBay Refurbished / Seller refurbished)
          const addItemRefurbishedEl = document.getElementById('addItemRefurbishedIndicator');
          if (addItemRefurbishedEl) {
            const rt = response.refurbished_type || response.refurbishedType;
            const getContent = typeof getRefurbishedIndicatorContent === 'function' ? getRefurbishedIndicatorContent : (window.getRefurbishedIndicatorContent && window.getRefurbishedIndicatorContent.bind(window));
            const content = getContent ? getContent(rt) : '';
            if (content) {
              addItemRefurbishedEl.innerHTML = '';
              if (content instanceof DocumentFragment) {
                addItemRefurbishedEl.appendChild(content);
              } else {
                addItemRefurbishedEl.textContent = content;
              }
              addItemRefurbishedEl.style.display = '';
            } else {
              addItemRefurbishedEl.innerHTML = '';
              addItemRefurbishedEl.style.display = 'none';
            }
          }
        }
      } catch (error) {
        logger.error('Failed to fetch eBay item details:', error);
        const idEl = document.getElementById('modalEbayCategoryId');
        const pathEl = document.getElementById('modalEbayCategoryPath');
        const conditionIdEl = document.getElementById('modalConditionId');
        const refurbishedTypeEl = document.getElementById('modalRefurbishedType');
        if (idEl) idEl.value = '';
        if (pathEl) pathEl.value = '';
        if (conditionIdEl) conditionIdEl.value = '';
        if (refurbishedTypeEl) refurbishedTypeEl.value = '';
        const categoryGroup = document.getElementById('addItemModalEbayCategoryGroup');
        const categoryDisplay = document.getElementById('addItemModalEbayCategoryDisplay');
        if (categoryGroup) categoryGroup.style.display = 'none';
        if (categoryDisplay) categoryDisplay.textContent = '';

        if (error.status === 429) {
          const retryMinutes = Math.ceil((error.data?.retry_after || error.response?.retry_after || 3600) / 60);
          clearErrorState();
          showErrorState(`Rate limit exceeded. Please try again in ${retryMinutes} minute${retryMinutes !== 1 ? 's' : ''}.`);
        } else if (error.status === 503) {
          clearErrorState();
          showErrorState('Service temporarily unavailable. Please try again tomorrow.');
        } else {
          let errorMessage = 'Failed to fetch item details. Please try again.';
          if (error.response || error.status) {
            const status = error.status || (error.response && error.response.status);
            if (status === 400) {
              errorMessage = 'Invalid eBay URL format. Please check the link and try again.';
            } else if (status === 404) {
              errorMessage = 'eBay item not found. The listing may have ended or been removed.';
            } else if (status === 401 || status === 403) {
              errorMessage = 'Authentication expired. Please refresh the page.';
            } else if (status >= 500) {
              errorMessage = 'Server error. Please try again in a moment.';
            }
          } else if (error.message) {
            if (error.message.includes('timeout') || error.message.includes('Timeout')) {
              errorMessage = 'Request timed out. Please try again.';
            } else if (error.message.includes('network') || error.message.includes('Network')) {
              errorMessage = 'Connection failed. Please check your internet and try again.';
            }
          }
          showErrorState(errorMessage);
        }
      } finally {
        isFetching = false;
        clearLoadingState();
      }
    };

    // Paste: wait for value to be in input, then run autofill
    urlInput.addEventListener('paste', () => {
      setTimeout(() => runUrlAutofill(), 100);
    });

    // Change: e.g. browser autocomplete / dropdown selection
    urlInput.addEventListener('change', () => {
      runUrlAutofill();
    });

    // Input: clear error and hide preview when empty; debounced autofill when URL looks valid
    urlInput.addEventListener('input', () => {
      const url = urlInput.value.trim();

      if (urlInput.classList.contains('error-state')) {
        clearErrorState();
      }

      if (!url && imagePreviewContainer) {
        imagePreviewContainer.classList.add('hidden');
        imagePreviewContainer.classList.remove('image-preview-visible');
        if (imagePreview) {
          imagePreview.src = '';
        }
      }
      if (!url) {
        const addItemRefurbishedEl = document.getElementById('addItemRefurbishedIndicator');
        if (addItemRefurbishedEl) {
          addItemRefurbishedEl.style.display = 'none';
          addItemRefurbishedEl.innerHTML = '';
        }
        const conditionIdEl = document.getElementById('modalConditionId');
        const refurbishedTypeEl = document.getElementById('modalRefurbishedType');
        if (conditionIdEl) conditionIdEl.value = '';
        if (refurbishedTypeEl) refurbishedTypeEl.value = '';
      }

      if (autofillDebounceTimer != null) {
        clearTimeout(autofillDebounceTimer);
        autofillDebounceTimer = null;
      }
      if (url.length >= MIN_URL_LENGTH_FOR_DEBOUNCE && isValidEbayUrlForAutofill(url)) {
        autofillDebounceTimer = setTimeout(() => {
          autofillDebounceTimer = null;
          runUrlAutofill();
        }, AUTOFILL_DEBOUNCE_MS);
      }
    });
  }
  
  async handleAddItem(e) {
    const form = e.target || document.getElementById('addItemForm');
    if (!form) {
      logger.error('Form not found');
      return;
    }
    
    // Check if user is rate limited
    if (this.isRateLimited()) {
      const rateLimitState = this.rateLimitState;
      const minutes = Math.floor(rateLimitState.retryAfter / 60);
      const seconds = rateLimitState.retryAfter % 60;
      this.showNotification(
        `Rate limit active. Please try again in ${minutes}:${seconds.toString().padStart(2, '0')}`,
        'error'
      );
      return;
    }
    
    // CRITICAL: Read URL value IMMEDIATELY before any other operations
    // Check for modal field first, then fallback to old field
    const urlInput = document.getElementById('modalItemUrl') || document.getElementById('itemUrl');
    const urlValue = urlInput ? String(urlInput.value || '').trim() : '';
    
    logger.info('=== HANDLE ADD ITEM DEBUG ===');
    logger.info('URL value at start:', urlValue);
    logger.info('URL length:', urlValue.length);
    
    // Early validation - if URL is empty, stop immediately
    if (!urlValue || urlValue.length === 0) {
      logger.error('URL is empty at form submission start');
      this.showNotification('eBay listing URL is required.', 'error');
      
      // Reset processing flag
      const formElement = document.getElementById('addItemForm');
      if (formElement) {
        formElement.dataset.processing = 'false';
      }
      
      if (urlInput) {
        urlInput.focus();
      }
      return; // Stop immediately
    }
    
    const submitButton = form.querySelector('.submit-button');
    
    // Prevent double submission - check if already processing
    if (submitButton && submitButton.disabled && submitButton.textContent === 'Verifying...') {
      logger.info('Form already processing, ignoring duplicate submission');
      return; // Already processing, ignore this submission
    }
    
    // Disable submit button to prevent double submission
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Fetching details...';
    }
    
    // Wrap main submission logic in try-catch for error handling
    try {
    // CAPTCHA DISABLED FOR TESTING - Uncomment below to re-enable
    // Validate and execute reCAPTCHA v3
    /*
    if (typeof grecaptcha === 'undefined') {
      this.showNotification('CAPTCHA service is not available. Please refresh the page and try again.', 'error');
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = 'Submit';
      }
      return;
    }
    
    // Execute reCAPTCHA v3 with timeout protection
    let captchaTimeout;
    const CAPTCHA_TIMEOUT = 10000; // 10 seconds
    
    const captchaPromise = new Promise((resolve, reject) => {
      try {
        grecaptcha.ready(() => {
          clearTimeout(captchaTimeout);
          grecaptcha.execute('6Lcvtf4rAAAAANNl9EFUPW7IX-q_6jvwQlCBJyGb', { action: 'submit_item' })
            .then((token) => {
              clearTimeout(captchaTimeout);
              resolve(token);
            })
            .catch((error) => {
              clearTimeout(captchaTimeout);
              reject(error);
            });
        });
      } catch (error) {
        clearTimeout(captchaTimeout);
        reject(error);
      }
      
      // Timeout if CAPTCHA doesn't respond
      captchaTimeout = setTimeout(() => {
        reject(new Error('CAPTCHA timeout - service unavailable'));
      }, CAPTCHA_TIMEOUT);
    });
    
    captchaPromise
      .then((token) => {
        // Strict validation - token must exist, be a string, and be reasonably long
        if (!token || typeof token !== 'string' || token.trim().length < 20) {
          logger.error('Invalid CAPTCHA token detected:', token);
          this.showNotification('CAPTCHA verification failed. Submission blocked for security.', 'error');
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent = 'Submit';
          }
          return;
        }
        
        // Additional check - valid reCAPTCHA tokens are much longer
        if (token.length < 100) {
          logger.error('Suspiciously short CAPTCHA token:', token.length);
          this.showNotification('CAPTCHA verification failed. Please refresh and try again.', 'error');
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent = 'Submit';
          }
          return;
        }
        
        // Log token for debugging (remove in production)
        logger.info('reCAPTCHA token generated successfully, length:', token.length);
        
        // Store token (for potential server-side verification)
        const tokenInput = document.getElementById('recaptchaToken');
        if (tokenInput) {
          tokenInput.value = token;
        }
        
        // Final check before submission - reCAPTCHA v3 tokens should be 1000+ characters
        // If domain is invalid, Google may return short/invalid tokens
        if (!token || token.length < 500) {
          logger.error('CAPTCHA token too short - likely invalid domain:', token?.length || 0);
          this.showNotification('CAPTCHA verification failed. Domain not properly configured.', 'error');
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent = 'Submit';
          }
          return;
        }
        */
        
        // Process form submission (bypassing CAPTCHA for testing)
        // URL was already validated at the start of handleAddItem - use that value
        let url = urlValue; // Use the URL we captured at the very beginning
        
        // Double-check URL is still valid (should never fail if we got here)
        if (!url || url.length === 0) {
          logger.error('URL became empty during processing - this should not happen');
          this.showNotification('eBay listing URL is required.', 'error');
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent = 'Submit';
          }
          const formElement = document.getElementById('addItemForm');
          if (formElement) {
            formElement.dataset.processing = 'false';
          }
          return;
        }
        
        // Clear any custom validation messages since URL is valid
        if (urlInput) {
          urlInput.setCustomValidity('');
        }
        
        // Get other form values for processing
        const formData = new FormData(form);
        
        // Normalize URL: add https:// if missing (before validation)
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          url = 'https://' + url;
        }
        
        // Validate eBay listing URL: allowed domains and /itm/ or /p/ path
        const lowerUrl = url.toLowerCase();
        const ebayDomains = ['ebay.com', 'ebay.co.uk', 'ebay.de', 'ebay.fr', 'ebay.it', 'ebay.es', 'ebay.com.au', 'ebay.ca', 'ebay.ie', 'ebay.at', 'ebay.ch', 'ebay.be', 'ebay.nl', 'ebay.pl'];
        const hasEbayDomain = ebayDomains.some(d => lowerUrl.includes(d));
        if (!hasEbayDomain) {
          this.showNotification('Please enter a valid eBay item URL (e.g. ebay.com/itm/123456789).', 'error');
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent = 'Submit';
          }
          return;
        }
        const hasItemPath = /\/itm\/[A-Za-z0-9|]+/i.test(url) || /\/p\/\d+/.test(url);
        if (!hasItemPath) {
          this.showNotification('Please enter a valid eBay item URL (e.g. ebay.com/itm/123456789).', 'error');
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent = 'Submit';
          }
          return;
        }
        
        // Allow duplicate submissions - same links can be submitted over time as offers change
        
        // Get form values - allow empty values (backend will auto-populate from eBay API if missing)
        const itemName = formData.get('itemName') || '';
        const itemCondition = formData.get('itemCondition') || '';
        const itemPrice = formData.get('itemPrice') || '';
        
        const modalEbayCategoryId = document.getElementById('modalEbayCategoryId');
        const modalEbayCategoryPath = document.getElementById('modalEbayCategoryPath');
        const modalConditionId = document.getElementById('modalConditionId');
        const modalRefurbishedType = document.getElementById('modalRefurbishedType');
        const ebayCategoryId = (modalEbayCategoryId && modalEbayCategoryId.value && modalEbayCategoryId.value.trim()) || null;
        const ebayCategoryPath = (modalEbayCategoryPath && modalEbayCategoryPath.value && modalEbayCategoryPath.value.trim()) || null;
        const conditionId = (modalConditionId && modalConditionId.value && modalConditionId.value.trim()) || null;
        const refurbishedType = (modalRefurbishedType && modalRefurbishedType.value && modalRefurbishedType.value.trim()) || null;
        if (ebayCategoryId) {
          logger.info('[handleAddItem] Sending ebayCategoryId with submission:', ebayCategoryId);
        } else {
          logger.info('[handleAddItem] No ebayCategoryId to send (paste URL to auto-fill category from listing)');
        }
        if (ebayCategoryPath) {
          logger.info('[handleAddItem] Sending ebayCategoryPath with submission:', ebayCategoryPath.slice(0, 80) + (ebayCategoryPath.length > 80 ? '…' : ''));
        }

        const itemData = {
            id: Date.now().toString(),
            name: itemName, // Optional - backend will auto-populate from eBay API if empty
            mainCategory: null, // No longer collected from user
            subcategory: null, // No longer collected from user
            condition: itemCondition, // Optional - backend will auto-populate from eBay API if empty
            price: itemPrice, // Optional - backend will auto-populate from eBay API if empty
            url: url, // Required - only field that must be provided
            ...(ebayCategoryId && { ebayCategoryId }),
            ...(ebayCategoryPath && { ebayCategoryPath }),
            ...(conditionId && { condition_id: conditionId }),
            ...(refurbishedType && { refurbished_type: refurbishedType }),
            addedAt: new Date().toISOString(),
            // Always provide user_id and submitted_by (backend will set defaults if missing)
            userId: this.currentUser ? String(this.currentUser.id) : 'anonymous',
            status: 'pending', // New items require admin approval
            submittedBy: this.currentUser ? (this.currentUser.firstName + ' ' + this.currentUser.lastName) : 'Anonymous User',
            recaptchaToken: null // CAPTCHA disabled for testing
          };
          
          // Submit for admin review
          const result = await this.submitForReview(itemData);
          
          // Only add to user's finds and award points if user is signed in AND submission succeeded via API
          if (this.currentUser && result.success && result.viaApi) {
            // Add to user's finds (for their personal tracking)
            this.userFinds.push(itemData);
            if (this.currentUser.itemsSubmittedCount !== undefined) {
              this.currentUser.itemsSubmittedCount = (this.currentUser.itemsSubmittedCount || 0) + 1;
              localStorage.setItem('usedfindr_user', JSON.stringify(this.currentUser));
              this.updateUserInArray(this.currentUser.id, { itemsSubmittedCount: this.currentUser.itemsSubmittedCount });
            }
            this.saveUserFinds();
            
            // Award 1 point for successful submission
            this.currentUser.pointsBalance = (this.currentUser.pointsBalance || 0) + 1;
            localStorage.setItem('usedfindr_user', JSON.stringify(this.currentUser));
            // Also update in users array
            this.updateUserInArray(this.currentUser.id, { pointsBalance: this.currentUser.pointsBalance });
            
            // Update dashboard
            this.updateDashboard();
          }
          
          // Clear form AFTER showing success (prevents validation issues)
          form.reset();
          
          // Reset price field formatting (check both modal and old fields)
          const priceField = document.getElementById('modalItemPrice') || document.getElementById('itemPrice');
          if (priceField) {
            priceField.value = '';
          }
          
          // Reset processing flag
          const formElement = document.getElementById('addItemForm');
          if (formElement) {
            formElement.dataset.processing = 'false';
          }
          
          // Close modal if it's open
          const modal = document.getElementById('addItemModal');
          if (modal && modal.style.display !== 'none') {
            this.closeAddItemModal();
          }
          
          // Show appropriate message based on submission success
          if (result && result.success) {
            const data = result.data || {};
            if (data.auto_approved) {
              this.showNotification('Item approved and added to catalog!', 'success');
            } else if (this.currentUser) {
              this.showNotification('Item submitted for review! You earned 1 point. You will be notified when it\'s approved.', 'success');
            } else {
              this.showNotification('Item submitted for review! Thank you for your contribution. You will be notified when it\'s approved.', 'success');
            }
          } else {
            // Submission failed - error message already shown in submitForReview
            // Just ensure form is reset
          }
      /*})
      .catch((error) => {
        logger.error('reCAPTCHA error:', error);
        this.showNotification('CAPTCHA verification failed. Submission blocked.', 'error');
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = 'Submit';
        }
      });*/
    } catch (error) {
      logger.error('Submission error:', error);
      
      // Show the error message from the backend if available, otherwise generic message
      const errorMsg = error.response?.detail || error.body?.detail || error.message || 'An error occurred while submitting. Please try again or contact support.';
      this.showNotification(errorMsg, 'error');
    } finally {
      // Always re-enable button in finally block to ensure it's never stuck
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = 'Submit';
      }
      
      // Reset processing flag
      const formElement = document.getElementById('addItemForm');
      if (formElement) {
        formElement.dataset.processing = 'false';
      }
    }
  }
  async submitForReview(itemData) {
    // Prepare submission data
    const submissionData = {
      ...itemData,
      reviewStatus: 'pending',
      submittedAt: new Date().toISOString(),
      reviewedAt: null,
      reviewedBy: null,
      reviewNotes: null
    };
    
    // Try to save via API first
    if (window.apiService) {
      try {
        const submitted = await window.apiService.createSubmission(submissionData);
        logger.info('Submission successful:', submitted);
        
        // Update local user stats on success
        if (this.currentUser) {
          this.currentUser.itemsSubmittedCount = (this.currentUser.itemsSubmittedCount || 0) + 1;
          localStorage.setItem('usedfindr_user', JSON.stringify(this.currentUser));
        }
        
        // Return success status
        return { success: true, data: submitted, viaApi: true };
      } catch (error) {
        // Handle rate limit errors (429)
        if (error.status === 429) {
          this.handleRateLimitError(error);
          return { success: false, data: null, viaApi: false };
        }
        
        if (error.status === 401 || error.status === 403 || error.message.includes('token') || error.message.includes('auth')) {
          this.showNotification('Authentication expired. Please log in again.', 'error');
          if (window.apiService && typeof window.apiService.clearAccessToken === 'function') {
            window.apiService.clearAccessToken();
          }
          setTimeout(() => window.location.href = 'login.html', 1000);
          // Return failure status - redirect will happen, but this prevents further processing
          return { success: false, data: null, viaApi: false };
        }
        
        logger.error('Submission failed:', error);
        
        // Log detailed error information for debugging
        if (error.response || error.body) {
          const errorDetails = error.response || error.body;
          logger.error('Backend validation errors:', errorDetails);
          
          // Show user-friendly error message
          if (errorDetails.detail) {
            this.showNotification(errorDetails.detail, 'error');
          } else if (errorDetails.non_field_errors) {
            this.showNotification(errorDetails.non_field_errors.join(', '), 'error');
          } else if (typeof errorDetails === 'object') {
            // Format field-specific errors
            const fieldErrors = Object.entries(errorDetails)
              .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
              .join('; ');
            this.showNotification(`Validation error: ${fieldErrors}`, 'error');
          } else {
            this.showNotification('Submission failed. Please check all fields and try again.', 'error');
          }
        } else {
          this.showNotification('Submission failed. Please check all fields and try again.', 'error');
        }
        
        // Throw error instead of falling back to localStorage
        // Guests can now submit directly via API, no localStorage fallback needed
        throw error;
      }
    } else {
      // No API service available - show error
      this.showNotification('Unable to submit. Please check your connection and try again.', 'error');
      return { success: false, data: null, viaApi: false };
    }
  }
  
  /**
   * Handle rate limit errors with countdown timer
   */
  handleRateLimitError(error) {
    const errorDetails = error.response || error.body || {};
    const rateLimitInfo = errorDetails.rate_limit || {};
    
    // Extract retry_after from response headers or body
    let retryAfter = rateLimitInfo.retry_after;
    if (!retryAfter && error.headers) {
      const retryAfterHeader = error.headers.get ? error.headers.get('Retry-After') : error.headers['Retry-After'];
      if (retryAfterHeader) {
        retryAfter = parseInt(retryAfterHeader, 10);
      }
    }
    
    // Default to 3600 seconds (1 hour) if not provided
    if (!retryAfter || isNaN(retryAfter)) {
      retryAfter = 3600;
    }
    
    const limit = rateLimitInfo.limit || (this.currentUser ? 30 : 10);
    const window = rateLimitInfo.window || '1 hour';
    
    // Store rate limit state
    this.rateLimitState = {
      active: true,
      retryAfter: retryAfter,
      limit: limit,
      window: window,
      resetAt: rateLimitInfo.reset_at || null
    };
    
    // Show initial error message
    const errorMessage = errorDetails.detail || errorDetails.error || 
      `You have submitted too many items. Please try again later.`;
    this.showNotification(errorMessage, 'error');
    
    // Start countdown timer
    this.showRateLimitCountdown(retryAfter);
    
    // Disable submit button
    this.updateSubmitButtonRateLimitState(true);
    
    // Log rate limit hit
    logger.warn('Rate limit exceeded:', {
      limit,
      window,
      retryAfter,
      authenticated: !!this.currentUser
    });
  }
  
  /**
   * Show countdown timer for rate limit
   */
  showRateLimitCountdown(retryAfterSeconds) {
    // Clear any existing countdown
    if (this.rateLimitCountdownInterval) {
      clearInterval(this.rateLimitCountdownInterval);
    }
    
    let remainingSeconds = retryAfterSeconds;
    
    const updateCountdown = () => {
      if (remainingSeconds <= 0) {
        // Rate limit expired
        clearInterval(this.rateLimitCountdownInterval);
        this.rateLimitCountdownInterval = null;
        this.rateLimitState = null;
        this.updateSubmitButtonRateLimitState(false);
        
        // Show success message that user can submit again
        this.showNotification('You can submit items again now.', 'success');
        return;
      }
      
      // Calculate minutes and seconds
      const minutes = Math.floor(remainingSeconds / 60);
      const seconds = remainingSeconds % 60;
      
      // Update submit button text
      const submitButton = document.querySelector('#addItemForm .submit-button');
      if (submitButton) {
        submitButton.textContent = `Rate Limited - Try again in ${minutes}:${seconds.toString().padStart(2, '0')}`;
        submitButton.disabled = true;
        submitButton.classList.add('rate-limited');
      }
      
      remainingSeconds--;
    };
    
    // Update immediately
    updateCountdown();
    
    // Update every second
    this.rateLimitCountdownInterval = setInterval(updateCountdown, 1000);
  }
  
  /**
   * Update submit button rate limit state
   */
  updateSubmitButtonRateLimitState(isRateLimited) {
    const submitButton = document.querySelector('#addItemForm .submit-button');
    if (submitButton) {
      if (isRateLimited) {
        submitButton.disabled = true;
        submitButton.classList.add('rate-limited');
      } else {
        submitButton.disabled = false;
        submitButton.classList.remove('rate-limited');
        // Restore original button text
        submitButton.textContent = 'Submit';
      }
    }
  }
  
  /**
   * Check if user is currently rate limited
   */
  isRateLimited() {
    return this.rateLimitState && this.rateLimitState.active;
  }
  
  async getSubmissions(params = {}) {
    // API is the single source of truth for reads
    if (!window.apiService) {
      this.apiConnectionStatus = 'unavailable';
      const apiFailed = params.showErrors !== false;
      if (apiFailed && typeof this.updateConnectionBanner === 'function') {
        this.updateConnectionBanner();
      }
      return { data: [], apiFailed: apiFailed };
    }
    
    try {
      const apiSubmissions = await window.apiService.getSubmissions(params);
      const submissions = apiSubmissions || [];
      
      // Mark that API is working
      this.apiConnectionStatus = 'connected';
      if (typeof this.updateConnectionBanner === 'function') {
        this.updateConnectionBanner();
      }
      if (typeof this.updateWriteOperationButtons === 'function') {
        this.updateWriteOperationButtons();
      }
      
      return { data: submissions, apiFailed: false };
    } catch (apiError) {
      logger.warn('Failed to get submissions from API:', apiError);
      this.apiConnectionStatus = 'disconnected';
      if (typeof this.updateConnectionBanner === 'function') {
        this.updateConnectionBanner();
      }
      return { data: [], apiFailed: true };
    }
  }
  
  // Helper method to normalize URLs for comparison (if not available in AdminManager, use simple version)
  normalizeUrlForComparison(url) {
    if (!url) return '';
    return url.toLowerCase().trim().replace(/\/$/, ''); // lowercase, trim, remove trailing slash
  }
  
  async getReports(params = {}) {
    // API is the single source of truth for reads
    if (!window.apiService) {
      this.apiConnectionStatus = 'unavailable';
      const apiFailed = params.showErrors !== false;
      if (apiFailed) {
        this.updateConnectionBanner();
      }
      return { data: [], apiFailed: apiFailed };
    }
    
    try {
      const apiReports = await window.apiService.getReports(params);
      const reports = apiReports || [];
      
      // Mark that API is working
      this.apiConnectionStatus = 'connected';
      this.updateConnectionBanner();
      
      return { data: reports, apiFailed: false };
    } catch (apiError) {
      logger.warn('Failed to get reports from API:', apiError);
      this.apiConnectionStatus = 'disconnected';
      this.updateConnectionBanner();
      return { data: [], apiFailed: true };
    }
  }
  
  // Check API connection health
  async checkApiConnection() {
    if (window.apiService) {
      try {
        // Make a lightweight request (e.g., fetch one submission)
        await window.apiService.getSubmissions({ limit: 1 });
        
        // Connection is successful
        if (this.apiConnectionStatus !== 'connected') {
          this.apiConnectionStatus = 'connected';
          this.updateConnectionBanner();
          this.updateWriteOperationButtons();
        }
      } catch (error) {
        // Connection failed
        if (this.apiConnectionStatus !== 'disconnected') {
          this.apiConnectionStatus = 'disconnected';
          this.updateConnectionBanner();
        }
      }
    } else {
      // API service is unavailable
      if (this.apiConnectionStatus !== 'unavailable') {
        this.apiConnectionStatus = 'unavailable';
        this.updateConnectionBanner();
      }
    }
  }
  
  // This helps start fresh when old test data may not exist in the Django database
  resetLocalStorage() {
    logger.warn('Resetting localStorage data...');
    // Preserve saved items and finds (they're synced to API)
    const savedItems = localStorage.getItem('usedfindr_saved_items');
    const userFinds = localStorage.getItem('usedfindr_finds');
    
    localStorage.removeItem('usedfindr_items');
    localStorage.removeItem('usedfindr_submissions');
    localStorage.removeItem('usedfindr_reports');
    localStorage.removeItem('usedfindr_removed_items');
    localStorage.removeItem('usedfindr_admin_events');
    
    // Restore saved items and finds
    if (savedItems) {
      localStorage.setItem('usedfindr_saved_items', savedItems);
    }
    if (userFinds) {
      localStorage.setItem('usedfindr_finds', userFinds);
    }
  }
  
  updateDashboard() {
    if (!this.currentUser) return;
    
    // Helper function to get display name (same logic as account-scripts.js)
    const getDisplayName = (userData) => {
      if (userData.firstName || userData.first_name) {
        const firstName = userData.firstName || userData.first_name || '';
        const lastName = userData.lastName || userData.last_name || '';
        const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
        if (fullName) return fullName;
      }
      // Fallback to username (but not email if it looks like an email)
      if (userData.username && !userData.username.includes('@')) {
        return userData.username;
      }
      return 'User';
    };
    
    // Update user name
    const userNameElement = document.getElementById('userName');
    if (userNameElement) {
      const displayName = getDisplayName(this.currentUser);
      const currentName = userNameElement.textContent.trim();
      
      // Only update if:
      // 1. We have a good name (not "User") and it's different from current
      // 2. Current is "User", "Loading...", or empty
      // 3. Don't overwrite a good name (not "User" and not an email) with "User" or email
      const isCurrentNameGood = currentName && 
                                 currentName !== 'User' && 
                                 currentName !== 'Loading...' &&
                                 !currentName.includes('@');
      
      if (displayName !== 'User' && displayName !== currentName) {
        // We have a good name, update it
        userNameElement.textContent = displayName;
      } else if (displayName === 'User' && !isCurrentNameGood) {
        // Only set to "User" if current name is not good
        userNameElement.textContent = displayName;
      }
      // Otherwise, keep the current name (don't overwrite a good name with "User" or email)
    }
    
    // Update status badge
    const statusBadgeElement = document.getElementById('userStatusBadge');
    if (statusBadgeElement) {
      const statusInfo = this.getUserStatus();
      if (statusInfo.badge) {
        statusBadgeElement.textContent = statusInfo.badge;
        statusBadgeElement.style.display = 'inline';
      } else {
        statusBadgeElement.textContent = '';
        statusBadgeElement.style.display = 'none';
      }
    }
    
    // DEPRECATED: Contest entries update code - kept for future use
    // Update contest entries (current month + bonus entries)
    // const monthlyEntries = this.getMonthlyEntries();
    // const bonusEntries = this.currentUser.bonusEntries || 0;
    // const totalContestEntries = monthlyEntries + bonusEntries;
    // const nextDrawingDate = this.getNextDrawingDate();
    // const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long' });
    // 
    // // Get previous month entries for display
    // const now = new Date();
    // const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    // const prevMonthEntries = this.getMonthlyEntriesForMonth(prevMonth.getFullYear(), prevMonth.getMonth());
    // 
    // // Update contest heading with current month
    // const contestHeading = document.getElementById('contestHeading');
    // if (contestHeading) {
    //   contestHeading.textContent = `🎁 Your ${currentMonth} Contest Entries`;
    // }
    // 
    // // Update current month entries display (includes bonus entries)
    // const currentMonthEntriesElement = document.getElementById('currentMonthEntries');
    // if (currentMonthEntriesElement) {
    //   currentMonthEntriesElement.textContent = totalContestEntries;
    // }
    // 
    // // Update contest entries breakdown
    // const breakdownEl = document.getElementById('contestEntriesBreakdown');
    // if (breakdownEl) {
    //   if (totalContestEntries > 0) {
    //     breakdownEl.innerHTML = `
    //       <div>This month: ${monthlyEntries} regular + ${bonusEntries} bonus</div>
    //       ${prevMonthEntries > 0 ? `<div style="margin-top: 4px;">Last month: ${prevMonthEntries} entries</div>` : ''}
    //     `;
    //   } else {
    //     breakdownEl.innerHTML = '';
    //   }
    // }
    // 
    // // Check if user is a winner
    // this.checkWinnerStatus();
    
    // DEPRECATED: Points balance display - kept for future use
    // const pointsBalanceElement = document.getElementById('pointsBalance');
    // if (pointsBalanceElement) {
    //   pointsBalanceElement.textContent = this.currentUser.pointsBalance || 0;
    // }
    
    // DEPRECATED: Bonus entries and redemption button - kept for future use
    // // Update bonus entries count
    // const bonusEntriesCountElement = document.getElementById('bonusEntriesCount');
    // if (bonusEntriesCountElement) {
    //   bonusEntriesCountElement.textContent = bonusEntries;
    // }
    // 
    // // Update redemption button state
    // const redeemButton = document.getElementById('redeemButton');
    // if (redeemButton) {
    //   const pointsBalance = this.currentUser.pointsBalance || 0;
    //   redeemButton.disabled = pointsBalance < 100;
    // }
    // 
    // // Update drawing date
    // const drawingDateElement = document.getElementById('drawingDate');
    // if (drawingDateElement) {
    //   drawingDateElement.textContent = nextDrawingDate;
    // }
    
    // Update finds count
    const totalEntries = this.userFinds.length;
    const findsCountElement = document.getElementById('findsCount');
    if (findsCountElement) {
      findsCountElement.textContent = totalEntries + ' item' + (totalEntries !== 1 ? 's' : '');
    }
    
    // Update reputation display
    this.updateReputationDisplay();
    this._setupReputationExplainer();
    this.updateAccountFeed();
    this.updateFollowingChips();
    this.updateWatchChips();
  }

  /**
   * Relative timestamp for notification rows ("2h ago").
   */
  formatRelativeTime(value) {
    if (!value) return '';
    const then = new Date(value);
    if (Number.isNaN(then.getTime())) return '';
    const seconds = Math.floor((Date.now() - then.getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return then.toLocaleDateString();
  }

  /**
   * Only allow links that navigate: http(s) or a same-origin relative path.
   * Escaping alone does not defuse a "javascript:" href -- it stays clickable.
   * These payloads are server-built today, so this is defence in depth.
   */
  safeLinkUrl(url) {
    const raw = String(url || '').trim();
    if (!raw) return '#';
    if (raw.startsWith('/') && !raw.startsWith('//')) return raw;
    if (/^https?:\/\//i.test(raw)) return raw;
    return '#';
  }

  /**
   * One row renderer for every notification surface (feed, alerts, bell dropdown)
   * so the three stop drifting apart as notification types are added.
   *
   * Unknown types fall through to the generic layout rather than rendering blank,
   * which matters when the backend ships a new type before a cached bundle updates.
   */
  renderNotificationRow(n, { variant = 'feed' } = {}) {
    const payload = n.payload || {};
    const type = n.notificationType || n.notification_type || '';
    const unread = !n.isRead && !n.readAt;
    const currency = payload.currency || 'USD';
    const url = this.safeLinkUrl(payload.productUrl || payload.product_url);
    const img = payload.imageUrl || payload.image_url;
    const category = payload.categoryLabel || payload.category_label || '';
    const newPrice = payload.newPrice || payload.new_price || payload.price;
    const oldPrice = payload.oldPrice || payload.old_price;
    const percentOff = payload.percentOff ?? payload.percent_off;
    const isSoldOut = type === 'watch_sold_out';
    const isPriceDrop = type === 'watch_price_drop';
    const time = this.formatRelativeTime(n.createdAt || n.created_at);

    const ICONS = {
      watch_price_drop: '📉',
      watch_back_in_stock: '🔄',
      watch_sold_out: '🚫',
      category_new_item: '🆕',
    };
    const icon = ICONS[type] || '📦';

    const thumb = img
      ? `<img class="account-feed-thumb" src="${this.escapeHtml(img)}" alt="" loading="lazy" />`
      : `<span class="account-feed-thumb account-feed-thumb--placeholder" aria-hidden="true">${icon}</span>`;

    // Price block: a drop shows the old price struck through plus the discount.
    let priceHtml = '';
    if (isPriceDrop && newPrice) {
      priceHtml = `
        <span class="account-feed-price account-feed-price--drop">
          ${oldPrice ? `<s class="account-feed-price-old">$${this.escapeHtml(oldPrice)}</s>` : ''}
          <strong>$${this.escapeHtml(newPrice)} ${this.escapeHtml(currency)}</strong>
          ${percentOff ? `<span class="account-feed-discount">−${this.escapeHtml(String(percentOff))}%</span>` : ''}
        </span>`;
    } else if (newPrice && !isSoldOut) {
      priceHtml = `<span class="account-feed-price">$${this.escapeHtml(newPrice)} ${this.escapeHtml(currency)}</span>`;
    }

    const badge = type === 'watch_back_in_stock'
      ? '<span class="account-feed-pill account-feed-pill--restock">Back in stock</span>'
      : (isSoldOut ? '<span class="account-feed-pill account-feed-pill--gone">Sold out</span>' : '');

    // A sold-out listing has nothing to view, so it gets no CTA and a muted row.
    const cta = isSoldOut ? '' : '<span class="account-feed-cta">View deal</span>';
    const idAttr = variant === 'alerts' ? 'data-alert-id' : 'data-feed-notification-id';
    const classes = [
      'account-feed-item',
      variant === 'alerts' ? 'account-alerts-item' : '',
      unread ? 'is-unread' : '',
      isSoldOut ? 'is-inactive' : '',
    ].filter(Boolean).join(' ');

    const heading = variant === 'alerts' ? n.title : (n.body || n.title);
    const sub = variant === 'alerts' ? (n.body || '') : '';

    return `
      <a class="${classes}" href="${this.escapeHtml(url)}" ${idAttr}="${this.escapeHtml(n.id)}"
         data-notification-type="${this.escapeHtml(type)}">
        ${thumb}
        <span class="account-feed-body">
          ${category ? `<span class="account-feed-category">${this.escapeHtml(category)}</span>` : ''}
          <span class="account-feed-title">${this.escapeHtml(heading)}</span>
          ${sub ? `<span class="account-feed-desc">${this.escapeHtml(sub)}</span>` : ''}
          <span class="account-feed-meta">${badge}${priceHtml}${time ? `<span class="account-feed-time">${this.escapeHtml(time)}</span>` : ''}</span>
        </span>
        ${unread ? '<span class="account-feed-unread-dot" aria-label="Unread"></span>' : ''}
        ${cta}
      </a>`;
  }

  /**
   * Mark a notification read when its row is clicked.
   *
   * The row is an <a>, so the browser starts navigating the moment it is clicked and
   * would cancel an ordinary fetch mid-flight -- which is why alerts used to stay
   * unread after being opened. keepalive lets the request outlive the unload, and we
   * clear the unread styling locally so the change is visible on the way out.
   */
  bindNotificationReadHandlers(containerEl, attr) {
    containerEl.querySelectorAll(`[${attr}]`).forEach((el) => {
      el.addEventListener('click', () => {
        const id = Number(el.getAttribute(attr));
        if (!id) return;
        el.classList.remove('is-unread');
        el.querySelector('.account-feed-unread-dot')?.remove();
        try {
          const p = window.apiService.markNotificationsRead({ ids: [id], keepalive: true });
          if (p && typeof p.catch === 'function') {
            p.then(() => {
              if (typeof refreshNotificationBadge === 'function') refreshNotificationBadge();
            }).catch((e) => logger.warn('Failed to mark notification read', e));
          }
        } catch (e) {
          logger.warn('Failed to mark notification read', e);
        }
      });
    });
  }

  async updateAccountFeed() {
    const feedEl = document.getElementById('accountFeedList');
    if (!feedEl || !this.currentUser || !window.apiService) return;

    feedEl.innerHTML = '<p class="account-feed-loading">Loading your feed…</p>';
    try {
      const data = await window.apiService.getNotifications({ limit: 20 });
      const items = data.notifications || [];
      if (!items.length) {
        feedEl.innerHTML = await this._buildFeedEmptyState();
        return;
      }

      feedEl.innerHTML = items.map((n) => this.renderNotificationRow(n, { variant: 'feed' })).join('');
      this.bindNotificationReadHandlers(feedEl, 'data-feed-notification-id');
    } catch (e) {
      feedEl.innerHTML = '<p class="account-feed-empty">Could not load your feed. Try again later.</p>';
      logger.warn('updateAccountFeed failed', e);
    }
  }

  /**
   * Empty feed means two very different things: nothing subscribed yet, or
   * subscribed and simply waiting. Saying "no new items" to someone who has already
   * followed two categories reads as broken, so distinguish the two.
   */
  async _buildFeedEmptyState() {
    let subscribed = 0;
    try {
      const [follows, watches] = await Promise.all([
        window.apiService.getCategoryFollows().catch(() => ({ follows: [] })),
        window.apiService.getWatches().catch(() => ({ watches: [] })),
      ]);
      subscribed = (follows.follows || []).length + (watches.watches || []).length;
    } catch (e) {
      subscribed = 0;
    }

    if (subscribed > 0) {
      return `
        <div class="account-feed-empty">
          <p>You're all set — we'll alert you when something changes.</p>
          <p class="empty-state-tip">Price drops, restocks, and new listings show up here.
            <a href="#following">Manage what you follow</a>.</p>
        </div>`;
    }
    return `
      <div class="account-feed-empty">
        <p>Nothing to show yet.</p>
        <p class="empty-state-tip"><a href="categories.html">Follow a category</a> for new listings,
          or tap Watch on any product to get alerts when its price drops.</p>
      </div>`;
  }

  async updateFollowingChips() {
    const chipsEl = document.getElementById('accountFollowingChips');
    if (!chipsEl || !this.currentUser || !window.apiService) return;

    try {
      if (window.categoryFollowingManager) {
        await window.categoryFollowingManager.loadFromApi();
      }
      const data = await window.apiService.getCategoryFollows();
      const follows = data.follows || [];
      if (!follows.length) {
        chipsEl.innerHTML = '<p class="account-following-empty">You are not following any categories yet. <a href="categories.html">Add categories</a></p>';
        return;
      }
      chipsEl.innerHTML = follows.map((f) => {
        const label = f.label || f.ebayCategoryId || f.ebay_category_id;
        const id = f.ebayCategoryId || f.ebay_category_id;
        return `<a class="account-following-chip" href="search.html?category=${encodeURIComponent(id)}">${this.escapeHtml(label)}</a>`;
      }).join('') + '<a class="account-following-chip account-following-chip--add" href="categories.html">+ Add</a>';
    } catch (e) {
      chipsEl.innerHTML = '<p class="account-following-empty"><a href="categories.html">Browse categories</a></p>';
    }
  }

  /** Watched-product chips on the dashboard Following card. */
  async updateWatchChips() {
    const chipsEl = document.getElementById('accountWatchChips');
    if (!chipsEl || !this.currentUser || !window.apiService) return;

    try {
      const data = await window.apiService.getWatches();
      const watches = data.watches || [];
      if (!watches.length) {
        chipsEl.innerHTML = '<p class="account-following-empty">Not watching anything yet — tap Watch on any product.</p>';
        return;
      }
      chipsEl.innerHTML = watches.slice(0, 8).map((w) => {
        const url = w.productUrl || '#';
        return `<a class="account-following-chip" href="${this.escapeHtml(url)}">${this.escapeHtml(w.name || 'Product')}</a>`;
      }).join('');
    } catch (e) {
      chipsEl.innerHTML = '<p class="account-following-empty">Could not load watched products.</p>';
    }
  }

  /**
   * Watched products on the Following tab, showing movement since the watch started.
   */
  async updateWatchedProductsView() {
    const gridEl = document.getElementById('watchedProductsGrid');
    if (!gridEl || !this.currentUser || !window.apiService) return;

    gridEl.innerHTML = '<p class="account-following-loading">Loading…</p>';
    try {
      const data = await window.apiService.getWatches();
      const watches = data.watches || [];
      if (!watches.length) {
        gridEl.innerHTML = `
          <div class="account-following-empty-state">
            <p>You're not watching any products yet.</p>
            <p class="empty-state-tip">Open any listing and tap Watch to get alerts when its price drops or it goes out of stock.</p>
            <a class="btn-primary" href="search.html">Browse listings</a>
          </div>`;
        return;
      }

      // Console renders board B6's rows instead of the card grid: same data,
      // but the threshold each watch already carries is visible and editable
      // rather than write-once at the moment you started watching.
      if (document.body && document.body.classList.contains('console')) {
        gridEl.innerHTML = consoleWatchlistHtml(watches);
        this.bindWatchThresholds(gridEl);
        this.bindUnwatchButtons(gridEl);
        return;
      }

      gridEl.innerHTML = watches.map((w) => {
        const current = w.currentPrice;
        const start = w.priceAtWatch;
        const currency = w.currency || 'USD';
        let movement = '';
        if (current && start && Number(current) !== Number(start)) {
          const dropped = Number(current) < Number(start);
          movement = `<span class="watch-card__movement ${dropped ? 'is-down' : 'is-up'}">
              ${dropped ? '▼' : '▲'} from $${this.escapeHtml(start)}
            </span>`;
        }
        const priceLine = current
          ? `<span class="watch-card__price">$${this.escapeHtml(current)} ${this.escapeHtml(currency)}</span>${movement}`
          : '<span class="watch-card__price watch-card__price--gone">Currently unavailable</span>';
        const thumb = w.imageUrl
          ? `<img class="watch-card__thumb" src="${this.escapeHtml(w.imageUrl)}" alt="" loading="lazy" />`
          : '<span class="watch-card__thumb watch-card__thumb--placeholder" aria-hidden="true">📦</span>';

        return `
          <article class="following-category-card watch-card${w.isAvailable ? '' : ' is-inactive'}">
            <div class="following-category-card__head">
              ${thumb}
              <div>
                <h3 class="following-category-card__title">${this.escapeHtml(w.name || 'Product')}</h3>
                <p class="following-category-card__meta">${priceLine}</p>
              </div>
            </div>
            <div class="following-category-actions">
              <a class="btn-secondary btn-sm" href="${this.escapeHtml(w.productUrl || '#')}">View listing</a>
              <button type="button" class="btn-ghost btn-sm unwatch-btn" data-watch-id="${this.escapeHtml(w.id)}"
                      data-parent-id="${this.escapeHtml(w.masterParentId ?? '')}">Unwatch</button>
            </div>
          </article>`;
      }).join('');

      this.bindUnwatchButtons(gridEl);
    } catch (e) {
      gridEl.innerHTML = '<p>Could not load watched products.</p>';
    }
  }

  /**
   * Save a row's alert threshold.
   *
   * POST /auth/watches/ is an upsert on (user, parent), so the same call that
   * starts a watch also changes the threshold on one that already exists --
   * there is no second endpoint to add. An empty input clears the threshold
   * back to "tell me about any drop", which is why the request has to send the
   * empty value rather than omit it.
   */
  bindWatchThresholds(gridEl) {
    gridEl.querySelectorAll('[data-watch-threshold]').forEach((input) => {
      let saved = input.value;
      input.addEventListener('change', async () => {
        const parentId = input.getAttribute('data-parent-id');
        if (!parentId || !window.apiService) return;
        const next = input.value.trim();
        if (next === saved) return;
        if (next !== '' && !(Number(next) >= 0)) {
          input.value = saved;
          this.showNotification('Enter a price, or leave it blank for any drop', 'error');
          return;
        }
        input.disabled = true;
        try {
          await window.apiService.watchProduct(parentId, { maxPrice: next === '' ? '' : next });
          saved = next;
          const row = input.closest('.watch');
          // The tint says "this is under your alert right now", so it has to
          // be recomputed here -- the number it depends on just changed.
          if (row) {
            const now = Number(row.getAttribute('data-now'));
            row.classList.toggle(
              'below',
              next !== '' && Number.isFinite(now) && now <= Number(next),
            );
          }
          this.showNotification(
            next === '' ? "Alert cleared — we'll tell you about any drop"
              : `We'll alert you when it drops under $${next}`,
          );
        } catch (err) {
          input.value = saved;
          this.showNotification('Could not save that alert', 'error');
        } finally {
          input.disabled = false;
        }
      });
    });
  }

  bindUnwatchButtons(gridEl) {
    gridEl.querySelectorAll('.unwatch-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const watchId = btn.getAttribute('data-watch-id');
        const parentId = btn.getAttribute('data-parent-id');
        const card = btn.closest('.watch-card, .watch');
        // Unwatch deletes the row, and the threshold on it. Undo has to put
        // that back too, or "undo" quietly downgrades the watch.
        const threshold = card?.querySelector('[data-watch-threshold]')?.value ?? '';
        // Remove optimistically; restore the row if the request fails.
        if (card) card.style.display = 'none';
        try {
          await window.apiService.unwatchById(watchId);
          this.showNotificationWithUndo('Stopped watching', async () => {
            await window.apiService.watchProduct(parentId, { maxPrice: threshold });
            this.updateWatchedProductsView();
            this.updateWatchChips();
          });
          if (card) card.remove();
          this.updateWatchChips();
          if (!gridEl.querySelector('.watch-card, .watch')) this.updateWatchedProductsView();
        } catch (e) {
          if (card) card.style.display = '';
          this.showNotification('Could not stop watching', 'error');
        }
      });
    });
  }

  /**
   * Toast with an Undo action. Unfollow/unwatch are instant and destructive, so a
   * misclick should be recoverable without hunting for the item again.
   */
  showNotificationWithUndo(message, onUndo) {
    const existing = document.querySelector('.undo-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = 'notification notification-info undo-toast';
    toast.innerHTML = `<span class="undo-toast__msg"></span><button type="button" class="undo-toast__btn">Undo</button>`;
    toast.querySelector('.undo-toast__msg').textContent = message;
    document.body.appendChild(toast);

    const timer = setTimeout(() => toast.remove(), 6000);
    toast.querySelector('.undo-toast__btn').addEventListener('click', async () => {
      clearTimeout(timer);
      toast.remove();
      try {
        await onUndo();
      } catch (e) {
        this.showNotification('Could not undo', 'error');
      }
    });
  }

  async updateFollowingView() {
    const gridEl = document.getElementById('followingCategoriesGrid');
    if (!gridEl || !this.currentUser || !window.apiService) return;

    gridEl.innerHTML = '<p class="account-following-loading">Loading…</p>';
    try {
      const data = await window.apiService.getCategoryFollows();
      const follows = data.follows || [];
      if (!follows.length) {
        gridEl.innerHTML = `
          <div class="account-following-empty-state">
            <p>You are not following any categories yet.</p>
            <a class="btn-primary" href="categories.html">Browse categories</a>
          </div>`;
        return;
      }

      gridEl.innerHTML = follows.map((f) => {
        const label = f.label || f.ebayCategoryId || f.ebay_category_id;
        const catId = f.ebayCategoryId || f.ebay_category_id;
        return `
          <article class="following-category-card">
            <div class="following-category-card__head">
              <span class="following-category-card__icon" aria-hidden="true">📂</span>
              <div>
                <h3 class="following-category-card__title">${this.escapeHtml(label)}</h3>
                <p class="following-category-card__meta">New listings will appear in your feed and alerts</p>
              </div>
            </div>
            <div class="following-category-actions">
              <a class="btn-secondary btn-sm" href="search.html?category=${encodeURIComponent(catId)}">Browse listings</a>
              <button type="button" class="btn-ghost btn-sm unfollow-category-btn" data-follow-id="${f.id}" data-category-id="${this.escapeHtml(catId)}">Unfollow</button>
            </div>
          </article>`;
      }).join('');

      gridEl.querySelectorAll('.unfollow-category-btn').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const followId = btn.getAttribute('data-follow-id');
          const catId = btn.getAttribute('data-category-id');
          const card = btn.closest('.following-category-card');
          // Drop the row immediately and reconcile in the background; restore it if
          // the request fails.
          if (card) card.style.display = 'none';
          try {
            await window.apiService.unfollowCategoryById(followId);
            if (card) card.remove();
            this.showNotificationWithUndo('Unfollowed category', async () => {
              await window.apiService.followCategory(catId);
              this.updateFollowingView();
              this.updateFollowingChips();
              if (window.categoryFollowingManager) {
                await window.categoryFollowingManager.loadFromApi(true);
              }
            });
            this.updateFollowingChips();
            if (window.categoryFollowingManager) {
              window.categoryFollowingManager.loadFromApi(true).catch(() => {});
            }
            if (!gridEl.querySelector('.following-category-card')) this.updateFollowingView();
          } catch (e) {
            if (card) card.style.display = '';
            this.showNotification('Could not unfollow category', 'error');
          }
        });
      });
    } catch (e) {
      gridEl.innerHTML = '<p>Could not load followed categories.</p>';
    }
  }

  async updateAlertsView() {
    const listEl = document.getElementById('alertsInboxList');
    if (!listEl || !this.currentUser || !window.apiService) return;

    listEl.innerHTML = '<p class="account-feed-loading">Loading…</p>';
    try {
      const data = await window.apiService.getNotifications({ limit: 50 });
      this._alertItems = data.notifications || [];
      this._setupAlertsToolbar();
      this._renderAlertsList();
    } catch (e) {
      listEl.innerHTML = '<p>Could not load notifications.</p>';
      logger.warn('updateAlertsView failed', e);
    }
  }

  /**
   * Render the alerts list through the active filter. Filtering is client-side over
   * the batch already fetched, so switching chips costs no request.
   */
  _renderAlertsList() {
    const listEl = document.getElementById('alertsInboxList');
    if (!listEl) return;
    const filter = this._alertFilter || 'all';
    const items = (this._alertItems || []).filter((n) => {
      if (filter === 'all') return true;
      return (n.notificationType || n.notification_type) === filter;
    });

    if (!items.length) {
      listEl.innerHTML = filter === 'all'
        ? `<div class="account-feed-empty">
             <p>No alerts yet.</p>
             <p class="empty-state-tip">Follow a category or watch a product and they'll show up here.</p>
           </div>`
        : '<p class="account-feed-empty">No alerts of this kind yet.</p>';
      return;
    }

    listEl.innerHTML = items.map((n) => this.renderNotificationRow(n, { variant: 'alerts' })).join('');
    this.bindNotificationReadHandlers(listEl, 'data-alert-id');
  }

  _setupAlertsToolbar() {
    const filtersEl = document.getElementById('alertsFilters');
    if (filtersEl && filtersEl.dataset.bound !== 'true') {
      filtersEl.dataset.bound = 'true';
      filtersEl.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-alert-filter]');
        if (!btn) return;
        this._alertFilter = btn.getAttribute('data-alert-filter');
        filtersEl.querySelectorAll('[data-alert-filter]').forEach((b) => {
          b.classList.toggle('is-active', b === btn);
        });
        this._renderAlertsList();
      });
    }

    const markAllBtn = document.getElementById('alertsMarkAllReadBtn');
    if (markAllBtn && markAllBtn.dataset.bound !== 'true') {
      markAllBtn.dataset.bound = 'true';
      markAllBtn.addEventListener('click', async () => {
        markAllBtn.disabled = true;
        try {
          await window.apiService.markNotificationsRead({ all: true });
          (this._alertItems || []).forEach((n) => { n.isRead = true; n.readAt = new Date().toISOString(); });
          this._renderAlertsList();
          if (typeof refreshNotificationBadge === 'function') refreshNotificationBadge();
          this.showNotification('All alerts marked read');
        } catch (e) {
          this.showNotification('Could not mark alerts read', 'error');
        } finally {
          markAllBtn.disabled = false;
        }
      });
    }
  }

  _setupReputationExplainer() {
    const trigger = document.getElementById('reputationExplainerTrigger');
    const popover = document.getElementById('reputationExplainerPopover');
    if (!trigger || !popover || trigger.dataset.explainerSetup === 'true') return;
    trigger.dataset.explainerSetup = 'true';
    const close = () => {
      popover.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', outsideHandler);
    };
    const outsideHandler = (e) => {
      if (!popover.contains(e.target) && !trigger.contains(e.target)) close();
    };
    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = !popover.hidden;
      if (isOpen) {
        close();
      } else {
        popover.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        document.addEventListener('click', outsideHandler);
      }
    });
  }
  
  async updateReputationDisplay() {
    if (!this.currentUser) return;
    
    // Prevent multiple simultaneous calls
    if (this._updatingReputation) {
      return;
    }
    this._updatingReputation = true;
    
    try {
      // Fetch user profile from API
      if (window.apiService) {
        try {
          const profile = await window.apiService.getUserProfile();
          if (profile) {
            // Update reputation score
            const reputationScoreElement = document.getElementById('reputationScore');
            if (reputationScoreElement) {
              reputationScoreElement.textContent = profile.reputation || 0;
            }
            
            // Update reputation level
            const reputationLevelElement = document.getElementById('reputationLevel');
            if (reputationLevelElement) {
              reputationLevelElement.textContent = profile.levelTitle || 'Newfinder';
            }
            
            // Update stats
            const itemsSubmittedElement = document.getElementById('itemsSubmittedCount');
            if (itemsSubmittedElement) {
              itemsSubmittedElement.textContent = profile.itemsSubmittedCount || 0;
            }
            
            const votesCastElement = document.getElementById('votesCastCount');
            if (votesCastElement) {
              votesCastElement.textContent = profile.votesCastCount || 0;
            }
            
            const dailyStreakElement = document.getElementById('dailyStreakCount');
            if (dailyStreakElement) {
              // API returns camelCase after toCamelCase conversion
              dailyStreakElement.textContent = profile.dailyLoginStreak || profile.daily_login_streak || 0;
            }
            
            // Update progress bar
            this.updateReputationProgress(profile.reputation || 0);
            
            // Update badges
            this.updateBadgesDisplay(profile.earnedBadges || []);

            // Update achievements timeline
            this.updateAchievementsTimeline(profile.earnedBadges || []);
            
            this._updatingReputation = false;
            return;
          }
        } catch (apiError) {
          // Check if it's a database error (table doesn't exist)
          const errorMessage = apiError.message || String(apiError);
          if (errorMessage.includes('no such table') || errorMessage.includes('500')) {
            logger.warn('Database tables not set up yet. Please run migrations. See MIGRATION_INSTRUCTIONS.md');
            // Fall through to localStorage fallback
          } else {
            // Other API errors - log but don't break
            logger.warn('Could not fetch reputation from API:', errorMessage);
            // Fall through to localStorage fallback
          }
        }
      }
      
      // Fallback to localStorage if API not available or failed
      const reputationScoreElement = document.getElementById('reputationScore');
      if (reputationScoreElement && this.currentUser.reputation !== undefined) {
        reputationScoreElement.textContent = this.currentUser.reputation || 0;
      }
      
      if (window.reputationManager) {
        const level = await window.reputationManager.getUserLevel(this.currentUser.id);
        const reputationLevelElement = document.getElementById('reputationLevel');
        if (reputationLevelElement) {
          reputationLevelElement.textContent = level;
        }
      }
    } catch (error) {
      logger.warn('Error updating reputation display:', error);
      // Don't break navigation - just log the error
    } finally {
      this._updatingReputation = false;
    }
  }
  
  updateReputationProgress(reputation) {
    // Calculate progress to next level
    const levels = [
      { min: 0, title: 'Newfinder' },
      { min: 100, title: 'Deal Spotter' },
      { min: 500, title: 'Pro Hunter' },
      { min: 1500, title: 'Category Expert' },
      { min: 5000, title: 'Elite Finder' }
    ];
    
    let currentLevel = levels[0];
    let nextLevel = levels[1];
    
    for (let i = levels.length - 1; i >= 0; i--) {
      if (reputation >= levels[i].min) {
        currentLevel = levels[i];
        if (i < levels.length - 1) {
          nextLevel = levels[i + 1];
        } else {
          // At max level
          nextLevel = null;
        }
        break;
      }
    }
    
    const progressBarFill = document.getElementById('reputationProgressFill');
    const progressText = document.getElementById('reputationProgressText');
    
    if (nextLevel) {
      const progress = reputation - currentLevel.min;
      const needed = nextLevel.min - currentLevel.min;
      const percentage = Math.min(100, (progress / needed) * 100);
      
      if (progressBarFill) {
        progressBarFill.style.width = `${percentage}%`;
      }
      
      if (progressText) {
        progressText.textContent = `${progress} / ${needed}`;
      }
    } else {
      if (progressBarFill) {
        progressBarFill.style.width = '100%';
      }
      
      if (progressText) {
        progressText.textContent = 'Max Level';
      }
    }

    const hintEl = document.getElementById('reputationProgressHint');
    if (hintEl) {
      let hint = 'Earn reputation by submitting items, voting, and keeping a daily streak.';
      if (nextLevel) {
        const pointsNeeded = nextLevel.min - reputation;
        hint += ` ${pointsNeeded} more to ${nextLevel.title}.`;
      }
      hintEl.textContent = hint;
    }
  }
  updateBadgesDisplay(earnedBadges) {
    const badgesGrid = document.getElementById('badgesGrid');
    if (!badgesGrid) return;
    
    if (!earnedBadges || earnedBadges.length === 0) {
      badgesGrid.innerHTML = '<p class="no-badges">No badges earned yet. Start submitting items and voting to earn badges!</p>';
      return;
    }
    
    const badgesHTML = earnedBadges.map(userBadge => {
      const badge = userBadge.badge;
      const earnedDate = new Date(userBadge.earnedAt).toLocaleDateString();
      return `
        <div class="badge-card" title="${badge.badgeDescription}">
          <div class="badge-icon">${badge.icon}</div>
          <div class="badge-name">${badge.badgeName}</div>
          <div class="badge-date">Earned ${earnedDate}</div>
        </div>
      `;
    }).join('');
    
    badgesGrid.innerHTML = badgesHTML;
  }
  updateAchievementsTimeline(earnedBadges) {
    const timeline = document.getElementById('achievementsTimeline');
    if (!timeline) return;

    if (!earnedBadges || earnedBadges.length === 0) {
      timeline.innerHTML = '<p class="no-activity">No recent activity to show.</p><p class="empty-state-tip">Submit an item or vote on products to see activity here.</p>';
      return;
    }

    // Sort badges by earned date, most recent first
    const sortedBadges = earnedBadges.sort((a, b) => new Date(b.earnedAt) - new Date(a.earnedAt));

    const timelineHTML = sortedBadges.map(userBadge => {
      const badge = userBadge.badge;
      const earnedDate = new Date(userBadge.earnedAt).toLocaleDateString();
      return `
        <div class="timeline-item">
          <div class="timeline-icon">${badge.icon}</div>
          <div class="timeline-content">
            <p><strong>${badge.badgeName}</strong> unlocked!</p>
            <small>${badge.badgeDescription} - Earned on ${earnedDate}</small>
          </div>
        </div>
      `;
    }).join('');

    timeline.innerHTML = timelineHTML;
  }

  showNotification(message, type = 'success', duration = 3000) {
    const container = document.getElementById('notification-toast-container') || this.createNotificationContainer();
    
    const toast = document.createElement('div');
    toast.className = `notification-toast ${type}`;
    toast.textContent = message;
    
    container.appendChild(toast);
    
    setTimeout(() => {
      toast.classList.add('fade-out');
      toast.addEventListener('animationend', () => {
        toast.remove();
        if (container.children.length === 0) {
          container.remove();
        }
      });
    }, duration);
  }

  createNotificationContainer() {
    const container = document.createElement('div');
    container.id = 'notification-toast-container';
    document.body.appendChild(container);
    return container;
  }
  
  // Update email preferences view (loads from API, saves to API)
  async updateEmailPreferencesView() {
    if (!this.currentUser) return;

    if (window.categoryFollowingManager) {
      await window.categoryFollowingManager.loadFromApi();
    }

    const prefAccountEmails = document.getElementById('prefAccountEmails');
    const prefNewItems = document.getElementById('prefNewItems');
    const prefCategoryInApp = document.getElementById('prefCategoryInApp');
    const prefWeeklyDigest = document.getElementById('prefWeeklyDigest');
    const prefWatchAlertEmail = document.getElementById('prefWatchAlertEmail');

    // Load email preferences from API (user profile)
    try {
      if (window.apiService) {
        const profile = await window.apiService.getUserProfile();
        if (profile) {
          if (prefAccountEmails) prefAccountEmails.checked = profile.accountEmails !== false;
          if (prefNewItems) prefNewItems.checked = profile.categoryAlertEmail !== false;
          if (prefCategoryInApp) prefCategoryInApp.checked = profile.categoryAlertInApp !== false;
          if (prefWeeklyDigest) prefWeeklyDigest.checked = !!profile.newsletterOptIn;
          if (prefWatchAlertEmail) prefWatchAlertEmail.checked = profile.watchAlertEmail !== false;
        }
      }
    } catch (e) {
      logger.warn('Could not load email preferences from API, using defaults:', e);
      if (prefAccountEmails) prefAccountEmails.checked = true;
      if (prefNewItems) prefNewItems.checked = true;
      if (prefCategoryInApp) prefCategoryInApp.checked = true;
      if (prefWeeklyDigest) prefWeeklyDigest.checked = false;
      if (prefWatchAlertEmail) prefWatchAlertEmail.checked = true;
    }

    // Followed categories moved to Following tab — no list here
    const followedCategoriesList = document.getElementById('followedCategoriesList');
    const noFollowedCategories = document.getElementById('noFollowedCategories');
    if (followedCategoriesList) followedCategoriesList.innerHTML = '';
    if (noFollowedCategories) noFollowedCategories.style.display = 'none';

    // Handle form submission (save to API)
    const emailPreferencesForm = document.getElementById('emailPreferencesForm');
    if (emailPreferencesForm && !emailPreferencesForm.dataset.listenerAttached) {
      emailPreferencesForm.dataset.listenerAttached = 'true';
      emailPreferencesForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const prefAccountEmailsEl = document.getElementById('prefAccountEmails');
        const prefNewItemsEl = document.getElementById('prefNewItems');
        const prefCategoryInAppEl = document.getElementById('prefCategoryInApp');
        const prefWeeklyDigestEl = document.getElementById('prefWeeklyDigest');
        const prefWatchAlertEmailEl = document.getElementById('prefWatchAlertEmail');
        const payload = {
          accountEmails: prefAccountEmailsEl ? prefAccountEmailsEl.checked : true,
          categoryAlertEmail: prefNewItemsEl ? prefNewItemsEl.checked : true,
          categoryAlertInApp: prefCategoryInAppEl ? prefCategoryInAppEl.checked : true,
          newsletterOptIn: prefWeeklyDigestEl ? prefWeeklyDigestEl.checked : false,
          watchAlertEmail: prefWatchAlertEmailEl ? prefWatchAlertEmailEl.checked : true,
        };
        try {
          if (window.apiService) {
            await window.apiService.updateUserProfile(payload);
            this.showNotification('Alert preferences saved successfully!');
          }
        } catch (err) {
          logger.error('Failed to save email preferences:', err);
          this.showNotification(err.message || 'Failed to save preferences.', 'error');
        }
      });
    }
  }
  // Update activity center view
  async updateActivityView() {
    if (!this.currentUser) return;
    
    const timeline = document.getElementById('activityTimeline');
    if (!timeline) return;
    
    timeline.innerHTML = '<div class="activity-loading"><p>Loading activities...</p></div>';
    
    try {
      const activities = [];
      
      // Fetch user submissions
      if (window.apiService) {
        try {
          const submissions = await window.apiService.getUserSubmissions(this.currentUser.id);
          if (submissions && Array.isArray(submissions)) {
            submissions.forEach(submission => {
              activities.push({
                type: 'submission',
                date: submission.submittedAt || submission.submitted_at || submission.createdAt || submission.created_at,
                data: submission,
                icon: '📝',
                description: `Submitted: ${submission.name || 'Product'}`,
                status: submission.reviewStatus || submission.review_status || 'pending'
              });
            });
          }
        } catch (error) {
          logger.warn('Error fetching submissions:', error);
        }
      }
      
      // Fetch user votes: API first (with product context), then localStorage fallback
      let votesFromApi = false;
      if (window.apiService) {
        try {
          const apiVotes = await window.apiService.getMyVotes(100);
          if (apiVotes && apiVotes.length > 0) {
            apiVotes.forEach(vote => {
              const product = vote.product || {};
              const productId = product.productId || product.product_id || '';
              const productName = product.name || 'Product';
              const productUrl = product.affiliateLink || product.affiliate_link || null;
              const voteType = vote.voteType || vote.vote_type || 'up';
              const voteDate = vote.createdAt || vote.created_at ? new Date(vote.createdAt || vote.created_at) : new Date();
              activities.push({
                type: 'vote',
                date: voteDate,
                data: { productId, voteType, productUrl },
                icon: voteType === 'up' ? '👍' : '👎',
                description: `Voted ${voteType === 'up' ? 'up' : 'down'}: ${productName}`,
                productName: productName
              });
            });
            votesFromApi = true;
          }
        } catch (error) {
          logger.warn('Error fetching votes from API, falling back to localStorage:', error);
        }
      }
      if (!votesFromApi) {
        try {
          const votesData = localStorage.getItem('usedfindr_votes');
          if (votesData && this.currentUser) {
            const votes = JSON.parse(votesData);
            const userId = String(this.currentUser.id);
            Object.entries(votes).forEach(([productId, userVotes]) => {
              if (userVotes && typeof userVotes === 'object') {
                const userVote = userVotes[userId] || userVotes[parseInt(userId)] || userVotes[String(parseInt(userId))];
                if (userVote) {
                  const voteData = userVote;
                  let voteType = 'up';
                  let voteDate = new Date();
                  if (typeof voteData === 'object' && voteData !== null) {
                    voteType = voteData.voteType || voteData.vote_type || 'up';
                    voteDate = voteData.timestamp ? new Date(voteData.timestamp) : new Date();
                  } else if (typeof voteData === 'string') {
                    voteType = voteData;
                  }
                  let productName = 'Product';
                  let productUrl = null;
                  try {
                    if (typeof searchData !== 'undefined' && searchData && searchData.items) {
                      const product = searchData.items.find(p => (p.productId || p.id || p.name) === productId);
                      if (product) {
                        productName = product.name || product.productName || 'Product';
                        productUrl = product.affiliateLink || product.affiliate_link || product.url || null;
                      }
                    }
                    if (productName === 'Product') {
                      const products = JSON.parse(localStorage.getItem('usedfindr_items') || '[]');
                      const storedProduct = products.find(p => (p.id || p.productId || p.name) === productId);
                      if (storedProduct) {
                        productName = storedProduct.name || storedProduct.productName || 'Product';
                        productUrl = storedProduct.affiliateLink || storedProduct.affiliate_link || storedProduct.url || null;
                      }
                    }
                  } catch (e) {}
                  activities.push({
                    type: 'vote',
                    date: voteDate,
                    data: { productId, voteType, productUrl },
                    icon: voteType === 'up' ? '👍' : '👎',
                    description: `Voted ${voteType === 'up' ? 'up' : 'down'}: ${productName}`,
                    productName: productName
                  });
                }
              }
            });
          }
        } catch (error) {
          logger.warn('Error fetching votes from localStorage:', error);
        }
      }
      
      // Fetch user badges from profile
      if (window.apiService) {
        try {
          const profile = await window.apiService.getUserProfile();
          if (profile && profile.earnedBadges && Array.isArray(profile.earnedBadges)) {
            profile.earnedBadges.forEach(userBadge => {
              const badge = userBadge.badge;
              activities.push({
                type: 'badge',
                date: userBadge.earnedAt || userBadge.earned_at,
                data: userBadge,
                icon: badge.icon || '🏆',
                description: `Badge earned: ${badge.badgeName || 'Badge'}`,
                badgeDescription: badge.badgeDescription || ''
              });
            });
          }
        } catch (error) {
          logger.warn('Error fetching badges:', error);
        }
      }
      
      // Sort activities by date (most recent first)
      activities.sort((a, b) => {
        const dateA = new Date(a.date || 0);
        const dateB = new Date(b.date || 0);
        return dateB - dateA;
      });
      
      logger.info('Activity Center - Total activities loaded:', activities.length);
      logger.info('Activity breakdown:', {
        submissions: activities.filter(a => a.type === 'submission').length,
        votes: activities.filter(a => a.type === 'vote').length,
        badges: activities.filter(a => a.type === 'badge').length
      });
      
      // Render activities
      this.renderActivityTimeline(activities);
      
      // Set up filter buttons
      this.setupActivityFilters(activities);
    } catch (error) {
      logger.error('Error updating activity view:', error);
      timeline.innerHTML = '<div class="activity-error"><p>Error loading activities. Please try again later.</p></div>';
    }
  }

  renderActivityTimeline(activities, filter = 'all') {
    const timeline = document.getElementById('activityTimeline');
    if (!timeline) {
      logger.warn('Activity timeline element not found');
      return;
    }
    
    if (!activities || activities.length === 0) {
      logger.info('No activities to render');
      timeline.innerHTML = '<div class="no-activity"><p>No activities to show.</p><p class="empty-state-tip">Submit items, vote on products, or earn badges to see your activity.</p></div>';
      return;
    }
    
    // Filter activities if needed
    let filteredActivities = activities;
    if (filter !== 'all') {
      // Map filter values to activity types (handle plural/singular mismatch)
      const filterTypeMap = {
        'votes': 'vote',
        'badges': 'badge',
        'submissions': 'submission',
        'vote': 'vote',
        'badge': 'badge',
        'submission': 'submission'
      };
      
      const targetType = filterTypeMap[filter] || filter;
      filteredActivities = activities.filter(activity => activity.type === targetType);
      
      logger.info(`Filtering by "${filter}" (target type: "${targetType}"):`, {
        total: activities.length,
        filtered: filteredActivities.length,
        activityTypes: activities.map(a => a.type),
        filteredTypes: filteredActivities.map(a => a.type)
      });
    } else {
      // Limit "All Activities" to most recent 5
      filteredActivities = activities.slice(0, 5);
      logger.info('Showing all activities (limited to 5):', filteredActivities.length);
    }
    
    if (filteredActivities.length === 0) {
      logger.info(`No activities found for filter: ${filter}`);
      
      // Special empty state for submissions
      if (filter === 'submissions' || filter === 'submission') {
        timeline.innerHTML = `
          <div class="no-activity no-submissions">
            <p>You haven't submitted any items yet.</p>
            <button class="btn-submit-first-item" id="submitFirstItemBtn">Submit Your First Item</button>
          </div>
        `;
        
        // Set up click handler for the button
        setTimeout(() => {
          const btn = document.getElementById('submitFirstItemBtn');
          if (btn) {
            btn.addEventListener('click', () => {
              this.highlightAddItemLink();
            });
          }
        }, 50);
      } else {
        timeline.innerHTML = '<div class="no-activity"><p>No activities to show.</p><p class="empty-state-tip">Submit items, vote on products, or earn badges to see your activity.</p></div>';
      }
      return;
    }
    
    const timelineHTML = filteredActivities.map(activity => {
      const date = new Date(activity.date);
      const dateStr = date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      let statusBadge = '';
      if (activity.type === 'submission' && activity.status) {
        const statusClass = activity.status.toLowerCase().replace('_', '-');
        statusBadge = `<span class="activity-status-badge status-${statusClass}">${activity.status}</span>`;
      }
      
      // Build description with clickable product name for votes
      let descriptionHTML = activity.description;
      if (activity.type === 'vote' && activity.data && activity.data.productUrl && activity.data.voteType === 'up') {
        // For upvotes, make the product name a clickable affiliate link
        const productName = activity.productName || 'Product';
        const affiliateUrl = this.buildAffiliateUrl(activity.data.productUrl);
        // Replace the product name in the description with a link
        // Escape the product name for regex matching
        const escapedName = productName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        descriptionHTML = descriptionHTML.replace(
          new RegExp(`: ${escapedName}$`),
          `: <a href="${this.escapeHtml(affiliateUrl)}" target="_blank" rel="noopener noreferrer" class="activity-product-link">${this.escapeHtml(productName)}</a>`
        );
      }
      
      let link = '';
      if (activity.type === 'submission' && activity.data.url) {
        link = `<a href="${this.escapeHtml(activity.data.url)}" target="_blank" class="activity-link">View Product</a>`;
      }
      
      return `
        <div class="activity-timeline-item" data-activity-type="${activity.type}">
          <div class="activity-icon">${activity.icon}</div>
          <div class="activity-content">
            <p><strong>${descriptionHTML}</strong> ${statusBadge}</p>
            ${activity.badgeDescription ? `<small>${activity.badgeDescription}</small>` : ''}
            <small class="activity-date">${dateStr}</small>
            ${link}
          </div>
        </div>
      `;
    }).join('');
    
    timeline.innerHTML = timelineHTML;
  }

  setupActivityFilters(activities) {
    const filterButtons = document.querySelectorAll('.activity-filter-btn');
    if (!filterButtons.length) {
      logger.warn('No activity filter buttons found');
      return;
    }
    
    // Store activities on the AccountManager instance so filters can access them
    this._currentActivities = activities;
    logger.info('Activity filters setup - total activities:', activities.length, 'Types:', activities.map(a => a.type));
    
    filterButtons.forEach(button => {
      // Remove old listener if exists and reattach
      if (button.dataset.listenerAttached === 'true') {
        const oldHandler = button._filterClickHandler;
        if (oldHandler) {
          button.removeEventListener('click', oldHandler);
        }
      }
      
      // Create new handler
      const handler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const filter = button.getAttribute('data-filter');
        logger.info('Filter button clicked:', filter, 'Total activities:', this._currentActivities?.length);
        
        // Update active state
        filterButtons.forEach(btn => btn.classList.remove('active'));
        button.classList.add('active');
        
        // Filter and render using stored activities
        if (this._currentActivities && this._currentActivities.length > 0) {
          logger.info('Rendering with filter:', filter, 'Activities available:', this._currentActivities.length);
          this.renderActivityTimeline(this._currentActivities, filter);
        } else {
          logger.warn('No activities available for filtering');
          const timeline = document.getElementById('activityTimeline');
          if (timeline) {
            timeline.innerHTML = '<div class="no-activity"><p>No activities to show.</p><p class="empty-state-tip">Submit items, vote on products, or earn badges to see your activity.</p></div>';
          }
        }
      };
      
      // Store handler for potential removal
      button._filterClickHandler = handler;
      button.addEventListener('click', handler);
      button.dataset.listenerAttached = 'true';
    });
  }

  // Update settings view
  async updateSettingsView() {
    if (!this.currentUser) return;
    
    try {
      // Load current user data
      let userData = this.currentUser;
      
      // Try to get fresh data from API
      if (window.apiService) {
        try {
          const currentUser = await window.apiService.getCurrentUser();
          if (currentUser && currentUser.user) {
            userData = currentUser.user;
          }
        } catch (error) {
          logger.warn('Could not fetch current user from API:', error);
        }
      }
      
      // Populate form fields
      const firstNameInput = document.getElementById('settingsFirstName');
      const lastNameInput = document.getElementById('settingsLastName');
      const usernameInput = document.getElementById('settingsUsername');
      
      if (firstNameInput) {
        firstNameInput.value = userData.firstName || userData.first_name || '';
      }
      if (lastNameInput) {
        lastNameInput.value = userData.lastName || userData.last_name || '';
      }
      const emailDisplayEl = document.getElementById('settingsEmailDisplay');
      if (emailDisplayEl) {
        emailDisplayEl.textContent = userData.email || userData.email_address || '';
      }
      if (usernameInput) {
        usernameInput.value = userData.username || '';
      }
      
      // Load profile icon
      if (window.apiService) {
        try {
          // Get user profile to get current icon
          const profile = await window.apiService.getUserProfile();
          if (profile) {
            const currentIcon = profile.profileIcon || profile.profile_icon || '👤';
            const profileIconDisplay = document.getElementById('profileIconDisplay');
            if (profileIconDisplay) {
              profileIconDisplay.textContent = currentIcon;
            }
            
            // Load available icons and populate grid
            const iconsData = await window.apiService.getProfileIcons();
            if (iconsData && iconsData.icons) {
              const iconGrid = document.getElementById('profileIconGrid');
              if (iconGrid) {
                iconGrid.innerHTML = '';
                iconsData.icons.forEach(icon => {
                  const iconButton = document.createElement('button');
                  iconButton.type = 'button';
                  iconButton.className = 'profile-icon-button';
                  if (icon === currentIcon) {
                    iconButton.classList.add('selected');
                  }
                  iconButton.dataset.icon = icon;
                  iconButton.textContent = icon;
                  iconButton.setAttribute('aria-label', `Select ${icon} as profile icon`);
                  iconButton.addEventListener('click', () => {
                    this.updateProfileIcon(icon);
                  });
                  iconGrid.appendChild(iconButton);
                });
              }
            }
          }
        } catch (error) {
          logger.error('Error loading profile icon:', error);
        }
      }
      
      // Set up form handlers
      this.setupSettingsForms();
    } catch (error) {
      logger.error('Error updating settings view:', error);
    }
  }

  setupSettingsForms() {
    // Profile form
    const accountProfileForm = document.getElementById('accountProfileForm');
    if (accountProfileForm && !accountProfileForm.dataset.listenerAttached) {
      accountProfileForm.dataset.listenerAttached = 'true';
      accountProfileForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleProfileUpdate();
      });
    }

    // Change email form (modal)
    const changeEmailForm = document.getElementById('changeEmailForm');
    if (changeEmailForm && !changeEmailForm.dataset.listenerAttached) {
      changeEmailForm.dataset.listenerAttached = 'true';
      changeEmailForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleChangeEmail();
      });
    }

    // Change email modal close
    const changeEmailModalClose = document.getElementById('changeEmailModalClose');
    if (changeEmailModalClose && !changeEmailModalClose.dataset.listenerAttached) {
      changeEmailModalClose.dataset.listenerAttached = 'true';
      changeEmailModalClose.addEventListener('click', () => this.closeChangeEmailModal());
    }
    
    // Password change form
    const changePasswordForm = document.getElementById('changePasswordForm');
    if (changePasswordForm && !changePasswordForm.dataset.listenerAttached) {
      changePasswordForm.dataset.listenerAttached = 'true';
      changePasswordForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handlePasswordChange();
      });
    }

    // Send password reset link button
    const sendResetLinkBtn = document.getElementById('sendPasswordResetLinkBtn');
    if (sendResetLinkBtn && !sendResetLinkBtn.dataset.listenerAttached) {
      sendResetLinkBtn.dataset.listenerAttached = 'true';
      sendResetLinkBtn.addEventListener('click', () => this.handleSendPasswordResetLink());
    }
  }

  async handleSendPasswordResetLink() {
    if (!this.currentUser || !this.currentUser.email) return;
    const messageEl = document.getElementById('passwordMessage');
    const btn = document.getElementById('sendPasswordResetLinkBtn');
    const originalText = btn ? btn.textContent : '';
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Sending...';
    }
    try {
      if (window.apiService) {
        await window.apiService.requestPasswordReset(this.currentUser.email);
        this.showSettingsMessage(messageEl, 'Check your email for a password reset link.', 'success');
      } else {
        this.showSettingsMessage(messageEl, 'API service not available', 'error');
      }
    } catch (error) {
      this.showSettingsMessage(messageEl, error.message || error.detail || 'Failed to send reset link', 'error');
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.textContent = originalText;
      }
    }
  }

  async handleProfileUpdate() {
    if (!this.currentUser) return;
    
    const firstNameInput = document.getElementById('settingsFirstName');
    const lastNameInput = document.getElementById('settingsLastName');
    const usernameInput = document.getElementById('settingsUsername');
    const messageEl = document.getElementById('accountProfileMessage');
    
    if (!firstNameInput || !lastNameInput || !usernameInput) return;
    
    const firstName = firstNameInput.value.trim();
    const lastName = lastNameInput.value.trim();
    const username = usernameInput.value.trim();
    
    // Username validation
    if (username) {
      if (username.length < 3 || username.length > 20) {
        this.showSettingsMessage(messageEl, 'Username must be between 3 and 20 characters long', 'error');
        return;
      }
      
      if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
        this.showSettingsMessage(messageEl, 'Username can only contain letters, numbers, underscores, and hyphens', 'error');
        return;
      }
    }
    
    try {
      if (window.apiService) {
        const updateData = {
          firstName: firstName,
          lastName: lastName
        };
        
        if (username && username !== this.currentUser.username) {
          updateData.username = username;
        }
        
        const updated = await window.apiService.updateUserProfile(updateData);
        
        if (updated && updated.user) {
          this.currentUser = { ...this.currentUser, ...updated.user };
          localStorage.setItem('usedfindr_user', JSON.stringify(this.currentUser));
        }
        
        if (typeof updateSidebarProfile === 'function') {
          updateSidebarProfile();
        }
        
        this.showSettingsMessage(messageEl, 'Profile updated successfully!', 'success');
      } else {
        this.showSettingsMessage(messageEl, 'API service not available', 'error');
      }
    } catch (error) {
      const errorMessage = error.message || error.detail || 'Failed to update profile';
      this.showSettingsMessage(messageEl, errorMessage, 'error');
    }
  }

  openChangeEmailModal() {
    const modal = document.getElementById('changeEmailModal');
    if (!modal) return;
    const newEmailInput = document.getElementById('changeEmailNewEmail');
    const passwordInput = document.getElementById('changeEmailPassword');
    const messageEl = document.getElementById('changeEmailMessage');
    if (newEmailInput) newEmailInput.value = this.currentUser?.email || '';
    if (passwordInput) passwordInput.value = '';
    if (messageEl) {
      messageEl.textContent = '';
      messageEl.style.display = 'none';
    }
    modal.style.display = 'flex';
    if (newEmailInput) newEmailInput.focus();

    const backdropHandler = (e) => {
      if (e.target === modal) this.closeChangeEmailModal();
    };
    modal._changeEmailBackdropHandler = backdropHandler;
    modal.addEventListener('click', backdropHandler);

    const escHandler = (e) => {
      if (e.key === 'Escape') this.closeChangeEmailModal();
    };
    modal._changeEmailEscHandler = escHandler;
    document.addEventListener('keydown', escHandler);
  }

  closeChangeEmailModal() {
    const modal = document.getElementById('changeEmailModal');
    if (!modal) return;
    if (modal._changeEmailBackdropHandler) {
      modal.removeEventListener('click', modal._changeEmailBackdropHandler);
      modal._changeEmailBackdropHandler = null;
    }
    if (modal._changeEmailEscHandler) {
      document.removeEventListener('keydown', modal._changeEmailEscHandler);
      modal._changeEmailEscHandler = null;
    }
    modal.style.display = 'none';
  }

  async handleChangeEmail() {
    if (!this.currentUser) return;
    const newEmailInput = document.getElementById('changeEmailNewEmail');
    const passwordInput = document.getElementById('changeEmailPassword');
    const messageEl = document.getElementById('changeEmailMessage');
    const form = document.getElementById('changeEmailForm');
    const submitBtn = form ? form.querySelector('button[type="submit"]') : null;

    if (!newEmailInput || !passwordInput) return;

    const newEmail = newEmailInput.value.trim();
    const currentPassword = passwordInput.value;

    if (!newEmail) {
      this.showSettingsMessage(messageEl, 'New email is required', 'error');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      this.showSettingsMessage(messageEl, 'Please enter a valid email address', 'error');
      return;
    }
    if (!currentPassword) {
      this.showSettingsMessage(messageEl, 'Current password is required', 'error');
      return;
    }

    const originalBtnText = submitBtn ? submitBtn.textContent : '';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Changing...';
    }

    try {
      if (window.apiService) {
        const result = await window.apiService.changeEmail({
          newEmail: newEmail,
          currentPassword: currentPassword
        });
        if (result && result.user) {
          this.currentUser = { ...this.currentUser, ...result.user };
          localStorage.setItem('usedfindr_user', JSON.stringify(this.currentUser));
        }
        const emailDisplayEl = document.getElementById('settingsEmailDisplay');
        if (emailDisplayEl) emailDisplayEl.textContent = this.currentUser.email || '';
        this.closeChangeEmailModal();
        const accountEmailMessage = document.getElementById('accountEmailMessage');
        this.showSettingsMessage(accountEmailMessage, 'Email updated successfully!', 'success');
      } else {
        this.showSettingsMessage(messageEl, 'API service not available', 'error');
      }
    } catch (error) {
      const errMsg = error.body?.error || error.message || error.detail || 'Failed to update email';
      this.showSettingsMessage(messageEl, errMsg, 'error');
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalBtnText;
      }
    }
  }
  
  async updateProfileIcon(icon) {
    if (!this.currentUser || !window.apiService) return;
    
    // Optimistic UI update - update immediately before API call
    // Update compact preview in settings
    const profileIconDisplayCompact = document.getElementById('profileIconDisplayCompact');
    if (profileIconDisplayCompact) {
      profileIconDisplayCompact.textContent = icon;
    }
    
    // Update modal display
    const profileIconDisplayModal = document.getElementById('profileIconDisplayModal');
    if (profileIconDisplayModal) {
      profileIconDisplayModal.textContent = icon;
    }
    
    // Update selected icon in grid immediately
    const iconButtons = document.querySelectorAll('.profile-icon-button-modal');
    iconButtons.forEach(btn => {
      if (btn.dataset.icon === icon) {
        btn.classList.add('selected');
      } else {
        btn.classList.remove('selected');
      }
    });
    
    // Update sidebar avatar immediately (optimistic update) — safe: textContent only (Phase 2 XSS)
    const profileAvatar = document.querySelector('.sidebar-profile-avatar');
    if (profileAvatar) {
      const span = document.createElement('span');
      span.className = 'sidebar-profile-icon-emoji';
      span.textContent = icon;
      profileAvatar.replaceChildren(span);
      profileAvatar.classList.add('has-icon');
    }
    
    try {
      const updateData = {
        profileIcon: icon
      };
      
      const updated = await window.apiService.updateUserProfile(updateData);
      
      // Update current user data with the new icon
      if (updated && updated.user) {
        this.currentUser = { ...this.currentUser, ...updated.user };
        // Ensure profileIcon is stored
        if (updated.user.profileIcon) {
          this.currentUser.profileIcon = updated.user.profileIcon;
        }
        localStorage.setItem('usedfindr_user', JSON.stringify(this.currentUser));
      } else {
        // If response doesn't include profileIcon, add it manually
        this.currentUser.profileIcon = icon;
        localStorage.setItem('usedfindr_user', JSON.stringify(this.currentUser));
      }
      
      // Force sidebar update to refresh from API (bypass cooldown)
      if (typeof updateSidebarProfile === 'function') {
        // Reset the cooldown timestamp to force an update
        window.lastProfileFetchTime = 0;
        // Small delay to ensure API response is processed
        setTimeout(() => {
          updateSidebarProfile();
        }, 100);
      }
    } catch (error) {
      logger.error('Error updating profile icon:', error);
      const errorMessage = error.message || error.detail || 'Failed to update profile icon';
      alert(errorMessage);
      
      // Revert optimistic update on error — safe: textContent only (Phase 2 XSS)
      if (this.currentUser && this.currentUser.profileIcon) {
        const actualIcon = this.currentUser.profileIcon;
        if (profileIconDisplayCompact) {
          profileIconDisplayCompact.textContent = actualIcon;
        }
        if (profileIconDisplayModal) {
          profileIconDisplayModal.textContent = actualIcon;
        }
        if (profileAvatar) {
          const span = document.createElement('span');
          span.className = 'sidebar-profile-icon-emoji';
          span.textContent = actualIcon;
          profileAvatar.replaceChildren(span);
        }
      }
    }
  }
  async openProfileIconModal() {
    const modal = document.getElementById('profileIconModal');
    if (!modal) return;
    
    // Show modal
    modal.style.display = 'flex';
    setTimeout(() => {
      modal.classList.add('is-visible');
    }, 10);
    
    // Load current icon
    try {
      if (window.apiService) {
        const profile = await window.apiService.getUserProfile();
        if (profile) {
          const currentIcon = profile.profileIcon || profile.profile_icon || '👤';
          const profileIconDisplayModal = document.getElementById('profileIconDisplayModal');
          if (profileIconDisplayModal) {
            profileIconDisplayModal.textContent = currentIcon;
          }
          
          // Load available icons and populate grid
          const iconsData = await window.apiService.getProfileIcons();
          if (iconsData && iconsData.icons) {
            const iconGrid = document.getElementById('profileIconGridModal');
            if (iconGrid) {
              iconGrid.innerHTML = '';
              iconsData.icons.forEach(icon => {
                const iconButton = document.createElement('button');
                iconButton.type = 'button';
                iconButton.className = 'profile-icon-button-modal';
                if (icon === currentIcon) {
                  iconButton.classList.add('selected');
                }
                iconButton.dataset.icon = icon;
                iconButton.textContent = icon;
                iconButton.setAttribute('aria-label', `Select ${icon} as profile icon`);
                iconButton.addEventListener('click', () => {
                  this.updateProfileIcon(icon);
                });
                iconGrid.appendChild(iconButton);
              });
            }
          }
        }
      }
    } catch (error) {
      logger.error('Error loading profile icons:', error);
    }
    
    // Add ESC key listener
    const escHandler = (e) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        this.closeProfileIconModal();
        document.removeEventListener('keydown', escHandler);
      }
    };
    document.addEventListener('keydown', escHandler);
    
    // Add backdrop click handler
    const backdropHandler = (e) => {
      if (e.target === modal) {
        this.closeProfileIconModal();
        modal.removeEventListener('click', backdropHandler);
      }
    };
    modal.addEventListener('click', backdropHandler);
  }
  
  closeProfileIconModal() {
    const modal = document.getElementById('profileIconModal');
    if (!modal) return;
    
    modal.classList.remove('is-visible');
    setTimeout(() => {
      modal.style.display = 'none';
    }, 250);
  }
  
  async updateSettingsView() {
    if (!this.currentUser) return;
    
    // Load current user data into form
    const firstNameInput = document.getElementById('settingsFirstName');
    const lastNameInput = document.getElementById('settingsLastName');
    const emailInput = document.getElementById('settingsEmail');
    const usernameInput = document.getElementById('settingsUsername');
    
    if (firstNameInput) firstNameInput.value = this.currentUser.firstName || this.currentUser.first_name || '';
    if (lastNameInput) lastNameInput.value = this.currentUser.lastName || this.currentUser.last_name || '';
    if (emailInput) emailInput.value = this.currentUser.email || '';
    if (usernameInput) usernameInput.value = this.currentUser.username || '';
    
    // Load current profile icon for compact preview (don't load grid - that's in modal)
    try {
      if (window.apiService) {
        // Get user profile to get current icon
        const profile = await window.apiService.getUserProfile();
        if (profile) {
          const currentIcon = profile.profileIcon || profile.profile_icon || '👤';
          const profileIconDisplayCompact = document.getElementById('profileIconDisplayCompact');
          if (profileIconDisplayCompact) {
            profileIconDisplayCompact.textContent = currentIcon;
          }
        }
      }
    } catch (error) {
      logger.error('Error loading profile icon:', error);
    }
  }

  async handlePasswordChange() {
    if (!this.currentUser) return;
    
    const changePasswordForm = document.getElementById('changePasswordForm');
    const currentPasswordInput = document.getElementById('currentPassword');
    const newPasswordInput = document.getElementById('newPassword');
    const confirmPasswordInput = document.getElementById('confirmPassword');
    const messageEl = document.getElementById('passwordMessage');
    const submitBtn = changePasswordForm ? changePasswordForm.querySelector('button[type="submit"]') : null;
    
    if (!currentPasswordInput || !newPasswordInput || !confirmPasswordInput) return;
    
    // Clear previous message so old errors/success don't linger
    if (messageEl) {
      messageEl.textContent = '';
      messageEl.style.display = 'none';
      messageEl.className = 'form-message';
    }
    
    const currentPassword = currentPasswordInput.value;
    const newPassword = newPasswordInput.value;
    const confirmPassword = confirmPasswordInput.value;
    
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      this.showSettingsMessage(messageEl, 'All password fields are required', 'error');
      return;
    }
    
    if (newPassword.length < 8) {
      this.showSettingsMessage(messageEl, 'New password must be at least 8 characters long', 'error');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      this.showSettingsMessage(messageEl, 'New passwords do not match', 'error');
      return;
    }
    
    const originalBtnText = submitBtn ? submitBtn.textContent : '';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Changing...';
    }
    
    try {
      if (window.apiService) {
        await window.apiService.changePassword({
          currentPassword: currentPassword,
          newPassword: newPassword
        });
        
        // Clear form
        currentPasswordInput.value = '';
        newPasswordInput.value = '';
        confirmPasswordInput.value = '';
        
        this.showSettingsMessage(messageEl, 'Password changed successfully!', 'success');
      } else {
        this.showSettingsMessage(messageEl, 'API service not available', 'error');
      }
    } catch (error) {
      const errorMessage = error.message || error.detail || 'Failed to change password';
      this.showSettingsMessage(messageEl, errorMessage, 'error');
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalBtnText;
      }
    }
  }

  showSettingsMessage(element, message, type) {
    if (!element) return;
    
    element.textContent = message;
    element.className = `form-message ${type}`;
    element.style.display = 'block';
    
    // Auto-hide success messages after 5 seconds
    if (type === 'success') {
      setTimeout(() => {
        element.style.display = 'none';
      }, 5000);
    }
  }
  
  removeFind(findId) {
    this.userFinds = this.userFinds.filter(find => find.id !== findId);
    this.saveUserFinds();
    this.updateDashboard();
    this.showNotification('Item removed successfully.');
  }
  
  // DEPRECATED: redeemPointsForEntry - kept for future use
  // redeemPointsForEntry() {
  //   if (!this.currentUser) {
  //     this.showNotification('Please log in to redeem points.', 'error');
  //     return;
  //   }
  //   
  //   const pointsBalance = this.currentUser.pointsBalance || 0;
  //   
  //   if (pointsBalance < 100) {
  //     this.showNotification('You need at least 100 points to redeem for a bonus entry.', 'error');
  //     return;
  //   }
  //   
  //   // Subtract 100 points and add 1 bonus entry
  //   this.currentUser.pointsBalance = pointsBalance - 100;
  //   this.currentUser.bonusEntries = (this.currentUser.bonusEntries || 0) + 1;
  //   
  //   // Save updated user
  //   localStorage.setItem('usedfindr_user', JSON.stringify(this.currentUser));
  //   // Also update in users array
  //   this.updateUserInArray(this.currentUser.id, {
  //     pointsBalance: this.currentUser.pointsBalance,
  //     bonusEntries: this.currentUser.bonusEntries
  //   });
  //   
  //   // Refresh current user reference
  //   this.currentUser = this.getCurrentUser();
  //   
  //   // Update dashboard to reflect changes
  //   this.updateDashboard();
  //   
  //   // Show success notification
  //   this.showNotification('Successfully redeemed 100 points for 1 bonus contest entry!');
  // }
  
  // Saved Items (deprecated): stubs for backward compatibility
  async getSavedItems() {
    return [];
  }

  saveSavedItems() {}

  async saveItem() {
    return false;
  }

  async unsaveItem() {
    return false;
  }

  async isItemSaved() {
    return false;
  }

  async updateSavedItemsView() {}

  buildAffiliateUrl(url) {
    if (!url) return '#';
    
    try {
      // Parse the URL
      const urlObj = new URL(url);
      
      // Check if it already has the affiliate tag
      if (urlObj.searchParams.has('tag') && urlObj.searchParams.get('tag') === 'usedfindr0f-20') {
        return url; // Already has correct tag
      }
      
      // Add or update the affiliate tag (Associates Store ID)
      urlObj.searchParams.set('tag', 'usedfindr0f-20');
      
      return urlObj.toString();
    } catch (e) {
      // If URL parsing fails, try to append the tag manually
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}tag=usedfindr0f-20`;
    }
  }
  
  escapeHtml(text) {
    if (text === null || text === undefined) return '';
    // Escapes quotes as well as angle brackets. The old textContent -> innerHTML
    // trick left " and ' alone, and this helper is used inside double-quoted
    // attributes (data-name, title, data-url), where an unescaped quote lets
    // attacker-supplied listing text close the attribute and add its own.
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  highlightAddItemLink() {
    // Expand the sidebar if it's collapsed
    if (!document.documentElement.classList.contains('account-sidebar-expanded')) {
      document.documentElement.classList.add('account-sidebar-expanded');
      localStorage.setItem('accountSidebarState', 'true');
    }

    // Find the Add Item link
    const addItemLink = document.getElementById('add-item-link');
    if (addItemLink) {
      // Scroll to the link if needed
      addItemLink.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

      // Add highlight class
      addItemLink.classList.add('highlight-add-item');

      // Remove highlight after animation
      setTimeout(() => {
        addItemLink.classList.remove('highlight-add-item');
      }, 2000); // Highlight for 2 seconds
    }
  }

  getMonthlyEntries() {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    return this.getMonthlyEntriesForMonth(currentYear, currentMonth);
  }
  
  getMonthlyEntriesForMonth(year, month) {
    const regularEntries = this.userFinds.filter(find => {
      if (!find.addedAt) return false;
      const findDate = new Date(find.addedAt);
      return findDate.getMonth() === month && findDate.getFullYear() === year;
    }).length;
    
    // Bonus entries are counted for the current month only (simplified)
    // In a more sophisticated system, we'd track which month each bonus entry was used for
    const bonusEntries = (this.currentUser && this.currentUser.bonusEntries) || 0;
    
    // For previous months, only count regular entries
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    if (year === currentYear && month === currentMonth) {
      return regularEntries + bonusEntries;
    } else {
      return regularEntries;
    }
  }
  
  // DEPRECATED: checkWinnerStatus - kept for future use
  // checkWinnerStatus() {
  //   if (!this.currentUser || !window.contestManager) return;
  //   
  //   const latestDrawing = window.contestManager.getLatestDrawing();
  //   if (!latestDrawing || !latestDrawing.winner) return;
  //   
  //   // Check if current user is the winner
  //   if (latestDrawing.winner.userId === this.currentUser.id) {
  //     const winnerBanner = document.getElementById('winnerAnnouncement');
  //     const winnerDetails = document.getElementById('winnerBannerDetails');
  //     
  //     if (winnerBanner) {
  //       winnerBanner.style.display = 'block';
  //     }
  //     
  //     if (winnerDetails) {
  //       winnerDetails.textContent = `Congratulations! You won the ${latestDrawing.monthName} ${latestDrawing.year} contest with ${latestDrawing.winner.entryCount} entries!`;
  //     }
  //   } else {
  //     const winnerBanner = document.getElementById('winnerAnnouncement');
  //     if (winnerBanner) {
  //       winnerBanner.style.display = 'none';
  //     }
  //   }
  // }
  
  getNextDrawingDate() {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return nextMonth.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  }
  
  getUserStatus() {
    if (!this.currentUser) return { status: null, badge: null };
    
    const points = this.currentUser.pointsBalance || 0;
    
    if (points >= 1000) {
      return { status: 'Pro Submitter', badge: 'Pro Submitter' };
    } else if (points >= 500) {
      return { status: 'Gold User', badge: 'Gold User' };
    }
    
    return { status: null, badge: null };
  }
  
  getUserById(userId) {
    const users = this.getAllUsers();
    return users.find(u => u.id === userId) || null;
  }
  
  showNotification(message, type = 'success') {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = 'notification';
    notification.textContent = message;
    const bgColor = type === 'error' ? '#ef4444' : 'var(--accent)';
    const textColor = type === 'error' ? '#ffffff' : '#111827';
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${bgColor};
      color: ${textColor};
      padding: 12px 20px;
      border-radius: var(--radius-sm);
      box-shadow: var(--shadow-hover);
      z-index: 1000;
      font-weight: 600;
    `;
    
    document.body.appendChild(notification);
    
    // Remove after 3 seconds
    setTimeout(() => {
      notification.remove();
    }, 3000);
  }
  
  // Local storage helpers
  getCurrentUser() {
    // First try to get from single user storage (for backward compatibility)
    const userData = localStorage.getItem('usedfindr_user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        let needsSave = false;
        
        // Initialize pointsBalance and bonusEntries for existing users
        if (user.pointsBalance === undefined) {
          user.pointsBalance = 0;
          needsSave = true;
        }
        if (user.bonusEntries === undefined) {
          user.bonusEntries = 0;
          needsSave = true;
        }
        
        // Save back if we had to initialize
        if (needsSave) {
          localStorage.setItem('usedfindr_user', JSON.stringify(user));
        }
        
        logger.debug('[getCurrentUser] User from localStorage', user?.id ? { id: user.id } : {});
        return user;
      } catch (e) {
        logger.error('Error parsing user data:', e);
      }
    }
    
    logger.debug('[getCurrentUser] No user in localStorage');
    return null;
  }
  
  async getUserFinds() {
    // Try API first
    if (window.apiService && this.currentUser) {
      try {
        const apiFinds = await window.apiService.getUserFinds();
        // Update localStorage cache
        try {
          localStorage.setItem('usedfindr_finds', JSON.stringify(apiFinds));
        } catch (storageError) {
          logger.warn('Failed to update localStorage cache:', storageError);
        }
        return apiFinds || [];
      } catch (apiError) {
        logger.warn('Failed to load user finds from API, falling back to localStorage:', apiError);
        // Fall through to localStorage fallback
      }
    }
    
    // Fallback to localStorage
    const findsData = localStorage.getItem('usedfindr_finds');
    return findsData ? JSON.parse(findsData) : [];
  }
  
  async saveUserFinds() {
    // Try API first for each find
    if (window.apiService && this.currentUser && this.userFinds && this.userFinds.length > 0) {
      try {
        // Get existing finds from API
        const apiFinds = await window.apiService.getUserFinds();
        const apiFindProductIds = new Set(apiFinds.map(f => f.productId));
        
        // Add any finds that aren't in API
        for (const find of this.userFinds) {
          const productId = find.productId || find.id || find.name;
          if (productId && !apiFindProductIds.has(productId)) {
            try {
              await window.apiService.addUserFind(productId);
            } catch (error) {
              logger.warn(`Failed to sync find ${productId} to API:`, error);
            }
          }
        }
        
        // Update localStorage cache with API data
        try {
          const updatedFinds = await window.apiService.getUserFinds();
          localStorage.setItem('usedfindr_finds', JSON.stringify(updatedFinds));
        } catch (storageError) {
          logger.warn('Failed to update localStorage cache:', storageError);
        }
        return;
      } catch (apiError) {
        logger.warn('Failed to sync user finds to API, falling back to localStorage:', apiError);
        // Fall through to localStorage fallback
      }
    }
    
    // Fallback to localStorage
    localStorage.setItem('usedfindr_finds', JSON.stringify(this.userFinds || []));
  }
  
  async migrateSavedItemsToAPI() {
    // Migrate localStorage saved items to API
    if (!window.apiService || !this.currentUser) {
      return;
    }
    
    try {
      const localSavedItems = JSON.parse(localStorage.getItem('usedfindr_saved_items') || '[]');
      if (!Array.isArray(localSavedItems) || localSavedItems.length === 0) {
        return; // Nothing to migrate
      }
      
      // Get existing saved items from API
      const apiSavedItems = await window.apiService.getSavedItems();
      const apiProductIds = new Set(apiSavedItems.map(item => item.productId));
      
      // Migrate items that don't exist in API
      let migratedCount = 0;
      for (const localItem of localSavedItems) {
        const productId = localItem.productId || localItem.id || localItem.name;
        if (productId && !apiProductIds.has(productId)) {
          try {
            await window.apiService.saveItem({
              productId: productId,
              name: localItem.name || 'Untitled Product',
              category: localItem.category || 'Unknown',
              condition: localItem.condition || 'N/A',
              price: localItem.price || 'N/A',
              affiliateLink: localItem.affiliateLink || localItem.url || '#',
              imageUrl: localItem.imageUrl || 'https://via.placeholder.com/300x300?text=No+Image'
            });
            migratedCount++;
          } catch (error) {
            logger.warn(`Failed to migrate saved item ${productId}:`, error);
          }
        }
      }
      
      if (migratedCount > 0) {
        logger.info(`Migrated ${migratedCount} saved items to API`);
        // Refresh from API to update cache
        await this.getSavedItems();
      }
    } catch (error) {
      logger.error('Error migrating saved items to API:', error);
    }
  }
  
  async migrateUserFindsToAPI() {
    // Migrate localStorage user finds to API
    if (!window.apiService || !this.currentUser) {
      return;
    }
    
    try {
      const localFinds = JSON.parse(localStorage.getItem('usedfindr_finds') || '[]');
      if (!Array.isArray(localFinds) || localFinds.length === 0) {
        return; // Nothing to migrate
      }
      
      // Get existing finds from API
      const apiFinds = await window.apiService.getUserFinds();
      const apiProductIds = new Set(apiFinds.map(find => find.productId));
      
      // Migrate finds that don't exist in API
      let migratedCount = 0;
      for (const localFind of localFinds) {
        const productId = localFind.productId || localFind.id || localFind.name;
        if (productId && !apiProductIds.has(productId)) {
          try {
            await window.apiService.addUserFind(productId);
            migratedCount++;
          } catch (error) {
            logger.warn(`Failed to migrate find ${productId}:`, error);
          }
        }
      }
      
      if (migratedCount > 0) {
        logger.info(`Migrated ${migratedCount} user finds to API`);
        // Refresh from API to update cache
        await this.getUserFinds();
      }
    } catch (error) {
      logger.error('Error migrating user finds to API:', error);
    }
  }
  
  logout() {
    if (window.apiService && typeof window.apiService.clearAccessToken === 'function') {
      window.apiService.clearAccessToken();
    }
    localStorage.removeItem('usedfindr_user');
    localStorage.removeItem('usedfindr_finds');
    window.location.href = 'index.html';
  }

  updateNavigation() {
    // The site shell is the Console rail + top bar, and its signed-in state is
    // server-rendered; there is no legacy `.nav-links` navbar to mutate.
    if (document.querySelector('.app[data-site-shell="1"]')) {
      return;
    }
    const navLinks = document.querySelector('.nav-links');
    if (!navLinks) {
      logger.warn('updateNavigation: nav-links element not found');
      return;
    }
    
    // Refresh current user state to ensure accuracy
    this.currentUser = this.getCurrentUser();
    
    // Preserve the "Add Item" button if it exists
    const addItemBtn = navLinks.querySelector('.add-item-btn');
    const addItemBtnHTML = addItemBtn ? addItemBtn.outerHTML : '';
    
    const baseLinks = `
      <a href="trusted-finder.html" class="nav-link" data-nav="trusted-finder">Trusted Finder</a>
      <a href="search.html" class="nav-link" data-nav="search">Search</a>
      <a href="categories.html" class="nav-link" data-nav="categories">Categories</a>
    `;
    
    if (this.currentUser) {
      const profileIcon = (this.currentUser.profileIcon || this.currentUser.profile_icon || '👤').trim() || '👤';
      const firstName = this.currentUser.firstName || this.currentUser.first_name || '';
      const displayLabel = firstName ? firstName : (this.currentUser.username && !this.currentUser.username.includes('@') ? this.currentUser.username : 'Account');
      navLinks.innerHTML = addItemBtnHTML + baseLinks + `
        <div class="nav-profile-wrap" id="navProfileWrap">
          <button type="button" class="nav-profile-trigger" id="navProfileTrigger" aria-haspopup="true" aria-expanded="false" title="Account menu">
            <span class="nav-profile-icon">${this.escapeHtml(profileIcon)}</span>
            <span class="nav-profile-label">${this.escapeHtml(displayLabel)}</span>
          </button>
          <div class="nav-profile-dropdown" id="navProfileDropdown" hidden>
            <a href="account.html#dashboard" class="nav-profile-dropdown-item">My Account</a>
            <a href="account.html#settings" class="nav-profile-dropdown-item">Settings</a>
            <button type="button" class="nav-profile-dropdown-item nav-profile-signout" id="navProfileSignOut">Sign Out</button>
          </div>
        </div>
      `;
      this._setupProfileDropdown();
    } else {
      navLinks.innerHTML = addItemBtnHTML + baseLinks + `
        <a href="login.html" class="nav-link" data-nav="sign-in">Sign In</a>
        <a href="register.html" class="nav-link" data-nav="sign-up">Sign Up</a>
      `;
    }
    
    if (addItemBtn) {
      const newAddItemBtn = navLinks.querySelector('.add-item-btn');
      if (newAddItemBtn && newAddItemBtn.id === 'globalAddItemBtn') {
        newAddItemBtn.onclick = openAddItemModalGlobal;
      }
    }
    
    if (typeof highlightActiveNavLink === 'function') {
      highlightActiveNavLink();
    }
  }

  _setupProfileDropdown() {
    const trigger = document.getElementById('navProfileTrigger');
    const dropdown = document.getElementById('navProfileDropdown');
    const signOutBtn = document.getElementById('navProfileSignOut');
    if (!trigger || !dropdown) return;

    const closeDropdown = () => {
      trigger.setAttribute('aria-expanded', 'false');
      dropdown.hidden = true;
      document.removeEventListener('click', outsideHandler);
    };

    const outsideHandler = (e) => {
      const wrap = document.getElementById('navProfileWrap');
      if (wrap && !wrap.contains(e.target)) closeDropdown();
    };

    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = dropdown.hidden === false;
      if (isOpen) {
        closeDropdown();
      } else {
        trigger.setAttribute('aria-expanded', 'true');
        dropdown.hidden = false;
        document.addEventListener('click', outsideHandler);
      }
    });

    dropdown.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', () => closeDropdown());
    });

    if (signOutBtn && typeof window.handleSignOut === 'function') {
      signOutBtn.addEventListener('click', (e) => {
        e.preventDefault();
        closeDropdown();
        window.handleSignOut();
      });
    }
  }
}

const ADD_ITEM_MODAL_TRANSITION_DURATION = 250;

function getAddItemModalElements() {
  const modal = document.getElementById('addItemModal');
  if (!modal) {
    return {};
  }

  const modalContent = modal.querySelector('.modal-content');
  const form = document.getElementById('addItemForm');

  return { modal, modalContent, form };
}

function resetAddItemModalForm(form) {
  if (!form) {
    return;
  }

  form.reset();
  form.dataset.processing = 'false';
  form.classList.remove('was-validated');
  const categoryGroup = document.getElementById('addItemModalEbayCategoryGroup');
  const categoryDisplay = document.getElementById('addItemModalEbayCategoryDisplay');
  if (categoryGroup) categoryGroup.style.display = 'none';
  if (categoryDisplay) categoryDisplay.textContent = '';
}

function ensureAddItemModalInitialized() {
  const { modal } = getAddItemModalElements();
  if (!modal || modal.dataset.modalInitialized === 'true') {
    return;
  }

  modal.dataset.modalInitialized = 'true';
  modal.dataset.state = modal.style.display === 'flex' ? 'open' : 'closed';

  // Track mousedown location to prevent closing when dragging from input to backdrop
  let mouseDownOnBackdrop = false;
  modal.addEventListener('mousedown', (event) => {
    mouseDownOnBackdrop = (event.target === modal);
  });
  modal.addEventListener('click', (event) => {
    // Only close if both mousedown and click (mouseup) happened on the backdrop
    if (event.target === modal && mouseDownOnBackdrop) {
      AccountManager.closeAddItemModalGlobal();
    }
    mouseDownOnBackdrop = false; // Reset
  });

  const closeButtons = modal.querySelectorAll('.modal-close, #addItemModalCancel');
  closeButtons.forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      AccountManager.closeAddItemModalGlobal();
    });
  });

  const emailPreferencesContainer = document.getElementById('emailPreferencesContainer');
  if (emailPreferencesContainer) {
    const emailPreferenceCheckboxes = emailPreferencesContainer.querySelectorAll('input[type="checkbox"]');
    emailPreferenceCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', (event) => {
        const preferenceName = event.target.name;
        const isChecked = event.target.checked;
        const user = accountManager.getCurrentUser();
        if (user) {
          user.emailPreferences = user.emailPreferences || {};
          user.emailPreferences[preferenceName] = isChecked;
          localStorage.setItem('usedfindr_user', JSON.stringify(user));
          accountManager.showNotification(`${preferenceName} preference updated.`);
        }
      });
    });
  }
}

function showAddItemModalUI() {
  const { modal, modalContent } = getAddItemModalElements();
  if (!modal) {
    logger.error('showAddItemModalUI: addItemModal element not found in DOM');
    return null;
  }

  ensureAddItemModalInitialized();

  if (modal._addItemModalCloseTimeout) {
    clearTimeout(modal._addItemModalCloseTimeout);
    modal._addItemModalCloseTimeout = null;
  }

  if (modal._addItemModalTransitionHandler) {
    modal.removeEventListener('transitionend', modal._addItemModalTransitionHandler);
    modal._addItemModalTransitionHandler = null;
  }

  if (modal.dataset.state === 'open') {
    return { modal, modalContent };
  }

  // Clean up any conflicting inline styles that might interfere with CSS transitions
  modal.style.removeProperty('opacity');
  modal.style.removeProperty('visibility');
  modal.style.removeProperty('pointer-events');
  if (modalContent) {
    modalContent.style.removeProperty('transform');
  }

  modal.dataset.state = 'open';
  
  // Ensure element is in the render tree: set display:flex first (robust across browsers)
  modal.style.display = 'flex';
  
  // Add the is-visible class so CSS takes over for opacity/visibility/pointer-events
  modal.classList.add('is-visible');

  const titleEl = modal.querySelector('.modal-header-title h3');
  if (titleEl && !titleEl.id) {
    titleEl.id = 'addItemModalTitle';
  }
  attachModalA11y(modal, {
    invoker: document.activeElement instanceof HTMLElement ? document.activeElement : null,
    labelledBy: titleEl?.id || 'addItemModalTitle',
    onClose: () => {
      if (typeof window.closeAddItemModal === 'function') {
        window.closeAddItemModal();
      } else if (typeof AccountManager !== 'undefined' && AccountManager.closeAddItemModalGlobal) {
        AccountManager.closeAddItemModalGlobal();
      }
    },
  });

  return { modal, modalContent };
}
function hideAddItemModalUI(onClosed) {
  const { modal, modalContent, form } = getAddItemModalElements();
  if (!modal) {
    if (typeof onClosed === 'function') {
      onClosed({ modal: null, form: null });
    }
    return null;
  }

  if (modal.dataset.state !== 'open') {
    if (typeof onClosed === 'function') {
      onClosed({ modal, form });
    }
    return { modal, modalContent, form };
  }

  modal.dataset.state = 'closing';
  
  // Remove any conflicting inline styles to let CSS handle transitions
  modal.style.removeProperty('opacity');
  modal.style.removeProperty('visibility');
  modal.style.removeProperty('pointer-events');
  if (modalContent) {
    modalContent.style.removeProperty('transform');
  }
  
  // Keep display: flex during transition (since CSS can't transition display property)
  // This ensures opacity/visibility transitions are visible
  modal.style.display = 'flex';
  
  // Remove the is-visible class to trigger CSS transition for opacity/visibility
  modal.classList.remove('is-visible');

  const cleanup = () => {
    modal.style.display = 'none';
    modal.dataset.state = 'closed';
    detachModalA11y(modal, { restoreFocus: true });
    resetAddItemModalForm(form);
    if (modal._addItemModalCloseTimeout) {
      clearTimeout(modal._addItemModalCloseTimeout);
      modal._addItemModalCloseTimeout = null;
    }
    modal._addItemModalTransitionHandler = null;
    if (typeof onClosed === 'function') {
      onClosed({ modal, form });
    }
  };

  if (modal._addItemModalTransitionHandler) {
    modal.removeEventListener('transitionend', modal._addItemModalTransitionHandler);
    modal._addItemModalTransitionHandler = null;
  }

  const handleTransitionEnd = (event) => {
    if (event.target === modal && (event.propertyName === 'opacity' || event.propertyName === 'visibility')) {
      modal.removeEventListener('transitionend', handleTransitionEnd);
      modal._addItemModalTransitionHandler = null;
      cleanup();
    }
  };

  modal.addEventListener('transitionend', handleTransitionEnd);
  modal._addItemModalTransitionHandler = handleTransitionEnd;

  // Fallback timeout in case transitionend doesn't fire
  modal._addItemModalCloseTimeout = window.setTimeout(() => {
    if (modal.dataset.state !== 'closed') {
      if (modal._addItemModalTransitionHandler) {
        modal.removeEventListener('transitionend', handleTransitionEnd);
        modal._addItemModalTransitionHandler = null;
      }
      cleanup();
    }
    modal._addItemModalCloseTimeout = null;
  }, ADD_ITEM_MODAL_TRANSITION_DURATION + 50); // Add small buffer for safety

  return { modal, modalContent, form };
}

function attachGlobalAddItemTriggers() {
  // Only attach for non-inline triggers to avoid double-binding
  const triggers = document.querySelectorAll('[data-open-add-item], .js-open-add-item');
  triggers.forEach((trigger) => {
    if (trigger._addItemModalAttached) {
      return;
    }

    trigger._addItemModalAttached = true;
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      AccountManager.openAddItemModalGlobal();
    });
  });
}

window.AccountManager = AccountManager;
window.openAddItemModalGlobal = function() {
  AccountManager.openAddItemModalGlobal();
};
window.closeAddItemModalGlobal = function() {
  AccountManager.closeAddItemModalGlobal();
};
window.initializeAddItemModalHandlers = ensureAddItemModalInitialized;
window.attachGlobalAddItemTriggers = attachGlobalAddItemTriggers;
// Cookie Consent Management (UI Controller)
// Delegates to ConsentManager for all consent logic
export class CookieConsent {
  constructor() {
    // Wait for ConsentManager to be available
    if (!window.consentManager) {
      logger.error('ConsentManager not available - cookie consent UI will not function');
      return;
    }
    
    this.consentManager = window.consentManager;
    this.consentBanner = document.getElementById('cookieConsent');
    this.acceptBtn = document.getElementById('cookieAccept');
    this.declineBtn = document.getElementById('cookieDecline');
    
    this.init();
    this.initCookiePreferences();
  }
  
  init() {
    // Check if user has already made a choice
    const consentState = this.consentManager.getConsentState();
    
    if (consentState === null) {
      // Show banner if no choice has been made
      this.showBanner();
    }
    
    // Bind event listeners
    if (this.acceptBtn) {
      this.acceptBtn.addEventListener('click', () => this.acceptAll());
    }
    
    if (this.declineBtn) {
      this.declineBtn.addEventListener('click', () => this.declineAll());
    }
  }
  
  showBanner() {
    if (this.consentBanner) {
      this.consentBanner.style.display = 'block';
      this.consentBanner.setAttribute('role', 'dialog');
      this.consentBanner.setAttribute('aria-modal', 'false');
      this.consentBanner.setAttribute('aria-labelledby', 'cookieConsentTitle');
      document.body?.classList.add('cookie-banner-visible');
      const accept = this.acceptBtn;
      queueMicrotask(() => accept?.focus?.());
      logger.debug('Cookie consent banner shown');
    }
  }
  
  hideBanner() {
    if (this.consentBanner) {
      this.consentBanner.style.display = 'none';
      this.consentBanner.removeAttribute('role');
      this.consentBanner.removeAttribute('aria-modal');
      document.body?.classList.remove('cookie-banner-visible');
      logger.debug('Cookie consent banner hidden');
    }
  }
  
  acceptAll() {
    this.consentManager.acceptAll();
    this.hideBanner();
    logger.info('User accepted all cookies via banner');
  }
  
  declineAll() {
    this.consentManager.declineAll();
    this.hideBanner();
    logger.info('User declined all cookies via banner');
  }
  
  // Initialize cookie preferences page
  initCookiePreferences() {
    const form = document.getElementById('cookiePreferencesForm');
    const acceptAllBtn = document.getElementById('acceptAll');
    const declineAllBtn = document.getElementById('declineAll');
    
    if (form) {
      // Load current preferences
      this.loadCurrentPreferences();
      
      // Handle form submission
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.savePreferencesFromForm();
      });
      
      // Handle accept all
      if (acceptAllBtn) {
        acceptAllBtn.addEventListener('click', () => {
          this.setAllPreferences(true);
        });
      }
      
      // Handle decline all
      if (declineAllBtn) {
        declineAllBtn.addEventListener('click', () => {
          this.setAllPreferences(false);
        });
      }
    }
  }
  
  loadCurrentPreferences() {
    const consentState = this.consentManager.getConsentState();
    if (consentState) {
      const analyticsCheckbox = document.getElementById('analytics');
      const marketingCheckbox = document.getElementById('marketing');
      const preferencesCheckbox = document.getElementById('preferences');
      
      if (analyticsCheckbox) analyticsCheckbox.checked = consentState.analytics || false;
      if (marketingCheckbox) marketingCheckbox.checked = consentState.marketing || false;
      if (preferencesCheckbox) preferencesCheckbox.checked = consentState.preferences || false;
    }
  }
  
  savePreferencesFromForm() {
    const analyticsCheckbox = document.getElementById('analytics');
    const marketingCheckbox = document.getElementById('marketing');
    const preferencesCheckbox = document.getElementById('preferences');
    
    const preferences = {
      analytics: analyticsCheckbox ? analyticsCheckbox.checked : false,
      marketing: marketingCheckbox ? marketingCheckbox.checked : false,
      preferences: preferencesCheckbox ? preferencesCheckbox.checked : false
    };
    
    this.consentManager.updatePreferences(preferences);
    this.showNotification('Cookie preferences saved successfully!');
    logger.info('User updated cookie preferences:', preferences);
  }
  
  setAllPreferences(value) {
    const analyticsCheckbox = document.getElementById('analytics');
    const marketingCheckbox = document.getElementById('marketing');
    const preferencesCheckbox = document.getElementById('preferences');
    
    if (analyticsCheckbox) analyticsCheckbox.checked = value;
    if (marketingCheckbox) marketingCheckbox.checked = value;
    if (preferencesCheckbox) preferencesCheckbox.checked = value;
    
    this.savePreferencesFromForm();
  }
}

// Report Management System
export class ReportManager {
  constructor() {
    this.currentProductId = null;
    this.init();
  }
  
  init() {
    const reportForm = document.getElementById('reportForm');
    if (reportForm) {
      reportForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleReportSubmission(e);
      });
    }
    
    // Add backdrop click handler to close modal when clicking outside
    const modal = document.getElementById('reportModal');
    if (modal) {
      // Track mousedown location to prevent closing when dragging from input to backdrop
      let mouseDownOnBackdrop = false;
      modal.addEventListener('mousedown', (e) => {
        mouseDownOnBackdrop = (e.target === modal);
      });
      modal.addEventListener('click', (e) => {
        // Only close if both mousedown and click (mouseup) happened on the backdrop
        if (e.target === modal && mouseDownOnBackdrop) {
          this.closeReportModal();
        }
        mouseDownOnBackdrop = false; // Reset
      });
    }
  }
  
  openReportModal(productId) {
    logger.info('openReportModal called with productId:', productId);
    this.currentProductId = productId;
    const modal = document.getElementById('reportModal');
    if (modal) {
      // Use the is-visible class to show the modal (matches CSS styling)
      modal.classList.add('is-visible');
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
      logger.info('Report modal opened successfully');
    } else {
      logger.error('Report modal not found. Make sure reportModal element exists in the HTML.');
    }
  }
  
  closeReportModal() {
    const modal = document.getElementById('reportModal');
    if (modal) {
      // Remove the is-visible class to hide the modal (matches CSS styling)
      modal.classList.remove('is-visible');
      document.body.style.overflow = 'auto'; // Restore scrolling
      this.resetForm();
    }
  }
  
  resetForm() {
    const form = document.getElementById('reportForm');
    if (form) {
      form.reset();
    }
    this.currentProductId = null;
  }
  
  handleReportSubmission(e) {
    e.preventDefault(); // Prevent default form submission
    
    const formData = new FormData(e.target);
    const reportData = {
      id: `report_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // More unique ID
      productId: this.currentProductId,
      reason: formData.get('reportReason'),
      reportedAt: new Date().toISOString(),
      status: 'pending',
      reviewedAt: null,
      reviewedBy: null,
      reviewNotes: null
    };
    
    // Save report
    this.saveReport(reportData);
    
    // Debug: Log the report data
    logger.info('Report submitted:', reportData);
    // Note: getReports() is async, so we can't log it directly here
    // The reports will be logged when they're actually retrieved
    
    // Close modal and show success message
    this.closeReportModal();
    this.showNotification('Thank you for your report! We\'ll review it and take appropriate action.');
  }
  
  async saveReport(reportData) {
    // Try to save via API first
    if (window.apiService) {
      try {
        await window.apiService.createReport(reportData);
        logger.info('Report saved successfully via API:', reportData);
        // Also save to localStorage as backup
        const reports = await this.getReports();
        // Check if report with same ID already exists (prevent duplicates)
        const existingReport = reports.find(report => report.id === reportData.id);
        if (!existingReport) {
          reports.push(reportData);
          localStorage.setItem('usedfindr_reports', JSON.stringify(reports));
        }
        return;
      } catch (apiError) {
        logger.warn('Failed to save report via API, falling back to localStorage:', apiError);
        // Queue for sync
        if (window.adminManager) {
          window.adminManager.addToSyncQueue('reports', 'create', reportData);
        }
        // Fall through to localStorage
      }
    }
    
    // Fallback to localStorage
    const reports = await this.getReports();
    
    // Check if report with same ID already exists (prevent duplicates)
    const existingReport = reports.find(report => report.id === reportData.id);
    if (existingReport) {
      logger.info('Duplicate report detected, skipping save:', reportData.id);
      return;
    }
    
    reports.push(reportData);
    localStorage.setItem('usedfindr_reports', JSON.stringify(reports));
    logger.info('Report saved successfully:', reportData);
  }
  
  async getReports() {
    // Try to get from API first
    if (window.apiService) {
      try {
        const reports = await window.apiService.getReports();
        return reports || [];
      } catch (error) {
        logger.error('Failed to get reports from API:', error);
        if (error.isAuthError) {
          // Attempt refresh if it's an auth error
          try {
            await window.apiService.refreshToken();
            // Retry the reports fetch after refresh
            await this.getReports();
            return;
          } catch (refreshError) {
            // Refresh failed, proceed with error handling
            this.updateConnectionBanner();
          }
        }
        this.updateConnectionBanner();
      }
    }
    
    // Fallback to localStorage
    const reports = localStorage.getItem('usedfindr_reports');
    return reports ? JSON.parse(reports) : [];
  }
  
  showNotification(message) {
    const notification = document.createElement('div');
    notification.className = 'notification';
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: var(--accent);
      color: #111827;
      padding: 12px 20px;
      border-radius: var(--radius-sm);
      box-shadow: var(--shadow-hover);
      z-index: 1000;
      font-weight: 600;
    `;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.remove();
    }, 3000);
  }
}

/**
 * Read the ?returnTo= login redirect target, rejecting anything that could send the
 * user off-site. Only same-origin relative paths are honoured: a leading single "/"
 * and no scheme, so "//evil.com" and "https://evil.com" are both refused.
 * Returns null when there is nothing safe to return to.
 */
function getSafeReturnTo() {
  try {
    const raw = new URLSearchParams(window.location.search).get('returnTo');
    if (!raw) return null;
    const decoded = decodeURIComponent(raw);
    if (!decoded.startsWith('/') || decoded.startsWith('//')) return null;
    if (/^[a-z][a-z0-9+.-]*:/i.test(decoded)) return null;
    // Browsers treat "\" as "/" and drop tab/newline, so "/\evil.com" and
    // "/<TAB>/evil.com" resolve off-site. Let the URL parser have the last word.
    const parsed = new URL(decoded, window.location.origin);
    if (parsed.origin !== window.location.origin) return null;
    return parsed.pathname + parsed.search + parsed.hash;
  } catch (e) {
    return null;
  }
}

// Helper function to escape HTML
function escapeHtml(text) {
  if (text === null || text === undefined) return '';
  // Escapes quotes as well as angle brackets -- see AdminManager.escapeHtml.
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Helper function to encode JSON for HTML data attributes
// Escapes quotes for use in HTML attributes
function encodeJsonForAttribute(jsonObj) {
  const jsonString = JSON.stringify(jsonObj);
  // Escape double quotes for use in double-quoted HTML attribute
  return jsonString.replace(/"/g, '&quot;');
}

// Helper function to parse JSON from data attribute
// The browser automatically decodes HTML entities when reading from dataset
function decodeJsonFromAttribute(encodedJson) {
  if (!encodedJson) return null;
  try {
    // Browser already decodes &quot; to " when reading from dataset
    // So we can parse directly
    return JSON.parse(encodedJson);
  } catch (e) {
    logger.error('Error parsing JSON:', e, 'Raw value:', encodedJson);
    return null;
  }
}
// Admin Management System
export class ContestManager {
  constructor() {
    // Initialize contest settings if they don't exist
    this.initContestSettings();
  }
  
  initContestSettings() {
    const settings = localStorage.getItem('usedfindr_contest_settings');
    if (!settings) {
      const defaultSettings = {
        prizeAmount: 50,
        prizeCurrency: 'USD',
        prizeType: 'Amazon Gift Card',
        drawingDay: 1, // 1st of each month
        createdAt: new Date().toISOString()
      };
      localStorage.setItem('usedfindr_contest_settings', JSON.stringify(defaultSettings));
    }
  }
  
  getContestSettings() {
    const settings = localStorage.getItem('usedfindr_contest_settings');
    return settings ? JSON.parse(settings) : null;
  }
  
  // Get all eligible entries for a specific month/year
  async getEligibleEntries(year, month) {
    const allUsers = this.getAllUsers();
    const entries = [];
    const userEntryMap = new Map(); // Track entries per user
    
    // Get all user finds from localStorage
    const allFinds = await this.getAllUserFinds();
    
    // Process regular entries (from user finds)
    allFinds.forEach(find => {
      if (!find.addedAt) return;
      
      const findDate = new Date(find.addedAt);
      const findYear = findDate.getFullYear();
      const findMonth = findDate.getMonth();
      
      // Only count entries from the target month/year
      if (findYear === year && findMonth === month) {
        const userId = find.userId;
        
        // Find the user to check if account is active
        const user = allUsers.find(u => u.id === userId);
        if (!user || user.locked || user.deleted) return; // Skip locked/deleted users
        
        // Increment entry count for this user
        if (!userEntryMap.has(userId)) {
          userEntryMap.set(userId, {
            userId: userId,
            email: user.email,
            name: user.firstName + ' ' + user.lastName,
            regularEntries: 0,
            bonusEntries: 0
          });
        }
        userEntryMap.get(userId).regularEntries++;
      }
    });
    
    // Process bonus entries (from user bonusEntries field)
    // Note: We need to track when bonus entries were used
    // For now, we'll count all bonus entries as being used in the current month
    // In a more sophisticated system, we'd track redeemedAt dates
    allUsers.forEach(user => {
      if (user.locked || user.deleted) return;
      
      const userId = user.id;
      const bonusEntries = user.bonusEntries || 0;
      
      if (bonusEntries > 0) {
        // For simplicity, we'll count bonus entries for the month they were redeemed
        // Since we don't track redemption dates yet, we'll use a heuristic:
        // If user has bonus entries, they count for the most recent month that has entries
        const userFinds = allFinds.filter(f => f.userId === userId);
        if (userFinds.length > 0) {
          // Check if user has any finds in the target month
          const hasFindsInMonth = userFinds.some(find => {
            if (!find.addedAt) return false;
            const findDate = new Date(find.addedAt);
            return findDate.getFullYear() === year && findDate.getMonth() === month;
          });
          
          if (hasFindsInMonth) {
            if (!userEntryMap.has(userId)) {
              userEntryMap.set(userId, {
                userId: userId,
                email: user.email,
                name: user.firstName + ' ' + user.lastName,
                regularEntries: 0,
                bonusEntries: 0
              });
            }
            // Add bonus entries (for simplicity, add all bonus entries)
            // In production, you'd track which month each bonus entry was used for
            userEntryMap.get(userId).bonusEntries = bonusEntries;
          }
        }
      }
    });
    
    // Convert map to array
    return Array.from(userEntryMap.values());
  }
  
  // Get all users (shared with AccountManager logic)
  getAllUsers() {
    const usersData = localStorage.getItem('usedfindr_users');
    if (usersData) {
      try {
        return JSON.parse(usersData);
      } catch (e) {
        logger.error('Error parsing users data:', e);
        return [];
      }
    }
    return [];
  }
  
  // Get all user finds from all users
  async getAllUserFinds() {
    // User finds are stored per user in usedfindr_finds
    // But we need to aggregate them. Let's check the structure.
    // Looking at AccountManager, finds are stored per user in localStorage
    // For drawing, we need to get finds from all users
    
    // Since finds are stored per user session, we need to check all users
    // and reconstruct their finds. However, finds are also stored in submissions.
    // Let's get them from submissions which have userId and addedAt
    
    const submissionResult = await this.getSubmissions();
    const submissions = submissionResult.data || [];
    return submissions.filter(sub => sub.addedAt && sub.userId);
  }
  
  getSubmissions() {
    const submissionsData = localStorage.getItem('usedfindr_submissions');
    if (submissionsData) {
      try {
        return JSON.parse(submissionsData);
      } catch (e) {
        logger.error('Error parsing submissions data:', e);
        return [];
      }
    }
    return [];
  }
  
  // Select random winner from entries using weighted random selection
  selectRandomWinner(entries) {
    if (!entries || entries.length === 0) {
      return null;
    }
    
    // Flatten entries array (user with 5 entries = 5 entries in pool)
    const entryPool = [];
    entries.forEach(entry => {
      const totalEntries = entry.regularEntries + entry.bonusEntries;
      for (let i = 0; i < totalEntries; i++) {
        entryPool.push({
          userId: entry.userId,
          email: entry.email,
          name: entry.name,
          entryType: i < entry.regularEntries ? 'regular' : 'bonus',
          entryCount: totalEntries
        });
      }
    });
    
    if (entryPool.length === 0) {
      return null;
    }
    
    // Select random index
    const randomIndex = Math.floor(Math.random() * entryPool.length);
    const selectedEntry = entryPool[randomIndex];
    
    // Find the full entry data
    const fullEntry = entries.find(e => e.userId === selectedEntry.userId);
    
    return {
      userId: selectedEntry.userId,
      email: selectedEntry.email,
      name: selectedEntry.name,
      entryCount: fullEntry.regularEntries + fullEntry.bonusEntries,
      entryType: selectedEntry.entryType,
      regularEntries: fullEntry.regularEntries,
      bonusEntries: fullEntry.bonusEntries
    };
  }
  
  // Run monthly drawing for a specific month/year
  async runMonthlyDrawing(year, month, drawnBy = 'admin@usedfindr.com') {
    // Check if drawing already exists for this month
    const existingDrawing = this.getDrawingForMonth(year, month);
    if (existingDrawing) {
      throw new Error(`Drawing for ${this.getMonthName(month)} ${year} has already been completed.`);
    }
    
    // Get eligible entries
    const entries = await this.getEligibleEntries(year, month);
    
    if (entries.length === 0) {
      throw new Error(`No eligible entries found for ${this.getMonthName(month)} ${year}.`);
    }
    
    // Calculate total entries
    const totalEntries = entries.reduce((sum, entry) => {
      return sum + entry.regularEntries + entry.bonusEntries;
    }, 0);
    
    // Select winner
    const winner = this.selectRandomWinner(entries);
    
    if (!winner) {
      throw new Error('Failed to select a winner. No valid entries found.');
    }
    
    // Create drawing result
    const drawingResult = {
      id: Date.now().toString(),
      year: year,
      month: month,
      monthName: this.getMonthName(month),
      drawingDate: new Date(year, month, 1).toISOString(),
      totalEntries: totalEntries,
      eligibleUsers: entries.length,
      winner: winner,
      runnerUps: this.getRunnerUps(entries, winner.userId, 5), // Top 5 excluding winner
      drawnBy: drawnBy,
      drawnAt: new Date().toISOString(),
      announced: false
    };
    
    // Save drawing result
    this.saveDrawingResult(drawingResult);
    
    return drawingResult;
  }
  
  // Get runner-ups (top entries excluding winner)
  getRunnerUps(entries, winnerUserId, count = 5) {
    const sorted = entries
      .filter(e => e.userId !== winnerUserId)
      .sort((a, b) => {
        const totalA = a.regularEntries + a.bonusEntries;
        const totalB = b.regularEntries + b.bonusEntries;
        return totalB - totalA;
      })
      .slice(0, count)
      .map(e => ({
        userId: e.userId,
        name: e.name,
        entryCount: e.regularEntries + e.bonusEntries
      }));
    
    return sorted;
  }
  
  // Save drawing result to localStorage
  saveDrawingResult(result) {
    const drawings = this.getDrawingHistory();
    drawings.push(result);
    // Sort by date (newest first)
    drawings.sort((a, b) => {
      const dateA = new Date(a.drawnAt);
      const dateB = new Date(b.drawnAt);
      return dateB - dateA;
    });
    localStorage.setItem('usedfindr_contest_drawings', JSON.stringify(drawings));
  }
  
  // Get all drawing history
  getDrawingHistory() {
    const drawingsData = localStorage.getItem('usedfindr_contest_drawings');
    if (drawingsData) {
      try {
        return JSON.parse(drawingsData);
      } catch (e) {
        logger.error('Error parsing drawings data:', e);
        return [];
      }
    }
    return [];
  }
  
  // Get drawing for specific month/year
  getDrawingForMonth(year, month) {
    const drawings = this.getDrawingHistory();
    return drawings.find(d => d.year === year && d.month === month);
  }
  
  // Get latest drawing
  getLatestDrawing() {
    const drawings = this.getDrawingHistory();
    return drawings.length > 0 ? drawings[0] : null;
  }
  
  // Get pending drawings (months that haven't been drawn yet)
  async getPendingDrawings() {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    // Get all completed drawings
    const completedDrawings = this.getDrawingHistory();
    const pending = [];
    
    // Check last 12 months for pending drawings
    for (let i = 1; i <= 12; i++) {
      const checkDate = new Date(currentYear, currentMonth - i, 1);
      const checkYear = checkDate.getFullYear();
      const checkMonth = checkDate.getMonth();
      
      const hasDrawing = completedDrawings.some(d => 
        d.year === checkYear && d.month === checkMonth
      );
      
      if (!hasDrawing) {
        // Check if there are any entries for this month
        const entries = await this.getEligibleEntries(checkYear, checkMonth);
        if (entries.length > 0) {
          pending.push({
            year: checkYear,
            month: checkMonth,
            monthName: this.getMonthName(checkMonth),
            eligibleEntries: entries.reduce((sum, e) => sum + e.regularEntries + e.bonusEntries, 0),
            eligibleUsers: entries.length
          });
        }
      }
    }
    
    return pending;
  }
  
  // Mark drawing as announced
  markDrawingAsAnnounced(drawingId) {
    const drawings = this.getDrawingHistory();
    const drawing = drawings.find(d => d.id === drawingId);
    if (drawing) {
      drawing.announced = true;
      drawing.announcedAt = new Date().toISOString();
      localStorage.setItem('usedfindr_contest_drawings', JSON.stringify(drawings));
    }
  }
  
  // Helper: Get month name
  getMonthName(month) {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[month];
  }
  
  // Get previous month for drawing (drawing on 1st uses previous month's entries)
  getPreviousMonth() {
    const now = new Date();
    const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return {
      year: prevMonth.getFullYear(),
      month: prevMonth.getMonth(),
      monthName: this.getMonthName(prevMonth.getMonth())
    };
  }
}

// Display winner announcement on homepage
function displayHomepageWinner() {
  if (!window.contestManager) return;
  
  const latestDrawing = window.contestManager.getLatestDrawing();
  if (!latestDrawing || !latestDrawing.winner) return;
  
  const announcementSection = document.getElementById('homepageWinnerAnnouncement');
  const winnerDetails = document.getElementById('homepageWinnerDetails');
  
  if (!announcementSection || !winnerDetails) return;
  
  // Format winner name (first name + last initial)
  const winnerNameParts = latestDrawing.winner.name.split(' ');
  const winnerDisplayName = winnerNameParts.length > 1 
    ? `${winnerNameParts[0]} ${winnerNameParts[winnerNameParts.length - 1][0]}.`
    : latestDrawing.winner.name;
  
  winnerDetails.innerHTML = `
    <p class="winner-announcement-text">
      Congratulations to <strong>${escapeHtml(winnerDisplayName)}</strong> for winning the 
      <strong>${escapeHtml(latestDrawing.monthName)} ${escapeHtml(latestDrawing.year)}</strong> contest!
    </p>
    <p class="winner-announcement-subtext">
      ${escapeHtml(latestDrawing.totalEntries)} total entries from ${escapeHtml(latestDrawing.eligibleUsers)} participants
    </p>
  `;
  
  announcementSection.style.display = 'block';
}
// Marketplace Detection Helpers (matching product-detail.js logic)
/**
 * Classify marketplace from an outbound affiliate / product URL host.
 * @param {string} url
 * @returns {'amazon'|'ebay'|'other'|''}
 */
function classifyMarketplaceFromUrl(url) {
  if (!url || typeof url !== 'string') return '';
  try {
    const host = new URL(url, window.location.origin).hostname.toLowerCase();
    if (host.includes('amazon.') || host === 'amzn.to' || host.endsWith('.amzn.to')) return 'amazon';
    if (host.includes('ebay.')) return 'ebay';
    if (host && host !== 'localhost') return 'other';
  } catch (_e) {
    const link = url.toLowerCase();
    if (link.includes('amazon.') || link.includes('amzn.to')) return 'amazon';
    if (link.includes('ebay.')) return 'ebay';
  }
  return '';
}

if (typeof window !== 'undefined') {
  window.classifyMarketplaceFromUrl = classifyMarketplaceFromUrl;
}

/**
 * Helper: Extract marketplace from product object (handles both camelCase and snake_case)
 * Defaults to 'ebay' if no marketplace detected
 */
function getProductMarketplace(product) {
  if (!product) return 'ebay';
  
  // PRIORITY 1: Check affiliate link first (most reliable; support both camelCase and snake_case from API)
  const affiliateLink = product.affiliateLink || product.affiliate_link;
  if (affiliateLink) {
    const link = String(affiliateLink).toLowerCase();
    if (link.includes('amazon.com') || link.includes('amzn.to') || link.includes('amazon.')) {
      return 'amazon';
    } else if (link.includes('ebay.com') || link.includes('ebay.us') || link.includes('ebay.')) {
      return 'ebay';
    }
  }
  
  // PRIORITY 2: Try camelCase (from toCamelCase conversion)
  let marketplace = product.sourceMarketplace;
  
  // PRIORITY 3: Fallback to snake_case (direct from API)
  if (!marketplace) {
    marketplace = product.source_marketplace;
  }
  
  // Normalize marketplace value
  if (marketplace) {
    marketplace = String(marketplace).toLowerCase().trim();
    // Never use 'other' - default to eBay
    if (marketplace === 'other') {
      return 'ebay';
    }
  }
  
  // Default to 'ebay' for all cases
  return marketplace || 'ebay';
}

/**
 * Helper: Get marketplace display name
 */
function getMarketplaceName(marketplace) {
  if (!marketplace) return 'eBay'; // Default to eBay
  
  // Normalize to lowercase for comparison
  const normalized = String(marketplace).toLowerCase().trim();
  
  const names = {
    'amazon': 'Amazon',
    'ebay': 'eBay',
    'best buy': 'Best Buy',
    'bestbuy': 'Best Buy',
    'walmart': 'Walmart',
    'target': 'Target',
    'newegg': 'Newegg',
    'etsy': 'Etsy',
    'mercari': 'Mercari',
    'facebook marketplace': 'Facebook Marketplace',
    'facebook': 'Facebook Marketplace',
    'offerup': 'OfferUp',
    'craigslist': 'Craigslist',
    'other': 'eBay' // Never show "Other" - default to eBay
  };
  
  // Never show 'Other' - default to eBay if unknown
  return names[normalized] || 'eBay';
}

/**
 * If productId is ebay_<id>, return https://www.ebay.com/itm/<id>. Otherwise return null.
 * Used so Buy on eBay always has a link when backend sends no affiliate_link yet.
 */
export function getEbayItemUrlFromProductId(productId) {
  if (!productId || typeof productId !== 'string') return null;
  const match = String(productId).match(/^ebay_(.+)$/);
  if (!match) return null;
  const parts = match[1].split('|').map(p => p.trim());
  // ebay_123: id=123; ebay_v1|123|0 or ebay_v1|123: take numeric part (middle or last)
  const id = parts.length >= 2 && /^\d+$/.test(parts[1])
    ? parts[1]
    : (parts[0] && /^\d+$/.test(parts[0]) ? parts[0] : parts[0] || null);
  return id ? `https://www.ebay.com/itm/${id}` : null;
}

/**
 * Get affiliate/eBay URL for an item. Uses affiliateLink/affiliate_link, then getEbayItemUrlFromProductId(productId), else '#'.
 */
function getAffiliateUrl(item) {
  if (!item) return '#';
  const raw = item.affiliateLink || item.affiliate_link || '';
  if (raw && raw !== '#') return raw;
  const productId = item.productId || item.product_id || (item.name ? item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-') : null);
  return getEbayItemUrlFromProductId(productId) || '#';
}

/**
 * Get internal product detail page URL for an item. Prefers master_parent_id for canonical URLs.
 */
// Single source of truth for internal product-page URLs on the frontend.
// Keep the URL shape in sync with usedfindr_django/products/product_urls.py:
// catalog items live at /p/<slug>-<id>/ (slug decorative, id is the key; the
// server 301s to the canonical slug on mismatch).
function getProductDetailUrl(item) {
  if (!item) return '#';
  const productId = item.productId || item.product_id;
  const masterParentId = item.master_parent_id ?? item.masterParentId;
  if (masterParentId != null) {
    const slug = generateProductSlug(item.name) || 'product';
    return `/p/${slug}-${masterParentId}/`;
  }
  if (productId) {
    return generateProductDetailURL(productId, item.name);
  }
  return '#';
}

function parseNumericPrice(value) {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number.parseFloat(String(value).replace(/[^0-9.]/g, ''));
  return Number.isFinite(numeric) ? numeric : null;
}

function normalizeCurrencyCode(value) {
  if (!value || typeof value !== 'string') return 'USD';
  const normalized = value.trim().toUpperCase();
  return normalized || 'USD';
}

function formatCurrencyValue(value, currencyCode = 'USD') {
  if (!Number.isFinite(value) || value <= 0) return 'N/A';
  const normalized = normalizeCurrencyCode(currencyCode);
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: normalized,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  } catch (_) {
    return `$${value.toFixed(2)}`;
  }
}

function isSafeAbsoluteHttpUrl(value) {
  if (!value || typeof value !== 'string') return false;
  const candidate = value.trim();
  if (!candidate) return false;
  try {
    const parsed = new URL(candidate);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch (_) {
    return false;
  }
}

function getSafeExternalHttpUrl(value) {
  if (!value || typeof value !== 'string') return null;
  const candidate = value.trim();
  if (!candidate) return null;
  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    return parsed.toString();
  } catch (_) {
    return null;
  }
}

// Public "% off" / strikethrough-retail claims. Keep this false until the
// comparison price is a verified current-new baseline, not eBay seller MSRP.
// Must stay in sync with Django PUBLIC_SAVINGS_CLAIMS_ENABLED (default off).
const UF_PUBLIC_SAVINGS_CLAIMS_ENABLED = false;
if (typeof window !== 'undefined') {
  window.UF_PUBLIC_SAVINGS_CLAIMS_ENABLED = UF_PUBLIC_SAVINGS_CLAIMS_ENABLED;
}

// Asking-price percentile context (MarketPriceStat). Separate from MSRP claims.
// Mirror Django MARKET_PRICE_INSIGHTS_ENABLED (default off until coverage is ready).
const UF_MARKET_PRICE_INSIGHTS_ENABLED = true;
if (typeof window !== 'undefined') {
  window.UF_MARKET_PRICE_INSIGHTS_ENABLED = UF_MARKET_PRICE_INSIGHTS_ENABLED;
}

function buildPriceContextViewModel(item) {
  if (!UF_MARKET_PRICE_INSIGHTS_ENABLED) return null;
  const ctx = item?.price_context || item?.priceContext;
  if (!ctx || typeof ctx !== 'object') return null;
  const label = (ctx.label || '').trim();
  if (!label) return null;
  return {
    label,
    receipt: (ctx.receipt || '').trim(),
    percentile: ctx.percentile,
    distinctListings: ctx.distinct_listings ?? ctx.distinctListings,
    soldCompsUrl: ctx.sold_comps_url || ctx.soldCompsUrl || '',
  };
}

function buildPricingViewModel(item) {
  const listingPrice = parseNumericPrice(item?.price ?? item?.listing_price ?? item?.listingPrice);
  if (!Number.isFinite(listingPrice) || listingPrice <= 0) {
    return null;
  }

  const currencyCode = normalizeCurrencyCode(item?.currency || item?.currency_code);

  // Only ever compare against a retail price we actually hold. The old code fell back to
  // `listingPrice * 1.2` when retail_price was null, which rendered a fabricated
  // "~17% off!" badge and a fake "est. retail" strikethrough on EVERY product (all 62 live
  // rows had retail_price = null on 2026-07-30). That is a deceptive pricing claim, not a
  // cosmetic default: a Bosch combo kit sat at its own 30-day HIGH price while the card
  // advertised 17% off. No retail price means no discount claim. Do not reintroduce a
  // multiplier fallback here.
  //
  // Even with a server-supplied retail_price, public claims stay off: those values
  // are seller-declared eBay originalPrice / launch MSRP (often 2–4x the used
  // listing), which produced wild "~43% off!" / "~74% off!" badges on used phones.
  if (!UF_PUBLIC_SAVINGS_CLAIMS_ENABLED) {
    return {
      listingPrice,
      listingLabel: formatCurrencyValue(listingPrice, currencyCode),
      retailBaseline: null,
      retailLabel: '',
      retailIsEstimate: false,
      discountPercentage: null,
      savingsLabel: '',
      currencyCode
    };
  }

  const retailCandidate = parseNumericPrice(item?.retailPrice ?? item?.retail_price);
  const hasTrueRetail = Number.isFinite(retailCandidate) && retailCandidate > listingPrice;
  const safeRetailBaseline = hasTrueRetail ? retailCandidate : null;

  let discountPercentage = null;
  if (safeRetailBaseline && safeRetailBaseline > listingPrice) {
    const computed = Math.round(((safeRetailBaseline - listingPrice) / safeRetailBaseline) * 100);
    discountPercentage = Number.isFinite(computed) && computed > 0 ? computed : null;
  }

  return {
    listingPrice,
    listingLabel: formatCurrencyValue(listingPrice, currencyCode),
    retailBaseline: safeRetailBaseline,
    retailLabel: safeRetailBaseline
      ? `${formatCurrencyValue(safeRetailBaseline, currencyCode)} new`
      : '',
    retailIsEstimate: false,
    discountPercentage,
    savingsLabel: discountPercentage ? `~${discountPercentage}% off!` : '',
    currencyCode
  };
}

function isValidFeaturedCandidate(item) {
  if (!item || typeof item !== 'object') return false;
  const productId = item.productId || item.product_id;
  if (!productId || String(productId).trim() === '') return false;
  const pricing = buildPricingViewModel(item);
  if (!pricing) return false;
  const rawAffiliate = item.affiliateLink || item.affiliate_link || item.url;
  return isSafeAbsoluteHttpUrl(rawAffiliate);
}

/**
 * Filter out unavailable items client-side (defense in depth).
 * Excludes OUT_OF_STOCK, zero quantity, and items past ebay_listing_end_time.
 */
function filterAvailableItems(items) {
  if (!Array.isArray(items)) return items;
  return items.filter((item) => {
    if (item.availability_status === 'OUT_OF_STOCK' || item.availabilityStatus === 'OUT_OF_STOCK') return false;
    const qty = item.quantity_available != null ? item.quantity_available : item.quantityAvailable;
    if (qty !== undefined && qty !== null && qty <= 0) return false;
    const endTime = item.ebay_listing_end_time || item.ebayListingEndTime;
    if (endTime) {
      try {
        if (new Date(endTime) < new Date()) return false;
      } catch (e) { /* ignore */ }
    }
    return true;
  });
}

// ---------------------------------------------------------------------------
// Shared listing-freshness policy (single source of truth for the frontend).
// Mirrors usedfindr_django/products/freshness.py: prefer the server-computed
// `freshness` block when present, otherwise derive the same states locally.
// States: 'live' (verified recently), 'stale' (unverified > 24h), 'ended'
// (excluded / out of stock / past eBay end time).
// ---------------------------------------------------------------------------
const UF_FRESHNESS_STALE_AFTER_HOURS = 24;

function ufGetFreshness(item) {
  if (item && item.freshness && item.freshness.state) {
    // Both key shapes: apiService.toCamelCase rewrites the block's inner keys on
    // the product-detail path, while the card paths pass the raw API payload.
    const f = item.freshness;
    const hrs = f.hours_since_verified != null ? f.hours_since_verified : f.hoursSinceVerified;
    return {
      state: f.state,
      verifiedAt: f.verified_at || f.verifiedAt || null,
      hoursSinceVerified: hrs != null ? hrs : null,
    };
  }
  const verifiedAtRaw =
    (item && (item.availabilityUpdatedAt || item.availability_updated_at)) ||
    (item && (item.priceCheckedAt || item.price_checked_at)) ||
    (item && (item.fetchedAt || item.fetched_at)) ||
    null;
  let hours = null;
  if (verifiedAtRaw) {
    try {
      const t = new Date(verifiedAtRaw).getTime();
      if (!Number.isNaN(t)) hours = Math.max(0, (Date.now() - t) / (1000 * 60 * 60));
    } catch (e) { /* ignore */ }
  }
  const status = item && (item.availabilityStatus || item.availability_status);
  const qty = item && (item.quantityAvailable != null ? item.quantityAvailable : item.quantity_available);
  const endRaw = item && (item.ebayListingEndTime || item.ebay_listing_end_time);
  let pastEnd = false;
  if (endRaw) {
    try { pastEnd = new Date(endRaw) < new Date(); } catch (e) { /* ignore */ }
  }
  const ended =
    Boolean(item && (item.excludedFromBot || item.excluded_from_bot)) ||
    status === 'OUT_OF_STOCK' ||
    qty === 0 ||
    pastEnd;
  let state;
  if (ended) state = 'ended';
  else if (hours == null || hours > UF_FRESHNESS_STALE_AFTER_HOURS) state = 'stale';
  else state = 'live';
  return { state, verifiedAt: verifiedAtRaw, hoursSinceVerified: hours != null ? Math.round(hours * 10) / 10 : null };
}

function ufFormatAge(hours, prefix = 'Updated') {
  if (hours == null) return '';
  if (hours < 0.5) return `${prefix} just now`;
  if (hours < 24) return `${prefix} ~${Math.round(hours)}h ago`;
  return `${prefix} ~${Math.round(hours / 24)}d ago`;
}

// Expose for product-detail.js (separate non-module script on product pages).
window.ufGetFreshness = ufGetFreshness;
window.ufFormatAge = ufFormatAge;

// Item Rendering Functions
function renderProductCard(item, options = {}) {
  const imageLoading = options.imageLoading === 'eager' ? 'eager' : 'lazy';
  // Generate product ID for report modal (use productId or create one from name)
  const productId = item.productId || (item.name ? item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-') : 'product-' + Date.now());
  
  // Debug logging for productId generation
  if (window.DEBUG_VOTES) {
    logger.info('[renderProductCard] Rendering card with productId:', productId, 'from item:', {
      hasProductId: !!item.productId,
      productId: item.productId,
      name: item.name
    });
  }
  
  // Listing image only: skip cards without a real http(s) product image (no category SVG placeholder).
  const listingImageRaw = item.imageUrl || item.image_url;
  const listingResolved = listingImageRaw ? getSafeImageSrc(listingImageRaw) : '';
  if (
    !listingResolved ||
    !(String(listingResolved).startsWith('http://') || String(listingResolved).startsWith('https://'))
  ) {
    return '';
  }
  const imageSrc = listingResolved;
  const imageAlt = escapeHtml(item.name || 'Product Image');
  
  // Use affiliateLink or affiliate_link; if missing/empty, fall back to eBay item URL from productId (ebay_<id>)
  const rawAffiliate = item.affiliateLink || item.affiliate_link || '';
  const affiliateLink = (rawAffiliate && rawAffiliate !== '#') ? rawAffiliate : (getEbayItemUrlFromProductId(productId) || '#');
  const internalUrl = getProductDetailUrl(item);

  // Escape all user input
  const productName = escapeHtml(item.name || 'Untitled Product');
  const conditionLabelRaw = item.conditionProvenanceLabel || item.condition_provenance_label || item.condition || 'N/A';
  const condition = escapeHtml(conditionLabelRaw);
  
  // Format price with superscript cents
  const formatPriceWithSuperscript = (priceStr) => {
    if (!priceStr || priceStr === 'N/A') {
      return { html: escapeHtml(priceStr), isNA: true };
    }
    // Match price patterns like $79.99, 79.99, $79, etc.
    // The prefix capture is `\D*`, not `\$?`: formatCurrencyValue() runs the
    // price through Intl, which emits "£499.00" / "CA$499.00" / "CHF 499.00"
    // as readily as "$499.00". Capturing only a dollar sign dropped every
    // other symbol on the floor, so a GBP listing rendered a bare "499.00"
    // and a CAD one rendered "$499.00" -- claiming US dollars.
    const priceMatch = String(priceStr).match(/(\D*)(\d+)(\.(\d+))?/);
    if (!priceMatch) {
      return { html: escapeHtml(priceStr), isNA: false };
    }
    
    const currencyPrefix = priceMatch[1] || '';
    const dollars = priceMatch[2];
    const cents = priceMatch[4];
    
    if (cents && cents.length > 0) {
      // Escape individual parts but allow HTML tags
      return { 
        html: `${escapeHtml(currencyPrefix)}${escapeHtml(dollars)}.<span class="price-cents">${escapeHtml(cents)}</span>`, 
        isNA: false 
      };
    } else {
      return { html: escapeHtml(priceStr), isNA: false };
    }
  };
  
  const pricing = buildPricingViewModel(item);
  const effectiveListingLabel = pricing?.listingLabel || 'N/A';
  const priceData = formatPriceWithSuperscript(effectiveListingLabel || 'N/A');
  const price = priceData.html;
  const isNAPrice = priceData.isNA;
  
  // Prepare item data JSON for data attribute (properly encoded for HTML attributes)
  const itemDataJson = encodeJsonForAttribute({
    productId: productId,
    name: item.name,
    category: item.category,
    condition: conditionLabelRaw,
    price: item.price,
    affiliateLink: affiliateLink,
    imageUrl: imageSrc,
    url: affiliateLink
  });
  
  // Check if native sharing is available (mobile)
  const hasNativeShare = navigator.share ? true : false;
  
  // Get vote stats - vote counts come from backend API
  const voteScore = item.voteScore !== undefined ? item.voteScore : 0;
  // Hide the number entirely at 0 — a sea of "0" counts reads as an empty site.
  const displayScore = voteScore === 0 ? '' : voteScore > 0 ? `+${voteScore}` : voteScore.toString();
  
  // Get current user's vote from backend API (not localStorage)
  // Backend returns 'up', 'down', or null (for guests or users who haven't voted)
  const userVote = item.currentUserVote || item.current_user_vote || null;
  const isUpvoted = userVote === 'up';
  const isDownvoted = userVote === 'down';
  
  // Get submitter info
  const submitterUsername = item.submitterUsername || null;
  
  // Check if item is saved (we'll render with false initially, then update if needed)
  // Note: This is called synchronously during render, so we can't await
  // The button will be updated when clicked if the state is wrong
  const isSaved = false; // Will be checked asynchronously when button is clicked
  
  // Get current user ID for vote button enable/disable
  const currentUser = window.accountManager ? window.accountManager.getCurrentUser() : null;
  const currentUserId = currentUser ? currentUser.id : null;
  
  const discountPercentage = pricing?.discountPercentage || null;
  
  // Position badges - discount badge takes priority, condition badge goes below if both exist
  let conditionBadgeTop = '12px';
  if (discountPercentage) {
    conditionBadgeTop = '52px'; // Position condition badge below discount badge
  }
  
  const productDetailUrl = generateProductDetailURL(productId, item.name);
  
  // Determine if item is "hot" (high votes) or "new" (recently added)
  const isHot = voteScore >= 5; // Consider items with 5+ votes as "hot"
  const isNew = item.createdAt || item.created_at;
  const daysSinceCreation = isNew ? (Date.now() - new Date(isNew).getTime()) / (1000 * 60 * 60 * 24) : Infinity;
  const isRecentlyNew = daysSinceCreation <= 7; // Items created within last 7 days are "new"
  
  // Determine if item should be featured (spans 2 rows)
  const isFeatured = isHot || (isRecentlyNew && voteScore >= 2);
  
  // Generate badge HTML
  let badgeHTML = '';
  if (isHot) {
    badgeHTML = '<span class="product-badge product-badge-hot">Hot Deal</span>';
  } else if (isRecentlyNew) {
    badgeHTML = '<span class="product-badge product-badge-new">New</span>';
  }
  // Stale data indicator: "May be sold" for items not verified recently
  const cardFreshness = ufGetFreshness(item);
  if (cardFreshness.state === 'stale' && cardFreshness.hoursSinceVerified != null) {
    badgeHTML += `<span class="stale-badge" title="Last verified ${Math.round(cardFreshness.hoursSinceVerified)} hours ago">May be sold — verify on eBay</span>`;
  }

  // Determine marketplace and button text dynamically
  const marketplace = getProductMarketplace(item);
  const marketplaceName = getMarketplaceName(marketplace);
  const buyButtonText = `Buy on ${marketplaceName}`;
  
  // Estimated availability: green (2+), yellow/orange (1), red (0/out)
  const status = item.availabilityStatus || item.availability_status;
  const qty = item.quantityAvailable != null ? item.quantityAvailable : item.quantity_available;
  let availabilityModifier = 'product-availability--in';
  let availabilityText = 'Estimated: In stock';
  if (status === 'OUT_OF_STOCK') {
    availabilityModifier = 'product-availability--out';
    availabilityText = 'Estimated: Out of stock';
  } else if (qty != null) {
    if (qty === 0) {
      availabilityModifier = 'product-availability--out';
      availabilityText = 'Estimated: Out of stock';
    } else if (qty === 1) {
      availabilityModifier = 'product-availability--low';
      availabilityText = 'Estimated: About 1 left';
    } else {
      availabilityModifier = 'product-availability--in';
      availabilityText = `Estimated: About ${qty} left`;
    }
  }
  const outOfStockClass = status === 'OUT_OF_STOCK' ? ' product-card--out-of-stock' : '';
  const cardImage = buildCardImageAttrs(imageSrc);
  const imgSrc = cardImage.src;
  const imgFallback = cardImage.src !== cardImage.fallbackSrc ? cardImage.fallbackSrc : '';
  // srcset outranks src, so it has to be dropped before the fallback can take effect.
  const imgOnError = imgFallback
    ? 'this.removeAttribute(\'srcset\');this.removeAttribute(\'sizes\');if(this.dataset.fallbackSrc){this.src=this.dataset.fallbackSrc;this.removeAttribute(\'data-fallback-src\');}else{handleProductImageError(this);}'
    : 'handleProductImageError(this)';
  const imgSrcsetAttr = cardImage.srcset
    ? ` srcset="${escapeHtml(cardImage.srcset)}" sizes="${escapeHtml(cardImage.sizes)}"`
    : '';
  const shippingDisplay = item.shippingDisplay || item.shipping_display;
  const isFreeShipping = shippingDisplay === 'Free';
  const freeShippingBadgeHtml = isFreeShipping
    ? '<span class="free-shipping-badge">Free shipping</span>'
    : '';

  // Build metadata: Row 1 (trust + shipping), Row 2 (pills: freshness, stock)
  const trustRowItems = [];
  const pillsRowItems = [];
  const sellerPct = item.sellerFeedbackPct != null ? item.sellerFeedbackPct : item.seller_feedback_pct;
  const sellerCount = item.sellerSalesCount != null ? item.sellerSalesCount : item.seller_sales_count;
  const pctNum = Number(sellerPct);
  const pctStr = (sellerPct != null && !Number.isNaN(pctNum)) ? (pctNum % 1 === 0 ? String(Math.round(pctNum)) : pctNum.toFixed(1)) : '';
  if (pctStr) {
    trustRowItems.push(`<span class="product-metadata-feedback">${pctStr}% positive${sellerCount != null ? ` (${sellerCount}+)` : ''}</span>`);
  }
  if (shippingDisplay && !isFreeShipping) {
    if (shippingDisplay === 'See listing') {
      trustRowItems.push('<span class="product-shipping product-shipping--muted">Shipping: See listing</span>');
    } else {
      trustRowItems.push(`<span class="product-shipping product-shipping--muted">+ ${escapeHtml(shippingDisplay)} shipping</span>`);
    }
  }
  const priceCheckedAt = item.priceCheckedAt || item.price_checked_at;
  if (cardFreshness.hoursSinceVerified != null) {
    const freshnessText = ufFormatAge(cardFreshness.hoursSinceVerified, priceCheckedAt ? 'Price checked' : 'Updated');
    if (freshnessText) {
      pillsRowItems.push(`<span class="metadata-pill">${escapeHtml(freshnessText)}</span>`);
    }
  }
  pillsRowItems.push(`<span class="metadata-pill metadata-pill--stock product-availability ${availabilityModifier}">${escapeHtml(availabilityText)}</span>`);
  const trustRowHtml = trustRowItems.length > 0 ? `<div class="product-metadata-row product-metadata-row--trust">${trustRowItems.join('')}</div>` : '';
  const pillsRowHtml = pillsRowItems.length > 0 ? `<div class="product-metadata-row product-metadata-row--pills">${pillsRowItems.join('')}</div>` : '';
  const metadataHtml = (trustRowHtml || pillsRowHtml) ? `<div class="product-metadata">${trustRowHtml}${pillsRowHtml}</div>` : '';

  const currencyCode = pricing?.currencyCode || normalizeCurrencyCode(item.currency || item.currency_code);
  const currencyHtml = currencyCode ? `<span class="product-currency"> ${escapeHtml(currencyCode)}</span>` : '<span class="product-currency"> USD</span>';
  const retailLineHtml = pricing?.retailLabel
    ? `<p class="product-retail-price${pricing.retailIsEstimate ? ' product-retail-price--estimated' : ''}" aria-label="${escapeHtml(pricing.retailIsEstimate ? `Estimated retail ${pricing.retailLabel}` : `Retail ${pricing.retailLabel}`)}">${escapeHtml(pricing.retailLabel)}</p>`
    : '';
  const savingsContextAttr = pricing?.retailIsEstimate ? ' data-savings-context="estimated-retail"' : '';
  const discountBadgeHtml = discountPercentage
    ? `<span class="discount-badge" data-position="top-right"${savingsContextAttr} aria-label="${escapeHtml(pricing?.retailIsEstimate ? `${discountPercentage}% off using estimated retail baseline` : `${discountPercentage}% off`)}">${escapeHtml(pricing?.savingsLabel || `${discountPercentage}% off!`)}</span>`
    : '';
  const priceContextVm = buildPriceContextViewModel(item);
  const priceContextHtml = priceContextVm
    ? `<p class="price-context-pill" title="${escapeHtml(priceContextVm.receipt || priceContextVm.label)}">${escapeHtml(priceContextVm.label)}</p>`
    : '';
  const watchBtnHtml = productId
    ? `<button type="button" class="card-watch-btn" data-action="card-watch" data-product-id="${escapeHtml(productId)}" title="Get price and stock alerts" aria-label="Watch for alerts">Watch</button>`
    : '';
  return `
    <div class="product-card product-card--clickable${outOfStockClass}" data-product-card-id="${escapeHtml(productId)}" data-featured="${isFeatured ? 'true' : 'false'}" data-action="navigate-product" data-product-url="${escapeHtml(internalUrl)}">
      <div class="card-content">
        <div class="product-image">
          <img src="${escapeHtml(imgSrc)}"${imgSrcsetAttr} ${imgFallback ? `data-fallback-src="${escapeHtml(imgFallback)}"` : ''} alt="${imageAlt}" loading="${imageLoading}" decoding="async" onerror="${imgOnError}">
          ${badgeHTML}
          ${freeShippingBadgeHtml}
          ${discountBadgeHtml}
          <div class="vote-container" data-action="stop-propagation">
            <button class="vote-btn upvote ${isUpvoted ? 'active' : ''}" data-action="vote" data-product-id="${escapeHtml(productId)}" data-vote-type="up" title="Upvote this deal" ${!currentUserId ? 'disabled' : ''}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 19V5M5 12l7-7 7 7"/>
              </svg>
            </button>
            <div class="vote-score ${voteScore > 0 ? 'positive' : voteScore < 0 ? 'negative' : ''}">${escapeHtml(displayScore)}</div>
            <button class="vote-btn downvote ${isDownvoted ? 'active' : ''}" data-action="vote" data-product-id="${escapeHtml(productId)}" data-vote-type="down" title="Downvote this deal" ${!currentUserId ? 'disabled' : ''}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 5v14M19 12l-7 7-7-7"/>
              </svg>
            </button>
          </div>
          <button class="floating-report-btn" data-action="report-product" data-product-id="${escapeHtml(productId)}" title="Report as unavailable">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </button>
        </div>
        <div class="product-info">
          <h3 class="product-title product-title--has-detail-link" title="${(item.name || 'Untitled Product').replace(/"/g, '&quot;')}"><a class="product-card-detail-link" href="${escapeHtml(internalUrl)}">${productName}</a></h3>
          <div class="product-price-container">
            <p class="product-price${isNAPrice ? ' na-price' : ''}">${price}${currencyHtml}</p>
            ${retailLineHtml}
            ${priceContextHtml}
          </div>
          ${metadataHtml}
        </div>
      </div>
      <div class="card-footer-actions">
        <div class="product-card-actions" data-action="stop-propagation">
          <a class="buy-button card-action-link" href="${escapeHtml(affiliateLink)}" target="_blank" rel="noopener noreferrer" data-action="navigate-external-product" data-product-url="${escapeHtml(affiliateLink)}" data-product-id="${escapeHtml(productId)}"><span class="card-action-icon" aria-hidden="true"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg></span><span class="card-action-label">${escapeHtml(buyButtonText)}</span></a>
          ${watchBtnHtml}
        </div>
        <div class="product-share-buttons" data-product-data="${itemDataJson}" data-action="stop-propagation">
          <button class="share-btn share-native" data-action="share" data-share-type="share" title="Share">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
              <polyline points="16 6 12 2 8 6"></polyline>
              <line x1="12" y1="2" x2="12" y2="15"></line>
            </svg>
          </button>
          <button class="share-btn share-x" data-action="share" data-share-type="twitter" title="Share on X">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
          </button>
          <button class="share-btn share-facebook" data-action="share" data-share-type="facebook" title="Share on Facebook">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
            </svg>
          </button>
          <button class="share-btn share-copy" data-action="share" data-share-type="copy" title="Copy link">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
          </button>
        </div>
      </div>
    </div>
  `;
}

// Handle broken product images
// Shared placeholder so we never use non-URLs (e.g. emoji) as img src (prevents 404 and CSP issues)
const PRODUCT_IMAGE_PLACEHOLDER_SVG = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';

/**
 * Return a safe img src: only http(s): or data: URLs; otherwise placeholder to avoid 404 (e.g. emoji used as URL).
 * @param {string} raw - imageUrl or image_url from API/item
 * @returns {string}
 */
export function getSafeImageSrc(raw) {
  if (!raw || typeof raw !== 'string') return PRODUCT_IMAGE_PLACEHOLDER_SVG;
  const s = raw.trim();
  if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('data:')) return s;
  return PRODUCT_IMAGE_PLACEHOLDER_SVG;
}

/**
 * Resize eBay gallery segment (s-lNNN) to a target edge length when present.
 * @param {string} url
 * @param {number} edgePx - e.g. 500, 800, 1600
 * @returns {string}
 */
function ebayGalleryResize(url, edgePx) {
  if (!url || typeof url !== 'string') return url;
  return url.replace(/s-l\d+/i, `s-l${edgePx}`);
}

/** Sizes hint for homepage featured hero (~shell grid + legacy marketplace hero). */
const FEATURED_HERO_IMAGE_SIZES =
  '(max-width: 1100px) min(96vw, 440px), min(34vw, 280px)';

/**
 * Build src / srcset / sizes for featured hero (responsive eBay gallery).
 * Uses API hints (camelCase or snake_case) when present from featured-deal-candidate.
 * @param {string} raw - raw image URL from product/candidate
 * @param {Object} [hints]
 * @param {string} [hints.thumb]
 * @param {string} [hints.hero]
 * @param {string} [hints.detail]
 * @returns {{ src: string, thumb: string, srcset: string|null, sizes: string|null, fallbackSrc: string }}
 */
function buildFeaturedHeroImageAttrs(raw, hints = {}) {
  const safe = getSafeImageSrc(raw);
  if (!safe || typeof safe !== 'string' || safe.startsWith('data:')) {
    return { src: safe, thumb: safe, srcset: null, sizes: null, fallbackSrc: safe };
  }
  // Small LQIP for instant paint; 500/800/1600 for responsive upgrade.
  const lqip = ebayGalleryResize(safe, 300);
  const thumb = hints.thumb || ebayGalleryResize(safe, 500);
  const hero = hints.hero || ebayGalleryResize(safe, 800);
  const detail = hints.detail || ebayGalleryResize(safe, 1600);
  const ebayLike = /s-l\d+/i.test(safe);
  const hasHints = Boolean(hints.thumb || hints.hero || hints.detail);
  if (!ebayLike && !hasHints) {
    return { src: safe, thumb: safe, srcset: null, sizes: null, fallbackSrc: safe };
  }
  const srcset = `${thumb} 500w, ${hero} 800w, ${detail} 1600w`;
  return {
    src: hero,
    thumb: lqip || thumb,
    srcset,
    sizes: FEATURED_HERO_IMAGE_SIZES,
    fallbackSrc: safe,
  };
}

/**
 * Sizes hint for product-card thumbs. `.deals-grid` is
 * `repeat(auto-fill, minmax(220px, 1fr))` at every breakpoint, so a column is
 * always 220–440 CSS px; below ~520px the grid collapses to one full-bleed column.
 */
const CARD_IMAGE_SIZES = '(max-width: 520px) 92vw, 280px';

/** Gallery widths offered to cards. Deliberately stops well short of s-l1600. */
const CARD_IMAGE_WIDTHS = [300, 400, 500, 640, 800];

/**
 * Build src / srcset / sizes for a product-card thumbnail.
 *
 * Cards paint into a ~220px box, so s-l1600 is ~50x the pixels we actually show
 * (measured avg 376 KB, vs 18 KB at s-l300 / 30 KB at s-l400). Offer the eBay
 * ladder and let the browser pick per column width and DPR instead of forcing
 * the largest rendition.
 * @param {string} resolvedSrc - URL already through getSafeImageSrc()
 * @returns {{ src: string, srcset: string|null, sizes: string|null, fallbackSrc: string }}
 */
function buildCardImageAttrs(resolvedSrc) {
  const plain = { src: resolvedSrc, srcset: null, sizes: null, fallbackSrc: resolvedSrc };
  if (!resolvedSrc || typeof resolvedSrc !== 'string') return plain;
  if (resolvedSrc.startsWith('data:') || !/s-l\d+/i.test(resolvedSrc)) return plain;
  return {
    src: ebayGalleryResize(resolvedSrc, 400),
    srcset: CARD_IMAGE_WIDTHS
      .map((w) => `${ebayGalleryResize(resolvedSrc, w)} ${w}w`)
      .join(', '),
    sizes: CARD_IMAGE_SIZES,
    fallbackSrc: resolvedSrc,
  };
}

/**
 * Paint a small LQIP first, then upgrade to hero + srcset when ready (no flash of empty/broken img).
 * @param {HTMLImageElement} img
 * @param {{ src: string, thumb?: string, srcset: string|null, sizes: string|null, fallbackSrc: string }} heroAttrs
 */
function applyFeaturedHeroProgressiveImage(img, heroAttrs) {
  const fullSrc = heroAttrs.src;
  const lqipSrc = heroAttrs.thumb || fullSrc;
  const fallbackSrc = heroAttrs.fallbackSrc;

  img.removeAttribute('data-hero-img-fallback');
  img.classList.remove('is-hero-ready');
  img.removeAttribute('srcset');
  img.removeAttribute('sizes');

  const applyFull = () => {
    if (heroAttrs.srcset && heroAttrs.sizes) {
      img.srcset = heroAttrs.srcset;
      img.setAttribute('sizes', heroAttrs.sizes);
    } else {
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
    }
    if (fullSrc) img.src = fullSrc;
    img.removeAttribute('hidden');
    img.classList.remove('is-hero-lqip');
    img.classList.add('is-hero-ready');
  };

  img.onerror = function () {
    const el = this;
    el.removeAttribute('srcset');
    el.removeAttribute('sizes');
    el.classList.remove('is-hero-lqip');
    el.removeAttribute('hidden');
    if (el.dataset.heroImgFallback !== '1') {
      el.dataset.heroImgFallback = '1';
      if (fallbackSrc && el.src !== fallbackSrc) {
        el.src = fallbackSrc;
        return;
      }
    }
    handleProductImageError(el);
  };

  if (!fullSrc || fullSrc === lqipSrc || String(fullSrc).startsWith('data:')) {
    applyFull();
    return;
  }

  // Instant low-res paint, then swap when high-res is decoded.
  img.classList.add('is-hero-lqip');
  img.src = lqipSrc;
  img.removeAttribute('hidden');

  const upgradeToken = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  img.dataset.heroUpgradeToken = upgradeToken;

  const upgrade = new Image();
  upgrade.decoding = 'async';
  if (heroAttrs.srcset && heroAttrs.sizes) {
    upgrade.srcset = heroAttrs.srcset;
    upgrade.sizes = heroAttrs.sizes;
  }
  upgrade.src = fullSrc;

  const finish = () => {
    // Ignore if a newer featured deal already replaced this img.
    if (img.dataset.heroUpgradeToken !== upgradeToken) return;
    applyFull();
  };

  if (typeof upgrade.decode === 'function') {
    upgrade.decode().then(finish).catch(() => {
      upgrade.onload = finish;
      if (upgrade.complete) finish();
    });
  } else {
    upgrade.onload = finish;
    if (upgrade.complete) finish();
  }
}

function handleProductImageError(img) {
  if (img.src !== PRODUCT_IMAGE_PLACEHOLDER_SVG) {
    img.src = PRODUCT_IMAGE_PLACEHOLDER_SVG;
  }
}

// Make handleProductImageError available globally for inline onerror handlers
if (typeof window !== 'undefined') {
  window.handleProductImageError = handleProductImageError;
}

// Global image error handler for product cards (fallback)
// ========================================================================
// Marketplace Homepage Population Functions
// ========================================================================

// Populate Featured Deal (with JIT freshness check for eBay items when >1h stale)
// Supports admin-pinned featured deal via homepage-settings/active
async function populateFeaturedDeal() {
  const module = document.getElementById('featuredDealModule');
  if (!module) return;
  const hideFeaturedModule = () => {
    module.style.display = 'none';
  };
  const showFeaturedModule = () => {
    module.style.display = '';
  };
  const clearFeaturedPending = () => {
    module.classList.remove('is-featured-pending');
    const titleEl = module.querySelector('#featuredDealTitle');
    if (titleEl) titleEl.removeAttribute('aria-busy');
  };

  const featuredDealLink = module.querySelector('#featuredDealLink');
  const featuredDealImage = module.querySelector('#featuredDealImage');

  const pendingTimeoutId = window.setTimeout(() => {
    if (module.classList.contains('is-featured-pending')) {
      logger.warn('Featured deal load timed out; clearing pending state');
      clearFeaturedPending();
    }
  }, 15000);

  const finishFeaturedLoad = () => {
    window.clearTimeout(pendingTimeoutId);
  };

  if (!window.apiService) {
    const items = searchData.items || [];
    if (items.length === 0) {
      finishFeaturedLoad();
      hideFeaturedModule();
      return;
    }
    const sortedItems = sortByHotness([...items]);
    const featuredItem = sortedItems.find((candidate) => isValidFeaturedCandidate(candidate));
    if (featuredItem) {
      showFeaturedModule();
      renderFeaturedItem(featuredItem, featuredDealLink, featuredDealImage);
      finishFeaturedLoad();
    } else {
      finishFeaturedLoad();
      hideFeaturedModule();
    }
    return;
  }

  try {
    let featuredItem = null;
    let settings = {};
    try {
      settings = await window.apiService.getHomepageSettingsActive();
    } catch (_) { /* ignore */ }
    const pinnedId = settings?.featuredDealId || settings?.featured_deal_id;
    if (pinnedId) {
      try {
        const product = await window.apiService.getProduct(pinnedId);
        const status = product?.availability_status ?? product?.availabilityStatus;
        const qty = product?.quantity_available ?? product?.quantityAvailable ?? 1;
        if (product && status !== 'OUT_OF_STOCK' && qty !== 0) {
          featuredItem = product;
          if (String(pinnedId).startsWith('ebay_')) {
            const updatedAt = product.availability_updated_at || product.availabilityUpdatedAt;
            const hoursAgo = updatedAt ? (Date.now() - new Date(updatedAt)) / (1000 * 60 * 60) : 999;
            if (hoursAgo > 1) {
              try {
                const encodedId = encodeURIComponent(pinnedId);
                const checkUrl = `${window.apiService.baseUrl}/products/${encodedId}/check-freshness/`;
                const freshRes = await window.apiService.request(checkUrl);
                if (freshRes && freshRes.status === 'unavailable') {
                  featuredItem = null;
                } else if (freshRes && !freshRes.is_fresh) {
                  const refreshed = await window.apiService.getProduct(pinnedId);
                  const refreshedStatus = refreshed?.availability_status ?? refreshed?.availabilityStatus;
                  const refreshedQty = refreshed?.quantity_available ?? refreshed?.quantityAvailable ?? 1;
                  if (refreshed && refreshedStatus !== 'OUT_OF_STOCK' && refreshedQty !== 0) {
                    featuredItem = refreshed;
                  } else {
                    featuredItem = null;
                  }
                }
              } catch (err) {
                if (err && (err.status === 404 || err.statusCode === 404)) {
                  featuredItem = null;
                } else {
                  logger.warn('Pinned featured deal freshness check failed', err);
                }
              }
            }
          }
        }
      } catch (_) { /* fall through to automatic */ }
    }
    if (!featuredItem) {
      featuredItem = await window.apiService.getFeaturedDealCandidate();

      if (featuredItem && (featuredItem.product_id || featuredItem.productId) && String(featuredItem.product_id || featuredItem.productId).startsWith('ebay_')) {
        const productId = featuredItem.product_id || featuredItem.productId;
        const updatedAt = featuredItem.availability_updated_at || featuredItem.availabilityUpdatedAt;
        const hoursAgo = updatedAt ? (Date.now() - new Date(updatedAt)) / (1000 * 60 * 60) : 999;

        if (hoursAgo > 1) {
          try {
            const encodedId = encodeURIComponent(productId);
            const checkUrl = `${window.apiService.baseUrl}/products/${encodedId}/check-freshness/`;
            const freshRes = await window.apiService.request(checkUrl);
            if (freshRes && freshRes.status === 'unavailable') {
              featuredItem = await window.apiService.getFeaturedDealCandidate({ excludeProductId: productId });
            } else if (freshRes && !freshRes.is_fresh) {
              featuredItem = await window.apiService.getFeaturedDealCandidate();
            }
          } catch (err) {
            if (err && (err.status === 404 || err.statusCode === 404)) {
              featuredItem = await window.apiService.getFeaturedDealCandidate({ excludeProductId: productId });
            } else {
              logger.warn('Featured deal freshness check failed', err);
            }
          }
        }
      }
    }

    if (!isValidFeaturedCandidate(featuredItem)) {
      const invalidId = featuredItem?.product_id || featuredItem?.productId;
      if (invalidId) {
        try {
          const retryCandidate = await window.apiService.getFeaturedDealCandidate({ excludeProductId: invalidId });
          if (isValidFeaturedCandidate(retryCandidate)) {
            featuredItem = retryCandidate;
          } else {
            featuredItem = null;
          }
        } catch (_) {
          featuredItem = null;
        }
      } else {
        featuredItem = null;
      }
    }

    // Kept even when it fails the visitor's floor: the floor can change after
    // this, and applyFeaturedForFloor() decides between this pick and the
    // feed's best qualifying listing each time. "/" is edge-cached, so the
    // server could only ever have judged it at the default floor.
    featuredPrimaryItem = featuredItem;
    applyFeaturedForFloor();
    finishFeaturedLoad();
  } catch (error) {
    logger.error('Failed to load featured deal', error);
    finishFeaturedLoad();
    hideFeaturedModule();
  }
}

// The admin-pinned (or automatic) featured pick, before any floor is applied.
let featuredPrimaryItem = null;

/** Seller numbers under either spelling the API and the feed rows use. */
function featuredSellerNumbers(item) {
  return {
    feedback: item.seller_feedback_pct ?? item.sellerFeedbackPct,
    reviews: item.seller_sales_count ?? item.sellerSalesCount,
    returns: item.returns_accepted ?? item.returnsAccepted,
  };
}

/**
 * The listing in the feed with the biggest drop below its typical ask that
 * the visitor's floor lets through, or null. Read from the feed response the
 * page already holds, so the fallback costs no request.
 */
function pickFeaturedFallback(floor) {
  let best = null;
  let bestPct = 0;
  for (const item of searchData.items || []) {
    if (!isValidFeaturedCandidate(item)) continue;
    if (!sellerClears(featuredSellerNumbers(item), floor)) continue;
    const delta = priceDelta(item.price, item.priceContext || item.price_context || null);
    if (!delta || delta.up || delta.pct <= bestPct) continue;
    best = item;
    bestPct = delta.pct;
  }
  return best;
}

/** The hero badge states the floor the featured deal clears, or hides at Off. */
function paintFeaturedBadge(module, floor) {
  const badge = module.querySelector('#featuredDealBadge');
  if (!badge) return;
  const limit = floorThresholds(floor);
  badge.hidden = !limit;
  const tip = badge.querySelector('.hh-tip__b');
  if (tip && limit) {
    tip.textContent = `This seller meets your ${FLOOR_NAMES[floor]} standard: `
      + `${limit.feedback}%+ positive feedback and ${limit.reviews}+ reviews. Returns accepted.`;
  }
}

/**
 * Show the pinned featured deal if its seller clears the visitor's floor;
 * otherwise the feed's best qualifying listing; otherwise hide the module.
 * Runs after hydration and again whenever the floor or the feed changes.
 */
function applyFeaturedForFloor() {
  const module = document.getElementById('featuredDealModule');
  if (!module) return;
  const floor = getFloor();
  let item = featuredPrimaryItem
    && isValidFeaturedCandidate(featuredPrimaryItem)
    && sellerClears(featuredSellerNumbers(featuredPrimaryItem), floor)
    ? featuredPrimaryItem
    : null;
  if (!item) item = pickFeaturedFallback(floor);
  // The label and the hint belong to the card: hide them together so the hero
  // never shows a heading over nothing.
  const chrome = module.parentElement
    ? module.parentElement.querySelectorAll('.hh-feat__hd, .hh-feat__hint')
    : [];
  for (const node of chrome) node.hidden = !item;
  if (!item) {
    module.style.display = 'none';
    return;
  }
  const link = module.querySelector('#featuredDealLink');
  const image = module.querySelector('#featuredDealImage');
  module.style.display = '';
  if (link) link.style.display = '';
  if (image) image.style.display = '';
  renderFeaturedItem(item, link, image);
  paintFeaturedBadge(module, floor);
}

function extractFeaturedSpecTokens(rawTitle) {
  const title = String(rawTitle || '').trim();
  if (!title) return { displayTitle: '', chips: [] };

  const patterns = [
    /\d+\s*GB\s*RAM/i,
    /\d+\s*(GB|TB)\s*(SSD|HDD)/i,
    /i[357]-[\w-]+/i,
    /M[123][\w\s-]*/i,
    /Ryzen\s+\d+/i,
    /Win\s*11\s*(Pro|Home)?/i,
  ];

  const chips = [];
  let remainder = title;
  for (const re of patterns) {
    const match = remainder.match(re);
    if (match && match[0]) {
      const token = match[0].trim();
      if (!chips.some((c) => c.toLowerCase() === token.toLowerCase())) {
        chips.push(token);
      }
      remainder = remainder.replace(match[0], ' ').replace(/\s+/g, ' ').trim();
    }
  }

  const displayTitle = remainder || title;
  return { displayTitle, chips };
}

function renderFeaturedItem(featuredItem, featuredDealLink, featuredDealImage) {
  const internalUrl = getProductDetailUrl(featuredItem);
  const affiliateUrl = getAffiliateUrl(featuredItem);
  const safeExternalUrl = getSafeExternalHttpUrl(affiliateUrl);
  const hasExternalTarget = Boolean(safeExternalUrl);
  const rawImg =
    featuredItem.imageUrl ||
    featuredItem.image_url ||
    (categoryManager && featuredItem.category ? categoryManager.getCategoryIcon(featuredItem.category) : '');
  const hintThumb =
    featuredItem.imageUrlThumb || featuredItem.image_url_thumb || '';
  const hintHero =
    featuredItem.imageUrlHero || featuredItem.image_url_hero || '';
  const hintDetail =
    featuredItem.imageUrlDetail || featuredItem.image_url_detail || '';
  const heroAttrs = buildFeaturedHeroImageAttrs(rawImg, {
    thumb: hintThumb || undefined,
    hero: hintHero || undefined,
    detail: hintDetail || undefined,
  });

  if (featuredDealImage) {
    featuredDealImage.alt = escapeHtml(featuredItem.name);
    featuredDealImage.decoding = 'async';
    applyFeaturedHeroProgressiveImage(featuredDealImage, heroAttrs);
  }
  const featuredModule = featuredDealLink?.closest('#featuredDealModule')
    || document.getElementById('featuredDealModule');
  const titleEl = featuredModule?.querySelector('#featuredDealTitle')
    || document.getElementById('featuredDealTitle');
  const rawName = featuredItem.name || '';
  const { displayTitle, chips } = extractFeaturedSpecTokens(rawName);
  if (titleEl) {
    titleEl.textContent = displayTitle;
    titleEl.title = rawName || '';
    titleEl.removeAttribute('aria-busy');
  }
  const specChipsEl = featuredModule?.querySelector('#featuredDealSpecChips')
    || document.getElementById('featuredDealSpecChips');
  if (specChipsEl) {
    if (chips.length > 0) {
      specChipsEl.innerHTML = chips.map((chip) =>
        `<span class="featured-deal-spec-chip">${escapeHtml(chip)}</span>`
      ).join('');
      specChipsEl.classList.remove('hidden');
      specChipsEl.removeAttribute('hidden');
    } else {
      specChipsEl.innerHTML = '';
      specChipsEl.classList.add('hidden');
      specChipsEl.setAttribute('hidden', '');
    }
  }
  if (featuredModule) featuredModule.classList.remove('is-featured-pending');
  const priceEl = featuredModule?.querySelector('#featuredDealPrice')
    || document.getElementById('featuredDealPrice');
  const strikeEl = featuredModule?.querySelector('#featuredDealStrike')
    || document.getElementById('featuredDealStrike');
  const discountEl = featuredModule?.querySelector('#featuredDealDiscount')
    || document.getElementById('featuredDealDiscount');
  const pricing = buildPricingViewModel(featuredItem);
  if (priceEl) {
    priceEl.textContent = pricing?.listingLabel || 'N/A';
    if (pricing?.retailIsEstimate) {
      priceEl.setAttribute('data-savings-context', 'estimated-retail');
      priceEl.setAttribute('aria-label', `${pricing.listingLabel}, savings calculated against estimated retail`);
      priceEl.setAttribute('title', pricing.retailLabel || 'Estimated retail baseline');
    } else {
      priceEl.removeAttribute('data-savings-context');
      priceEl.removeAttribute('aria-label');
      priceEl.removeAttribute('title');
    }
  }
  const showStrike = pricing?.retailBaseline && pricing?.retailLabel;
  if (strikeEl) {
    if (showStrike) {
      strikeEl.textContent = pricing.retailLabel;
      strikeEl.classList.remove('hidden');
      strikeEl.removeAttribute('hidden');
    } else {
      strikeEl.textContent = '';
      strikeEl.classList.add('hidden');
      strikeEl.setAttribute('hidden', '');
    }
  }
  if (discountEl) {
    if (showStrike && pricing?.discountPercentage) {
      discountEl.textContent = `${pricing.discountPercentage}% under`;
      discountEl.classList.remove('hidden');
      discountEl.removeAttribute('hidden');
    } else {
      discountEl.textContent = '';
      discountEl.classList.add('hidden');
      discountEl.setAttribute('hidden', '');
    }
  }
  const trustRowEl = featuredModule?.querySelector('#featuredDealTrustRow')
    || document.getElementById('featuredDealTrustRow');
  const seller = featuredSellerNumbers(featuredItem);
  const card = toCard({
    ...featuredItem,
    price: featuredItem.price ?? featuredItem.price_label,
    sellerFeedbackPct: seller.feedback,
    sellerSalesCount: seller.reviews,
    returnsAccepted: seller.returns,
  });
  if (trustRowEl) {
    // The same ring and seller line a tile carries, so the hero reads as the
    // tile it is. The tip explains the ring; no ring when there are no seller
    // numbers, and never a zero-score one.
    if (card.trust) {
      const [pct, count] = card.seller_line.split(' \u00b7 ');
      const tip = `Trust score ${card.trust.score}/100, from the seller's ${pct} `
        + `positive feedback across ${count} reviews.`;
      trustRowEl.innerHTML = `<span class="hh-tip" tabindex="0" aria-describedby="featTipTrust">`
        + `${ringHtml(card.trust)}<span class="hh-tip__b" role="tooltip" id="featTipTrust">`
        + `${escapeHtml(tip)}</span></span><span>${escapeHtml(card.seller_line)} reviews</span>`;
    } else {
      trustRowEl.innerHTML = '<span>Seller not rated</span>';
    }
    trustRowEl.classList.remove('hidden');
    trustRowEl.removeAttribute('hidden');
  }
  const conditionEl = featuredModule?.querySelector('#featuredDealCondition');
  if (conditionEl) {
    conditionEl.textContent = card.condition || '';
    conditionEl.hidden = !card.condition;
  }
  // "down 22% vs typical ask", with the receipt in the tip: the comparison is
  // always against the model's median ask, never a retail price.
  const deltaEl = featuredModule?.querySelector('#featuredDealDelta');
  if (deltaEl) {
    const delta = card.delta;
    deltaEl.hidden = !delta;
    deltaEl.classList.toggle('up', Boolean(delta && delta.up));
    if (delta) {
      deltaEl.querySelector('#featuredDealDeltaText').textContent = `${delta.arrow} ${delta.text}`;
      const receipt = card.price_context && card.price_context.receipt;
      deltaEl.querySelector('.hh-tip__b').textContent = receipt
        || `${delta.pct}% ${delta.up ? 'above' : 'below'} the 30-day median asking price for this model.`;
    }
  }
  const countdownEl = featuredModule?.querySelector('#featuredDealCountdown')
    || document.getElementById('featuredDealCountdown');
  const endTime = featuredItem.ebay_listing_end_time || featuredItem.ebayListingEndTime;
  if (countdownEl) {
    if (endTime) {
      try {
        const end = new Date(endTime);
        if (!Number.isNaN(end.getTime())) {
          countdownEl.textContent = `Ends ${end.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`;
          countdownEl.classList.remove('hidden');
          countdownEl.removeAttribute('hidden');
        } else {
          countdownEl.classList.add('hidden');
          countdownEl.setAttribute('hidden', '');
        }
      } catch {
        countdownEl.classList.add('hidden');
        countdownEl.setAttribute('hidden', '');
      }
    } else {
      countdownEl.textContent = '';
      countdownEl.classList.add('hidden');
      countdownEl.setAttribute('hidden', '');
    }
  }
  if (featuredDealLink) {
    const trackProductId = featuredItem.productId || featuredItem.product_id || '';
    if (hasExternalTarget) {
      featuredDealLink.href = safeExternalUrl;
      featuredDealLink.setAttribute('data-action', 'navigate-external-product');
      featuredDealLink.setAttribute('data-product-url', safeExternalUrl);
      if (trackProductId) {
        featuredDealLink.setAttribute('data-product-id', String(trackProductId));
      } else {
        featuredDealLink.removeAttribute('data-product-id');
      }
      featuredDealLink.setAttribute('target', '_blank');
      featuredDealLink.setAttribute('rel', 'noopener noreferrer');
    } else {
      // Defensive fallback when affiliate data is missing.
      featuredDealLink.href = internalUrl;
      featuredDealLink.setAttribute('data-action', 'navigate-product');
      featuredDealLink.setAttribute('data-product-url', internalUrl);
      featuredDealLink.removeAttribute('data-product-id');
      featuredDealLink.removeAttribute('data-external-link');
      featuredDealLink.removeAttribute('target');
      featuredDealLink.removeAttribute('rel');
    }
  }
}

// Populate Continue Browsing
function populateContinueBrowsing() {
  const container = document.getElementById('continueBrowsingList');
  if (!container) return;
  
  // Get recently viewed items from localStorage
  const recentViews = getRecentViews();
  const items = searchData.items || [];
  
  if (recentViews.length === 0 || items.length === 0) {
    container.innerHTML = '<p class="module-placeholder">No recent items</p>';
    return;
  }
  
  // Get up to 3 recent items
  const recentItems = recentViews
    .slice(0, 3)
    .map(viewId => items.find(item => (item.productId || item.name) === viewId))
    .filter(Boolean);
  
  if (recentItems.length === 0) {
    container.innerHTML = '<p class="module-placeholder">No recent items</p>';
    return;
  }
  
  container.innerHTML = recentItems.map(item => {
    const internalUrl = getProductDetailUrl(item);
    const imageSrc = getSafeImageSrc(item.imageUrl || item.image_url || (categoryManager && item.category ? categoryManager.getCategoryIcon(item.category) : ''));
    
    return `
      <a href="${escapeHtml(internalUrl)}" class="continue-browsing-item" data-action="navigate-product" data-product-url="${escapeHtml(internalUrl)}">
        <img src="${imageSrc}" alt="${escapeHtml(item.name)}" loading="lazy">
        <div class="continue-browsing-item-info">
          <h4>${escapeHtml(item.name)}</h4>
          <p>${formatPrice(item.price)}</p>
        </div>
      </a>
    `;
  }).join('');
}

// Populate Top Categories (uses admin-configured order from homepage-settings when set)
const CATEGORY_LISTING_COUNT_MIN = 20;
const TOP_CATEGORY_INDEX_MAX = 9;
const HERO_CATEGORY_LOOKBACK_MS = 7 * 24 * 60 * 60 * 1000;

/** 3-column category grid: only 3, 6, or 9 rows — never orphan last row. */
function normalizeHomepageCategoryCount(n) {
  if (n <= 0) return 0;
  if (n % 3 === 0) return Math.min(n, TOP_CATEGORY_INDEX_MAX);
  if (n > 6) return 6;
  if (n > 3) return 3;
  return n;
}

function buildCategorySearchHref(categoryId) {
  const enc = encodeURIComponent(categoryId);
  const path = (typeof window !== 'undefined' && window.location && window.location.pathname) ? window.location.pathname : '';
  if (path.endsWith('.html') || /categories/i.test(path)) {
    return `search.html?category=${enc}`;
  }
  return `/search.html?category=${enc}`;
}

function getCategoryLabel(c) {
  return c?.short_label ?? c?.shortLabel ?? c?.label ?? c?.fullLabel ?? c?.full_label ?? '';
}

function shouldShowCategoryListingCount(count) {
  return typeof count === 'number' && Number.isFinite(count) && count >= CATEGORY_LISTING_COUNT_MIN;
}

function formatCategoryListingCount(count) {
  return `${count.toLocaleString()} listing${count === 1 ? '' : 's'}`;
}

function dedupeNearDuplicateCategories(categories) {
  if (!Array.isArray(categories) || categories.length < 2) return categories;
  const labels = categories.map((c) => getCategoryLabel(c).trim().toLowerCase());
  const hasAudio = labels.includes('audio');
  const hasHomeAudio = labels.includes('home audio');
  if (!hasAudio || !hasHomeAudio) return categories;
  return categories.filter((c) => getCategoryLabel(c).trim().toLowerCase() !== 'audio');
}

function renderCategoryIndexRow(c, id) {
  const label = getCategoryLabel(c) || `Category ID: ${id}`;
  const listingCount = c?.listing_count ?? c?.listingCount;
  const countHtml = shouldShowCategoryListingCount(listingCount)
    ? `<span class="category-index-count">${escapeHtml(formatCategoryListingCount(listingCount))}</span>`
    : '';
  const { html, family } = getCategoryIcon(c || { label, id }, { size: 18, preferLeaf: !!c });
  return `
    <a href="${escapeHtml(buildCategorySearchHref(id))}" class="category-index-row" role="listitem">
      <span class="category-index-icon" data-family="${escapeHtml(family)}">${html}</span>
      <span class="category-index-label">${escapeHtml(label)}</span>
      ${countHtml}
    </a>
  `;
}

async function populateTopCategories() {
  const container = document.getElementById('topCategoriesGrid');
  if (!container) return;

  let topIds = [];
  const catMap = new Map();

  if (window.apiService) {
    try {
      const [settings, availableList] = await Promise.all([
        window.apiService.getHomepageSettingsActive(),
        window.apiService.getAvailableCategories(),
      ]);
      const rawIds = settings?.topCategoryIds ?? settings?.top_category_ids ?? [];
      topIds = Array.isArray(rawIds) ? rawIds.slice(0, TOP_CATEGORY_INDEX_MAX) : [];
      let cats = Array.isArray(availableList) ? availableList : (availableList?.categories ?? []);
      cats = dedupeNearDuplicateCategories(cats);
      cats.forEach((c) => {
        const id = c.id ?? c;
        if (id) catMap.set(String(id), c);
      });
      if (topIds.length === 0 && cats.length > 0) {
        topIds = cats.slice(0, TOP_CATEGORY_INDEX_MAX).map((c) => String(c.id ?? c));
      }
      if (topIds.length > 0) {
        topIds = topIds.slice(0, normalizeHomepageCategoryCount(topIds.length));
      }
    } catch (_) { /* use default */ }
  }

  if (topIds.length === 0 && categoryManager) {
    const fallback = categoryManager.getMainCategories().slice(0, normalizeHomepageCategoryCount(6));
    container.innerHTML = fallback.map((category) => {
      const { html, family } = getCategoryIcon({ label: category }, { size: 18 });
      return `
        <a href="${escapeHtml(buildCategorySearchHref(category))}" class="category-index-row" role="listitem">
          <span class="category-index-icon" data-family="${escapeHtml(family)}">${html}</span>
          <span class="category-index-label">${escapeHtml(category)}</span>
        </a>
      `;
    }).join('');
    return;
  }

  container.innerHTML = topIds.map((id) => {
    const c = catMap.get(String(id));
    return renderCategoryIndexRow(c, id);
  }).join('');
}

const QUADRANT_GRID_SLOTS = 4;
/** Sentinel for an intentionally empty grid cell (bucket had items but fewer than four after dedupe). */
const QUADRANT_SPACER = '__quadrant_spacer__';

/**
 * Deduplicate quadrant candidates by product id (or name+price fallback) so marketing tiles never repeat the same listing.
 */
function dedupeQuadrantItemsByProductId(items) {
  if (!Array.isArray(items) || items.length === 0) return [];
  const seen = new Set();
  const out = [];
  for (const item of items) {
    const id = item.productId || item.product_id;
    const key =
      id != null && String(id).trim() !== ''
        ? `id:${String(id)}`
        : `fallback:${(item.name || '').slice(0, 120)}|${item.price || ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}

/**
 * Paginate GET /products/ until we have enough distinct in-stock rows for quadrant tiles (DRF default page size is 20).
 */
async function fetchDistinctProductsForQuadrant(baseParams, minDistinct = QUADRANT_GRID_SLOTS) {
  if (!window.apiService || typeof window.apiService.getProducts !== 'function') return [];
  const seen = new Set();
  const merged = [];
  const maxPages = 15;
  for (let page = 1; page <= maxPages && merged.length < minDistinct; page += 1) {
    let batch = [];
    try {
      batch = await window.apiService.getProducts({ ...baseParams, page });
    } catch (_) {
      break;
    }
    if (!Array.isArray(batch)) batch = batch && batch.results ? batch.results : [];
    batch = filterAvailableItems(batch);
    if (batch.length === 0) break;
    for (const item of batch) {
      const id = item.productId || item.product_id;
      const key =
        id != null && String(id).trim() !== ''
          ? `id:${String(id)}`
          : `fallback:${(item.name || '').slice(0, 120)}|${item.price || ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(item);
      if (merged.length >= minDistinct) break;
    }
    if (batch.length < 20) break;
  }
  return merged;
}

/**
 * Try to populate the three quadrant sections from homepage bucket config (API).
 * On failure or fewer than 3 buckets, falls back to keyword-based populateQuadrants().
 */
async function populateQuadrantsFromBuckets() {
  // Homepage category-quadrants removed; skip API/keyword work when grids are gone.
  if (!document.getElementById('quadrantGamingGrid')) {
    return;
  }
  const slotIds = [
    { cardId: 'quadrantGaming', gridId: 'quadrantGamingGrid', defaultTitle: 'Gaming Gear' },
    { cardId: 'quadrantHome', gridId: 'quadrantHomeGrid', defaultTitle: 'Home Essentials' },
    { cardId: 'quadrantTech', gridId: 'quadrantTechGrid', defaultTitle: 'Tech Deals' },
  ];
  if (!window.apiService || typeof window.apiService.getHomepageBucketsActive !== 'function') {
    populateQuadrants();
    return;
  }
  try {
    const buckets = await window.apiService.getHomepageBucketsActive();
    if (!Array.isArray(buckets) || buckets.length < 3) {
      populateQuadrants();
      return;
    }
    for (let i = 0; i < 3; i++) {
      const bucket = buckets[i];
      const slot = slotIds[i];
      const titleEl = document.querySelector(`#${slot.cardId} .quadrant-title`);
      if (titleEl) titleEl.textContent = bucket.name || slot.defaultTitle;
      const categoryIds = bucket.categoryIds || bucket.category_ids || [];
      const params = { status: 'active', in_stock_only: true };
      if (categoryIds.length > 0) params.category = categoryIds;
      let items = [];
      try {
        items = await fetchDistinctProductsForQuadrant(params, QUADRANT_GRID_SLOTS);
      } catch (_) {
        items = [];
      }
      items = dedupeQuadrantItemsByProductId(filterAvailableItems(items));
      populateQuadrant(slot.gridId, items.slice(0, QUADRANT_GRID_SLOTS), bucket.name || slot.defaultTitle);
    }
  } catch (_) {
    populateQuadrants();
  }
}

// Populate Quadrants (keyword-based fallback when bucket API is unavailable or has no config)
function populateQuadrants() {
  if (!document.getElementById('quadrantGamingGrid')) {
    return;
  }
  const items = searchData.items || [];
  if (items.length === 0) return;
  const pool = items.slice(0, 500);

  // Gaming Gear - filter by keywords (broadened so four distinct tiles are more likely before dedupe)
  const gamingKeywords = [
    'gaming', 'game', 'xbox', 'playstation', 'nintendo', 'controller', 'headset', 'keyboard', 'mouse',
    'console', 'pc', 'steam', 'radeon', 'geforce', 'ps5', 'ps4', 'switch', 'ssd', 'router', 'ddr',
  ];
  const gamingItems = dedupeQuadrantItemsByProductId(
    pool.filter((item) => {
      const name = (item.name || '').toLowerCase();
      const category = (item.category || '').toLowerCase();
      return gamingKeywords.some((keyword) => name.includes(keyword) || category.includes(keyword));
    })
  ).slice(0, QUADRANT_GRID_SLOTS);

  populateQuadrant('quadrantGamingGrid', gamingItems, 'Gaming Gear');

  // Home Essentials
  const homeKeywords = [
    'appliance', 'vacuum', 'coffee', 'maker', 'kitchen', 'home', 'furniture', 'chair', 'desk', 'lamp',
    'bedding', 'storage', 'dyson', 'blender', 'toaster', 'microwave', 'cookware', 'decor', 'rug', 'sofa',
  ];
  const homeItems = dedupeQuadrantItemsByProductId(
    pool.filter((item) => {
      const name = (item.name || '').toLowerCase();
      const category = (item.category || '').toLowerCase();
      const mainCategory = (item.mainCategory || '').toLowerCase();
      return homeKeywords.some(
        (keyword) =>
          name.includes(keyword) || category.includes(keyword) || mainCategory.includes(keyword)
      );
    })
  ).slice(0, QUADRANT_GRID_SLOTS);

  populateQuadrant('quadrantHomeGrid', homeItems, 'Home Essentials');

  // Tech Deals
  const techKeywords = [
    'laptop', 'phone', 'tablet', 'headphone', 'speaker', 'camera', 'tv', 'monitor', 'electronic',
    'iphone', 'ipad', 'macbook', 'chromebook', 'usb', 'ssd', 'nvme', 'gpu', 'wireless', 'bluetooth',
    'earbud', 'android', 'intel', 'amd',
  ];
  const techItems = dedupeQuadrantItemsByProductId(
    pool.filter((item) => {
      const name = (item.name || '').toLowerCase();
      const category = (item.category || '').toLowerCase();
      const mainCategory = (item.mainCategory || '').toLowerCase();
      return techKeywords.some(
        (keyword) =>
          name.includes(keyword) || category.includes(keyword) || mainCategory.includes(keyword)
      );
    })
  ).slice(0, QUADRANT_GRID_SLOTS);

  populateQuadrant('quadrantTechGrid', techItems, 'Tech Deals');
}

function populateQuadrant(containerId, items, fallbackTitle) {
  const container = document.getElementById(containerId);
  if (!container) return;

  const targetCount = QUADRANT_GRID_SLOTS;
  const uniqueItems = dedupeQuadrantItemsByProductId(Array.isArray(items) ? items : []);

  let displayItems;
  if (uniqueItems.length === 0) {
    displayItems = Array.from({ length: targetCount }, () => null);
  } else {
    displayItems = uniqueItems.slice(0, targetCount);
    while (displayItems.length < targetCount) {
      displayItems.push(QUADRANT_SPACER);
    }
  }

  container.innerHTML = displayItems.map((item) => {
    if (item === QUADRANT_SPACER) {
      return `
        <div class="quadrant-item quadrant-item-placeholder quadrant-item-placeholder--spacer" aria-hidden="true"></div>`;
    }
    if (!item) {
      return `
        <div class="quadrant-item quadrant-item-placeholder" role="presentation">
          <div class="quadrant-item-image quadrant-item-placeholder-visual" aria-hidden="true"></div>
          <h4 class="quadrant-item-title">New picks soon</h4>
          <p class="quadrant-item-price">—</p>
        </div>`;
    }

    const internalUrl = getProductDetailUrl(item);
    const listingImg = item.imageUrl || item.image_url;
    const imageSrc = listingImg ? getSafeImageSrc(listingImg) : '';
    const hasHttpImg =
      imageSrc &&
      (String(imageSrc).startsWith('http://') || String(imageSrc).startsWith('https://'));
    if (!hasHttpImg) {
      return `
        <div class="quadrant-item quadrant-item-placeholder" role="presentation">
          <div class="quadrant-item-image quadrant-item-placeholder-visual" aria-hidden="true"></div>
          <h4 class="quadrant-item-title">Image unavailable</h4>
          <p class="quadrant-item-price">—</p>
        </div>`;
    }

    return `
      <a href="${escapeHtml(internalUrl)}" class="quadrant-item" data-action="navigate-product" data-product-url="${escapeHtml(internalUrl)}">
        <img src="${imageSrc}" alt="${escapeHtml(item.name)}" class="quadrant-item-image" loading="lazy">
        <h4 class="quadrant-item-title" title="${(item.name || '').replace(/"/g, '&quot;')}">${escapeHtml(item.name)}</h4>
        <p class="quadrant-item-price">${formatPrice(item.price)}</p>
      </a>
    `;
  }).join('');
}

// Category key for Trending diversity (prefer main category, then legacy category)
function getTrendingCategoryKey(item) {
  if (!item) return 'uncategorized';
  const raw = item.mainCategory || item.main_category || item.category || 'uncategorized';
  return String(raw).trim().toLowerCase() || 'uncategorized';
}

// Populate Trending Widget from /api/products/trending/ (picked and cached
// server-side). This used to be computed here from searchData.items, which
// made the widget wait on the full catalog download and show a demo item
// when that download timed out.
async function populateTrendingWidget() {
  const container = document.getElementById('trendingList');
  if (!container) return;

  const trendingLimit = window.innerWidth >= 1400 ? 8 : 7;
  let trendingItems = [];
  try {
    if (window.apiService && typeof window.apiService.getTrendingProducts === 'function') {
      trendingItems = await window.apiService.getTrendingProducts(trendingLimit);
    }
  } catch (error) {
    logger.warn('Trending: request failed, keeping what is on the page', error);
  }

  if (!Array.isArray(trendingItems) || trendingItems.length === 0) {
    // The server seeds this list; a failed refresh must not blank it.
    if (container.querySelector('.trending-item')) return;
    container.innerHTML = '<p class="widget-placeholder">No trending items</p>';
    return;
  }

  container.innerHTML = trendingItems.map((item, index) => {
    const internalUrl = item.url || getProductDetailUrl(item);
    const imageSrc = getSafeImageSrc(item.imageUrl || item.image_url || (categoryManager && item.category ? categoryManager.getCategoryIcon(item.category) : ''));
    const rank = index + 1;

    return `
      <a href="${escapeHtml(internalUrl)}" class="trending-item" data-action="navigate-product" data-product-url="${escapeHtml(internalUrl)}">
        <span class="trending-item-rank">${rank}</span>
        <img src="${imageSrc}" alt="${escapeHtml(item.name)}" class="trending-item-image" loading="lazy">
        <div class="trending-item-info">
          <span class="trending-item-title">${escapeHtml(item.name)}</span>
          <div class="trending-item-meta">
            <span class="trending-item-price">${formatPrice(item.price)}</span>
          </div>
        </div>
      </a>
    `;
  }).join('');
}

// Populate Quick Links Widget
function populateQuickLinks() {
  const container = document.getElementById('quickLinksList');
  if (!container || !categoryManager) return;
  
  const quickLinksLimit = window.innerWidth >= 1400 ? 10 : 6;
  const mainCategories = categoryManager.getMainCategories().slice(0, quickLinksLimit);
  
  container.innerHTML = mainCategories.map(category => {
    return `
      <a href="search.html?category=${encodeURIComponent(category)}" class="quick-link">
        ${escapeHtml(category)}
      </a>
    `;
  }).join('');
}

function bindDealAlertsForm({
  formId,
  messageId,
  consentSelector = '#dealAlertsConsent',
  requireConsent = true,
}) {
  const form = document.getElementById(formId);
  if (!form || form.dataset.boundDealAlertsForm === '1') return;

  const messageEl = document.getElementById(messageId);
  const consentCheckbox = form.querySelector(consentSelector);

  function showMessage(text, isError = false) {
    if (messageEl) {
      messageEl.textContent = text;
      messageEl.style.display = 'block';
      messageEl.className = 'deal-alerts-message' + (isError ? ' deal-alerts-message-error' : ' deal-alerts-message-success');
    } else {
      alert(text);
    }
  }

  function hideMessage() {
    if (messageEl) messageEl.style.display = 'none';
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const input = form.querySelector('.deal-alerts-input');
    const email = input.value.trim();

    hideMessage();

    if (!email) {
      showMessage('Please enter your email address', true);
      return;
    }

    if (requireConsent && (!consentCheckbox || !consentCheckbox.checked)) {
      showMessage('Please consent to receive deal alerts and marketing emails.', true);
      return;
    }

    const btn = form.querySelector('.deal-alerts-submit');
    const originalText = btn ? btn.textContent : 'Subscribe';
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Subscribing...';
    }

    const apiBase = (window.apiService && window.apiService.baseUrl) || (window.location.port === '5500' || window.location.port === '5173' ? 'http://localhost:8000/api' : `${window.location.origin}/api`);

    try {
      const res = await fetch(`${apiBase}/auth/newsletter/subscribe/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, consent: true })
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok && data.success) {
        input.value = '';
        if (consentCheckbox) consentCheckbox.checked = false;
        showMessage(data.confirmed ? "You're subscribed!" : (data.message || 'Check your email to confirm your subscription.'), false);
      } else {
        showMessage(data.error || 'Subscription failed. Please try again.', true);
      }
    } catch (err) {
      showMessage('Subscription failed. Please try again.', true);
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.textContent = originalText;
      }
    }
  });

  form.dataset.boundDealAlertsForm = '1';
}

// Initialize Deal Alerts Forms (footer/module only — hero email capture removed in 10-09)
function initializeDealAlertsForm() {
  bindDealAlertsForm({
    formId: 'dealAlertsForm',
    messageId: 'dealAlertsMessage',
    requireConsent: true,
  });
}

function populateTrustFloorCard() {
  const preset = TRUSTED_PRESETS.strict;
  const feedbackEl = document.getElementById('heroTrustFloorFeedback');
  const reviewsEl = document.getElementById('heroTrustFloorReviews');
  const returnsEl = document.getElementById('heroTrustFloorReturns');
  if (!feedbackEl && !reviewsEl && !returnsEl) return;
  if (feedbackEl) feedbackEl.textContent = `${preset.feedback}%+`;
  if (reviewsEl) reviewsEl.textContent = `${preset.reviews}+`;
  if (returnsEl) returnsEl.textContent = 'Required';
}

/** Tokens that all mean wearable/portable audio when matching category filters. */
const AUDIO_WEARABLE_TOKENS = new Set([
  'audio',
  'headphone',
  'headphones',
  'headset',
  'headsets',
  'earbud',
  'earbuds',
  'earphone',
  'earphones',
]);

function categoryWordTokens(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 2 || t === 'vr');
}

function canonicalizeCategoryTokens(tokens) {
  const raw = Array.isArray(tokens) ? tokens : [];
  const hasVr = raw.includes('vr') || raw.includes('virtual');
  return raw.map((t) => {
    if (!hasVr && AUDIO_WEARABLE_TOKENS.has(t)) return 'audio_wearable';
    return t;
  });
}

function categoryTokensOverlap(filterTokens, haystackTokens) {
  const ft = canonicalizeCategoryTokens(filterTokens);
  const ht = canonicalizeCategoryTokens(haystackTokens);
  if (!ft.length || !ht.length) return false;
  return ft.some((a) => ht.some((b) => {
    if (a === b) return true;
    const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
    // phone~phones, not phone~headphones
    if (shorter.length < 4) return longer === `${shorter}s`;
    return longer.startsWith(shorter);
  }));
}

function availableCategoryLeafParent(cat) {
  const short = String(cat?.short_label ?? cat?.shortLabel ?? '').trim();
  const label = String(cat?.label ?? '').trim();
  const full = String(cat?.full_label ?? cat?.fullLabel ?? '').trim();
  const path = (full || label)
    .split(/\s*>\s*/)
    .map((p) => p.trim())
    .filter(Boolean);
  const leaf = short || path[path.length - 1] || String(cat?.id || '');
  const parent = path.length >= 2 ? path[path.length - 2] : '';
  return { leaf, parent, id: String(cat?.id ?? '') };
}

/**
 * Match a catalog item to a category filter.
 * Filters may be an eBay id ("112529"), a live leaf ("Headphones"), or a
 * legacy/suggestion name ("Headphones & Audio"). Inventory is stored under
 * eBay leaves, so whole-string equality misses those names, and naive
 * substring matching treats "phone" as a hit inside "headphones".
 */
function itemMatchesCategoryId(item, categoryId, availableCategories) {
  const norm = (v) => (v == null || v === '' ? '' : String(v).trim().toLowerCase());
  const idStr = String(categoryId || '').trim();
  if (!idStr) return false;
  const idNorm = idStr.toLowerCase();

  const itemEbayId = norm(item.ebayCategoryId || item.ebay_category_id);
  const itemMainCat = norm(item.mainCategory || item.main_category);
  const itemCat = norm(item.category);
  const itemSub = norm(item.subcategory);
  const fv = item.firstVariant || item.first_variant;
  const nestedEbay = fv && norm(fv.ebayCategoryId || fv.ebay_category_id);

  if ((itemEbayId && itemEbayId === idNorm) || (nestedEbay && nestedEbay === idNorm)) {
    return true;
  }
  if ((itemMainCat && itemMainCat === idNorm) || (itemCat && itemCat === idNorm) || (itemSub && itemSub === idNorm)) {
    return true;
  }

  const filterTokens = categoryWordTokens(idStr);
  const itemLabelTokens = categoryWordTokens([itemMainCat, itemCat, itemSub].filter(Boolean).join(' '));
  if (categoryTokensOverlap(filterTokens, itemLabelTokens)) {
    return true;
  }

  const matchingCatIds = new Set();
  for (const cat of (availableCategories || [])) {
    const { leaf, parent, id } = availableCategoryLeafParent(cat);
    const idLower = id.toLowerCase();
    if (
      idLower === idNorm
      || categoryTokensOverlap(filterTokens, categoryWordTokens(leaf))
      || (filterTokens.length >= 2 && categoryTokensOverlap(filterTokens, categoryWordTokens(parent)))
    ) {
      matchingCatIds.add(idLower);
    }
  }

  if (itemEbayId && matchingCatIds.has(itemEbayId)) return true;
  if (nestedEbay && matchingCatIds.has(nestedEbay)) return true;
  if (itemCat && matchingCatIds.has(itemCat)) return true;
  if (itemMainCat && matchingCatIds.has(itemMainCat)) return true;
  if (itemSub && matchingCatIds.has(itemSub)) return true;
  return false;
}

function populateTrustedFinderBand() {
  const preset = TRUSTED_PRESETS.strict;
  const feedbackEl = document.getElementById('tfBandFeedback');
  const reviewsEl = document.getElementById('tfBandReviews');
  const returnsEl = document.getElementById('tfBandReturns');
  if (feedbackEl) feedbackEl.textContent = `${preset.feedback}%+`;
  if (reviewsEl) reviewsEl.textContent = `${preset.reviews}+`;
  if (returnsEl) returnsEl.textContent = 'Required';

  const form = document.getElementById('homepageTfBandForm');
  if (form && form.dataset.boundTfBand !== '1') {
    form.addEventListener('submit', (e) => {
      const input = document.getElementById('homepageTfBandInput');
      const q = input?.value?.trim();
      if (!q) {
        e.preventDefault();
        const tfPath = window.location.pathname.endsWith('.html') || window.location.pathname.includes('.html')
          ? 'trusted-finder.html'
          : '/trusted-finder.html';
        window.location.href = tfPath;
      }
    });
    form.dataset.boundTfBand = '1';
  }
}

function heroItemMatchesCategory(item, categoryId, availableCategories) {
  return itemMatchesCategoryId(item, categoryId, availableCategories);
}

function countNewItemsForCategory(categoryId, items, availableCategories) {
  const cutoff = Date.now() - HERO_CATEGORY_LOOKBACK_MS;
  return (items || []).filter((item) => {
    if (!heroItemMatchesCategory(item, categoryId, availableCategories)) return false;
    const added = item.addedAt || item.added_at || item.createdAt || item.created_at;
    if (!added) return false;
    const t = new Date(added).getTime();
    return !Number.isNaN(t) && t >= cutoff;
  }).length;
}

function getHeroCategoryLabel(catMap, id) {
  const c = catMap.get(String(id));
  return c ? (c.short_label ?? c.shortLabel ?? c.label ?? c.id ?? id) : String(id);
}

function setHeroAccountState(state) {
  const map = {
    'signed-out': document.getElementById('heroAccountStateSignedOut'),
    following: document.getElementById('heroAccountStateFollowing'),
    discover: document.getElementById('heroAccountStateDiscover'),
  };
  Object.entries(map).forEach(([key, el]) => {
    if (!el) return;
    const active = key === state;
    el.classList.toggle('hidden', !active);
    el.hidden = !active;
  });
}

async function populateHeroAccountCta() {
  const card = document.getElementById('heroAccountCtaCard');
  if (!card) return;

  const user = window.accountManager?.getCurrentUser?.() ?? null;
  const isLoggedIn = !!(user && (user.id || user.email));
  const userId = user?.id || user?.email;

  if (!isLoggedIn) {
    setHeroAccountState('signed-out');
    return;
  }

  if (!window.categoryFollowingManager) {
    window.categoryFollowingManager = new CategoryFollowingManager();
  }
  await window.categoryFollowingManager.loadFromApi();

  let followedSummary = [];
  if (window.apiService) {
    try {
      const summary = await window.apiService.getNotificationCategorySummary();
      followedSummary = summary.followed || [];
    } catch (_) { /* fallback below */ }
  }

  const followed = followedSummary.length
    ? followedSummary.map((f) => f.ebayCategoryId || f.ebay_category_id)
    : window.categoryFollowingManager.getFollowedCategories(userId);

  let availableCategories = [];
  if (window.apiService) {
    try {
      const list = await window.apiService.getAvailableCategories();
      availableCategories = Array.isArray(list) ? list : (list?.categories ?? []);
    } catch (_) { /* ignore */ }
  }
  const catMap = new Map(availableCategories.map((c) => [String(c.id ?? c), c]));

  if (followed.length > 0) {
    setHeroAccountState('following');
    const rowsEl = document.getElementById('heroAccountCategoryRows');
    const primaryLink = document.getElementById('heroAccountPrimaryLink');
    const topThree = followed.slice(0, 3);
    const summaryById = new Map(
      followedSummary.map((f) => [String(f.ebayCategoryId || f.ebay_category_id), f]),
    );
    if (rowsEl) {
      rowsEl.innerHTML = topThree.map((id) => {
        const summaryRow = summaryById.get(String(id));
        const label = escapeHtml(summaryRow?.label || getHeroCategoryLabel(catMap, id));
        const unread = summaryRow?.unreadCount ?? summaryRow?.unread_count ?? 0;
        const countLabel = unread === 1 ? '1 new' : `${unread} new`;
        return `<li class="hero-account-category-row"><span class="hero-account-category-name">${label}</span><span class="hero-account-category-count">${escapeHtml(countLabel)}</span></li>`;
      }).join('');
    }
    if (primaryLink) {
      primaryLink.href = 'account.html#dashboard';
      const linkText = primaryLink.querySelector('.hero-account-primary-text');
      if (linkText) linkText.textContent = 'View your feed';
    }
    return;
  }

  setHeroAccountState('discover');
  const suggestEl = document.getElementById('heroAccountSuggestRows');
  if (!suggestEl) return;

  let suggestIds = [];
  if (window.apiService) {
    try {
      const settings = await window.apiService.getHomepageSettingsActive();
      const rawIds = settings?.topCategoryIds ?? settings?.top_category_ids ?? [];
      suggestIds = Array.isArray(rawIds) ? rawIds.slice(0, 3).map(String) : [];
    } catch (_) { /* ignore */ }
  }
  if (suggestIds.length === 0 && availableCategories.length > 0) {
    suggestIds = availableCategories.slice(0, 3).map((c) => String(c.id ?? c));
  }

  suggestEl.innerHTML = suggestIds.map((id) => {
    const label = escapeHtml(getHeroCategoryLabel(catMap, id));
    return `<li class="hero-account-suggest-row"><span class="hero-account-category-name">${label}</span><button type="button" class="hero-account-follow-btn" data-hero-follow-category="${escapeHtml(id)}">+ Follow</button></li>`;
  }).join('');

  suggestEl.querySelectorAll('[data-hero-follow-category]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const catId = btn.getAttribute('data-hero-follow-category');
      if (!catId || !userId) return;
      try {
        await window.categoryFollowingManager.followCategory(userId, catId);
        populateHeroAccountCta();
      } catch (_) { /* ignore */ }
    });
  });
}

// Main function to populate all marketplace components
async function populateMarketplace() {
  if (!window.location.pathname.includes('index.html') && window.location.pathname !== '/' && !window.location.pathname.endsWith('/')) {
    return; // Only run on homepage
  }

  // Trending has its own small endpoint; start it now so it never waits on
  // the catalog download below.
  const trendingPromise = populateTrendingWidget();

  const itemsLoadPromise =
    searchData.items.length === 0 ? loadItemsFromStorage() : Promise.resolve();

  // Featured deal uses apiService + homepage endpoints when available—run in parallel with
  // items hydration so the hero image request starts sooner (offline fallback still needs items).
  if (window.apiService) {
    await Promise.all([itemsLoadPromise, populateFeaturedDeal()]);
  } else {
    await itemsLoadPromise;
    await populateFeaturedDeal();
  }

  populateContinueBrowsing();
  await populateTopCategories();
  // Quadrants only if DOM present (removed from homepage chrome)
  if (document.getElementById('quadrantGamingGrid')) {
    await populateQuadrantsFromBuckets();
  }
  await trendingPromise;
  populateQuickLinks();
  initializeDealAlertsForm();
  populateTrustFloorCard();
  populateTrustedFinderBand();
  await populateHeroAccountCta();
}

// Helper function to format price (simple version for marketplace components)
function formatPrice(price) {
  if (!price || price === 'N/A') return 'N/A';
  if (typeof price === 'number') {
    return '$' + price.toFixed(2);
  }
  if (typeof price === 'string') {
    // If already has $, return as is
    if (price.startsWith('$')) {
      return price;
    }
    // Try to parse as number
    const num = parseFloat(price);
    if (!isNaN(num)) {
      return '$' + num.toFixed(2);
    }
    // Return as is if can't parse
    return price;
  }
  return 'N/A';
}

document.addEventListener('DOMContentLoaded', function() {
  // Use event delegation for dynamically added images
  document.addEventListener('error', function(e) {
    if (e.target.tagName === 'IMG' && e.target.closest('.product-card')) {
      handleProductImageError(e.target);
    }
  }, true);
});

// Handle share button clicks
// Handle share button clicks (must be in global scope for onclick)
window.handleShareClick = async function(button, platform) {
  if (!window.socialShareManager) {
    logger.error('SocialShareManager not available');
    return;
  }
  
  const productDataStr = button.closest('.product-share-buttons').getAttribute('data-product-data');
  if (!productDataStr) {
    logger.error('Product data not found');
    return;
  }
  
  try {
    const product = JSON.parse(decodeURIComponent(productDataStr));
    
    switch (platform) {
      case 'share':
        // Hybrid share: use native share on mobile, fallback to email on desktop
        if (navigator.share) {
          await window.socialShareManager.shareNative(product);
        } else {
          window.socialShareManager.shareEmail(product);
        }
        break;
      case 'native':
        window.socialShareManager.shareNative(product);
        break;
      case 'facebook':
        window.socialShareManager.shareFacebook(product);
        break;
      case 'twitter':
        window.socialShareManager.shareTwitter(product);
        break;
      case 'email':
        window.socialShareManager.shareEmail(product);
        break;
      case 'copy':
        window.socialShareManager.copyLink(product);
        break;
      default:
        logger.error('Unknown share platform:', platform);
    }
  } catch (error) {
    logger.error('Error parsing product data:', error);
  }
};

// Setup deals tabs on homepage
function setupDealsTabs() {
  const tabs = document.querySelectorAll('.deals-tab');
  tabs.forEach(tab => {
    tab.addEventListener('click', function() {
      const tabName = this.getAttribute('data-tab');
      switchDealsTab(tabName);
    });
  });
  
  // Set initial active tab
  const activeTab = localStorage.getItem('dealsTab') || 'hot';
  const tabButtons = document.querySelectorAll('.deals-tab');
  tabButtons.forEach(btn => {
    const isCurrent = btn.getAttribute('data-tab') === activeTab;
    btn.classList.toggle('active', isCurrent);
    btn.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');
  });
}

/**
 * The Feed's column sort: the Rows header, and the select that stands in for it
 * in Tiles view.
 *
 * Two controls, one state. The header only exists in Rows view and is rewritten
 * on every render, so its clicks are delegated from the grid rather than bound
 * to buttons that will not survive the next fetch; the select is in the page and
 * is repainted from the state like the floor selector is.
 *
 * Neither control re-renders anything itself. Both set the sort, and the
 * onSortChange listener redraws the feed through the same path a filter change
 * takes -- so the sort composes with the category filter and the quick chips
 * instead of racing them.
 */
function setupFeedSort() {
  const grid = document.getElementById('dealsGrid');
  const select = document.getElementById('feedSort');
  if (!grid && !select) return;

  const paint = (sort) => {
    if (!select) return;
    select.value = sort.key === 'featured' ? 'featured' : `${sort.key}:${sort.dir}`;
  };

  if (grid && !grid.dataset.boundFeedSort) {
    grid.addEventListener('click', (event) => {
      const button = event.target.closest('button[data-sortkey]');
      if (!button || !grid.contains(button)) return;
      // The header sits inside `.rows`, which is not a link -- but the rows
      // under it are, and a stray bubble to one of those would navigate.
      event.preventDefault();
      toggleSort(button.dataset.sortkey);
    });
    grid.dataset.boundFeedSort = '1';
  }

  if (select && !select.dataset.boundFeedSort) {
    select.addEventListener('change', () => {
      const [key, dir] = String(select.value).split(':');
      setSort(key, dir);
    });
    select.dataset.boundFeedSort = '1';
  }

  paint(getSort());
  onSortChange((sort) => {
    paint(sort);
    // Same entry point the category pills use, so every filter still applies.
    if (allDealsData && allDealsData.length) filterAndRenderDeals();
  });
}

// Initialize category filters on homepage
function initializeCategoryFilters() {
  const filtersContainer = document.getElementById('categoryFilters');
  
  if (!filtersContainer) {
    return; // Not on homepage
  }
  
  // Dynamically populate category filter buttons from API
  if (window.apiService) {
    populateCategoryFilterButtons();
  }

  // Single delegated handler avoids duplicate listeners and supports dynamic pills.
  if (!filtersContainer.dataset.boundCategoryFilters) {
    filtersContainer.addEventListener('click', (e) => {
      const btn = e.target.closest('.category-filter-btn');
      if (!btn) return;
      const category = btn.getAttribute('data-category');
      setActiveCategoryFilter(category);
      filterAndRenderDeals();
    });
    filtersContainer.dataset.boundCategoryFilters = '1';
  }
}

let topbarSearchShortcutBound = false;

function initializeTopbarSearchAffordance() {
  const focusPrimarySearch = () => {
    const searchInput = getFirstVisibleSearchInput();
    if (searchInput && typeof searchInput.focus === 'function') {
      searchInput.focus();
      searchInput.select?.();
      return true;
    }
    return false;
  };

  const topbarSearch = document.querySelector('.topbar-search-affordance');
  if (topbarSearch && topbarSearch.dataset.boundTopbarSearch !== '1') {
    topbarSearch.addEventListener('click', (event) => {
      if (focusPrimarySearch()) {
        event.preventDefault();
      }
    });
    topbarSearch.dataset.boundTopbarSearch = '1';
  }

  if (!topbarSearchShortcutBound) {
    document.addEventListener('keydown', (event) => {
      const usesShortcut = (event.metaKey || event.ctrlKey) && String(event.key).toLowerCase() === 'k';
      if (!usesShortcut) return;
      if (focusPrimarySearch()) {
        event.preventDefault();
      }
    });
    topbarSearchShortcutBound = true;
  }
}

function setActiveHomepageQuickChip(chip) {
  currentHomepageQuickChip = chip || 'for-you';
  document.querySelectorAll('.homepage-quick-chip').forEach((btn) => {
    const isCurrent = btn.getAttribute('data-chip') === currentHomepageQuickChip;
    btn.classList.toggle('active', isCurrent);
    // A link chip (Like-new is a crawlable href) is navigation, not a toggle;
    // aria-pressed on an anchor is meaningless, so only buttons get it.
    if (btn.tagName === 'BUTTON') {
      btn.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');
    }
  });
}

function applyHomepageQuickChipConstraints(items) {
  let constrained = Array.isArray(items) ? [...items] : [];

  if (currentQuickChipCondition) {
    const conditionNeedle = currentQuickChipCondition.toLowerCase();
    constrained = constrained.filter((item) => {
      const value = String(item?.condition || '').toLowerCase();
      return value.includes(conditionNeedle);
    });
  }

  if (Number.isFinite(currentQuickChipMaxPrice) && currentQuickChipMaxPrice > 0) {
    constrained = constrained.filter((item) => parsePrice(item?.price) <= currentQuickChipMaxPrice);
  }

  return constrained;
}

async function initializeHomepageQuickChips() {
  const chipsContainer = document.getElementById('homepageQuickChipRow');
  if (!chipsContainer || chipsContainer.dataset.boundQuickChips === '1') return;

  chipsContainer.addEventListener('click', async (e) => {
    const chip = e.target.closest('.homepage-quick-chip');
    if (!chip) return;

    const chipName = chip.getAttribute('data-chip') || 'for-you';
    const tabName = chip.getAttribute('data-tab');
    const category = chip.getAttribute('data-category');
    const condition = chip.getAttribute('data-condition');
    const maxPrice = Number(chip.getAttribute('data-max-price'));

    setActiveHomepageQuickChip(chipName);
    currentQuickChipCondition = condition ? String(condition).toLowerCase() : '';
    currentQuickChipMaxPrice = Number.isFinite(maxPrice) ? maxPrice : null;

    if (category) {
      setActiveCategoryFilter(category);
    }

    if (tabName && ['hot', 'new'].includes(tabName)) {
      await switchDealsTab(tabName);
      return;
    }

    // Safe fallback for unsupported chips: keep current tab/filter state and re-render.
    filterAndRenderDeals();
  });

  setActiveHomepageQuickChip(currentHomepageQuickChip);
  chipsContainer.dataset.boundQuickChips = '1';
}

function homepageItemMatchesCategory(item, categoryId) {
  return itemMatchesCategoryId(item, categoryId, homepageAvailableCategories);
}

// Populate category filter buttons from available categories API
async function populateCategoryFilterButtons() {
  const filtersContainer = document.getElementById('categoryFilters');
  if (!filtersContainer) return;
  
  const categories = window.apiService ? await apiService.getAvailableCategories() : [];
  homepageAvailableCategories = Array.isArray(categories) ? categories : [];

  // Keep static "All Categories" button, replace only dynamic ones.
  filtersContainer.querySelectorAll('.category-filter-btn[data-category]:not([data-category="all"])').forEach(btn => btn.remove());
  
  categories.forEach(cat => {
    const btn = document.createElement('button');
    btn.className = 'category-filter-btn';
    btn.setAttribute('data-category', cat.id);
    const pillText = cat.short_label ?? cat.shortLabel ?? cat.label ?? cat.id;
    const { html, family } = getCategoryIcon(cat, { size: 18, preferLeaf: true });
    btn.innerHTML = `<span class="category-filter-icon" data-family="${escapeHtml(family)}">${html}</span> ${escapeHtml(pillText)} <span class="filter-count"></span>`;
    if (cat.full_label) btn.title = cat.full_label;
    
    filtersContainer.appendChild(btn);
  });

  // Refresh counts/visibility after rebuilding pills.
  updateFilterCounts(allDealsData);
  setActiveCategoryFilter(currentCategoryFilter);
}

// Set active category filter
function setActiveCategoryFilter(category) {
  currentCategoryFilter = category;
  
  // Update UI
  document.querySelectorAll('.category-filter-btn').forEach(btn => {
    btn.classList.remove('active');
  });
  
  const activeBtn = document.querySelector(`[data-category="${category}"]`);
  if (activeBtn) {
    activeBtn.classList.add('active');
  }
}

// Filter and render deals based on current filters
function filterAndRenderDeals() {
  if (!allDealsData || allDealsData.length === 0) {
    logger.info('No deals data available to filter');
    return;
  }
  
  let filteredDeals = [...allDealsData];
  
  // Apply category filter (eBay category id or legacy main_category/category)
  if (currentCategoryFilter !== 'all') {
    filteredDeals = filteredDeals.filter(item => homepageItemMatchesCategory(item, currentCategoryFilter));
    logger.info(`Filtered to ${filteredDeals.length} items in category: ${currentCategoryFilter}`);
  }

  filteredDeals = applyHomepageQuickChipConstraints(filteredDeals);
  
  // Render filtered deals
  renderItems(filteredDeals, 'dealsGrid');
  
  // Update filter counts
  updateFilterCounts(allDealsData);
}
// Update product counts in category filter buttons
function updateFilterCounts(deals) {
  if (!deals || deals.length === 0) {
    return;
  }
  
  // Update count badges
  document.querySelectorAll('.category-filter-btn').forEach(btn => {
    const category = btn.getAttribute('data-category');
    const countSpan = btn.querySelector('.filter-count');
    
    if (!countSpan) return;
    
    if (category === 'all') {
      countSpan.textContent = `(${deals.length})`;
      btn.style.display = ''; // Always show "All"
    } else {
      const count = deals.reduce((acc, item) => acc + (homepageItemMatchesCategory(item, category) ? 1 : 0), 0);
      if (count > 0 || category === currentCategoryFilter) {
        countSpan.textContent = `(${count})`;
        btn.style.display = '';
      } else {
        countSpan.textContent = '';
        btn.style.display = 'none'; // Hide categories with no products
      }
    }
  });
}

// Switch between Hot and New deals tabs
let isSwitchingTab = false; // Guard to prevent concurrent tab switches
async function switchDealsTab(tabName) {
  if (!['hot', 'new'].includes(tabName)) return;
  
  // Prevent concurrent calls
  if (isSwitchingTab) {
    logger.info(`switchDealsTab: Already switching tabs, ignoring duplicate call to ${tabName}`);
    return;
  }
  
  isSwitchingTab = true;
  
  try {
    // Update current deal type
    currentDealType = tabName;
    
    // Update active tab UI
    const tabButtons = document.querySelectorAll('.deals-tab');
    tabButtons.forEach(btn => {
      const isCurrent = btn.getAttribute('data-tab') === tabName;
      btn.classList.toggle('active', isCurrent);
      // The Console seg is a pressed-state control; the class alone styles it
      // but says nothing to a screen reader.
      btn.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');
    });
    
    // Save preference
    localStorage.setItem('dealsTab', tabName);
    
    // Clear the container first to ensure clean state and provide immediate feedback
    const container = document.getElementById('dealsGrid');
    if (container) {
      setRegionBusy(container, true);
      container.innerHTML = generateSkeletonLoader(6);
    }
    
    // Reload items from API/storage to ensure fresh data (force refresh)
    logger.info(`switchDealsTab: Switching to ${tabName} tab, forcing refresh...`);
    const itemsCount = await loadItemsFromStorage(true);
    
    // Verify items are loaded and match the returned count
    if (!searchData.items || !Array.isArray(searchData.items)) {
      logger.warn('switchDealsTab: searchData.items is not properly initialized after load');
      searchData.items = [];
    }
    
    // Verify count matches (helps catch race conditions)
    if (itemsCount !== searchData.items.length) {
      logger.warn(`switchDealsTab: Count mismatch - loadItemsFromStorage returned ${itemsCount} but searchData.items.length is ${searchData.items.length}`);
      // Wait a tick and re-check (race condition workaround)
      await new Promise(resolve => setTimeout(resolve, 10));
    }
    
    logger.info(`switchDealsTab: Loaded ${searchData.items.length} items after refresh (expected ${itemsCount})`);
    
    // Get items and sort
    let itemsToDisplay = [...searchData.items];
    logger.info(`switchDealsTab: Before sorting: ${itemsToDisplay.length} items`);
    
    if (tabName === 'new') {
      itemsToDisplay = sortByNewest(itemsToDisplay);
      logger.info(`switchDealsTab: Sorted by newest: ${itemsToDisplay.length} items`);
    } else {
      itemsToDisplay = orderHotDealsWithCategoryMix(itemsToDisplay);
      logger.info(`switchDealsTab: Hot deals with category mix: ${itemsToDisplay.length} items`);
    }
    
    // Store the sorted deals for filtering
    allDealsData = itemsToDisplay;
    
    // Apply category filter and render
    if (currentCategoryFilter !== 'all') {
      filterAndRenderDeals();
    } else {
      itemsToDisplay = applyHomepageQuickChipConstraints(itemsToDisplay);
      // Re-render all items
      logger.info(`switchDealsTab: Rendering ${itemsToDisplay.length} items`);
      renderItems(itemsToDisplay, 'dealsGrid');
      // Update filter counts
      updateFilterCounts(itemsToDisplay);
    }
  } catch (error) {
    logger.error('Error loading items for tab switch:', error);
    // Fallback: use current items
    let itemsToDisplay = [...(searchData.items || [])];
    if (tabName === 'new') {
      itemsToDisplay = sortByNewest(itemsToDisplay);
    } else {
      itemsToDisplay = orderHotDealsWithCategoryMix(itemsToDisplay);
    }
    allDealsData = itemsToDisplay;
    
    if (currentCategoryFilter !== 'all') {
      filterAndRenderDeals();
    } else {
      itemsToDisplay = applyHomepageQuickChipConstraints(itemsToDisplay);
      renderItems(itemsToDisplay, 'dealsGrid');
      updateFilterCounts(itemsToDisplay);
    }
  } finally {
    isSwitchingTab = false;
  }
}

/**
 * Shared loading-region busy flag for customer grids (SITE-UX-17).
 * Prefer with generateSkeletonLoader / .empty-state-tip — not admin skeletons.
 */
function setRegionBusy(region, busy) {
  if (!region) return;
  region.setAttribute('aria-busy', busy ? 'true' : 'false');
}

/*
 * Home feed cap.
 *
 * The feed used to render every deal it had — 97 cards, which on a phone was a
 * single 220px column roughly 46,000px deep. Cards past the cap stay in the DOM
 * (still crawlable, and revealing them is instant) but leave the visual feed
 * until "Show more" is pressed. They are display:none, so the view-tracking
 * IntersectionObserver never fires for them and impressions stay honest.
 */
// The homepage used to download the whole catalog before it could render a
// feed that shows 24 cards. It now asks for this many rows first, renders
// them, and fetches the remainder in the background -- the category filters
// and their counts read every row, so the first page cannot simply be the cap.
function isHomepagePath() {
  const path = window.location.pathname;
  return path === '/' || path.endsWith('/') || path.includes('index.html');
}

const HOME_FIRST_PAGE_LIMIT = 48;
let homeFeedToppedUp = false;

function homeFeedItemKey(item) {
  return item.productId || item.product_id || `${item.name || ''}|${item.price || ''}`;
}

/**
 * The full set, ordered so that everything already on screen keeps its
 * position and the newly arrived rows follow in the active tab's order.
 * Re-running the ordering over the whole set instead would reshuffle the
 * visible feed under the reader -- the hot ordering is deliberately random.
 */
function orderTopUpPreservingDisplayed(displayed, all, activeTab) {
  const position = new Map(displayed.map((item, index) => [homeFeedItemKey(item), index]));
  const kept = [];
  const arrived = [];
  for (const item of all) {
    (position.has(homeFeedItemKey(item)) ? kept : arrived).push(item);
  }
  kept.sort((a, b) => position.get(homeFeedItemKey(a)) - position.get(homeFeedItemKey(b)));
  const ordered = activeTab === 'new'
    ? sortByNewest(arrived)
    : orderHotDealsWithCategoryMix(arrived);
  return [...kept, ...ordered];
}

async function topUpHomeFeed() {
  if (homeFeedToppedUp) return;
  homeFeedToppedUp = true;

  const displayed = [...allDealsData];
  try {
    await loadItemsFromStorage(true); // no limit: the server's full default
  } catch (error) {
    logger.warn('Home feed top-up failed; keeping the first page', error);
    return;
  }

  const all = searchData.items || [];
  if (all.length <= displayed.length) return;

  const activeTab = localStorage.getItem('dealsTab') || 'hot';
  allDealsData = orderTopUpPreservingDisplayed(displayed, all, activeTab);
  // Re-render through the normal path so the active category filter, the quick
  // chips, the counts and the show-more cap all re-apply together.
  filterAndRenderDeals();
  logger.info(`Home feed topped up to ${all.length} items`);
}

function scheduleHomeFeedTopUp() {
  if (homeFeedToppedUp) return;
  // Only worth doing when the first page was actually capped.
  if ((searchData.items || []).length < HOME_FIRST_PAGE_LIMIT) return;
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => { topUpHomeFeed(); }, { timeout: 2500 });
  } else {
    setTimeout(() => { topUpHomeFeed(); }, 1200);
  }
}

const HOME_FEED_CAP_MOBILE = 16;
const HOME_FEED_CAP_DESKTOP = 24;
let homeFeedExpanded = false;

function homeFeedCap() {
  return window.matchMedia('(max-width: 800px)').matches
    ? HOME_FEED_CAP_MOBILE
    : HOME_FEED_CAP_DESKTOP;
}

/**
 * Every shape a listing can take in a feed container.
 *
 * The cap and the view tracker both count listings, and under the Console
 * shell a listing is a `.tile` or a `.row` rather than a `.product-card`. A
 * selector that named only the legacy card left the Console feed uncapped --
 * every fetched listing in one scroll, with no "Show more" to collapse it.
 */
const CARD_SELECTOR = '.product-card, .tile, .rows .row:not(.h)';

function applyHomeFeedCap() {
  const container = document.getElementById('dealsGrid');
  const wrap = document.getElementById('dealsShowMoreWrap');
  const btn = document.getElementById('dealsShowMoreBtn');
  if (!container || !wrap) return;

  const cards = container.querySelectorAll(CARD_SELECTOR);
  const cap = homeFeedCap();
  const capped = !homeFeedExpanded && cards.length > cap;

  container.toggleAttribute('data-cap', capped);
  cards.forEach((card, index) => {
    if (capped && index >= cap) card.setAttribute('data-beyond-cap', '1');
    else card.removeAttribute('data-beyond-cap');
  });

  wrap.hidden = !capped;
  if (btn) {
    // Only meaningful while capped; computing it otherwise yields "Show -23 more".
    if (capped) btn.textContent = `Show ${cards.length - cap} more deals`;
    if (btn.dataset.boundShowMore !== '1') {
      btn.addEventListener('click', () => {
        homeFeedExpanded = true;
        applyHomeFeedCap();
        // Land focus on the first newly revealed card so keyboard users are not
        // dropped back at the top of the feed.
        const revealed = container.querySelectorAll(CARD_SELECTOR)[cap];
        revealed?.querySelector('a, button')?.focus();
      });
      btn.dataset.boundShowMore = '1';
    }
  }
}

// The cap differs either side of 800px, so a rotate or resize has to re-run it.
window.matchMedia('(max-width: 800px)').addEventListener('change', () => {
  if (document.getElementById('dealsGrid')) applyHomeFeedCap();
});

/** Did the trust floor account for an empty feed? */
function floorEmptiedTheFeed() {
  const counts = window.apiService && window.apiService.lastTrustFloor;
  return !!counts && !counts.clears && ((counts.below || 0) + (counts.unrated || 0)) > 0;
}

/**
 * What an empty grid says.
 *
 * "No items found in this category" is the right sentence when the catalog is
 * thin, and the wrong one when the trust floor did the emptying -- the visitor
 * then has a control that would fix it and no reason to think so. Worse, most
 * of what a floor hides today is hidden for want of a seller rating rather
 * than for a bad one, and blaming the sellers for that would be a lie.
 *
 * @returns {string}
 */
function emptyFeedHtml() {
  const counts = window.apiService && window.apiService.lastTrustFloor;
  const hidden = counts ? (counts.below || 0) + (counts.unrated || 0) : 0;
  if (!counts || !hidden) {
    return '<div class="no-items"><p>No items found in this category.</p>'
      + '<p class="empty-state-tip">Try another category or clear filters to see more deals.</p></div>';
  }
  const name = FLOOR_NAMES[counts.floor] || counts.floor;
  const parts = [];
  if (counts.below) parts.push(`${counts.below} below it`);
  if (counts.unrated) parts.push(`${counts.unrated} from sellers we hold no rating for`);
  return '<div class="no-items">'
    + `<p>No listings clear the ${escapeHtml(name)} trust floor right now.</p>`
    + `<p class="empty-state-tip">${counts.total} listing${counts.total === 1 ? '' : 's'} hidden — `
    + `${parts.join(', ')}. Lower the floor in the top bar to see them.</p>`
    + '</div>';
}

export function renderItems(items, containerId) {
  const container = document.getElementById(containerId);
  if (!container) {
    logger.warn(`Container with id "${containerId}" not found`);
    return;
  }
  
  // Read before anything writes: the empty-state branch below has to know
  // whether the server rendered this feed, and a clear-for-clean-state here
  // would destroy the very markup it is asked about. There is no clear to do --
  // every branch below assigns `innerHTML` wholesale.
  const hasServerRenderedCards = !!container.querySelector('[data-ssr-card]');

  // Skeleton and empty states return early below; the button must not survive
  // from the previous render into a feed that no longer has hidden cards.
  if (containerId === 'dealsGrid') {
    const showMoreWrap = document.getElementById('dealsShowMoreWrap');
    if (showMoreWrap) showMoreWrap.hidden = true;
  }

  if (!items || items.length === 0) {
    // Show skeleton loader while loading -- but only when empty really might
    // mean "not yet". A floored fetch that came back with nothing is a
    // finished answer, and treating it as loading would leave the
    // server-rendered cards on screen: those were rendered at no floor, so the
    // page would be showing listings that do not clear the floor it says it is
    // applying.
    if (containerId === 'dealsGrid' && !floorEmptiedTheFeed()
        && (!searchData.items || searchData.items.length === 0)) {
      // The home feed now ships server-rendered. Those cards are real content
      // and are what a crawler that runs JS reads, so blanking them for a
      // skeleton would empty the page during the fetch -- the same mistake
      // that got /search.html classified Soft 404.
      if (!hasServerRenderedCards) {
        container.innerHTML = generateSkeletonLoader(6);
      }
      setRegionBusy(container, true);
      return;
    }
    setRegionBusy(container, false);
    container.innerHTML = emptyFeedHtml();
    // The strip and the status line describe the same fetch the grid does, so
    // they are repainted on the empty answer too -- that is the one where the
    // counts matter most.
    paintFeedStats(0);
    return;
  }
  
  // Under the Console shell a listing is a .tile or a .row, and the same
  // renderers the server used produce it -- see console-cards.js, which is
  // diffed against the Django partials so the cards do not visibly rearrange
  // when this replaces the server-rendered ones.
  if (document.body && document.body.classList.contains('console')) {
    let cards = items
      .map((item) => {
        // Same gate renderProductCard applies: a listing with no real image is
        // dropped rather than drawn as a broken-image icon. The server-rendered
        // half filters on image_url__startswith='http' for the same reason.
        const src = getSafeImageSrc(item.imageUrl || item.image_url || '');
        if (!/^https?:\/\//.test(String(src))) return null;
        return toCard(item, { url: getProductDetailUrl(item), imageUrl: src });
      })
      .filter(Boolean);
    // The Feed is the one surface with a column sort. Find has its own sort
    // (AdvancedSearch.sortItems, which runs before the items get here) and
    // applying both would have two orderings fighting over the same list.
    const feedSort = containerId === 'dealsGrid' ? getSort() : null;
    if (feedSort) cards = sortCards(cards, feedSort);
    setRegionBusy(container, false);
    container.innerHTML = cards.length
      ? (getView() === 'rows' ? consoleRows(cards, feedSort) : consoleGrid(cards))
      : emptyFeedHtml();
    attachButtonAnimations(container);
    paintFeedStats(cards.length);
    // The same two jobs the legacy tail below does, and for the same reasons:
    // count a listing as seen once it scrolls into view, and collapse a fresh
    // feed back to the cap so it is not one 46,000px scroll.
    observeProductCards();
    if (containerId === 'dealsGrid') {
      homeFeedExpanded = false;
      applyHomeFeedCap();
    }
    return;
  }

  // Roughly the first two rows at desktop; beyond that eager loading just
  // competes with the hero for bandwidth before anything is on screen.
  const firstViewportEagerImages = 8;
  const html = items
    .map((item, idx) =>
      renderProductCard(item, {
        imageLoading: idx < firstViewportEagerImages ? 'eager' : 'lazy',
      })
    )
    .filter(Boolean)
    .join('');
  if (!html.trim()) {
    setRegionBusy(container, false);
    container.innerHTML = emptyFeedHtml();
    return;
  }
  container.innerHTML = html;
  setRegionBusy(container, false);
  
  // Add button animations
  attachButtonAnimations(container);
  
  // Restore vote states after rendering (in case voteManager wasn't ready during renderProductCard)
  // Call immediately and the function will retry if managers aren't ready
  restoreVoteStates(container);
  
  // Re-observe product cards for view tracking
  observeProductCards();

  // A fresh render is a fresh feed: collapse back to the cap.
  if (containerId === 'dealsGrid') {
    homeFeedExpanded = false;
    applyHomeFeedCap();
  }
}

// Generate skeleton loader HTML (shared: deals grid + AdvancedSearch)
function generateSkeletonLoader(count = 6) {
  // Console draws the real grid in outline at the real tile proportions, so
  // nothing on the page moves when the data lands. B8: no spinner, no copy.
  if (document.body && document.body.classList.contains('console')) {
    return `<div class="grid">${consoleSkeletonGrid(count)}</div>`;
  }
  const skeletons = Array(count).fill(0).map(() => `
    <div class="skeleton-product-card">
      <div class="skeleton-image"></div>
      <div class="skeleton-content">
        <div class="skeleton-line skeleton-title"></div>
        <div class="skeleton-line skeleton-price"></div>
      </div>
    </div>
  `).join('');
  return `<div class="deals-grid skeleton-grid">${skeletons}</div>`;
}

// Attach button animations
function attachButtonAnimations(container) {
  // Add ripple effect to buttons
  const buttons = container.querySelectorAll('.search-button, .add-item-btn, .featured-deal-cta, .quick-filter-pill, .buy-button, .view-details-button');
  buttons.forEach(button => {
    button.classList.add('button-ripple', 'button-bounce');
    button.addEventListener('click', function(e) {
      // Create ripple effect
      const ripple = document.createElement('span');
      ripple.classList.add('button-ripple');
      this.appendChild(ripple);
      setTimeout(() => ripple.remove(), 600);
    });
  });
}

/**
 * Restore vote button states after items are rendered
 * Note: Vote states now come from the backend API, so this function is primarily
 * a safety check. The vote states should already be correct from renderProductCard().
 */
function restoreVoteStates(container) {
  if (!container) {
    return;
  }
  
  // Since vote states now come from the API and are set during renderProductCard(),
  // this function is no longer needed. However, we keep it as a minimal safety check
  // in case there are any edge cases where the HTML doesn't match the API data.
  // The vote button active states should already be correct from the initial render.
  
  // Optional: Could add a verification step here if needed, but for now we'll keep it minimal
  // since the API is the source of truth and renderProductCard() uses it directly.
}


function filterItemsByCategory(categoryName) {
  if (!categoryName) {
    return searchData.items;
  }
  
  return searchData.items.filter(item => {
    const categoryMatch = item.category && item.category.toLowerCase() === categoryName.toLowerCase();
    return categoryMatch;
  });
}

function filterItemsByCondition(conditionName) {
  if (!conditionName) {
    return searchData.items;
  }
  
  return searchData.items.filter(item => {
    const conditionMatch = item.condition && item.condition.toLowerCase() === conditionName.toLowerCase();
    return conditionMatch;
  });
}

// Calculate hotness score for an item (activity + freshness; floor so zero-vote items still participate)
function calculateHotnessScore(item) {
  if (!item) return 0;

  const voteScore = Math.max(0, Number(item.voteScore) || 0);
  const clickCount = Math.max(0, Number(item.clickCount) || 0);
  const activity = voteScore + 0.5 * clickCount;
  const addedRaw = item.addedAt || item.added_at || item.createdAt || item.created_at;
  const addedAt = addedRaw ? new Date(addedRaw) : new Date();
  const now = new Date();
  const hoursSinceSubmission = Math.max(0, (now - addedAt) / (1000 * 60 * 60));

  // Formula: (activity + 1) / (1 + hoursSinceSubmission^1.5)
  // Floor of +1 ensures every item has weight > 0 for weighted shuffle
  return (activity + 1) / (1 + Math.pow(hoursSinceSubmission, 1.5));
}

// Deterministic sort by hotness (Featured Deal fallback / pick-best)
function sortByHotness(items) {
  return [...items].sort((a, b) => {
    const hotnessA = calculateHotnessScore(a);
    const hotnessB = calculateHotnessScore(b);
    return hotnessB - hotnessA; // Descending order
  });
}

// Weighted random shuffle favoring higher hotness (Efraimidis–Spirakis); used for Hot deals grid
function weightedShuffleByHotness(items) {
  return [...items]
    .map((item) => {
      const weight = Math.max(calculateHotnessScore(item), Number.EPSILON);
      const key = Math.pow(Math.random(), 1 / weight);
      return { item, key };
    })
    .sort((a, b) => b.key - a.key)
    .map(({ item }) => item);
}

// Hot deals order: weighted hotness shuffle, then round-robin by category so the top row mixes
export function orderHotDealsWithCategoryMix(items) {
  if (!Array.isArray(items) || items.length === 0) return [];

  const shuffled = weightedShuffleByHotness(items);
  const byCategory = new Map();
  for (const item of shuffled) {
    const key = getTrendingCategoryKey(item);
    if (!byCategory.has(key)) byCategory.set(key, []);
    byCategory.get(key).push(item);
  }

  // Prefer starting rotation with hotter first-item categories
  const queues = [...byCategory.values()].sort(
    (a, b) => calculateHotnessScore(b[0]) - calculateHotnessScore(a[0])
  );

  const result = [];
  let progressed = true;
  while (progressed) {
    progressed = false;
    for (const queue of queues) {
      if (queue.length > 0) {
        result.push(queue.shift());
        progressed = true;
      }
    }
  }
  return result;
}

// Sort items by newest
export function sortByNewest(items) {
  return [...items].sort((a, b) => {
    const dateA = a.addedAt ? new Date(a.addedAt).getTime() : 0;
    const dateB = b.addedAt ? new Date(b.addedAt).getTime() : 0;
    return dateB - dateA; // Newest first
  });
}
// Initialize item rendering on public pages
async function initializeItemDisplay() {
  // Items should already be loaded by DOMContentLoaded with force refresh
  // But verify they're loaded and valid
  logger.info('initializeItemDisplay: Starting - items should already be loaded from API...');
  
  // Ensure searchData.items exists and is an array
  if (!searchData || !searchData.items || !Array.isArray(searchData.items)) {
    logger.warn('initializeItemDisplay: searchData.items is not properly initialized, forcing reload...');
    searchData.items = [];
    await loadItemsFromStorage(true); // Force refresh if not loaded
  }
  
  // If items array is empty, try one more forced reload (defensive check)
  if (searchData.items.length === 0) {
    logger.debug('initializeItemDisplay: No items found, forcing fresh load from API...');
    await loadItemsFromStorage(true); // Force refresh
  }
  
  logger.info(`initializeItemDisplay: Ready to display ${searchData.items.length} items`);
  
  // Check if we're on index.html
  if (window.location.pathname.includes('index.html') || window.location.pathname === '/' || window.location.pathname.endsWith('/')) {
    const dealsContainer = document.getElementById('dealsGrid');
    if (!dealsContainer) {
      logger.warn('initializeItemDisplay: dealsGrid container not found');
      return;
    }
    
    // Get active tab (default to 'hot')
    const activeTab = localStorage.getItem('dealsTab') || 'hot';
    
    // Create a copy of items to avoid mutating the original array
    let itemsToDisplay = [...searchData.items];
    
    // Log for debugging
    logger.info(`initializeItemDisplay: Found ${itemsToDisplay.length} items to display`);
    
    if (activeTab === 'new') {
      itemsToDisplay = sortByNewest(itemsToDisplay);
    } else {
      itemsToDisplay = orderHotDealsWithCategoryMix(itemsToDisplay);
    }
    
    // Store items for filtering
    allDealsData = itemsToDisplay;
    
    // Display items on index page
    renderItems(itemsToDisplay, 'dealsGrid');
    
    // Setup tab switching
    setupDealsTabs();
    setupFeedSort();
    
    initializeTopbarSearchAffordance();

    // Initialize category filters
    initializeCategoryFilters();
    await initializeHomepageQuickChips();
    
    // In-stock-only filter (estimated availability; persists to localStorage)
    const inStockOnlyCheckbox = document.getElementById('inStockOnlyCheckbox');
    if (inStockOnlyCheckbox) {
      inStockOnlyCheckbox.checked = localStorage.getItem('usedfindr_in_stock_only') === 'true';
      inStockOnlyCheckbox.addEventListener('change', function() {
        localStorage.setItem('usedfindr_in_stock_only', this.checked ? 'true' : '');
        loadItemsFromStorage(true).then(function() {
          if (typeof initializeItemDisplay === 'function') initializeItemDisplay();
        });
      });
    }
    
    // Update filter counts
    updateFilterCounts(itemsToDisplay);

    // The first page is capped; fetch the rest once the page is idle so the
    // filters and their counts cover the whole feed.
    scheduleHomeFeedTopUp();

    // Populate marketplace components
    populateMarketplace();
  }
  
  // Check if we're on categories.html
  if (window.location.pathname.includes('categories.html')) {
    // Populate main category cards
    initializeCategoriesPage();
    
    const urlParams = new URLSearchParams(window.location.search);
    const category = urlParams.get('category');
    const condition = urlParams.get('condition');
    
    let itemsToDisplay = [...searchData.items];
    
    if (category) {
      itemsToDisplay = itemsToDisplay.filter((item) => itemMatchesCategoryId(item, category, []));
    } else if (condition) {
      itemsToDisplay = filterItemsByCondition(condition);
    }
    
    // Find or create items container
    let itemsContainer = document.getElementById('itemsContainer');
    if (!itemsContainer) {
      // Create container if it doesn't exist
      const mainSection = document.querySelector('.categories-section');
      if (mainSection) {
        itemsContainer = document.createElement('div');
        itemsContainer.id = 'itemsContainer';
        itemsContainer.className = 'deals-grid';
        itemsContainer.style.marginTop = '32px';
        mainSection.appendChild(itemsContainer);
      }
    }
    
    if (itemsContainer) {
      renderItems(itemsToDisplay, 'itemsContainer');
    }
  }
}
// Initialize categories page with available categories from API (eBay + legacy)
async function initializeCategoriesPage() {
  const mainCategoriesGrid = document.getElementById('mainCategoriesGrid');
  if (!mainCategoriesGrid) return;
  
  // Was `children.length > 0`, which is now also true on first load because the
  // grid ships server-rendered. The flag distinguishes "already rendered by this
  // function" from "seeded by Django", so the SSR cards get upgraded to the
  // icon-resolved ones exactly once.
  if (mainCategoriesGrid.dataset.clientRendered === '1') return;

  setRegionBusy(mainCategoriesGrid, true);
  const rawCategories = window.apiService ? await apiService.getAvailableCategories() : [];
  const categories = rawCategories.filter(
    (cat) => (cat.listingCount ?? cat.listing_count ?? 0) > 0
  );
  const swatches = ['sw-tech', 'sw-home', 'sw-sports', 'sw-apparel'];
  let swatchIndex = 0;

  const searchCategoryHref = (categoryId) => {
    const enc = encodeURIComponent(categoryId);
    const path = (typeof window !== 'undefined' && window.location && window.location.pathname) ? window.location.pathname : '';
    if (path.endsWith('.html') || /categories/i.test(path)) {
      return `search.html?category=${enc}`;
    }
    return `/search.html?category=${enc}`;
  };

  if (!categories.length) {
    // Only blank the server-rendered grid when the API actually answered with
    // an empty list. A failed fetch also lands here, and replacing real links
    // with "no categories" on a network blip is worse than leaving them.
    if (!mainCategoriesGrid.querySelector('[data-ssr-category]')) {
      mainCategoriesGrid.innerHTML = '<div class="no-items"><p>No categories available yet.</p><p class="empty-state-tip">Check back soon or browse Hot deals on the homepage.</p></div>';
    }
    mainCategoriesGrid.dataset.clientRendered = '1';
    setRegionBusy(mainCategoriesGrid, false);
    return;
  }
  
  // Cleared before appending: the grid arrives server-rendered now, and this
  // loop only ever appended -- which would render every category twice.
  mainCategoriesGrid.innerHTML = '';

  categories.forEach(cat => {
    const card = document.createElement('a');
    card.className = 'category-item';
    card.href = searchCategoryHref(cat.id);
    if (cat.full_label) card.title = cat.full_label;
    const { html, family } = getCategoryIcon(cat, { size: 24, preferLeaf: true });
    const sw = swatches[swatchIndex % swatches.length];
    swatchIndex += 1;
    const label = escapeHtml(cat.short_label ?? cat.shortLabel ?? cat.label ?? cat.id);
    card.innerHTML = `
      <div class="category-item-icon swatch ${sw}" data-family="${escapeHtml(family)}">${html}</div>
      <span class="category-item-label">${label}</span>
      <span class="category-item-meta">Browse all items in this category</span>
      <button class="follow-category-btn" data-category="${escapeHtml(cat.id)}" onclick="event.preventDefault(); event.stopPropagation();">Follow</button>
      <span class="follow-category-guest-hint"><a href="login.html" onclick="event.stopPropagation();">Sign in to follow</a></span>
    `;
    mainCategoriesGrid.appendChild(card);
  });

  mainCategoriesGrid.dataset.clientRendered = '1';
  setRegionBusy(mainCategoriesGrid, false);
  initializeCategoryFollowing();
}

// Initialize category following buttons
async function initializeCategoryFollowing() {
  // Check if we're on the categories page
  const catPath = window.location.pathname || '';
  if (!catPath.includes('categories.html') && !/\/categories\/?$/.test(catPath)) {
    return;
  }

  const currentUser = window.accountManager ? window.accountManager.getCurrentUser() : null;
  const guestHints = document.querySelectorAll('.follow-category-guest-hint');
  const followBtns = document.querySelectorAll('.follow-category-btn');

  if (!currentUser) {
    followBtns.forEach((btn) => { btn.style.display = 'none'; });
    guestHints.forEach((el) => { el.style.display = 'block'; });
    return;
  }

  guestHints.forEach((el) => { el.style.display = 'none'; });

  if (!window.categoryFollowingManager) {
    window.categoryFollowingManager = new CategoryFollowingManager();
  }
  await window.categoryFollowingManager.loadFromApi();

  const followedCategories = window.categoryFollowingManager.getFollowedCategories(currentUser.id);

  followBtns.forEach((btn) => {
    const categoryName = btn.getAttribute('data-category');
    if (!categoryName) return;

    btn.style.display = 'inline-flex';
    const isFollowing = followedCategories.includes(String(categoryName));
    btn.textContent = isFollowing ? 'Following' : 'Follow';
    btn.classList.toggle('following', isFollowing);

    const newBtn = btn.cloneNode(true);
    btn.parentNode.replaceChild(newBtn, btn);

    newBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (!currentUser) {
        window.accountManager?.showNotification('Please log in to follow categories.', 'error');
        return;
      }

      const catId = newBtn.getAttribute('data-category');
      if (!catId) return;

      const wasFollowing = window.categoryFollowingManager.isFollowingCategory(currentUser.id, catId);
      try {
        if (wasFollowing) {
          await window.categoryFollowingManager.unfollowCategory(currentUser.id, catId);
          newBtn.textContent = 'Follow';
          newBtn.classList.remove('following');
          window.accountManager?.showNotification('Unfollowed category');
        } else {
          await window.categoryFollowingManager.followCategory(currentUser.id, catId);
          newBtn.textContent = 'Following';
          newBtn.classList.add('following');
          window.accountManager?.showNotification('Now following category');
        }
      } catch (err) {
        window.accountManager?.showNotification(err.message || 'Could not update follow', 'error');
      }
    });
  });
}
// Advanced Search Class
/**
 * Sort orders the results page understands, in the order the seg draws them.
 *
 * `value` is the delta the cards already show: how far under the typical asking
 * price a listing sits. It is the one ordering that answers "which of these is
 * actually a deal", which neither price nor popularity can — the cheapest row
 * on a page of two different models says nothing about either of them.
 */
const SORT_ORDERS = ['default', 'price-low', 'price-high', 'value', 'popular'];

export class AdvancedSearch {
  constructor() {
    this.filters = {
      conditions: [],
      categories: [],
      minPrice: null,
      maxPrice: null,
      daysOnSite: null,
      query: null
    };
    this.sortBy = 'default';
    this.allItems = [];
    this._catalogSearchAnalyticsTimer = null;
    this._filtersInDialog = false;
    this._filterDialogInvoker = null;
    this._pendingCondition = null;

    // Deliberately not calling init() here as it's now async
  }
  
  async init() {
    // Skeletons + aria-busy before first results paint (SITE-UX-06)
    this.showResultsSkeleton();

    // Check if items are already loaded (they should be from DOMContentLoaded)
    // Only call loadItemsFromStorage if items are missing or empty
    if (!searchData || !searchData.items || !Array.isArray(searchData.items) || searchData.items.length === 0) {
      logger.info('AdvancedSearch.init: Items not loaded yet, loading from storage...');
      await loadItemsFromStorage();
      
      // Double-check after loading
      if (!searchData || !searchData.items || !Array.isArray(searchData.items) || searchData.items.length === 0) {
        logger.warn('AdvancedSearch.init: No items found after loading. Using hardcoded items fallback.');
        // Fallback to hardcoded items if available
        if (typeof originalHardcodedItems !== 'undefined') {
          searchData.items = [...originalHardcodedItems];
        }
      } else {
        logger.info(`AdvancedSearch.init: Successfully loaded ${searchData.items.length} items.`);
      }
    } else {
      logger.info(`AdvancedSearch.init: Items already loaded (${searchData.items.length} items), skipping loadItemsFromStorage.`);
    }
    
    this.allItems = [...(searchData.items || [])];
    
    try {
      // Fetch available categories (eBay + legacy) then populate Product Type filters
      this.availableCategories = await apiService.getAvailableCategories();
      this.populateCategoryFilters();
      
      // Read URL parameters
      this.readUrlParameters();
      
      // Populate condition filters (category-based; uses first selected category)
      await this.populateConditionFilters(this.filters.categories);
      // Now that the options exist, a `condition:` operator from the query can
      // be matched against them.
      this.adoptPendingCondition();
      
      // Attach event listeners
      this.attachEventListeners();
    } catch (error) {
      logger.error('AdvancedSearch.init: Error setting up search:', error);
    }
    
    // Initial render (always call this to replace "Loading..." text)
    try {
      this.updateResults();
    } catch (error) {
      logger.error('AdvancedSearch.init: Error updating results:', error);
      this.setResultsBusy(false);
      // At least update the loading text
      const resultsCount = document.getElementById('resultsCount');
      if (resultsCount) {
        resultsCount.textContent = 'No items available';
      }
    }
  }

  isMobileFilterBreakpoint() {
    if (typeof window.matchMedia !== 'function') return false;
    // Under the Console the filters live in the contextual pane, and shell.css
    // drops that column at 1100. Leaving this at 968 left a 132px band with no
    // filters anywhere: the pane hidden and the dialog refusing to open.
    const width = document.body && document.body.classList.contains('console') ? 1100 : 968;
    return window.matchMedia(`(max-width: ${width}px)`).matches;
  }

  hasActiveFilters() {
    const f = this.filters || {};
    return Boolean(
      (f.query && String(f.query).trim())
      || (f.conditions && f.conditions.length)
      || (f.categories && f.categories.length)
      || f.minPrice !== null && f.minPrice !== undefined
      || f.maxPrice !== null && f.maxPrice !== undefined
      || f.daysOnSite !== null && f.daysOnSite !== undefined
    );
  }

  showResultsSkeleton() {
    const resultsGrid = document.getElementById('searchResultsGrid');
    if (!resultsGrid) return;
    // '' rather than 'grid': "visible" is the stylesheet's business, and the
    // Console shell lays this container out as a block with its own .grid
    // inside. An inline display beats any rule, including that one.
    resultsGrid.style.display = '';
    // Server-rendered cards are real content and are what a crawler that
    // renders JS reads. Replacing them with skeletons emptied the page during
    // the fetch, which is why Search Console reported /search.html as a
    // Soft 404. Keep them and only flag the region busy; renderItems()
    // replaces them once the real results arrive.
    if (!resultsGrid.querySelector('[data-ssr-card]')) {
      resultsGrid.innerHTML = generateSkeletonLoader(8);
    }
    this.setResultsBusy(true);
    const noResults = document.getElementById('noResults');
    if (noResults) noResults.style.display = 'none';
  }

  setResultsBusy(busy) {
    setRegionBusy(document.getElementById('searchResultsGrid'), busy);
  }

  /**
   * Draw B8's empty state into the results grid. Returns false when it is not
   * this page's job — legacy theme, or no grid — so the caller falls back.
   *
   * Which of the two states depends on whether the floor is doing anything.
   * With a floor on and listings behind it, the honest sentence is about the
   * floor and the way out is one tap. With the floor off, the floor is not the
   * problem and repeating that pitch would be noise, so the query gets echoed
   * back instead.
   */
  paintConsoleEmptyState(resultsGrid) {
    if (!resultsGrid || !document.body || !document.body.classList.contains('console')) {
      return false;
    }
    // Scoped to this query when we have it. Off `lastTrustFloor` the branch
    // below is answering a question about the whole catalog -- see
    // setScopedFloorCounts in console-stats.js.
    const counts = getFloorCounts();
    const search = window.location.search;
    const hidden = counts
      && Number(counts.total) > 0
      && Number(counts.clears) === 0;

    resultsGrid.style.display = '';
    resultsGrid.innerHTML = hidden
      ? consoleEmptyFloorHtml(counts, { search })
      : consoleEmptyQueryHtml(this.parsedIntent || {}, { search });
    return true;
  }

  moveFiltersIntoDialog() {
    const filters = document.getElementById('searchFilters');
    const dialogBody = document.getElementById('searchFiltersDialogBody');
    if (!filters || !dialogBody || this._filtersInDialog) return;
    dialogBody.appendChild(filters);
    this._filtersInDialog = true;
  }

  restoreFiltersToSlot() {
    const filters = document.getElementById('searchFilters');
    const slot = document.getElementById('searchFiltersSlot');
    if (!filters || !slot) return;
    if (filters.parentElement !== slot) {
      slot.appendChild(filters);
    }
    this._filtersInDialog = false;
  }

  openSearchFiltersDialog() {
    if (!this.isMobileFilterBreakpoint()) return;
    const dialog = document.getElementById('searchFiltersDialog');
    const openBtn = document.getElementById('openSearchFiltersBtn');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    this._filterDialogInvoker = openBtn || document.activeElement;
    this.moveFiltersIntoDialog();
    if (!dialog.open) {
      dialog.showModal();
    }
  }

  closeSearchFiltersDialog({ apply = false } = {}) {
    const dialog = document.getElementById('searchFiltersDialog');
    if (dialog && dialog.open && typeof dialog.close === 'function') {
      dialog.close();
    } else {
      this.restoreFiltersToSlot();
    }
    if (apply) {
      this.updateResults();
    }
    const invoker = this._filterDialogInvoker;
    this._filterDialogInvoker = null;
    if (invoker && typeof invoker.focus === 'function') {
      invoker.focus();
    }
  }
  
  readUrlParameters() {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Read search query
    const query = urlParams.get('q');
    if (query) {
      this.filters.query = query.trim();
      // Update search input if it exists
      const searchInput = document.getElementById('mainSearchInput');
      if (searchInput) {
        searchInput.value = this.filters.query;
      }
      // Under the Console the search box advertises operators, so this page has
      // to read them -- otherwise `sony a7 iii $900-1300 condition:used` is
      // matched against product names as one literal string and returns
      // nothing, while the header cheerfully reports what it parsed. Console
      // only: the legacy UI has no syntax help anywhere on it, and quietly
      // reinterpreting "$100-600" as a range there would be a surprise.
      if (document.body && document.body.classList.contains('console')) {
        this.applyParsedQuery(this.filters.query);
      }
    }
    
    // Read condition filters
    const conditions = urlParams.getAll('condition');
    if (conditions.length > 0) {
      conditions.forEach(condition => {
        const checkbox = document.getElementById(`condition${condition.replace(/\s+/g, '')}`);
        if (checkbox) {
          checkbox.checked = true;
          this.filters.conditions.push(condition);
        }
      });
    }
    
    // Read category filters (can be from query param or category param)
    const categories = urlParams.getAll('category');
    if (categories.length > 0) {
      categories.forEach((categoryParam) => {
        const categoryId = this.resolveCategoryParamToId(categoryParam);
        const checkbox = document.querySelector(
          `input[name="category"][value="${categoryId.replace(/"/g, '\\"')}"]`
        );
        if (checkbox) {
          checkbox.checked = true;
          if (!this.filters.categories.includes(categoryId)) {
            this.filters.categories.push(categoryId);
          }
        } else if (categoryId && !this.filters.categories.includes(categoryId)) {
          this.filters.categories.push(categoryId);
        }
      });
    }
    
    // Read price range
    const minPrice = urlParams.get('minPrice');
    const maxPrice = urlParams.get('maxPrice');
    if (minPrice) {
      document.getElementById('minPrice').value = minPrice;
      this.filters.minPrice = parsePrice(minPrice);
    }
    if (maxPrice) {
      document.getElementById('maxPrice').value = maxPrice;
      this.filters.maxPrice = parsePrice(maxPrice);
    }
    
    // Read days on site
    const days = urlParams.get('days');
    if (days) {
      document.getElementById('daysOnSiteFilter').value = days;
      this.filters.daysOnSite = parseInt(days);
    }
    
    // Read sort
    const sort = urlParams.get('sort');
    if (sort) {
      // Console renders a `.seg` instead of the select, so there may be no
      // select on the page at all -- this used to throw on a null element.
      this.sortBy = SORT_ORDERS.includes(sort) ? sort : 'default';
      const sortSelectEl = document.getElementById('sortSelect');
      if (sortSelectEl) sortSelectEl.value = this.sortBy;
      this.paintSortSeg();
    }
  }
  
  updateUrlParameters() {
    // Start from what is already in the URL and drop only the keys this method
    // owns. Building a fresh URLSearchParams threw away everything else on the
    // first filter click -- `?theme=console`, `?floor=elite`, campaign tags --
    // so a link someone shared with a floor on it quietly lost it as soon as
    // the visitor touched a filter.
    const params = new URLSearchParams(window.location.search);
    for (const key of ['q', 'condition', 'category', 'minPrice', 'maxPrice', 'days', 'sort']) {
      params.delete(key);
    }

    // Add search query
    if (this.filters.query) {
      params.set('q', this.filters.query);
    }
    
    // Add conditions
    this.filters.conditions.forEach(condition => {
      params.append('condition', condition);
    });
    
    // Add categories
    this.filters.categories.forEach(category => {
      params.append('category', category);
    });
    
    // Add price range
    if (this.filters.minPrice !== null) {
      params.set('minPrice', this.filters.minPrice.toString());
    }
    if (this.filters.maxPrice !== null) {
      params.set('maxPrice', this.filters.maxPrice.toString());
    }
    
    // Add days on site
    if (this.filters.daysOnSite !== null) {
      params.set('days', this.filters.daysOnSite.toString());
    }
    
    // Add sort
    if (this.sortBy !== 'default') {
      params.set('sort', this.sortBy);
    }
    
    // Update URL without reload
    const newUrl = params.toString() ? `search.html?${params.toString()}` : 'search.html';
    window.history.pushState({}, '', newUrl);
  }

  // Shared category matcher used by both filter application and filter option visibility.
  resolveCategoryParamToId(categoryParam) {
    const raw = String(categoryParam || '').trim();
    if (!raw) return raw;
    const cats = this.availableCategories || [];
    if (cats.some((c) => String(c.id) === raw)) return raw;
    const norm = (v) => String(v || '').trim().toLowerCase();
    const p = norm(raw);
    const found = cats.find((c) => {
      const shortLabel = norm(c.short_label ?? c.shortLabel);
      const label = norm(c.label);
      const fullLabel = norm(c.full_label ?? c.fullLabel);
      return shortLabel === p || label === p || fullLabel === p
        || fullLabel.endsWith(`> ${p}`) || fullLabel.includes(`> ${p}`);
    });
    return found ? String(found.id) : raw;
  }

  itemMatchesCategory(item, categoryId) {
    return itemMatchesCategoryId(item, categoryId, this.availableCategories);
  }
  
  populateCategoryFilters() {
    const categoryFilterOptions = document.getElementById('categoryFilterOptions');
    if (!categoryFilterOptions) return;

    categoryFilterOptions.innerHTML = '';
    const categories = (this.availableCategories || []).filter(cat =>
      (this.allItems || []).some(item => this.itemMatchesCategory(item, cat.id))
    );
    categories.forEach(cat => {
      const label = document.createElement('label');
      label.className = 'filter-checkbox';
      
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.name = 'category';
      checkbox.value = cat.id;
      checkbox.id = `category${String(cat.id).replace(/[^a-zA-Z0-9]/g, '')}`;
      
      const span = document.createElement('span');
      span.textContent = cat.short_label ?? cat.shortLabel ?? cat.label ?? cat.id;
      if (cat.full_label) label.title = cat.full_label;

      label.appendChild(checkbox);
      label.appendChild(span);
      categoryFilterOptions.appendChild(label);
    });
  }

  async populateConditionFilters(categoryIds) {
    const container = document.getElementById('conditionFilterOptions');
    if (!container) return;

    const defaultConditions = [
      { conditionDescription: 'New', conditionId: '1000' },
      { conditionDescription: 'Like New', conditionId: '2750' },
      { conditionDescription: 'Used', conditionId: '3000' },
      { conditionDescription: 'Very Good', conditionId: '4000' },
      { conditionDescription: 'Good', conditionId: '5000' },
      { conditionDescription: 'Acceptable', conditionId: '6000' }
    ];

    const renderConditions = (conditions) => {
      container.innerHTML = '';
      conditions.forEach(c => {
        const desc = (c.conditionDescription || c.condition_description || '').trim();
        if (!desc) return;
        const label = document.createElement('label');
        label.className = 'filter-checkbox';
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.name = 'condition';
        checkbox.value = desc;
        checkbox.id = `condition${String(desc).replace(/\s+/g, '')}`;
        const span = document.createElement('span');
        span.textContent = desc;
        label.appendChild(checkbox);
        label.appendChild(span);
        container.appendChild(label);
      });
      this.filters.conditions.forEach(conditionValue => {
        const cb = container.querySelector(`input[name="condition"][value="${conditionValue.replace(/"/g, '\\"')}"]`);
        if (cb) cb.checked = true;
      });
    };

    if (!categoryIds || categoryIds.length === 0) {
      renderConditions(defaultConditions);
      return;
    }

    const firstEbayCategory = categoryIds.find(id => /^\d+$/.test(String(id)));
    let categoryConditions = [];
    if (firstEbayCategory && typeof fetchConditionPolicies === 'function') {
      const loadingEl = document.createElement('p');
      loadingEl.className = 'condition-loading';
      loadingEl.textContent = 'Loading conditions...';
      container.innerHTML = '';
      container.appendChild(loadingEl);
      categoryConditions = await fetchConditionPolicies(firstEbayCategory) || [];
    }

    // Additive: merge category-specific conditions with defaults (dedupe by description)
    const seen = new Set(defaultConditions.map(c => (c.conditionDescription || c.condition_description || '').trim().toLowerCase()));
    const merged = [...defaultConditions];
    (categoryConditions || []).forEach(c => {
      const desc = (c.conditionDescription || c.condition_description || '').trim();
      if (!desc) return;
      const key = desc.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(c);
      }
    });
    renderConditions(merged);
  }

  attachEventListeners() {
    // Search input in header (if on search page)
    const mainSearchInput = document.getElementById('mainSearchInput');
    if (mainSearchInput) {
      mainSearchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.updateQueryFilter();
          this.updateResults();
        }
      });
      
      // Find search button in the same container
      const searchContainer = mainSearchInput.closest('.search-container');
      const searchButton = searchContainer ? searchContainer.querySelector('.search-button') : null;
      if (searchButton) {
        searchButton.addEventListener('click', () => {
          this.updateQueryFilter();
          this.updateResults();
        });
      }
    }
    
    // Condition checkboxes (event delegation - container populated dynamically)
    const conditionFilterOptions = document.getElementById('conditionFilterOptions');
    if (conditionFilterOptions) {
      conditionFilterOptions.addEventListener('change', (e) => {
        if (e.target && e.target.matches && e.target.matches('input[name="condition"]')) {
          this.updateConditionFilters();
          this.updateResults();
        }
      });
    }
    
    // Category checkboxes (attached after dynamic population)
    document.querySelectorAll('input[name="category"]').forEach(checkbox => {
      checkbox.addEventListener('change', async () => {
        this.updateCategoryFilters();
        await this.populateConditionFilters(this.filters.categories);
        this.updateResults();
      });
    });
    
    // Price inputs
    const minPriceEl = document.getElementById('minPrice');
    const maxPriceEl = document.getElementById('maxPrice');
    if (minPriceEl) minPriceEl.addEventListener('input', () => { this.updatePriceFilters(); this.updateResults(); });
    if (maxPriceEl) maxPriceEl.addEventListener('input', () => { this.updatePriceFilters(); this.updateResults(); });
    
    // Days on site select
    const daysOnSiteEl = document.getElementById('daysOnSiteFilter');
    if (daysOnSiteEl) daysOnSiteEl.addEventListener('change', () => { this.updateDaysOnSiteFilter(); this.updateResults(); });
    
    // Sort select
    const sortSelectEl = document.getElementById('sortSelect');
    if (sortSelectEl) sortSelectEl.addEventListener('change', () => {
      this.sortBy = sortSelectEl.value;
      this.paintSortSeg();
      this.updateUrlParameters();
      this.updateResults();
    });

    // The Console draws the same choice as a `.seg` of buttons. It is the same
    // control, not a second one: whichever exists on the page drives
    // `this.sortBy`, and both are repainted from it.
    for (const group of document.querySelectorAll('[data-sortsel]')) {
      group.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-sort]');
        if (!button) return;
        this.sortBy = button.dataset.sort;
        if (sortSelectEl) sortSelectEl.value = this.sortBy;
        this.paintSortSeg();
        this.updateUrlParameters();
        this.updateResults();
      });
    }
    this.paintSortSeg();
    
    // Clear filters button
    const clearFiltersBtn = document.getElementById('clearFiltersBtn');
    if (clearFiltersBtn) {
      clearFiltersBtn.addEventListener('click', () => { this.clearAllFilters(); });
    } else {
      logger.warn('AdvancedSearch: clearFiltersBtn not found, Clear All will not work');
    }

    const clearFiltersEmptyBtn = document.getElementById('clearFiltersEmptyBtn');
    if (clearFiltersEmptyBtn) {
      clearFiltersEmptyBtn.addEventListener('click', () => { this.clearAllFilters(); });
    }

    const activeFiltersList = document.getElementById('activeFiltersList');
    if (activeFiltersList) {
      activeFiltersList.addEventListener('click', (e) => {
        const btn = e.target.closest('.remove-filter-btn');
        if (!btn) return;
        this.removeFilter(btn.getAttribute('data-type'), btn.getAttribute('data-value'));
      });
    }

    // Mobile filter dialog (SITE-UX-05): move/restore .search-filters into <dialog>
    const openFiltersBtn = document.getElementById('openSearchFiltersBtn');
    if (openFiltersBtn) {
      openFiltersBtn.addEventListener('click', () => {
        this.openSearchFiltersDialog();
      });
    }

    const applyFiltersBtn = document.getElementById('applySearchFiltersBtn');
    if (applyFiltersBtn) {
      applyFiltersBtn.addEventListener('click', () => {
        this.closeSearchFiltersDialog({ apply: true });
      });
    }

    const clearFiltersDialogBtn = document.getElementById('clearFiltersDialogBtn');
    if (clearFiltersDialogBtn) {
      clearFiltersDialogBtn.addEventListener('click', () => {
        this.clearAllFilters();
      });
    }

    const closeFiltersBtn = document.getElementById('closeSearchFiltersBtn');
    if (closeFiltersBtn) {
      closeFiltersBtn.addEventListener('click', () => {
        this.closeSearchFiltersDialog();
      });
    }

    const filtersDialog = document.getElementById('searchFiltersDialog');
    if (filtersDialog) {
      filtersDialog.addEventListener('close', () => {
        this.restoreFiltersToSlot();
        const invoker = this._filterDialogInvoker;
        this._filterDialogInvoker = null;
        if (invoker && typeof invoker.focus === 'function') {
          // Focus restore may also run from closeSearchFiltersDialog; safe if already focused
          invoker.focus();
        }
      });
      filtersDialog.addEventListener('cancel', () => {
        // Escape: native dialog closes; close handler restores node
      });
    }

    // If viewport grows past mobile breakpoint while dialog open, restore sidebar
    if (typeof window.matchMedia === 'function') {
      const desktopMq = window.matchMedia('(min-width: 969px)');
      const onBreakpointChange = (e) => {
        if (e.matches) {
          const dialog = document.getElementById('searchFiltersDialog');
          if (dialog && dialog.open) {
            dialog.close();
          }
          this.restoreFiltersToSlot();
        }
      };
      if (typeof desktopMq.addEventListener === 'function') {
        desktopMq.addEventListener('change', onBreakpointChange);
      } else if (typeof desktopMq.addListener === 'function') {
        desktopMq.addListener(onBreakpointChange);
      }
    }
  }
  
  updateQueryFilter() {
    const searchInput = document.getElementById('mainSearchInput');
    if (searchInput) {
      const query = searchInput.value.trim();
      this.filters.query = query.length > 0 ? query : null;
      this.updateUrlParameters();
    }
  }
  
  updateConditionFilters() {
    this.filters.conditions = [];
    document.querySelectorAll('input[name="condition"]:checked').forEach(checkbox => {
      this.filters.conditions.push(checkbox.value);
    });
    this.updateUrlParameters();
  }
  
  updateCategoryFilters() {
    this.filters.categories = [];
    document.querySelectorAll('input[name="category"]:checked').forEach(checkbox => {
      this.filters.categories.push(checkbox.value);
    });
    this.updateUrlParameters();
  }
  
  updatePriceFilters() {
    const minPriceInput = document.getElementById('minPrice').value;
    const maxPriceInput = document.getElementById('maxPrice').value;
    
    this.filters.minPrice = minPriceInput ? parsePrice(minPriceInput) : null;
    this.filters.maxPrice = maxPriceInput ? parsePrice(maxPriceInput) : null;
    this.updateUrlParameters();
  }
  
  updateDaysOnSiteFilter() {
    const daysValue = document.getElementById('daysOnSiteFilter').value;
    this.filters.daysOnSite = daysValue ? parseInt(daysValue) : null;
    this.updateUrlParameters();
  }
  
  clearAllFilters() {
    // Clear search query
    const searchInput = document.getElementById('mainSearchInput');
    if (searchInput) {
      searchInput.value = '';
    }
    
    // Clear condition checkboxes (or reset to placeholder if dynamic)
    const conditionContainer = document.getElementById('conditionFilterOptions');
    if (conditionContainer) {
      conditionContainer.querySelectorAll('input[name="condition"]').forEach(checkbox => {
        checkbox.checked = false;
      });
    }
    
    // Clear category checkboxes
    document.querySelectorAll('input[name="category"]').forEach(checkbox => {
      checkbox.checked = false;
    });
    
    // Clear price inputs
    const minPriceEl = document.getElementById('minPrice');
    const maxPriceEl = document.getElementById('maxPrice');
    if (minPriceEl) minPriceEl.value = '';
    if (maxPriceEl) maxPriceEl.value = '';
    
    // Clear days on site
    const daysOnSiteEl = document.getElementById('daysOnSiteFilter');
    if (daysOnSiteEl) daysOnSiteEl.value = '';
    
    // Reset sort
    const sortSelectEl = document.getElementById('sortSelect');
    if (sortSelectEl) sortSelectEl.value = 'default';
    
    // Reset filters object
    this.filters = {
      conditions: [],
      categories: [],
      minPrice: null,
      maxPrice: null,
      daysOnSite: null,
      query: null
    };
    this.sortBy = 'default';
    this.paintSortSeg();

    // Reset condition filter UI to placeholder (no category selected)
    this.populateConditionFilters([]);
    
    // Update URL and results
    this.updateUrlParameters();
    this.updateResults();
  }
  
  applyFilters() {
    let filtered = [...this.allItems];
    
    // Filter by search query (product name)
    if (this.filters.query) {
      const queryLower = this.filters.query.toLowerCase();
      filtered = filtered.filter(item => 
        item.name && item.name.toLowerCase().includes(queryLower)
      );
    }
    
    // Filter by conditions (case-insensitive match; product.condition may differ slightly from API labels)
    if (this.filters.conditions.length > 0) {
      const selNorm = this.filters.conditions.map(c => (c || '').toLowerCase().trim());
      filtered = filtered.filter(item => {
        const cond = (item.condition || '').trim();
        if (!cond) return false;
        const condLower = cond.toLowerCase();
        return selNorm.some(s => condLower === s || condLower.includes(s) || s.includes(condLower));
      });
    }
    
    // Filter by categories (eBay id, or label-based fallback for items without ebayCategoryId)
    if (this.filters.categories.length > 0) {
      filtered = filtered.filter(item =>
        this.filters.categories.some(catId => this.itemMatchesCategory(item, catId))
      );
    }
    
    // Filter by price range
    if (this.filters.minPrice !== null) {
      filtered = filtered.filter(item => {
        const price = parsePrice(item.price);
        return price >= this.filters.minPrice;
      });
    }
    if (this.filters.maxPrice !== null) {
      filtered = filtered.filter(item => {
        const price = parsePrice(item.price);
        return price <= this.filters.maxPrice;
      });
    }
    
    // Filter by days on site
    if (this.filters.daysOnSite !== null) {
      filtered = filtered.filter(item => {
        const days = getDaysOnSite(item.addedAt);
        return days <= this.filters.daysOnSite;
      });
    }
    
    return filtered;
  }
  
  /**
   * Read the search operators out of a raw query and move them into the
   * filters, leaving only the keywords behind as the text match.
   *
   * The parse is trusted-query-parser.js, the same one Trusted Finder uses, so
   * the operators mean the same thing on both pages. Only the parts the query
   * actually carried are taken: the parser fills an intent with defaults, and
   * applying those would silently narrow a plain keyword search.
   *
   * The condition cannot be applied yet -- the checkboxes are populated after
   * this runs -- so it is held and adopted in adoptPendingCondition() against
   * the options that actually exist.
   */
  applyParsedQuery(raw) {
    let intent = null;
    try {
      const parsed = parseInput(raw);
      intent = parsed && parsed.intent;
    } catch (error) {
      logger.warn('Could not parse the search query; using it as plain text', error);
    }
    if (!intent) return;
    // Kept so the empty state can show what we actually searched for rather
    // than the raw string the visitor typed.
    this.parsedIntent = intent;

    if (intent.query && intent.query !== raw) this.filters.query = intent.query;

    if (intent.minPrice != null && intent.minPrice !== DEFAULT_INTENT.minPrice) {
      this.filters.minPrice = intent.minPrice;
      const el = document.getElementById('minPrice');
      if (el) el.value = String(intent.minPrice);
    }
    if (intent.maxPrice != null && intent.maxPrice !== DEFAULT_INTENT.maxPrice) {
      this.filters.maxPrice = intent.maxPrice;
      const el = document.getElementById('maxPrice');
      if (el) el.value = String(intent.maxPrice);
    }
    if (intent.condition && intent.condition !== DEFAULT_INTENT.condition) {
      this._pendingCondition = intent.condition;
    }
  }

  /**
   * Tick the condition boxes a parsed `condition:` operator asked for.
   *
   * Matched against the options the API actually returned rather than assumed:
   * "Refurb" from the query has to become whatever this category calls it, or
   * the pane would show nothing ticked while the grid was filtered.
   */
  adoptPendingCondition() {
    const wanted = this._pendingCondition;
    this._pendingCondition = null;
    if (!wanted) return;
    const needle = String(wanted).toLowerCase();
    const matches = [...document.querySelectorAll('#conditionFilterOptions input[name="condition"]')]
      .filter((box) => {
        const value = String(box.value || '').toLowerCase();
        return value === needle || value.includes(needle) || needle.includes(value);
      });
    if (!matches.length) return;
    for (const box of matches) box.checked = true;
    this.filters.conditions = matches.map((box) => box.value);
  }

  /**
   * How many results each filter option would add, for the Console pane's
   * counts.
   *
   * Each option is counted with every *other* active filter still applied and
   * its own group replaced by that one value, which is what a facet count
   * means: "click this and you get N". Computed here rather than in the
   * Console module so it goes through the same applyFilters() the grid does --
   * a count derived from a second, similar predicate is a count that will
   * eventually disagree with the list it labels.
   *
   * One pass over the loaded items per option; a couple of dozen passes over a
   * few hundred rows, so it runs inline with the render.
   */
  facetCounts() {
    const base = this.filters;
    const countWith = (patch) => {
      this.filters = { ...base, ...patch };
      try {
        return this.applyFilters().length;
      } finally {
        this.filters = base;
      }
    };
    const forGroup = (selector, key) => {
      const counts = {};
      for (const input of document.querySelectorAll(selector)) {
        if (!input.value) continue;
        counts[input.value] = countWith({ [key]: [input.value] });
      }
      return counts;
    };
    return {
      conditions: forGroup('#conditionFilterOptions input[type="checkbox"]', 'conditions'),
      categories: forGroup('#categoryFilterOptions input[type="checkbox"]', 'categories'),
    };
  }

  /** Repaint every sort control from `this.sortBy`. */
  paintSortSeg() {
    for (const group of document.querySelectorAll('[data-sortsel]')) {
      for (const button of group.querySelectorAll('button[data-sort]')) {
        const isCurrent = button.dataset.sort === this.sortBy;
        button.classList.toggle('on', isCurrent);
        button.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');
      }
    }
  }

  sortItems(items) {
    const sorted = [...items];
    
    if (this.sortBy === 'price-low') {
      // The sort a used-goods search is usually reaching for, and the one the
      // Console's Find board asks for by name. Unpriced rows sort last rather
      // than to the top as a zero.
      sorted.sort((a, b) => {
        const priceA = parsePrice(a.price) || Infinity;
        const priceB = parsePrice(b.price) || Infinity;
        return priceA - priceB;
      });
    } else if (this.sortBy === 'price-high') {
      sorted.sort((a, b) => {
        const priceA = parsePrice(a.price);
        const priceB = parsePrice(b.price);
        return priceB - priceA; // High to low
      });
    } else if (this.sortBy === 'value') {
      // Signed percentage, ascending: furthest below the typical ask first.
      // A listing with no MarketPriceStat behind it has no delta at all — 38%
      // of live listings — and those sort last rather than to the top as a
      // zero, which would read as "exactly typical" when we simply do not know.
      const delta = (item) => {
        const d = priceDelta(item.price, item.priceContext || item.price_context || null);
        if (!d) return null;
        return d.up ? d.pct : -d.pct;
      };
      sorted.sort((a, b) => {
        const va = delta(a);
        const vb = delta(b);
        if (va === null && vb === null) return 0;
        if (va === null) return 1;
        if (vb === null) return -1;
        return va - vb;
      });
    } else if (this.sortBy === 'popular') {
      sorted.sort((a, b) => {
        const clicksA = a.clickCount || 0;
        const clicksB = b.clickCount || 0;
        if (clicksB !== clicksA) {
          return clicksB - clicksA; // Most clicks first
        }
        // If clicks are equal, sort by date added (newest first)
        const dateA = a.addedAt ? new Date(a.addedAt).getTime() : 0;
        const dateB = b.addedAt ? new Date(b.addedAt).getTime() : 0;
        return dateB - dateA;
      });
    }
    
    return sorted;
  }

  /**
   * Debounced POST of catalog search analytics (search.html text query only).
   */
  _scheduleCatalogSearchAnalytics(filteredCount) {
    if (typeof window === 'undefined' || !window.location.pathname.includes('search.html')) {
      return;
    }
    const q = (this.filters.query || '').trim();
    if (!q) return;
    if (filteredCount <= 0) return;
    if (this._catalogSearchAnalyticsTimer) {
      clearTimeout(this._catalogSearchAnalyticsTimer);
    }
    this._catalogSearchAnalyticsTimer = setTimeout(() => {
      this._catalogSearchAnalyticsTimer = null;
      try {
        if (window.apiService && typeof window.apiService.postAnonymousSearch === 'function') {
          window.apiService.postAnonymousSearch({
            search_term: q,
            search_source: 'catalog',
          }).catch(() => {});
        }
        if (window.consentManager && !window.consentManager.hasConsent('analytics')) {
          return;
        }
        if (window.apiService && typeof window.apiService.trackCatalogSearch === 'function') {
          window.apiService.trackCatalogSearch(q, filteredCount).catch(() => {});
        }
      } catch (_e) {
        /* non-blocking */
      }
    }, 1200);
  }

  /**
   * Ask the API how *this* query divides at the floor.
   *
   * The page's own listing fetch is the site-wide one -- every purchasable
   * listing, no `search` parameter, filtered by keyword in the browser -- so
   * the `trust_floor` block riding on it describes the catalog, not the query.
   * That is fine for the home feed and wrong for everything this page says.
   *
   * One extra request with `limit=1`: we want the counts, not the rows, and
   * the server computes them over the matched set before the limit applies
   * (see ProductViewSet.list). Debounced through a token so a fast typist does
   * not have an old answer land after a newer one.
   */
  async refreshQueryFloorCounts() {
    if (!document.body || !document.body.classList.contains('console')) return;
    const query = (this.filters.query || '').trim();
    // No keyword means the query *is* the catalog, and the block already on
    // the last fetch describes it exactly.
    if (!query || !window.apiService || typeof window.apiService.getProducts !== 'function') {
      setScopedFloorCounts(null);
      return;
    }
    const token = (this._floorCountsToken = (this._floorCountsToken || 0) + 1);
    try {
      await window.apiService.getProducts({
        status: 'active',
        in_stock_only: true,
        search: query,
        floor: getFloor(),
        limit: 1,
      });
      if (token !== this._floorCountsToken) return;
      setScopedFloorCounts(window.apiService.lastTrustFloor || null);
    } catch (_e) {
      // A failed count must not change what the page claims, so drop back to
      // saying nothing rather than to a number about a different set.
      if (token === this._floorCountsToken) setScopedFloorCounts(null);
    }
  }

  updateResults() {
    // Apply filters
    let filteredItems = this.applyFilters();
    
    // Apply sorting
    filteredItems = this.sortItems(filteredItems);
    
    // Update results count
    const resultsCount = document.getElementById('resultsCount');
    if (resultsCount) {
      const count = filteredItems.length;
      const totalCount = this.allItems.length;
      let countText = `Showing ${count} ${count === 1 ? 'item' : 'items'}`;
      if (this.filters.query) {
        countText += ` for "${this.filters.query}"`;
      }
      if (count < totalCount && (this.filters.query || this.filters.conditions.length > 0 || this.filters.categories.length > 0 || this.filters.minPrice !== null || this.filters.maxPrice !== null || this.filters.daysOnSite !== null)) {
        countText += ` of ${totalCount} total`;
      }
      resultsCount.textContent = countText;
    }
    
    // Update results header title
    const resultsHeader = document.querySelector('.results-info h1');
    if (resultsHeader) {
      if (this.filters.query) {
        resultsHeader.textContent = `Search Results for "${this.filters.query}"`;
      } else {
        resultsHeader.textContent = 'Search Results';
      }
    }
    
    // Render results
    const resultsGrid = document.getElementById('searchResultsGrid');
    const noResults = document.getElementById('noResults');
    
    if (filteredItems.length === 0) {
      // With no filters applied there is nothing to say "no results" about, and
      // hiding the grid would discard the server-rendered deals a crawler reads.
      // Only an actual empty search gets the empty state.
      const keepSsr = !this.hasActiveFilters()
        && resultsGrid && resultsGrid.querySelector('[data-ssr-card]');
      if (keepSsr) {
        resultsGrid.style.display = '';
        if (noResults) noResults.style.display = 'none';
      } else if (this.paintConsoleEmptyState(resultsGrid)) {
        // B8: the Console says which kind of nothing this is, in the grid's
        // own place, so there is no separate panel to show or hide.
        if (noResults) noResults.style.display = 'none';
      } else {
        if (resultsGrid) resultsGrid.style.display = 'none';
        if (noResults) noResults.style.display = 'block';
      }
    } else {
      if (resultsGrid) resultsGrid.style.display = '';
      if (noResults) noResults.style.display = 'none';
      renderItems(filteredItems, 'searchResultsGrid');
      // Note: restoreVoteStates is called inside renderItems, but we also call it here
      // with a slight delay to ensure it runs after the DOM is updated
      setTimeout(() => {
        if (resultsGrid) {
          restoreVoteStates(resultsGrid);
        }
      }, 100);
    }

    this.setResultsBusy(false);

    // The query's own floor counts arrive after the render, because they cost
    // a request. Repaint only the two things that read them -- calling
    // updateResults() again from here would loop.
    this.refreshQueryFloorCounts().then(() => {
      paintFeedStats(filteredItems.length);
      if (filteredItems.length === 0) {
        const grid = document.getElementById('searchResultsGrid');
        if (grid && !grid.querySelector('[data-ssr-card]')) this.paintConsoleEmptyState(grid);
      }
      // The Find pane's header count and floor note read the same block.
      document.dispatchEvent(new CustomEvent('uf:floorcounts'));
    });

    this._scheduleCatalogSearchAnalytics(filteredItems.length);

    // Update active filters display
    this.updateActiveFiltersDisplay();

    // What the Console's Find page hangs its facet counts, header and status
    // line off. An event rather than a direct call: this class predates the
    // Console and should not have to know which theme is on, and the same
    // event serves any later surface that wants to describe the same results.
    document.dispatchEvent(new CustomEvent('uf:searchresults', {
      detail: {
        shown: filteredItems,
        all: this.allItems,
        filters: this.filters,
        sortBy: this.sortBy,
        facets: this.facetCounts(),
      },
    }));
  }
  updateActiveFiltersDisplay() {
    const activeFiltersContainer = document.getElementById('activeFilters');
    const activeFiltersList = document.getElementById('activeFiltersList');
    
    if (!activeFiltersContainer || !activeFiltersList) return;
    
    const activeFilters = [];
    
    // Add search query filter
    if (this.filters.query) {
      activeFilters.push({
        type: 'query',
        value: this.filters.query,
        label: `Search: "${this.filters.query}"`
      });
    }
    
    // Add condition filters
    this.filters.conditions.forEach(condition => {
      activeFilters.push({
        type: 'condition',
        value: condition,
        label: condition
      });
    });
    
    // Add category filters (show human-readable label, not raw ID)
    this.filters.categories.forEach(categoryId => {
      const cat = (this.availableCategories || []).find(c => String(c.id) === String(categoryId));
      const label = cat ? (cat.short_label ?? cat.shortLabel ?? cat.label ?? cat.full_label ?? cat.fullLabel ?? categoryId) : categoryId;
      activeFilters.push({
        type: 'category',
        value: categoryId,
        label: label
      });
    });
    
    // Add price range
    if (this.filters.minPrice !== null || this.filters.maxPrice !== null) {
      const min = this.filters.minPrice !== null ? `$${this.filters.minPrice.toFixed(2)}` : '$0';
      const max = this.filters.maxPrice !== null ? `$${this.filters.maxPrice.toFixed(2)}` : '∞';
      activeFilters.push({
        type: 'price',
        value: 'price',
        label: `Price: ${min} - ${max}`
      });
    }
    
    // Add days on site
    if (this.filters.daysOnSite !== null) {
      const daysLabels = {
        7: 'Last 7 Days',
        30: 'Last 30 Days',
        90: 'Last 90 Days',
        180: 'Last 6 Months'
      };
      activeFilters.push({
        type: 'days',
        value: this.filters.daysOnSite.toString(),
        label: daysLabels[this.filters.daysOnSite] || `${this.filters.daysOnSite} Days`
      });
    }
    
    if (activeFilters.length === 0) {
      activeFiltersContainer.style.display = 'none';
      return;
    }
    
    activeFiltersContainer.style.display = 'flex';
    activeFiltersList.innerHTML = activeFilters.map(filter => `
      <span class="active-filter-tag">
        ${escapeHtml(filter.label)}
        <button type="button" class="remove-filter-btn" data-type="${escapeHtml(filter.type)}" data-value="${escapeHtml(filter.value)}" aria-label="Remove ${escapeHtml(filter.label)} filter">×</button>
      </span>
    `).join('');
  }
  
  removeFilter(type, value) {
    if (type === 'query') {
      const searchInput = document.getElementById('mainSearchInput');
      if (searchInput) {
        searchInput.value = '';
      }
      this.filters.query = null;
      this.updateUrlParameters();
    } else if (type === 'condition') {
      const checkbox = Array.from(document.querySelectorAll('input[name="condition"]')).find(cb => cb.value === value);
      if (checkbox) checkbox.checked = false;
      this.updateConditionFilters();
    } else if (type === 'category') {
      this.filters.categories = this.filters.categories.filter((c) => String(c) !== String(value));
      document.querySelectorAll('input[name="category"]').forEach((cb) => {
        cb.checked = this.filters.categories.some((c) => String(c) === String(cb.value));
      });
      this.updateUrlParameters();
    } else if (type === 'price') {
      document.getElementById('minPrice').value = '';
      document.getElementById('maxPrice').value = '';
      this.updatePriceFilters();
    } else if (type === 'days') {
      document.getElementById('daysOnSiteFilter').value = '';
      this.updateDaysOnSiteFilter();
    }
    
    this.updateResults();
  }
}
// Email Notification Management System
export class EmailNotificationManager {
  constructor() {
    this.init();
  }
  
  init() {
    // Initialize email preferences if they don't exist
    this.initializeDefaultPreferences();
    
    // Process any delayed emails that are ready
    this.processDelayedEmails();
    
    // Set up periodic processing of delayed emails (every minute)
    setInterval(() => {
      this.processDelayedEmails();
    }, 60000); // Check every minute
  }
  
  // Initialize default email preferences for users
  initializeDefaultPreferences() {
    // This is handled per-user when they access preferences
  }
  
  // Get email preferences for a user
  getUserPreferences(userId) {
    try {
      const prefsData = localStorage.getItem('usedfindr_email_preferences');
      const allPrefs = prefsData ? JSON.parse(prefsData) : {};
      return allPrefs[userId] || this.getDefaultPreferences();
    } catch (error) {
      logger.error('Error getting email preferences:', error);
      return this.getDefaultPreferences();
    }
  }
  
  // Get default preferences
  getDefaultPreferences() {
    return {
      priceAlerts: true,
      contestUpdates: true,
      newItems: false,
      weeklyDigest: false
    };
  }
  
  // Save email preferences for a user
  saveUserPreferences(userId, preferences) {
    try {
      const prefsData = localStorage.getItem('usedfindr_email_preferences');
      const allPrefs = prefsData ? JSON.parse(prefsData) : {};
      allPrefs[userId] = preferences;
      localStorage.setItem('usedfindr_email_preferences', JSON.stringify(allPrefs));
    } catch (error) {
      logger.error('Error saving email preferences:', error);
    }
  }
  
  // Get email queue
  getEmailQueue() {
    try {
      const queueData = localStorage.getItem('usedfindr_email_queue');
      return queueData ? JSON.parse(queueData) : [];
    } catch (error) {
      logger.error('Error getting email queue:', error);
      return [];
    }
  }
  
  // Save email queue
  saveEmailQueue(queue) {
    try {
      localStorage.setItem('usedfindr_email_queue', JSON.stringify(queue));
    } catch (error) {
      logger.error('Error saving email queue:', error);
    }
  }
  
  // Queue an email notification
  queueEmail(type, recipientEmail, subject, body, metadata = {}) {
    try {
      const queue = this.getEmailQueue();
      
      const email = {
        id: `email_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: type, // 'price_alert', 'contest_update', 'new_item', 'weekly_digest'
        recipientEmail: recipientEmail,
        subject: subject,
        body: body,
        metadata: metadata,
        queuedAt: new Date().toISOString(),
        sent: false,
        sentAt: null
      };
      
      queue.push(email);
      
      // Keep only last 1000 emails
      if (queue.length > 1000) {
        queue.shift();
      }
      
      this.saveEmailQueue(queue);
      return email;
    } catch (error) {
      logger.error('Error queueing email:', error);
      return null;
    }
  }
  
  // Queue contest winner notification
  queueContestWinnerEmail(userId, drawing) {
    const users = window.accountManager ? window.accountManager.getAllUsers() : [];
    const user = users.find(u => u.id === userId);
    if (!user || !user.email) return;
    
    const prefs = this.getUserPreferences(userId);
    if (!prefs.contestUpdates) return;
    
    const subject = `Congratulations! You won the UsedFindr ${drawing.monthName} ${drawing.year} Contest!`;
    const body = `
      Congratulations ${user.firstName || 'User'}!
      
      You are the winner of the UsedFindr ${drawing.monthName} ${drawing.year} Monthly Contest!
      
      You won with ${drawing.winner.entryCount} entries.
      
      Please respond to this email within 72 hours to claim your prize.
      
      We'll send your Amazon gift card within 7 days of confirmation.
      
      ---
      UsedFindr Team
    `;
    
    return this.queueEmail('contest_update', user.email, subject, body, {
      drawingId: drawing.id,
      month: drawing.month,
      year: drawing.year
    });
  }
  
  // Mark email as sent
  markEmailAsSent(emailId) {
    const queue = this.getEmailQueue();
    const email = queue.find(e => e.id === emailId);
    if (email) {
      email.sent = true;
      email.sentAt = new Date().toISOString();
      this.saveEmailQueue(queue);
      return true;
    }
    return false;
  }
  
  // Get pending emails
  getPendingEmails() {
    const queue = this.getEmailQueue();
    return queue.filter(e => !e.sent);
  }
  
  // Get sent emails
  getSentEmails() {
    const queue = this.getEmailQueue();
    return queue.filter(e => e.sent).sort((a, b) => 
      new Date(b.sentAt) - new Date(a.sentAt)
    );
  }
  
  // Queue new item notification email
  queueNewItemEmail(userId, itemData) {
    const users = window.accountManager ? window.accountManager.getAllUsers() : [];
    const user = users.find(u => u.id === userId);
    if (!user || !user.email) return;
    
    // Check if user has new item notifications enabled
    const prefs = this.getUserPreferences(userId);
    if (!prefs.newItems) return;
    
    // Check if user is premium
    const isPremium = window.accountManager && window.accountManager.isUserPremium(userId);
    
    // Get item posted time
    const itemPostedAt = itemData.itemPostedAt || itemData.addedAt || new Date().toISOString();
    const postedTime = new Date(itemPostedAt).getTime();
    const now = new Date().getTime();
    const timeSincePosted = now - postedTime;
    const tenMinutes = 10 * 60 * 1000; // 10 minutes in milliseconds
    
    const subject = `New Item Added: ${itemData.name}`;
    const body = `
      A new item has been added to ${itemData.category}!
      
      ${itemData.name}
      Price: ${itemData.price}
      Condition: ${itemData.condition}
      
      View the item: ${window.location.origin}/search.html?q=${encodeURIComponent(itemData.name)}
      
      ---
      UsedFindr
      You can manage your email preferences in your account settings.
    `;
    
    const emailMetadata = {
      userId: userId,
      productId: itemData.productId,
      productName: itemData.name,
      category: itemData.category,
      itemPostedAt: itemPostedAt
    };
    
    // Premium users get alerts immediately (within first 10 minutes)
    // Regular users get alerts after 10 minutes
    if (isPremium && timeSincePosted < tenMinutes) {
      // Premium user, item posted less than 10 minutes ago - send immediately
      return this.queueEmail('new_item', user.email, subject, body, emailMetadata);
    } else if (!isPremium) {
      // Regular user - schedule for 10 minutes after item was posted
      const delay = Math.max(0, tenMinutes - timeSincePosted);
      
      if (delay > 0) {
        // Schedule delayed email
        setTimeout(() => {
          this.queueEmail('new_item', user.email, subject, body, emailMetadata);
        }, delay);
        
        // Also store in delayed queue for persistence
        this.queueDelayedEmail(userId, user.email, subject, body, emailMetadata, delay);
        return { delayed: true, delayMs: delay };
      } else {
        // More than 10 minutes have passed, send immediately
        return this.queueEmail('new_item', user.email, subject, body, emailMetadata);
      }
    } else {
      // Premium user but more than 10 minutes have passed - send immediately anyway
      return this.queueEmail('new_item', user.email, subject, body, emailMetadata);
    }
  }
  
  // Queue a delayed email (for persistence across page refreshes)
  queueDelayedEmail(userId, recipientEmail, subject, body, metadata, delayMs) {
    try {
      const delayedData = localStorage.getItem('usedfindr_delayed_emails');
      const delayedEmails = delayedData ? JSON.parse(delayedData) : [];
      
      const delayedEmail = {
        id: `delayed_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        userId: userId,
        recipientEmail: recipientEmail,
        subject: subject,
        body: body,
        metadata: metadata,
        scheduledFor: new Date(Date.now() + delayMs).toISOString(),
        queuedAt: new Date().toISOString()
      };
      
      delayedEmails.push(delayedEmail);
      localStorage.setItem('usedfindr_delayed_emails', JSON.stringify(delayedEmails));
      
      return delayedEmail;
    } catch (error) {
      logger.error('Error queueing delayed email:', error);
      return null;
    }
  }
  
  // Process delayed emails (should be called periodically)
  processDelayedEmails() {
    try {
      const delayedData = localStorage.getItem('usedfindr_delayed_emails');
      if (!delayedData) return;
      
      const delayedEmails = JSON.parse(delayedData);
      const now = new Date();
      const processed = [];
      const remaining = [];
      
      delayedEmails.forEach(email => {
        const scheduledTime = new Date(email.scheduledFor);
        if (now >= scheduledTime) {
          // Time to send this email
          this.queueEmail('new_item', email.recipientEmail, email.subject, email.body, email.metadata);
          processed.push(email.id);
        } else {
          remaining.push(email);
        }
      });
      
      if (processed.length > 0) {
        localStorage.setItem('usedfindr_delayed_emails', JSON.stringify(remaining));
        logger.info(`Processed ${processed.length} delayed email(s)`);
      }
    } catch (error) {
      logger.error('Error processing delayed emails:', error);
    }
  }
}

// Category Following Management System (server-backed with in-memory cache)
export class CategoryFollowingManager {
  constructor() {
    this._followedIds = [];
    this._followRows = new Map();
    this._loaded = false;
  }

  async loadFromApi(force = false) {
    if (this._loaded && !force) return this._followedIds;
    if (!window.apiService) return this._followedIds;
    try {
      const data = await window.apiService.getCategoryFollows();
      const follows = data.follows || [];
      this._followedIds = follows.map((f) => String(f.ebayCategoryId || f.ebay_category_id || '')).filter(Boolean);
      this._followRows = new Map(
        follows.map((f) => [String(f.ebayCategoryId || f.ebay_category_id), f]),
      );
      this._loaded = true;
    } catch (e) {
      logger.warn('Could not load category follows from API', e);
    }
    return this._followedIds;
  }

  getFollowedCategories(userId) {
    return [...this._followedIds];
  }

  isFollowingCategory(userId, categoryName) {
    return this._followedIds.includes(String(categoryName));
  }

  async followCategory(userId, categoryName) {
    if (!window.apiService) throw new Error('API unavailable');
    const catId = String(categoryName);
    await window.apiService.followCategory(catId);
    if (!this._followedIds.includes(catId)) {
      this._followedIds.push(catId);
    }
    await this.loadFromApi(true);
    return true;
  }

  async unfollowCategory(userId, categoryName) {
    if (!window.apiService) throw new Error('API unavailable');
    const catId = String(categoryName);
    const row = this._followRows.get(catId);
    if (row?.id) {
      await window.apiService.unfollowCategoryById(row.id);
    } else {
      await window.apiService.unfollowCategoryByEbayId(catId);
    }
    this._followedIds = this._followedIds.filter((id) => id !== catId);
    await this.loadFromApi(true);
    return true;
  }

  /** @deprecated Server-side only; returns [] */
  getUsersFollowingCategory(categoryName) {
    return [];
  }
}
// Social Sharing Management System
export class SocialShareManager {
  constructor() {
    this.init();
  }
  
  init() {
    // Initialize sharing analytics
  }
  
  // Track social share
  trackShare(platform, productId, productName) {
    try {
      const sharesData = localStorage.getItem('usedfindr_social_shares');
      const shares = sharesData ? JSON.parse(sharesData) : [];
      
      shares.push({
        platform: platform,
        productId: productId,
        productName: productName,
        timestamp: new Date().toISOString(),
        date: new Date().toLocaleDateString()
      });
      
      // Keep only last 1000 shares
      if (shares.length > 1000) {
        shares.shift();
      }
      
      localStorage.setItem('usedfindr_social_shares', JSON.stringify(shares));
    } catch (error) {
      logger.error('Error tracking social share:', error);
    }
  }
  
  // Get share statistics
  getShareStats() {
    try {
      const sharesData = localStorage.getItem('usedfindr_social_shares');
      const shares = sharesData ? JSON.parse(sharesData) : [];
      
      const stats = {
        total: shares.length,
        byPlatform: {},
        recent: shares.slice(-50)
      };
      
      shares.forEach(share => {
        stats.byPlatform[share.platform] = (stats.byPlatform[share.platform] || 0) + 1;
      });
      
      return stats;
    } catch (error) {
      logger.error('Error getting share stats:', error);
      return { total: 0, byPlatform: {}, recent: [] };
    }
  }
  
  // Share via native Web Share API (mobile)
  async shareNative(product) {
    if (!navigator.share) {
      return false;
    }
    
    try {
      const shareData = {
        title: product.name || 'Check out this product on UsedFindr',
        text: `Found a great deal: ${product.name} - ${product.price} on UsedFindr!`,
        url: product.affiliateLink || window.location.href
      };
      
      await navigator.share(shareData);
      this.trackShare('native', product.productId || product.id || product.name, product.name);
      return true;
    } catch (error) {
      if (error.name !== 'AbortError') {
        logger.error('Error sharing:', error);
      }
      return false;
    }
  }
  
  // Share via Facebook
  shareFacebook(product) {
    const url = encodeURIComponent(product.affiliateLink || window.location.href);
    const text = encodeURIComponent(`Check out ${product.name} - ${product.price} on UsedFindr!`);
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`;
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
    this.trackShare('facebook', product.productId || product.id || product.name, product.name);
  }
  
  // Share via Twitter
  shareTwitter(product) {
    const text = encodeURIComponent(`Check out ${product.name} - ${product.price} on UsedFindr!`);
    const url = encodeURIComponent(product.affiliateLink || window.location.href);
    const shareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
    this.trackShare('twitter', product.productId || product.id || product.name, product.name);
  }
  
  // Share via Email
  shareEmail(product) {
    const subject = encodeURIComponent(`Check out ${product.name} on UsedFindr`);
    const body = encodeURIComponent(
      `I found a great deal on UsedFindr:\n\n` +
      `${product.name}\n` +
      `Price: ${product.price}\n` +
      `Condition: ${product.condition || 'N/A'}\n\n` +
      `View it here: ${product.affiliateLink || window.location.href}`
    );
    const mailtoUrl = `mailto:?subject=${subject}&body=${body}`;
    
    window.location.href = mailtoUrl;
    this.trackShare('email', product.productId || product.id || product.name, product.name);
  }
  
  // Copy link to clipboard
  async copyLink(product) {
    const url = product.affiliateLink || window.location.href;
    
    try {
      await navigator.clipboard.writeText(url);
      this.trackShare('copy', product.productId || product.id || product.name, product.name);
      
      // Show notification
      if (window.accountManager && window.accountManager.showNotification) {
        window.accountManager.showNotification('Link copied to clipboard!');
      }
      return true;
    } catch (error) {
      logger.error('Error copying link:', error);
      // Fallback: use old method
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        this.trackShare('copy', product.productId || product.id || product.name, product.name);
        if (window.accountManager && window.accountManager.showNotification) {
          window.accountManager.showNotification('Link copied to clipboard!');
        }
        return true;
      } catch (err) {
        logger.error('Fallback copy failed:', err);
        return false;
      } finally {
        document.body.removeChild(textArea);
      }
    }
  }
}

// Level mapping for reputation system
const REPUTATION_LEVELS = [
  { min: 0, title: 'Newfinder' },
  { min: 100, title: 'Deal Spotter' },
  { min: 500, title: 'Pro Hunter' },
  { min: 1500, title: 'Category Expert' },
  { min: 5000, title: 'Elite Finder' }
];

// Reputation Management System
export class ReputationManager {
  constructor() {
    this.init();
  }
  
  init() {
    // Migrate existing users to have reputation field
    this.migrateUserReputation();
  }
  
  migrateUserReputation() {
    try {
      const usersData = localStorage.getItem('usedfindr_users');
      if (!usersData) return;
      
      const users = JSON.parse(usersData);
      let updated = false;
      
      users.forEach(user => {
        if (user.reputation === undefined) {
          user.reputation = 0;
          updated = true;
        }
      });
      
      if (updated) {
        localStorage.setItem('usedfindr_users', JSON.stringify(users));
        // Also update current user if it exists
        if (window.accountManager && window.accountManager.currentUser) {
          if (window.accountManager.currentUser.reputation === undefined) {
            window.accountManager.currentUser.reputation = 0;
          }
        }
      }
    } catch (error) {
      logger.error('Error migrating user reputation:', error);
    }
  }
  
  async getUserReputation(userId) {
    if (!userId) return 0;
    
    // Try to get from API if available
    if (window.apiService && window.accountManager && window.accountManager.currentUser && 
        window.accountManager.currentUser.id === userId) {
      try {
        const profile = await window.apiService.getUserProfile();
        if (profile) {
          return profile.reputation || 0;
        }
      } catch (error) {
        logger.warn('Could not fetch reputation from API, using fallback');
      }
    }
    
    // Fallback to localStorage
    const users = window.accountManager ? window.accountManager.getAllUsers() : [];
    const user = users.find(u => u.id === userId);
    return user ? (user.reputation || 0) : 0;
  }
  
  async getUserLevel(userId) {
    const reputation = await this.getUserReputation(userId);
    return this.getLevelTitle(reputation);
  }
  
  getLevelTitle(reputation) {
    // Find the highest level the user has reached
    for (let i = REPUTATION_LEVELS.length - 1; i >= 0; i--) {
      if (reputation >= REPUTATION_LEVELS[i].min) {
        return REPUTATION_LEVELS[i].title;
      }
    }
    return REPUTATION_LEVELS[0].title; // Default to Newfinder
  }
  
  awardReputation(userId, amount, reason) {
    // Reputation is now handled by the backend API
    // This method is kept for backward compatibility but is a no-op
    // Reputation changes happen server-side via vote/item endpoints
    if (!userId || userId === 'anonymous' || !amount || amount <= 0) {
      return false;
    }
    // Silent no-op - reputation handled by backend
    return true;
  }
  
  deductReputation(userId, amount, reason) {
    // Reputation is now handled by the backend API
    // This method is kept for backward compatibility but is a no-op
    // Reputation changes happen server-side via vote/item endpoints
    if (!userId || userId === 'anonymous' || !amount || amount <= 0) {
      return false;
    }
    // Silent no-op - reputation handled by backend
    return true;
  }
}
// Vote Management System
export class VoteManager {
  constructor() {
    this.init();
  }
  
  init() {
    // Initialize vote storage if it doesn't exist
    if (!localStorage.getItem('usedfindr_votes')) {
      localStorage.setItem('usedfindr_votes', JSON.stringify({}));
    }
    
    // Initialize vote counts on existing items (delay to ensure items are loaded)
    setTimeout(() => {
      this.initializeVoteCounts();
    }, 100);
  }
  
  initializeVoteCounts() {
    // NOTE: Vote counts now come from the backend API, which is the source of truth.
    // This function should NOT overwrite API vote scores.
    // It's kept for backward compatibility but only updates items that don't have vote scores from the API.
    
    const items = searchData.items || [];
    
    items.forEach(item => {
      const productId = item.productId || item.name;
      if (!productId) return;
      
      // Skip if item has API data (indicated by currentUserVote field or explicit voteScore from API)
      // If currentUserVote exists, this item came from the API and we shouldn't overwrite its vote counts
      if (item.currentUserVote !== undefined || item.current_user_vote !== undefined) {
        // Item has API vote data, don't overwrite
        return;
      }
      
      // Skip if voteScore is explicitly set (even if 0, it means it was loaded from API)
      if (item.voteScore !== undefined) {
        // Vote score already exists (likely from API), don't overwrite
        return;
      }
      
      // Fallback: Calculate from localStorage votes (for legacy items without API data)
      const votes = this.getVotes();
      const itemVotes = votes[productId] || {};
      let upvotes = 0;
      let downvotes = 0;
      
      Object.values(itemVotes).forEach(vote => {
        if (vote === 'up') upvotes++;
        if (vote === 'down') downvotes++;
      });
      
      // Only update if vote counts are missing
      if (item.upvotes === undefined) {
        item.upvotes = upvotes;
      }
      if (item.downvotes === undefined) {
        item.downvotes = downvotes;
      }
      if (item.voteScore === undefined) {
        item.voteScore = upvotes - downvotes;
      }
    });
  }
  
  getVotes() {
    try {
      const votesData = localStorage.getItem('usedfindr_votes');
      return votesData ? JSON.parse(votesData) : {};
    } catch (error) {
      logger.error('Error getting votes:', error);
      return {};
    }
  }
  
  saveVotes(votes) {
    try {
      localStorage.setItem('usedfindr_votes', JSON.stringify(votes));
    } catch (error) {
      logger.error('Error saving votes:', error);
    }
  }
  
  getUserVote(productId, userId) {
    if (!productId || !userId) return null;
    const votes = this.getVotes();
    const voteData = votes[productId] && votes[productId][userId] ? votes[productId][userId] : null;
    
    // Handle both old format (string) and new format (object)
    if (typeof voteData === 'string') {
      return voteData;
    } else if (voteData && typeof voteData === 'object' && voteData.voteType) {
      return voteData.voteType;
    }
    
    return null;
  }
  
  getVoteScore(productId) {
    const stats = this.getVoteStats(productId);
    return stats.score;
  }
  
  getVoteStats(productId) {
    if (!productId) return { upvotes: 0, downvotes: 0, score: 0 };
    
    // Get vote stats from product data (which comes from API)
    const item = searchData.items.find(i => (i.productId || i.name) === productId);
    if (item) {
      return {
        upvotes: item.upvotes || 0,
        downvotes: item.downvotes || 0,
        score: item.voteScore || 0
      };
    }
    
    // Fallback to localStorage cache if item not found
    const votes = this.getVotes();
    const itemVotes = votes[productId] || {};
    
    let upvotes = 0;
    let downvotes = 0;
    
    Object.values(itemVotes).forEach(vote => {
      if (vote === 'up') upvotes++;
      if (vote === 'down') downvotes++;
    });
    
    return {
      upvotes,
      downvotes,
      score: upvotes - downvotes
    };
  }
  
  showBadgeNotification(badgeName, icon) {
    // Show a toast notification for badge earned
    if (window.accountManager && window.accountManager.showNotification) {
      window.accountManager.showNotification(
        `${icon} Badge Earned: ${badgeName}!`,
        'success'
      );
    } else {
      // Fallback to alert if notification system not available
      alert(`${icon} Badge Earned: ${badgeName}!`);
    }
  }
  
  async vote(productId, userId, direction) {
    if (!productId || !userId || !direction) return false;
    if (direction !== 'up' && direction !== 'down') return false;
    
    // Phase 3: token from memory; on full page load token may be empty until refresh runs
    let token = (window.apiService && typeof window.apiService.getAccessToken === 'function')
      ? window.apiService.getAccessToken()
      : null;
    if (!token && window.apiService && typeof window.apiService.checkAuth === 'function') {
      try {
        const ok = await window.apiService.checkAuth();
        if (ok) token = window.apiService.getAccessToken();
      } catch (e) {
        // ignore
      }
    }
    if (!token) {
      logger.error('No authentication token found. Please log in again.');
      alert('You must be logged in to vote. Please log in and try again.');
      if (window.location.pathname !== '/login.html' && window.location.pathname !== '/register.html') {
        window.location.href = 'login.html';
      }
      return false;
    }
    
    // Use API to vote
    try {
      if (!window.apiService) {
        logger.error('API service not available');
        return false;
      }
      
      const response = await window.apiService.voteProduct(productId, direction);
      
      // Check if user already had a vote (to determine if this is a new vote or vote change)
      const votes = this.getVotes();
      const hadPreviousVote = votes[productId] && votes[productId][userId];
      
      // Update local cache for quick access
      if (!votes[productId]) {
        votes[productId] = {};
      }
      // Store vote with timestamp for activity tracking
      votes[productId][userId] = {
        voteType: direction,
        timestamp: new Date().toISOString()
      };
      this.saveVotes(votes);
      
      // Update item vote counts from product data (should be refreshed from API)
      const item = searchData.items.find(i => (i.productId || i.name) === productId);
      if (item && window.apiService) {
        // Refresh product data to get updated vote counts
        try {
          const productData = await window.apiService.getProduct(productId);
          if (productData) {
            item.upvotes = productData.upvotes || 0;
            item.downvotes = productData.downvotes || 0;
            item.voteScore = productData.voteScore || 0;
            // Update currentUserVote from API response (source of truth) or use direction
            item.currentUserVote = productData.currentUserVote !== undefined 
              ? productData.currentUserVote 
              : (productData.current_user_vote !== undefined ? productData.current_user_vote : direction);
            item.current_user_vote = item.currentUserVote; // Also update legacy field name
            this.updateItemVoteCounts(productId, item.upvotes, item.downvotes);
          }
        } catch (e) {
          logger.warn('Could not refresh product data, using cached counts');
          // Optimistically update currentUserVote even if API call fails
          if (item) {
            item.currentUserVote = direction;
            item.current_user_vote = direction;
          }
        }
      } else if (item) {
        // Optimistically update currentUserVote if item exists but API service unavailable
        item.currentUserVote = direction;
        item.current_user_vote = direction;
      }
      
      // Show badge notification if badges were awarded
      if (response.newlyAwardedBadges && response.newlyAwardedBadges.length > 0) {
        response.newlyAwardedBadges.forEach(badge => {
          this.showBadgeNotification(badge.badgeName, badge.icon);
        });
      }
      
      // Update sidebar reputation instantly (+1 for casting a vote)
      if (typeof window.updateSidebarReputation === 'function') {
        window.updateSidebarReputation(1);
      }
      
      // Update sidebar vote count: +1 for new vote, 0 for vote change
      if (typeof window.updateSidebarVoteCount === 'function') {
        const voteCountDelta = hadPreviousVote ? 0 : 1; // Only increment for new votes
        window.updateSidebarVoteCount(voteCountDelta);
      }
      
      // Refresh activity view if we're on the activity page
      if (window.accountManager && typeof window.accountManager.updateActivityView === 'function') {
        // Check if we're on the activity view
        const activityView = document.getElementById('activity-view');
        if (activityView && activityView.style.display !== 'none') {
          setTimeout(() => {
            window.accountManager.updateActivityView();
          }, 500); // Small delay to ensure vote is saved
        }
      }
      
      return true;
    } catch (error) {
      logger.error('Error voting on product:', error);
      
      // Handle authentication errors specifically (Phase 3: clear in-memory token)
      if (error.message && error.message.includes('Authentication credentials')) {
        alert('Your session has expired. Please log in again to vote.');
        if (window.apiService && typeof window.apiService.clearAccessToken === 'function') {
          window.apiService.clearAccessToken();
        }
        if (window.location.pathname !== '/login.html' && window.location.pathname !== '/register.html') {
          window.location.href = 'login.html';
        }
      } else {
        alert('Failed to submit vote. Please try again.');
      }
      
      // Re-render the product card to revert any optimistic updates
      const productCard = document.querySelector(`[data-product-card-id="${escapeHtml(productId)}"]`);
      if (productCard) {
        const item = searchData.items.find(i => (i.productId || i.name) === productId);
        if (item) {
          const newHTML = renderProductCard(item);
          if (newHTML) productCard.outerHTML = newHTML;
        }
      }
      
      return false;
    }
  }
  
  async removeVote(productId, userId) {
    if (!productId || !userId) return false;
    
    // Phase 3: token from memory; try refresh first on full page load
    let token = (window.apiService && typeof window.apiService.getAccessToken === 'function')
      ? window.apiService.getAccessToken()
      : null;
    if (!token && window.apiService && typeof window.apiService.checkAuth === 'function') {
      try {
        const ok = await window.apiService.checkAuth();
        if (ok) token = window.apiService.getAccessToken();
      } catch (e) {
        // ignore
      }
    }
    if (!token) {
      logger.error('No authentication token found. Please log in again.');
      alert('You must be logged in to vote. Please log in and try again.');
      if (window.location.pathname !== '/login.html' && window.location.pathname !== '/register.html') {
        window.location.href = 'login.html';
      }
      return false;
    }
    
    // Use API to remove vote
    try {
      if (!window.apiService) {
        logger.error('API service not available');
        return false;
      }
      
      try {
        await window.apiService.removeVote(productId);
      } catch (error) {
        // Handle 404 gracefully - vote doesn't exist in DB (might only be in localStorage)
        const errorMessage = error.message || String(error);
        if (errorMessage.includes('404') || errorMessage.includes('Not found')) {
          // Vote doesn't exist in database - treat as success (already removed)
          logger.debug('Vote not found in database (may only exist in localStorage)');
        } else if (errorMessage.includes('Authentication credentials')) {
          // Authentication error - rethrow to be handled below
          throw error;
        } else {
          // Other errors - rethrow
          throw error;
        }
      }
      
      // Update local cache
      const votes = this.getVotes();
      if (votes[productId] && votes[productId][userId]) {
        delete votes[productId][userId];
        
        // Clean up empty product entries
        if (Object.keys(votes[productId]).length === 0) {
          delete votes[productId];
        }
        
        this.saveVotes(votes);
      }
      
      // Update item vote counts from product data
      const item = searchData.items.find(i => (i.productId || i.name) === productId);
      if (item && window.apiService) {
        try {
          const productData = await window.apiService.getProduct(productId);
          if (productData) {
            item.upvotes = productData.upvotes || 0;
            item.downvotes = productData.downvotes || 0;
            item.voteScore = productData.voteScore || 0;
            // Update currentUserVote from API response (source of truth) or set to null
            item.currentUserVote = productData.currentUserVote !== undefined 
              ? productData.currentUserVote 
              : (productData.current_user_vote !== undefined ? productData.current_user_vote : null);
            item.current_user_vote = item.currentUserVote; // Also update legacy field name
            this.updateItemVoteCounts(productId, item.upvotes, item.downvotes);
          }
        } catch (e) {
          logger.warn('Could not refresh product data, using cached counts');
          // Optimistically update currentUserVote even if API call fails
          if (item) {
            item.currentUserVote = null;
            item.current_user_vote = null;
          }
        }
      } else if (item) {
        // Optimistically update currentUserVote if item exists but API service unavailable
        item.currentUserVote = null;
        item.current_user_vote = null;
      }
      
      // Update sidebar reputation instantly (-1 for removing a vote)
      if (typeof window.updateSidebarReputation === 'function') {
        window.updateSidebarReputation(-1);
      }
      
      // Update sidebar vote count instantly (-1 for removing a vote)
      if (typeof window.updateSidebarVoteCount === 'function') {
        window.updateSidebarVoteCount(-1);
      }
      
      return true;
    } catch (error) {
      logger.error('Error removing vote:', error);
      
      // Handle authentication errors specifically (Phase 3: clear in-memory token)
      if (error.message && error.message.includes('Authentication credentials')) {
        alert('Your session has expired. Please log in again to vote.');
        if (window.apiService && typeof window.apiService.clearAccessToken === 'function') {
          window.apiService.clearAccessToken();
        }
        if (window.location.pathname !== '/login.html' && window.location.pathname !== '/register.html') {
          window.location.href = 'login.html';
        }
      } else {
        alert('Failed to remove vote. Please try again.');
      }
      
      // Re-render the product card to revert any optimistic updates
      const productCard = document.querySelector(`[data-product-card-id="${escapeHtml(productId)}"]`);
      if (productCard) {
        const item = searchData.items.find(i => (i.productId || i.name) === productId);
        if (item) {
          const newHTML = renderProductCard(item);
          if (newHTML) productCard.outerHTML = newHTML;
        }
      }
      
      return false;
    }
  }
  
  updateItemVoteCounts(productId, upvotes, downvotes) {
    const score = upvotes - downvotes;
    
    // Update in memory
    const item = searchData.items.find(i => (i.productId || i.name) === productId);
    if (item) {
      item.upvotes = upvotes;
      item.downvotes = downvotes;
      item.voteScore = score;
    }
    
    // Update in localStorage
    const storedItems = localStorage.getItem('usedfindr_items');
    if (storedItems) {
      try {
        const items = JSON.parse(storedItems);
        const itemIndex = items.findIndex(i => (i.productId || i.name) === productId);
        if (itemIndex !== -1) {
          items[itemIndex].upvotes = upvotes;
          items[itemIndex].downvotes = downvotes;
          items[itemIndex].voteScore = score;
          localStorage.setItem('usedfindr_items', JSON.stringify(items));
        }
      } catch (error) {
        logger.error('Error updating item vote counts:', error);
      }
    }
  }
}
let advancedSearch;

// Make managers globally available
let accountManager;
let cookieConsent;
let adminManager;
export let reportManager;
let contestManager;
let socialShareManager;
let emailNotificationManager;
let categoryFollowingManager;
let voteManager;
let reputationManager;

// Helper functions for report button clicks (must be in global scope for onclick)
function handleApproveReport(button) {
  const reportId = button.getAttribute('data-report-id');
  logger.info('handleApproveReport called with reportId:', reportId);
  
  if (!window.adminManager) {
    logger.error('adminManager not available');
    alert('Admin manager not available. Please refresh the page.');
    return;
  }
  
  if (!reportId) {
    logger.error('No report ID found on button');
    alert('Error: Report ID not found.');
    return;
  }
  
  window.adminManager.approveReport(reportId).catch(error => {
    logger.error('Error in approveReport:', error);
    alert('Error approving report: ' + (error.message || 'Unknown error'));
  });
}

function handleRejectReport(button) {
  const reportId = button.getAttribute('data-report-id');
  logger.info('handleRejectReport called with reportId:', reportId);
  
  if (!window.adminManager) {
    logger.error('adminManager not available');
    alert('Admin manager not available. Please refresh the page.');
    return;
  }
  
  if (!reportId) {
    logger.error('No report ID found on button');
    alert('Error: Report ID not found.');
    return;
  }
  
  window.adminManager.rejectReport(reportId).catch(error => {
    logger.error('Error in rejectReport:', error);
    alert('Error rejecting report: ' + (error.message || 'Unknown error'));
  });
}

document.addEventListener('DOMContentLoaded', async () => {
  // Show/hide file:// protocol warning based on protocol (for public pages)
  // Admin pages handle this in admin.html script section
  const fileProtocolWarning = document.getElementById('fileProtocolWarning');
  if (fileProtocolWarning) {
    if (window.location.protocol === 'file:') {
      fileProtocolWarning.classList.add('show');
    } else {
      // Explicitly hide banner when not on file:// protocol
      fileProtocolWarning.classList.remove('show');
      fileProtocolWarning.style.display = 'none';
    }
  }
  
  // CRITICAL: Clear any existing items first to prevent stale data
  searchData.items = [];
  
  // OPTIMIZATION: Initialize categories page immediately (doesn't need products)
  // This prevents blocking category display on slow API calls
  // categoryManager is imported at the top of this file, so it's available immediately
  const pathForCats = window.location.pathname || '';
  if ((pathForCats.includes('categories.html') || /\/categories\/?$/.test(pathForCats)) && categoryManager) {
    initializeCategoriesPage();
  }
  
  // OPTIMIZATION: For search page, load from localStorage immediately for fast initial render
  // Then update from API in background
  const isSearchPage = window.location.pathname.includes('search.html');
  const isCategoriesPage = window.location.pathname.includes('categories.html');
  
  if (isSearchPage) {
    // Load from localStorage immediately for fast initial render
    const storedItems = localStorage.getItem('usedfindr_items');
    if (storedItems) {
      try {
        const items = JSON.parse(storedItems);
        // Filter out removed items
        const removedItemsStorage = localStorage.getItem('usedfindr_removed_items');
        const removedItems = removedItemsStorage ? JSON.parse(removedItemsStorage) : [];
        const filteredItems = items.filter(item => {
          const key = item.productId || item.product_id || item.name;
          return !removedItems.some(removed => (removed.productId || removed.product_id || removed.name) === key);
        });
        
        // Convert to searchData format
        filteredItems.forEach(storedItem => {
          searchData.items.push({
            name: storedItem.name,
            category: storedItem.category,
            mainCategory: storedItem.mainCategory || storedItem.main_category || null,
            subcategory: storedItem.subcategory || null,
            condition: storedItem.condition,
            price: storedItem.price,
            retailPrice: storedItem.retailPrice || storedItem.retail_price || null,
            affiliateLink: storedItem.affiliateLink || storedItem.affiliate_link,
            imageUrl: storedItem.imageUrl || storedItem.image_url,
            productId: storedItem.productId || storedItem.product_id,
            addedAt: storedItem.addedAt || storedItem.added_at,
            clickCount: storedItem.clickCount || storedItem.click_count || 0,
            submitterUserId: storedItem.submitterUserId || storedItem.submitter_user_id || null,
            submitterUsername: storedItem.submitterUsername || storedItem.submitter_username || null,
            voteScore: storedItem.voteScore || storedItem.vote_score || 0,
            upvotes: storedItem.upvotes || 0,
            downvotes: storedItem.downvotes || 0,
            currentUserVote: storedItem.currentUserVote || storedItem.current_user_vote || null,
            hotDealBonusAwarded: storedItem.hotDealBonusAwarded || storedItem.hot_deal_bonus_awarded || false,
            earlyAccess: storedItem.earlyAccess || storedItem.early_access || false,
            earlyAccessUntil: storedItem.earlyAccessUntil || storedItem.early_access_until || null
          });
        });
        logger.info(`Loaded ${searchData.items.length} items from localStorage for immediate display (search page).`);
      } catch (e) {
        logger.warn('Failed to load items from localStorage:', e);
      }
    }
  }
  
  /**
   * Set up global event delegation for data-action attributes
   * Handles clicks on elements with data-action attributes throughout the app
   */
  function setupGlobalEventDelegation() {
    document.addEventListener('click', (e) => {
      const element = e.target.closest('[data-action]');
      if (!element) return;
      
      const action = element.dataset.action;
      
      switch (action) {
        case 'dismiss-file-warning':
          const warning = document.getElementById('fileProtocolWarning');
          if (warning) {
            warning.style.display = 'none';
          }
          break;
        case 'open-add-item-modal':
          if (typeof openAddItemModalGlobal === 'function') {
            openAddItemModalGlobal();
          }
          break;
        case 'close-modal':
          const modalId = element.dataset.modal;
          if (modalId && typeof closeAddItemModalGlobal === 'function') {
            closeAddItemModalGlobal();
          }
          break;
        case 'close-report-modal':
          if (window.reportManager && typeof window.reportManager.closeReportModal === 'function') {
            window.reportManager.closeReportModal();
          }
          break;
        case 'reload-for-update':
          if (typeof reloadForUpdate === 'function') {
            reloadForUpdate();
          }
          break;
        case 'dismiss-update':
          if (typeof dismissUpdate === 'function') {
            dismissUpdate();
          }
          break;
        case 'rail-sign-out':
          e.preventDefault();
          if (window.accountManager && typeof window.accountManager.logout === 'function') {
            window.accountManager.logout();
          }
          break;
        case 'stop-propagation':
          e.stopPropagation();
          break;
        case 'navigate-external-product': {
          e.preventDefault();
          e.stopPropagation();
          const extUrl = element.dataset.productUrl;
          const trackId = element.dataset.productId || element.dataset.listingId;
          if (extUrl) {
            // No trackId guard: trackProductClick tolerates an empty id and is
            // what fires the cookieless click beacon, so skipping it here would
            // drop the click from the traffic tab entirely.
            if (typeof trackProductClick === 'function') {
              const marketplace = element.dataset.marketplace
                || (typeof classifyMarketplaceFromUrl === 'function' ? classifyMarketplaceFromUrl(extUrl) : '')
                || '';
              trackProductClick(trackId, {
                url: extUrl,
                marketplace,
                condition: element.dataset.condition || '',
              });
            }
            window.open(extUrl, '_blank');
            showOutboundNotice(extUrl);
          }
          break;
        }
        case 'navigate-product': {
          const productUrl = element.dataset.productUrl;
          if (productUrl) {
            if (element.dataset.externalLink === 'true') {
              window.open(productUrl, '_blank');
              showOutboundNotice(productUrl);
            } else {
              window.location.href = productUrl;
            }
          }
          break;
        }
        case 'vote':
          const voteProductId = element.dataset.productId;
          const voteType = element.dataset.voteType;
          if (voteProductId && voteType && typeof handleVoteClick === 'function') {
            e.stopPropagation();
            handleVoteClick(voteProductId, voteType);
          }
          break;
        case 'card-watch': {
          e.stopPropagation();
          const card = element.closest('[data-product-url]');
          const watchUrl = (card && card.dataset.productUrl) || element.dataset.productUrl;
          if (watchUrl) {
            window.location.href = watchUrl.includes('#') ? watchUrl : `${watchUrl}#watch`;
          }
          break;
        }
        case 'report-product':
          const reportProductId = element.dataset.productId;
          if (reportProductId && window.reportManager && typeof window.reportManager.openReportModal === 'function') {
            e.stopPropagation();
            window.reportManager.openReportModal(reportProductId);
          }
          break;
        case 'share':
          const shareType = element.dataset.shareType;
          if (shareType && typeof handleShareClick === 'function') {
            e.stopPropagation();
            handleShareClick(element, shareType);
          }
          break;
        case 'navigate':
          const url = element.dataset.url;
          if (url) {
            window.location.href = url;
          }
          break;
        case 'retry-catalog-load':
          if (window.adminManager && typeof window.adminManager.loadCatalogTable === 'function') {
            window.adminManager.loadCatalogTable();
          }
          break;
      }
    });
  }

  // CRITICAL: Always force a fresh load from API on page load
  // This ensures we never use stale localStorage data
  // Load in background (non-blocking) for categories and search pages
  // Skip product loading on trusted-finder page - it has its own search functionality
  // Skip on admin console to avoid triggering token refresh when no session (Phase 3)
  const isTrustedFinderPage = window.location.pathname.includes('trusted-finder.html');
  const isAdminPageForProducts = window.location.pathname.includes('/admin');
  const loadProductsPromise = (isTrustedFinderPage || isAdminPageForProducts)
    ? Promise.resolve().then(() => {
        if (isAdminPageForProducts) {
          logger.info('script.js: Skipping product loading on admin page (no auth)');
        } else {
          logger.info('script.js: Skipping product loading on trusted-finder page');
        }
      })
    : loadItemsFromStorage(true, isHomepagePath() ? { limit: HOME_FIRST_PAGE_LIMIT } : {}).then(() => {
        logger.info('DOMContentLoaded: Items loaded from API, searchData.items.length =', searchData.items.length);
      }).catch(error => {
        logger.error('DOMContentLoaded: Error loading items:', error);
        // Continue anyway - initializeItemDisplay will handle fallback
      });
  
  // Only wait for products if we're NOT on categories or search pages
  // These pages can show content immediately from localStorage
  if (!isCategoriesPage && !isSearchPage) {
    await loadProductsPromise;
  }

  // One subscriber for the whole page: console-shell.js owns the selector,
  // floor-state.js owns the value, and this is the listing surfaces' response
  // to it. Registered under the shell only, for the same reason the fetch
  // sends `floor` only there.
  if (document.body && document.body.classList.contains('console')) {
    onFloorChange(() => {
      // The pinned deal is judged again at once (it is already in hand), and
      // once more when the refetched feed lands, whose best listing may be the
      // fallback.
      applyFeaturedForFloor();
      refreshListingsForFloor().then(applyFeaturedForFloor);
    });
    // Tiles or Rows is a layout change over listings already in hand, so it
    // redraws rather than refetching.
    onViewChange(() => { initializeItemDisplay(); });
  }

  // Ensure global modal handlers are in place as early as possible
  ensureAddItemModalInitialized();
  attachGlobalAddItemTriggers();
  
  // Set up global event delegation for data-action attributes
  setupGlobalEventDelegation();
  
  // Any page whose pathname contains /admin is admin (SPA or admin-*.html)
  const isAdminPage = window.location.pathname.includes('/admin');

  // Initialize ContestManager globally (available on all pages)
  contestManager = new ContestManager();
  window.contestManager = contestManager;
  
  // Initialize VoteManager globally (available on all pages)
  voteManager = new VoteManager();
  window.voteManager = voteManager;
  
  // Initialize ReputationManager globally (available on all pages)
  reputationManager = new ReputationManager();
  window.reputationManager = reputationManager;
  
    // Initialize SocialShareManager globally (available on all pages)
    socialShareManager = new SocialShareManager();
    window.socialShareManager = socialShareManager;
  
    // Initialize EmailNotificationManager globally (available on all pages)
    emailNotificationManager = new EmailNotificationManager();
    window.emailNotificationManager = emailNotificationManager;
    
    // Initialize AnalyticsManager globally (available on all pages)
    // analyticsManager is already imported and set on window at the top
    // Just ensure it's available
    if (!window.analyticsManager) {
      window.analyticsManager = analyticsManager;
    }
  
  // Initialize CategoryFollowingManager globally (available on all pages)
  categoryFollowingManager = new CategoryFollowingManager();
  window.categoryFollowingManager = categoryFollowingManager;

  if (isAdminPage) {
    // --- ADMIN-ONLY SCRIPTS ---
    // Only initialize the AdminManager.
    // DO NOT initialize AccountManager or CookieConsent here.
    // Chunk request started at module-evaluation time; see adminManagerModule.
    const { AdminManager } = await (adminManagerModule ?? import('./admin-manager.js'));
    adminManager = new AdminManager();
    // Make adminManager globally available for router
    window.adminManager = adminManager;
    window.adminManagerReady = Promise.resolve(adminManager);
    new SearchAutofill(); // The search bar is on admin pages
  } else {
    // --- PUBLIC-ONLY SCRIPTS ---
    // Initialize all scripts *except* the AdminManager.
    accountManager = new AccountManager();
    window.accountManager = accountManager; // Make globally available
    initNotificationsInbox().catch((e) => logger.warn('notifications inbox init failed', e));
    const bootUser = accountManager.getCurrentUser?.();
    if (bootUser?.id) {
      migrateLocalCategoryFollows(bootUser.id)
        .then(() => categoryFollowingManager.loadFromApi())
        .catch((e) => logger.warn('category follow migration failed', e));
    }
    setInterval(() => {
      refreshNotificationBadge().catch(() => {});
    }, 60000);
    cookieConsent = new CookieConsent();
    reportManager = new ReportManager(); // Report modal is on public site
    window.reportManager = reportManager; // Make globally available for onclick handlers
    new SearchAutofill();

    // Cookieless secondary analytics (no consent gate). Extra skip for auth/account
    // pages that still load script.js — allowlist also rejects these server-side.
    const cookielessPath = (window.location.pathname || '').toLowerCase();
    const skipCookieless =
      cookielessPath.includes('/admin') ||
      cookielessPath.includes('account') ||
      cookielessPath.includes('login') ||
      cookielessPath.includes('register');
    if (!skipCookieless) {
      initializeCookielessPageAnalytics();
    }
    
    // Initialize item display on public pages (except search, categories, and trusted-finder pages)
    // Categories page already initialized categories above, and doesn't need products for category cards
    // Trusted-finder page has its own initialization logic
    // Items should already be loaded above, but initializeItemDisplay will handle it if not
    if (window.location.pathname.includes('categories.html')) {
      // Categories page: wait for products in background, then initialize item display if needed
      // (e.g., if filtering by category/condition)
      loadProductsPromise.then(() => {
        initializeItemDisplay();
      }).catch(() => {
        // If products fail to load, still try to initialize (will use fallback)
        initializeItemDisplay();
      });
    } else if (window.location.pathname.includes('trusted-finder.html')) {
      // Trusted-finder page has its own initialization - don't interfere
      logger.info('script.js: Skipping initializeItemDisplay on trusted-finder page');
    } else if (!window.location.pathname.includes('search.html')) {
      await initializeItemDisplay();
    } else {
      // Initialize advanced search on search page
      // Items are already loaded from localStorage above for fast initial render
      advancedSearch = new AdvancedSearch();
      window.advancedSearch = advancedSearch; // Make globally available
      
      // Initialize immediately with localStorage items, then update from API in background
      await advancedSearch.init();
      
      // Update from API in background (non-blocking)
      loadProductsPromise.then(() => {
        // Refresh search results with fresh API data
        if (advancedSearch && typeof advancedSearch.updateResults === 'function') {
          advancedSearch.allItems = [...(searchData.items || [])];
          advancedSearch.updateResults();
        }
      }).catch(() => {
        // API failed, but we already have localStorage items displayed
        logger.info('Search page: API update failed, continuing with localStorage items');
      });
    }
    
    // Initialize category following on categories page (already done in initializeCategoriesPage, but ensure it's called)
    if (window.location.pathname.includes('categories.html')) {
      // Already initialized in initializeCategoriesPage, but ensure it's set up
      initializeCategoryFollowing();
    }
    
    // Initialize view tracking for product cards
    initViewTracking();
    
    // Track initial page view
    trackPageView();
    
    // Ensure navigation is updated after all initialization
    if (accountManager && typeof accountManager.updateNavigation === 'function') {
      accountManager.updateNavigation();
    }

    // When session is invalidated (401), clear in-memory user and refresh nav/sidebar
    window.addEventListener('auth:sessionInvalidated', () => {
      if (accountManager) {
        accountManager.currentUser = accountManager.getCurrentUser();
        if (typeof accountManager.updateNavigation === 'function') {
          accountManager.updateNavigation();
        }
      }
    });

    // Initialize navbar highlighting
    highlightActiveNavLink();
    
    // Initialize quick filter pills on homepage
    if (window.location.pathname.includes('index.html') || window.location.pathname === '/' || window.location.pathname.endsWith('/')) {
      initializeQuickFilters();
    }
  }
  
  // Also highlight on admin pages (after admin initialization)
  if (isAdminPage) {
    highlightActiveNavLink();
  }

  // BFCache restore: drop stray body scroll lock when no modal or mobile menu is open.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    const drawer = document.getElementById('mobileMenuDrawer');
    const drawerOpen = drawer?.classList.contains('is-open');
    const overlay = document.getElementById('mobileMenuOverlay');
    const overlayOpen = overlay?.classList.contains('is-open');
    const isMobile = window.innerWidth <= 768;
    const mobileAccountSidebarLock =
      document.documentElement.classList.contains('account-sidebar-expanded') && isMobile;
    const anyModalOpen = !!document.querySelector('.modal.is-visible');
    const shouldLockScroll = anyModalOpen || drawerOpen || mobileAccountSidebarLock;
    if (!shouldLockScroll) {
      document.body.style.removeProperty('overflow');
      document.body.classList.remove('mobile-menu-open');
      if (overlayOpen) {
        overlay.classList.remove('is-open');
      }
      if (window.mobileMenuInstance) {
        window.mobileMenuInstance.isOpen = false;
      }
    }
  });
});

// Initialize Quick Filter Pills
function initializeQuickFilters() {
  const quickFiltersContainer = document.getElementById('quickFilters');
  if (!quickFiltersContainer) {
    return; // Not on homepage
  }
  
  // Wait for categoryManager to be available
  if (typeof categoryManager === 'undefined') {
    // Retry after a short delay
    setTimeout(initializeQuickFilters, 100);
    return;
  }
  
  const mainCategories = categoryManager.getMainCategories();
  
  // Limit to top 6 categories for quick filters
  const topCategories = mainCategories.slice(0, 6);
  
  // Create pill for each category
  topCategories.forEach(category => {
    const pill = document.createElement('button');
    pill.className = 'quick-filter-pill';
    pill.setAttribute('data-category', category);
    pill.textContent = category;
    
    // Add click event
    pill.addEventListener('click', () => {
      // Toggle active state
      const isActive = pill.classList.contains('active');
      
      // Remove active from all pills
      document.querySelectorAll('.quick-filter-pill').forEach(p => {
        p.classList.remove('active');
      });
      
      if (!isActive) {
        // Set this pill as active
        pill.classList.add('active');
        
        // Navigate to categories page with filter, or filter current page
        if (typeof currentCategoryFilter !== 'undefined') {
          // If we're on homepage with category filters, use that
          setActiveCategoryFilter(category);
          if (typeof filterAndRenderDeals === 'function') {
            filterAndRenderDeals();
          }
        } else {
          // Navigate to categories page
          window.location.href = `categories.html?category=${encodeURIComponent(category)}`;
        }
      } else {
        // If clicking active pill, deselect and show all
        if (typeof currentCategoryFilter !== 'undefined' && typeof filterAndRenderDeals === 'function') {
          setActiveCategoryFilter('all');
          filterAndRenderDeals();
        }
      }
    });
    
    quickFiltersContainer.appendChild(pill);
  });
}

/**
 * Highlights the active navigation link based on the current page
 */
function highlightActiveNavLink() {
  const currentPath = window.location.pathname;
  const currentPage = currentPath.split('/').pop() || 'index.html';
  
  // Remove active class from all nav links
  document.querySelectorAll('.nav-link').forEach(link => {
    link.classList.remove('active');
  });
  
  // Determine which nav link to highlight based on current page
  let targetNav = null;
  
  if (currentPage === 'index.html' || currentPage === '' || currentPath.endsWith('/')) {
    targetNav = 'home';
  } else if (currentPage === 'search.html') {
    targetNav = 'search';
  } else if (currentPage === 'categories.html') {
    targetNav = 'categories';
  } else if (currentPage === 'trusted-finder.html') {
    targetNav = 'trusted-finder';
  }
  // Note: account.html does NOT highlight any navbar item (only sidebar Dashboard)
  
  // Add active class to the appropriate nav link
  if (targetNav) {
    const activeLink = document.querySelector(`.nav-link[data-nav="${targetNav}"]`);
    if (activeLink) {
      activeLink.classList.add('active');
    }
  }
}

// Global vote handler for inline onclicks (placed at end to avoid syntax issues)
// Track voting in progress to prevent duplicate clicks
window.votingInProgress = window.votingInProgress || new Set();

window.handleVoteClick = async function(productId, direction) {
  // Prevent multiple simultaneous votes on the same product
  if (window.votingInProgress.has(productId)) {
    logger.info('Vote already in progress for', productId);
    return false;
  }

  let userId = window.accountManager ? window.accountManager.currentUser && window.accountManager.currentUser.id : null;
  // On full page load, currentUser may not be set yet; try to establish session first
  if ((!userId || userId === 'anonymous') && window.apiService && typeof window.apiService.checkAuth === 'function') {
    try {
      const ok = await window.apiService.checkAuth();
      if (ok && window.accountManager) {
        if (!window.accountManager.currentUser && typeof window.apiService.getCurrentUser === 'function') {
          const userData = await window.apiService.getCurrentUser();
          if (userData && userData.id) {
            window.accountManager.currentUser = userData;
          }
        }
        userId = window.accountManager.currentUser ? window.accountManager.currentUser.id : null;
      }
    } catch (e) {
      // ignore
    }
  }
  if (!userId || userId === 'anonymous') {
    alert('Please log in to vote.');
    return false;
  }

  if (!window.voteManager) {
    logger.error('VoteManager not available');
    return false;
  }

  // Get current vote state
  const currentVote = window.voteManager.getUserVote(productId, userId);
  const isUpvoted = currentVote === 'up';
  const isDownvoted = currentVote === 'down';

  // Disable vote buttons during voting
  const productCard = document.querySelector(`[data-product-card-id="${escapeHtml(productId)}"]`);
  const upvoteBtn = productCard?.querySelector('.vote-btn.upvote');
  const downvoteBtn = productCard?.querySelector('.vote-btn.downvote');
  
  if (upvoteBtn) upvoteBtn.disabled = true;
  if (downvoteBtn) downvoteBtn.disabled = true;

  try {
    window.votingInProgress.add(productId);
    
    let success = false;
    
    // If clicking the same vote button, remove the vote
    if ((direction === 'up' && isUpvoted) || (direction === 'down' && isDownvoted)) {
      success = await window.voteManager.removeVote(productId, userId);
    } else {
      // Otherwise, record the vote (or change from up to down or vice versa)
      success = await window.voteManager.vote(productId, userId, direction);
    }

    if (success) {
      // Re-render the product card to update vote buttons and scores
      const item = searchData.items.find(i => (i.productId || i.name) === productId);
      if (item && productCard) {
        const newHTML = renderProductCard(item);
        if (newHTML) productCard.outerHTML = newHTML;
      }
      // Refresh sidebar so user's vote count updates in real time (force refresh bypasses cooldown)
      if (typeof window.updateSidebarProfile === 'function') {
        window.updateSidebarProfile(0, { forceRefresh: true });
      }
    }

    return success;
  } catch (error) {
    logger.error('Error in handleVoteClick:', error);
    // Re-enable buttons on error
    if (upvoteBtn) upvoteBtn.disabled = false;
    if (downvoteBtn) downvoteBtn.disabled = false;
    return false;
  } finally {
    // Remove from voting in progress set
    window.votingInProgress.delete(productId);
  }
};

// Ensure VoteManager is global
if (typeof VoteManager !== 'undefined') {
  window.voteManager = new VoteManager();
}

// Note: Managers are available via window.* after DOMContentLoaded
// No export needed as nothing imports from this file